// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
  baseURL: `${string}://${string}/api` | (string & {});
};

export const AddonHealthStatus = {
  /**
   * AddonHealthUnknown
   */
  ADDON_HEALTH_UNKNOWN: 'unknown',
  /**
   * AddonHealthHealthy
   */
  ADDON_HEALTH_HEALTHY: 'healthy',
  /**
   * AddonHealthUnhealthy
   */
  ADDON_HEALTH_UNHEALTHY: 'unhealthy',
  /**
   * AddonHealthCredentialInvalid
   */
  ADDON_HEALTH_CREDENTIAL_INVALID: 'credential-invalid',
} as const;

export type AddonHealthStatus =
  (typeof AddonHealthStatus)[keyof typeof AddonHealthStatus];

export const AddonLifecycleStatus = {
  /**
   * AddonStatusInstalling
   */
  ADDON_STATUS_INSTALLING: 'installing',
  /**
   * AddonStatusUpgrading
   */
  ADDON_STATUS_UPGRADING: 'upgrading',
  /**
   * AddonStatusInstalled
   */
  ADDON_STATUS_INSTALLED: 'installed',
  /**
   * AddonStatusFailed
   */
  ADDON_STATUS_FAILED: 'failed',
  /**
   * AddonStatusUnknown
   */
  ADDON_STATUS_UNKNOWN: 'unknown',
  /**
   * AddonStatusUninstalling
   */
  ADDON_STATUS_UNINSTALLING: 'uninstalling',
} as const;

export type AddonLifecycleStatus =
  (typeof AddonLifecycleStatus)[keyof typeof AddonLifecycleStatus];

export type AddonsAddonAccessResponse = {
  enabled?: boolean;
  id?: string;
  teamAccessPolicies?: PortainerTeamAccessPolicies;
  userAccessPolicies?: PortainerUserAccessPolicies;
};

export type AddonsAddonAccessUpdatePayload = {
  /**
   * Enable or disable the addon; omit to leave the stored value unchanged
   */
  enabled?: boolean;
  /**
   * Teams granted access to the addon; omit to leave the stored policies
   * unchanged, send an empty object to revoke all team grants
   */
  teamAccessPolicies?: PortainerTeamAccessPolicies;
  /**
   * Users granted access to the addon; omit to leave the stored policies
   * unchanged, send an empty object to revoke all user grants
   */
  userAccessPolicies?: PortainerUserAccessPolicies;
};

export type AddonsAddonEnvironmentResponse = {
  /**
   * EnvironmentID is the environment verified to point at Portainer's own
   * cluster. Zero when none could be: the addon runs, it just cannot be read back.
   */
  environmentId?: number;
};

export type AddonsAddonLifecycleResponse = {
  chartVersion?: string;
  enabled?: boolean;
  id?: string;
  lifecycleStatus?: AddonLifecycleStatus;
  lifecycleStatusMessage?: string;
};

export type AddonsAddonListItem = {
  description?: string;
  displayName?: string;
  /**
   * Enabled reports whether the addon is enabled (installed and switched on).
   */
  enabled?: boolean;
  /**
   * HealthMessage explains an "unhealthy" result (the HTTP status the probe
   * got, or why the endpoint could not be reached). Empty otherwise.
   */
  healthMessage?: string;
  /**
   * HealthStatus is whether the running addon is actually working, observed
   * live from an HTTP probe of the addon's service each time this endpoint is
   * called (never persisted). It is orthogonal to LifecycleStatus: an addon
   * can be "installed" yet unhealthy, or "upgrading" while still serving the
   * previous version. "unknown" means an operation is in progress so the addon
   * is not probed; the field is empty for an addon with no live release. Like
   * LifecycleStatus, it is visible to every user who can see the addon.
   */
  healthStatus?: AddonHealthStatus;
  icon?: string;
  id?: string;
  /**
   * LifecycleStatus is the addon's Helm release lifecycle state: installing,
   * upgrading, installed, failed or uninstalling. It reports what Portainer's
   * install/upgrade/uninstall operations have done, and is visible to every
   * user who can see the addon (not just admins, unlike Record). It is empty
   * when the addon has no record, i.e. it was never installed.
   *
   * It is NOT a health status. Whether the running addon actually works (pods
   * ready, endpoint responding) is a separate field added later, because the
   * two are independent: an addon can be "installed" but unhealthy, or
   * "upgrading" while still serving healthy traffic on the previous version.
   */
  lifecycleStatus?: AddonLifecycleStatus;
  /**
   * LifecycleStatusMessage explains a failure: why a fresh install failed, or
   * (when LifecycleStatus is "installed") that the last upgrade failed and was
   * rolled back to the version still running. Empty on a clean install/upgrade.
   */
  lifecycleStatusMessage?: string;
  path?: string;
  /**
   * Record is admin-only: absent from the JSON response for non-admin users.
   */
  record?: AddonsAddonRecord;
  /**
   * ShortDescription is a one-line tagline for space-constrained UI (the app
   * switcher); Description is the full summary shown in management views.
   */
  shortDescription?: string;
};

export type AddonsAddonListResponse = {
  addons?: Array<AddonsAddonListItem>;
  /**
   * CatalogError explains why the addon catalog could not be refreshed. With
   * addons present the list is a cached copy; with none, nothing could be
   * loaded. Admin-only, and never set on the switcher view.
   */
  catalogError?: string;
};

export type AddonsAddonRecord = {
  /**
   * AvailableVersions lists the stable chart versions published for the addon,
   * ordered latest-first (the head is the newest). Empty (and omitted) when the
   * lookup could not run; see VersionCheckError.
   */
  availableVersions?: Array<string>;
  /**
   * ChartPath is the chart's repository path within RegistryID. Empty when
   * RegistryID is 0.
   */
  chartPath?: string;
  /**
   * ChartVersion is the currently installed chart version, from the persisted
   * record (kept current by the install/upgrade lifecycle).
   */
  chartVersion?: string;
  /**
   * HelmChartRepository is the catalog's public OCI chart reference: the default
   * chart source, offered by the install wizard when the addon is not pinned to
   * a Portainer registry.
   */
  helmChartRepository?: string;
  /**
   * ImageRegistryID is the Portainer registry whose credentials the addon's pods
   * pull their container image with, or 0 when the image needs none.
   */
  imageRegistryId?: number;
  /**
   * RegistryID is the Portainer registry the addon's chart is pulled from, or 0
   * when it comes from HelmChartRepository. Recorded at install time and fixed
   * for the life of the release.
   */
  registryId?: number;
  teamAccessPolicies?: PortainerTeamAccessPolicies;
  /**
   * UpgradeAvailable is true when a version newer than the installed ChartVersion
   * is available.
   */
  upgradeAvailable?: boolean;
  userAccessPolicies?: PortainerUserAccessPolicies;
  /**
   * VersionCheckError explains why available-version information is absent, so a
   * degraded lookup (e.g. the registry is unreachable, or external requests are
   * disabled) is surfaced rather than silently reported as "no upgrade".
   */
  versionCheckError?: string;
};

export type AddonsChartSourceStatusResponse = {
  /**
   * Reachable reports whether the registry answered. A registry that answered
   * but holds no such chart is reachable, with no versions.
   */
  reachable?: boolean;
  /**
   * Reason explains an unreachable source. Empty when Reachable.
   */
  reason?: string;
  /**
   * TLSVerify reports whether the certificate the registry served verified
   * against the trust store. It is false only where a certificate did not
   * verify and the source allows that, so the chart was accepted from whoever
   * answered — not merely because verification was allowed to be skipped.
   */
  tlsVerify?: boolean;
  /**
   * Versions are the chart's published stable versions, ordered latest-first.
   */
  versions?: Array<string>;
};

export type AddonsConfigEntryPayload = {
  key?: string;
  /**
   * Sensitive marks the value as secret so a client displays it carefully.
   */
  sensitive?: boolean;
  /**
   * Omit for a sensitive entry to keep the stored value; omitting for a
   * non-sensitive one is an error. Null means the same but generated clients
   * cannot send it (the schema types this as a plain string).
   */
  value?: string;
};

export type AddonsConfigEntryResponse = {
  key?: string;
  /**
   * Sensitive marks the value as secret so a client displays it carefully.
   */
  sensitive?: boolean;
  /**
   * Returned in full, secrets included: these endpoints are admin-only and the addon
   * reads its settings back through them. A plain string, not a pointer, because
   * Swagger 2.0 has no nullable string and a null fails validating clients.
   */
  value?: string;
};

export type AddonsConfigEntryUpdatePayload = {
  /**
   * Sensitive marks the value as secret so a client displays it carefully.
   */
  sensitive?: boolean;
  /**
   * Omit for a sensitive entry to keep the stored value; omitting for a
   * non-sensitive one is an error. Same rules as configEntryPayload.Value, so
   * the two write paths agree on what an omitted value means.
   */
  value?: string;
};

export type AddonsConfigMutationResponse = {
  entries?: Array<AddonsConfigEntryResponse>;
};

export type AddonsConfigResponse = {
  entries?: Array<AddonsConfigEntryResponse>;
};

export type AddonsConfigUpdatePayload = {
  entries?: Array<AddonsConfigEntryPayload>;
};

export type AddonsInstallPayload = {
  /**
   * Chart is the chart's repository path within RegistryID (e.g.
   * "portainer/charts/portainer-run"). Required with RegistryID.
   */
  chart?: string;
  /**
   * ImageRegistryID is the Portainer registry whose credentials the addon's pods
   * use to pull their container image. Send 0 for an image that needs none.
   * Unlike RegistryID it may be changed on upgrade, since the image reference is
   * an editable chart value; omitting it keeps the registry the addon is
   * currently installed with.
   */
  imageRegistryId?: number;
  /**
   * RegistryID is the Portainer registry to pull the chart from. Omit to use the
   * catalog's public chart reference. Only honoured on a first-time install: an
   * installed addon keeps the source it was installed from.
   */
  registryId?: number;
  /**
   * Helm value overrides merged over the server-side per-addon defaults.
   */
  values?: {
    [key: string]: unknown;
  };
  /**
   * Chart version to install; omit to resolve the latest published version.
   */
  version?: string;
};

export type AllowlistAllowListUpdatePayload = {
  Entries?: Array<string>;
  Mode?: PortainerSsrfMode;
};

export type AuthAuthenticatePayload = {
  /**
   * API Key
   */
  apiKey: string;
  /**
   * Password
   */
  password: string;
  /**
   * Username
   */
  username: string;
};

export type AuthAuthenticateResponse = {
  /**
   * JWT token used to authenticate against the API
   */
  jwt?: string;
};

export type AuthOauthPayload = {
  /**
   * OAuth code returned from OAuth Provided
   */
  Code?: string;
};

export type AutoupdatesAutoUpdateResponseItem = {
  doneAtUnix?: number;
  startedAtUnix?: number;
  status?: PortainereeAutoUpdateStatus;
  version?: string;
};

export type AutoupdatesListResponse = {
  autoUpdates?: Array<AutoupdatesAutoUpdateResponseItem>;
};

export type BackupAzureBackupStatus = {
  Failed?: boolean;
  TimestampUTC?: string;
};

export type BackupBackupPayload = {
  Password?: string;
};

export type BackupBackupStatus = {
  Failed?: boolean;
  TimestampUTC?: string;
};

export type BackupLocalBackupStatus = {
  failed?: boolean;
  path?: string;
  timestampUTC?: string;
};

export type BackupRestoreAzureSettings = {
  AuthMethod?: PortainereeAzureAuthMethod;
  BlobName?: string;
  ClientCertificate?: string;
  ClientCertificatePassword?: string;
  ClientID?: string;
  ClientSecret?: string;
  ContainerName?: string;
  ManagedIdentityClientID?: string;
  Password?: string;
  ServiceURL?: string;
  StorageAccountKey?: string;
  StorageAccountName?: string;
  TenantID?: string;
};

export type BackupRestorePayload = {
  /**
   * Content of the backup
   */
  FileContent: Array<number>;
  /**
   * File name
   */
  FileName: string;
  /**
   * Password to decrypt the backup with
   */
  Password?: string;
};

export type BackupRestoreS3Settings = {
  /**
   * AWS access key id
   */
  AccessKeyID?: string;
  /**
   * AWS S3 bucket name
   */
  BucketName?: string;
  /**
   * AWS S3 filename in the bucket
   */
  Filename?: string;
  Password?: string;
  /**
   * AWS S3 region. Default to "us-east-1"
   */
  Region?: string;
  /**
   * S3 compatible host
   */
  S3CompatibleHost?: string;
  /**
   * AWS secret access key
   */
  SecretAccessKey?: string;
};

export type BackupS3BackupPayload = {
  /**
   * AWS access key id
   */
  accessKeyID?: string;
  /**
   * AWS S3 bucket name
   */
  bucketName?: string;
  /**
   * Crontab rule to make periodical backups
   */
  cronRule?: string;
  /**
   * Password to encrypt the backup with
   */
  password?: string;
  /**
   * AWS S3 region. Default to "us-east-1"
   */
  region?: string;
  /**
   * S3 compatible host
   */
  s3CompatibleHost?: string;
  /**
   * AWS secret access key
   */
  secretAccessKey?: string;
};

export type BuildBuildInfo = {
  BuildNumber?: string;
  GitCommit?: string;
  GoVersion?: string;
  ImageTag?: string;
  NodejsVersion?: string;
  PnpmVersion?: string;
  WebpackVersion?: string;
};

export type BuildDependenciesInfo = {
  ComposeVersion?: string;
  DockerVersion?: string;
  HelmVersion?: string;
  KubectlVersion?: string;
};

export type BuildRuntimeInfo = {
  Env?: Array<string>;
};

export const ContainerContainerState = {
  /**
   * StateCreated
   *
   * StateCreated indicates the container is created, but not (yet) started.
   */
  STATE_CREATED: 'created',
  /**
   * StateRunning
   *
   * StateRunning indicates that the container is running.
   */
  STATE_RUNNING: 'running',
  /**
   * StatePaused
   *
   * StatePaused indicates that the container's current state is paused.
   */
  STATE_PAUSED: 'paused',
  /**
   * StateRestarting
   *
   * StateRestarting indicates that the container is currently restarting.
   */
  STATE_RESTARTING: 'restarting',
  /**
   * StateRemoving
   *
   * StateRemoving indicates that the container is being removed.
   */
  STATE_REMOVING: 'removing',
  /**
   * StateExited
   *
   * StateExited indicates that the container exited.
   */
  STATE_EXITED: 'exited',
  /**
   * StateDead
   *
   * StateDead indicates that the container failed to be deleted. Containers in this state are attempted to be cleaned up when the daemon restarts.
   */
  STATE_DEAD: 'dead',
} as const;

export type ContainerContainerState =
  (typeof ContainerContainerState)[keyof typeof ContainerContainerState];

export type ContainerMountPoint = {
  /**
   * Destination is the path relative to the container root (`/`) where the
   * Source is mounted inside the container.
   */
  Destination?: string;
  /**
   * Driver is the volume driver used to create the volume (if it is a volume).
   */
  Driver?: string;
  /**
   * Mode is a comma separated list of options supplied by the user when
   * creating the bind/volume mount.
   *
   * The default is platform-specific (`"z"` on Linux, empty on Windows).
   */
  Mode?: string;
  /**
   * Name is the name reference to the underlying data defined by `Source`
   * e.g., the volume name.
   */
  Name?: string;
  /**
   * Propagation describes how mounts are propagated from the host into the
   * mount point, and vice-versa. Refer to the Linux kernel documentation
   * for details:
   * https://www.kernel.org/doc/Documentation/filesystems/sharedsubtree.txt
   *
   * This field is not used on Windows.
   */
  Propagation?: MountPropagation;
  /**
   * RW indicates whether the mount is mounted writable (read-write).
   */
  RW?: boolean;
  /**
   * Source is the source location of the mount.
   *
   * For volumes, this contains the storage location of the volume (within
   * `/var/lib/docker/volumes/`). For bind-mounts, and `npipe`, this contains
   * the source (host) part of the bind-mount. For `tmpfs` mount points, this
   * field is empty.
   */
  Source?: string;
  /**
   * Type is the type of mount, see `Type<foo>` definitions in
   * github.com/docker/docker/api/types/mount.Type
   */
  Type?: MountType;
};

export type ContainerNetworkSettingsSummary = {
  Networks?: {
    [key: string]: NetworkEndpointSettings;
  };
};

export type ContainerPort = {
  /**
   * Host IP address that the container's port is mapped to
   */
  IP?: string;
  /**
   * Port on the container
   * Required: true
   */
  PrivatePort?: number;
  /**
   * Port exposed on the host
   */
  PublicPort?: number;
  /**
   * type
   * Required: true
   */
  Type?: string;
};

export type ContainersContainerGpusResponse = {
  gpus?: string;
};

export type DockerDashboardData = {
  containers?: StatsContainerStats;
  images?: DockerImagesCounters;
  networks?: number;
  services?: number;
  stacks?: number;
  volumes?: number;
};

export type DockerImagesCounters = {
  size?: number;
  total?: number;
};

export type EdgeconfigsEdgeConfigCreatePayload = {
  BaseDir?: string;
  Category?: PortainereeEdgeConfigCategory;
  EdgeGroupIDs?: Array<number>;
  Name?: string;
  Type?: string;
};

export type EdgegroupsDecoratedEdgeGroup = {
  Dynamic?: boolean;
  EdgeUpdateID?: number;
  /**
   * Shadow to avoid exposing in the API
   */
  EndpointIds?: number;
  EndpointTypes?: Array<PortainerEndpointType>;
  /**
   * Deprecated: only used for API responses
   */
  Endpoints?: Array<number>;
  EndpointsCount?: number;
  HasEdgeConfig?: boolean;
  HasEdgeJob?: boolean;
  HasEdgeStack?: boolean;
  /**
   * EdgeGroup Identifier
   */
  Id?: number;
  Name?: string;
  PartialMatch?: boolean;
  TagIds?: Array<number>;
  TrustedEndpoints?: Array<number>;
  TrustedEndpointsCount?: number;
};

export type EdgegroupsEdgeGroupCreatePayload = {
  Dynamic?: boolean;
  Endpoints?: Array<number>;
  Name?: string;
  PartialMatch?: boolean;
  TagIDs?: Array<number>;
};

export type EdgegroupsEdgeGroupUpdatePayload = {
  Dynamic?: boolean;
  Endpoints?: Array<number>;
  Name?: string;
  PartialMatch?: boolean;
  TagIDs?: Array<number>;
};

export type EdgejobsEdgeJobCreateFromFileContentPayload = {
  CronExpression?: string;
  EdgeGroups?: Array<number>;
  Endpoints?: Array<number>;
  FileContent?: string;
  Name?: string;
  Recurring?: boolean;
};

export type EdgejobsEdgeJobFileResponse = {
  FileContent?: string;
};

export type EdgejobsEdgeJobUpdatePayload = {
  CronExpression?: string;
  EdgeGroups?: Array<number>;
  Endpoints?: Array<number>;
  FileContent?: string;
  Name?: string;
  Recurring?: boolean;
};

export type EdgejobsFileResponse = {
  FileContent?: string;
};

export type EdgejobsTaskContainer = {
  EndpointId?: number;
  EndpointName?: string;
  Id?: string;
  LogsStatus?: PortainerEdgeJobLogsStatus;
};

export const EdgestacksSummarizedStatus = {
  /**
   * sumStatusUnavailable
   */
  SUM_STATUS_UNAVAILABLE: 'Unavailable',
  /**
   * sumStatusDeploying
   */
  SUM_STATUS_DEPLOYING: 'Deploying',
  /**
   * sumStatusFailed
   */
  SUM_STATUS_FAILED: 'Failed',
  /**
   * sumStatusPaused
   */
  SUM_STATUS_PAUSED: 'Paused',
  /**
   * sumStatusPartiallyRunning
   */
  SUM_STATUS_PARTIALLY_RUNNING: 'PartiallyRunning',
  /**
   * sumStatusCompleted
   */
  SUM_STATUS_COMPLETED: 'Completed',
  /**
   * sumStatusRunning
   */
  SUM_STATUS_RUNNING: 'Running',
} as const;

export type EdgestacksSummarizedStatus =
  (typeof EdgestacksSummarizedStatus)[keyof typeof EdgestacksSummarizedStatus];

export type EdgestacksDeployerOptionsPayload = {
  Prune?: boolean;
  RemoveVolumes?: boolean;
};

export type EdgestacksEdgeStackFromHelmRepositoryPayload = {
  /**
   * List of identifiers of EdgeGroups
   */
  EdgeGroups?: Array<number>;
  /**
   * Helm repository configuration. ChartURL and ChartName are required.
   */
  HelmConfig?: EdgestacksHelmRepositoryConfig;
  /**
   * Name of the stack
   * Max length: 255
   * Name must only contains lowercase characters, numbers, hyphens, or underscores
   * Name must start with a lowercase character or number
   * Example: stack-name or stack_123 or stack123_name
   */
  Name: string;
  /**
   * Configuration for stagger updates
   */
  StaggerConfig?: PortainereeEdgeStaggerConfig;
};

export type EdgestacksEdgeStackStaggerStatusResponse = {
  status?: string;
};

export type EdgestacksGitHelmConfig = {
  /**
   * Atomic enables automatic rollback on deployment failure (equivalent to helm --atomic).
   */
  Atomic?: boolean;
  /**
   * ChartPath is the path to the Helm chart folder within the cloned git repository. Required.
   */
  ChartPath?: string;
  /**
   * Namespace is the Kubernetes namespace to deploy the Helm chart into.
   */
  Namespace?: string;
  /**
   * Timeout sets the deadline for Helm operations (equivalent to helm --timeout, e.g. "5m0s").
   */
  Timeout?: string;
  /**
   * ValuesFiles is a list of relative paths to Helm values YAML files within the cloned git repository.
   */
  ValuesFiles?: Array<string>;
};

export type EdgestacksHelmRepositoryConfig = {
  /**
   * Atomic enables automatic rollback on deployment failure (equivalent to helm --atomic).
   */
  Atomic?: boolean;
  /**
   * ChartName is the name of the Helm chart within the repository. Required.
   */
  ChartName?: string;
  /**
   * ChartURL is the URL of the Helm chart repository. Required.
   */
  ChartURL?: string;
  /**
   * ChartVersion is the version of the Helm chart to deploy. Empty means latest.
   */
  ChartVersion?: string;
  /**
   * Namespace is the Kubernetes namespace to deploy the Helm chart into.
   */
  Namespace?: string;
  /**
   * Timeout sets the deadline for Helm operations (equivalent to helm --timeout, e.g. "5m0s").
   */
  Timeout?: string;
  /**
   * ValuesInline is an inline YAML string of Helm values to apply.
   */
  ValuesInline?: string;
};

export type EdgestacksStackGitUpdatePayload = {
  /**
   * Deprecated. to update auth, update the related Source instead
   */
  Authentication?: GithubComPortainerPortainerEeApiGitTypesGitAuthentication;
  AutoUpdate?: PortainerAutoUpdateSettings;
  /**
   * Options to control the deployer behaviour
   */
  DeployerOptions?: EdgestacksDeployerOptionsPayload;
  DeploymentType?: PortainerEdgeStackDeploymentType;
  EnvVars?: Array<PortainerPair>;
  GroupIds?: Array<number>;
  /**
   * HelmConfig contains Helm-specific update options. Only applies to Helm git deployments.
   */
  HelmConfig?: EdgestacksGitHelmConfig;
  PrePullImage?: boolean;
  /**
   * Deprecated(2.36): to be removed in future versions (2.44+)
   * Use RepullImageAndRedeploy instead
   */
  RePullImage?: boolean;
  RefName?: string;
  /**
   * List of Registries to use for this stack
   */
  Registries?: Array<number>;
  /**
   * RepullImageAndRedeploy indicates whether the edge stack is manually forced to redeploy
   */
  RepullImageAndRedeploy?: boolean;
  RetryDeploy?: boolean;
  /**
   * RetryPeriod specifies the duration, in seconds, for which the agent should continue attempting to deploy the stack after a failure
   */
  RetryPeriod?: number;
  /**
   * Configuration for stagger updates
   */
  StaggerConfig?: PortainereeEdgeStaggerConfig;
  /**
   * Update the stack file content from the git repository
   * If this is set to true, it indicates that the stack is being redeployed (Pull and update stack),
   * if it is false, it indicates that the stack is being updated (Update settings)
   */
  UpdateVersion?: boolean;
};

export type EdgestacksUpdateEdgeStackHelmRepositoryPayload = {
  /**
   * List of identifiers of EdgeGroups
   */
  EdgeGroups?: Array<number>;
  /**
   * Helm repository configuration to update. ChartURL and ChartName are required.
   */
  HelmConfig?: EdgestacksHelmRepositoryConfig;
  /**
   * Configuration for stagger updates
   */
  StaggerConfig?: PortainereeEdgeStaggerConfig;
};

export type EdgeupdateschedulesActiveSchedulePayload = {
  EnvironmentIDs?: Array<number>;
};

export type EdgeupdateschedulesCreatePayload = {
  /**
   * default to "" == portainer/agent:<current_server_version>
   */
  AgentImage?: string;
  GroupIDs?: Array<number>;
  Name?: string;
  /**
   * default to 0 = dockerhub
   */
  RegistryID?: number;
  ScheduledTime?: string;
  Type?: TypesUpdateScheduleType;
  /**
   * default to "" == portainer/portainer-updater:<current_server_version>
   */
  UpdaterImage?: string;
};

export type EdgeupdateschedulesDecoratedUpdateSchedule = {
  /**
   * Name of the agent image, does not include the registry
   */
  agentImage?: string;
  /**
   * Created timestamp
   */
  created?: number;
  /**
   * Created by user id
   */
  createdBy?: number;
  edgeGroupIds?: Array<number>;
  edgeStackId?: number;
  /**
   * Deprecated - use Environment.Agent.PreviousVersion instead
   */
  environmentsPreviousVersions?: {
    [key: string]: string;
  };
  /**
   * EdgeUpdateSchedule Identifier
   */
  id?: number;
  /**
   * Name of the schedule
   */
  name?: string;
  /**
   * ID of registry
   */
  registryId?: number;
  scheduledTime?: string;
  status?: TypesUpdateScheduleStatusType;
  statusMessage?: string;
  /**
   * Type of the update (1 - update, 2 - rollback)
   */
  type?: 1 | 2;
  /**
   * Updated timestamp
   */
  updated?: number;
  /**
   * Name of the updater image, does not include the registry but must include a tag
   */
  updaterImage?: string;
  /**
   * Deprecated
   */
  version?: string;
};

export type EdgeupdateschedulesInfoResponse = {
  HasLocalTimeZone?: boolean;
  HasNoLocalTimeZone?: boolean;
  MinAgentVersion?: string;
  OutdatedCount?: number;
  UpToDateCount?: number;
};

export type EdgeupdateschedulesInspectResponse = {
  /**
   * Name of the agent image, does not include the registry
   */
  agentImage?: string;
  /**
   * Created timestamp
   */
  created?: number;
  /**
   * Created by user id
   */
  createdBy?: number;
  /**
   * EdgeGroups to be updated
   *
   * There is some duplication here with EdgeStack.EdgeGroups
   * EdgeStack.EdgeGroup should have only one group which a temporary group, used only for the update
   * This field is saved only to show which groups the user chose when creating the schedule
   */
  edgeGroupIds?: Array<number>;
  edgeStackId?: number;
  environmentIds?: Array<number>;
  /**
   * Deprecated - use Environment.Agent.PreviousVersion instead
   */
  environmentsPreviousVersions?: {
    [key: string]: string;
  };
  /**
   * EdgeUpdateSchedule Identifier
   */
  id?: number;
  isActive?: boolean;
  /**
   * Name of the schedule
   */
  name?: string;
  /**
   * ID of registry
   */
  registryId?: number;
  scheduledTime?: string;
  /**
   * Type of the update (1 - update, 2 - rollback)
   */
  type?: 1 | 2;
  /**
   * Updated timestamp
   */
  updated?: number;
  /**
   * Name of the updater image, does not include the registry but must include a tag
   */
  updaterImage?: string;
  /**
   * Deprecated
   */
  version?: string;
};

export type EdgeupdateschedulesUpdatePayload = {
  /**
   * default to "" == portainer/agent:<current_server_version>
   */
  AgentImage?: string;
  GroupIDs?: Array<number>;
  Name?: string;
  /**
   * default to 0 = dockerhub
   */
  RegistryID?: number;
  ScheduledTime?: string;
  Type?: TypesUpdateScheduleType;
  /**
   * default to "" == portainer/portainer-updater:latest
   */
  UpdaterImage?: string;
};

export type EndpointedgeEndpointTrustUpdateRelation = {
  EdgeGroups?: Array<number>;
  Group?: number;
  Tags?: Array<number>;
};

export type EndpointedgeEndpointsTrustPayload = {
  EndpointIDs?: Array<number>;
  Relations?: {
    [key: string]: EndpointedgeEndpointTrustUpdateRelation;
  };
};

export type EndpointedgeGenerateKeyResponse = {
  edgeKey?: string;
};

export type EndpointedgePolicyStatusesPayload = {
  statuses?: Array<PortainerPolicyActualState>;
};

export type EndpointgroupsEndpointGroupTypeInfo = {
  Docker: number;
  Kubernetes: number;
  Mixed: boolean;
  Podman: number;
};

export type EndpointgroupsEndpointGroupResponse = {
  /**
   * Description associated to the environment(endpoint) group
   */
  Description: string;
  /**
   * Environment(Endpoint) group Identifier
   */
  Id: number;
  /**
   * Environment(Endpoint) group name
   */
  Name: string;
  Policies: Array<PoliciesGenericPolicy>;
  /**
   * List of tags associated to this environment(endpoint) group
   */
  TagIds?: Array<number>;
  TeamAccessPolicies?: PortainerTeamAccessPolicies;
  Total?: number;
  TypeInfo?: EndpointgroupsEndpointGroupTypeInfo;
  UserAccessPolicies?: PortainerUserAccessPolicies;
};

export type EndpointsEnvironmentSummaryCountsResponse = {
  byGroup?: Array<EndpointsGroupCount>;
  byHealth?: EndpointsHealthCounts;
  byPlatformType?: EndpointsPlatformCounts;
  down?: number;
  outdated?: number;
  total?: number;
  unassigned?: number;
  up?: number;
};

export type EndpointsAgentResponse = {
  IsOutdated?: boolean;
  PreviousVersion?: string;
  Version?: string;
};

export type EndpointsDockerhubStatusResponse = {
  /**
   * Daily limit
   */
  limit?: number;
  /**
   * Remaiming images to pull
   */
  remaining?: number;
};

export type EndpointsEndpointCreateGlobalKeyResponse = {
  endpointID?: number;
};

export type EndpointsEndpointDeleteBatchPartialResponse = {
  deleted?: Array<number>;
  errors?: Array<number>;
};

export type EndpointsEndpointDeleteBatchPayload = {
  endpoints?: Array<EndpointsEndpointDeleteRequest>;
};

export type EndpointsEndpointDeleteRequest = {
  deleteCluster?: boolean;
  id?: number;
};

export type EndpointsEndpointMtlsCertResponse = {
  /**
   * MTLSCertificate is the X.509 Certificate of the MTLS Certificate
   */
  MTLSCertificate?: SslCertificate;
};

export type EndpointsEndpointResponse = {
  Agent: EndpointsAgentResponse;
  AzureCredentials?: PortainerAzureCredentials;
  /**
   * GitOps update change window restriction for stacks and apps
   */
  ChangeWindow: PortainereeEndpointChangeWindow;
  /**
   * A Kubernetes as a service cloud provider. Only included if this
   * endpoint was created using KaaS provisioning.
   */
  CloudProvider?: PortainereeCloudProvider;
  /**
   * Maximum version of docker-compose
   */
  ComposeSyntaxMaxVersion: string;
  /**
   * ContainerEngine represents the container engine type. This can be 'docker' or 'podman' when interacting directly with these environments, otherwise '' for kubernetes environments.
   */
  ContainerEngine: string;
  /**
   * Hide manual deployment forms for an environment
   */
  DeploymentOptions?: PortainereeDeploymentOptions;
  Edge: PortainerEnvironmentEdgeSettings;
  /**
   * The check in interval for edge agent (in seconds)
   */
  EdgeCheckinInterval: number;
  /**
   * The identifier of the edge agent associated with this environment(endpoint)
   */
  EdgeID?: string;
  /**
   * The key which is used to map the agent to Portainer
   */
  EdgeKey: string;
  EnableGPUManagement?: boolean;
  EnableImageNotification: boolean;
  Gpus?: Array<PortainerPair>;
  /**
   * Environment(Endpoint) group identifier
   */
  GroupId: number;
  /**
   * Heartbeat indicates the heartbeat status of an edge environment
   */
  Heartbeat?: boolean;
  /**
   * Environment(Endpoint) Identifier
   */
  Id: number;
  /**
   * Associated Kubernetes data
   */
  Kubernetes: PortainereeKubernetesData;
  /**
   * LastCheckInDate mark last check-in date on checkin
   */
  LastCheckInDate: number;
  /**
   * LocalTimeZone is the local time zone of the endpoint
   */
  LocalTimeZone: string;
  MTLSStatus?: PortainereeMtlsStatus;
  /**
   * Environment(Endpoint) name
   */
  Name: string;
  Policies?: PortainereeEndpointPolicies;
  /**
   * URL or IP address where exposed containers will be reachable
   */
  PublicURL: string;
  /**
   * Environment(Endpoint) specific security settings
   */
  SecuritySettings: PortainerEndpointSecuritySettings;
  /**
   * List of snapshots
   */
  Snapshots?: Array<PortainerDockerSnapshot>;
  /**
   * The status of the environment(endpoint) (1 - up, 2 - down, 3 - provisioning, 4 - error)
   */
  Status?: 1 | 2 | 3 | 4;
  /**
   * A message that describes the status. Should be included for Status 3
   * or 4.
   */
  StatusMessage: PortainereeEndpointStatusMessage;
  TLSConfig: PortainerTlsConfiguration;
  /**
   * List of tag identifiers to which this environment(endpoint) is associated
   */
  TagIds?: Array<number>;
  /**
   * List of team identifiers authorized to connect to this environment(endpoint)
   */
  TeamAccessPolicies?: PortainerTeamAccessPolicies;
  /**
   * Environment(Endpoint) environment(endpoint) type. 1 for a Docker environment(endpoint), 2 for an agent on Docker environment(endpoint) or 3 for an Azure environment(endpoint).
   */
  Type: PortainerEndpointType;
  /**
   * URL or IP address of the Docker host associated to this environment(endpoint)
   */
  URL: string;
  /**
   * List of user identifiers authorized to connect to this environment(endpoint)
   */
  UserAccessPolicies?: PortainerUserAccessPolicies;
  /**
   * Whether the device has been trusted or not by the user
   */
  UserTrusted?: boolean;
};

export type EndpointsEndpointSettingsUpdatePayload = {
  /**
   * Whether GitOps update time restrictions are enabled
   */
  changeWindow?: PortainereeEndpointChangeWindow;
  /**
   * Hide manual deployment forms for an environment
   */
  deploymentOptions?: PortainereeDeploymentOptions;
  enableGPUManagement?: boolean;
  enableImageNotification?: boolean;
  gpus?: Array<PortainerPair>;
  /**
   * Security settings updates
   */
  securitySettings?: {
    /**
     * Whether non-administrator should be able to use bind mounts when creating containers
     */
    allowBindMountsForRegularUsers?: boolean;
    /**
     * Whether non-administrator should be able to use container capabilities
     */
    allowContainerCapabilitiesForRegularUsers?: boolean;
    /**
     * Whether non-administrator should be able to use device mapping
     */
    allowDeviceMappingForRegularUsers?: boolean;
    /**
     * Whether non-administrator should be able to use the host pid
     */
    allowHostNamespaceForRegularUsers?: boolean;
    /**
     * Whether non-administrator should be able to use privileged mode when creating containers
     */
    allowPrivilegedModeForRegularUsers?: boolean;
    /**
     * Whether non-administrator should be able to use security-opt settings
     */
    allowSecurityOptForRegularUsers?: boolean;
    /**
     * Whether non-administrator should be able to manage stacks
     */
    allowStackManagementForRegularUsers?: boolean;
    /**
     * Whether non-administrator should be able to use sysctl settings
     */
    allowSysctlSettingForRegularUsers?: boolean;
    /**
     * Whether non-administrator should be able to browse volumes
     */
    allowVolumeBrowserForRegularUsers?: boolean;
    /**
     * Whether host management features are enabled
     */
    enableHostManagementFeatures?: boolean;
  };
};

export type EndpointsEndpointUpdatePayload = {
  /**
   * Azure application ID
   */
  AzureApplicationID?: string;
  /**
   * Azure authentication key
   */
  AzureAuthenticationKey?: string;
  /**
   * Azure tenant ID
   */
  AzureTenantID?: string;
  /**
   * Whether GitOps update time restrictions are enabled
   */
  ChangeWindow?: PortainereeEndpointChangeWindow;
  /**
   * Hide manual deployment forms for an environment
   */
  DeploymentOptions?: PortainereeDeploymentOptions;
  Edge?: {
    /**
     * The command list interval for edge agent - used in edge async mode (in seconds)
     */
    CommandInterval?: number;
    /**
     * The ping interval for edge agent - used in edge async mode (in seconds)
     */
    PingInterval?: number;
    /**
     * The snapshot interval for edge agent - used in edge async mode (in seconds)
     */
    SnapshotInterval?: number;
  };
  /**
   * The check in interval for edge agent (in seconds)
   */
  EdgeCheckinInterval?: number;
  /**
   * GPUs information
   */
  Gpus?: Array<PortainerPair>;
  /**
   * Group identifier
   */
  GroupID?: number;
  IsSetStatusMessage?: boolean;
  /**
   * Associated Kubernetes data
   */
  Kubernetes?: PortainereeKubernetesData;
  /**
   * Name that will be used to identify this environment(endpoint)
   */
  Name?: string;
  /**
   * URL or IP address where exposed containers will be reachable.\
   * Defaults to URL if not specified
   */
  PublicURL?: string;
  /**
   * The status of the environment(endpoint) (1 - up, 2 - down)
   */
  Status?: number;
  StatusMessage?: {
    detail?: string;
    /**
     * 'scale', 'upgrade', 'addons'
     */
    operation?: string;
    /**
     * '', 'error', 'processing'
     */
    operationStatus?: PortainereeEndpointOperationStatus;
    summary?: string;
  };
  /**
   * Require TLS to connect against this environment(endpoint)
   */
  TLS?: boolean;
  /**
   * Skip client verification when using TLS
   */
  TLSSkipClientVerify?: boolean;
  /**
   * Skip server verification when using TLS
   */
  TLSSkipVerify?: boolean;
  /**
   * List of tag identifiers to which this environment(endpoint) is associated
   */
  TagIDs?: Array<number>;
  TeamAccessPolicies?: PortainerTeamAccessPolicies;
  /**
   * URL or IP address of a Docker host
   */
  URL?: string;
  UserAccessPolicies?: PortainerUserAccessPolicies;
};

export type EndpointsEndpointUpdateRelation = {
  EdgeGroups?: Array<number>;
  Group?: number;
  Tags?: Array<number>;
};

export type EndpointsEndpointUpdateRelationsPayload = {
  Relations?: {
    [key: string]: EndpointsEndpointUpdateRelation;
  };
};

export type EndpointsForceUpdateServicePayload = {
  /**
   * PullImage if true will pull the image
   */
  PullImage?: boolean;
  /**
   * ServiceId to update
   */
  ServiceID?: string;
};

export type EndpointsGroupCount = {
  count?: number;
  groupID?: number;
  groupName?: string;
};

export type EndpointsHealthCounts = {
  down?: number;
  heartbeat?: number;
  outdated?: number;
  up?: number;
};

export type EndpointsPlatformCounts = {
  azure?: number;
  docker?: number;
  kubernetes?: number;
  podman?: number;
};

export type EndpointsRegistryAccessPayload = {
  Namespaces?: Array<string>;
  TeamAccessPolicies?: PortainerTeamAccessPolicies;
  UserAccessPolicies?: PortainerUserAccessPolicies;
};

export type EndpointsResourcePoolUpdatePayload = {
  TeamsToAdd?: Array<number>;
  TeamsToRemove?: Array<number>;
  UsersToAdd?: Array<number>;
  UsersToRemove?: Array<number>;
};

export type GitShadowedGitCredential = {
  /**
   * Shadow to include the AuthType even if it's empty
   */
  authorizationType?: GithubComPortainerPortainerEeApiGitTypesGitCredentialAuthType;
  creationDate?: number;
  id?: number;
  name?: string;
  password?: string;
  /**
   * Shadow to include Provider even if it's empty
   */
  provider?: GithubComPortainerPortainerEeApiGitTypesGitProvider;
  username?: string;
};

export const GithubComPortainerPortainerEeApiDockerImagesStatus = {
  /**
   * Processing
   */
  PROCESSING: 'processing',
  /**
   * Outdated
   */
  OUTDATED: 'outdated',
  /**
   * Updated
   */
  UPDATED: 'updated',
  /**
   * Skipped
   */
  SKIPPED: 'skipped',
  /**
   * Preparing
   */
  PREPARING: 'preparing',
  /**
   * Error
   */
  ERROR: 'error',
  PROCESSING2: 'processing',
  OUTDATED2: 'outdated',
  UPDATED2: 'updated',
  SKIPPED2: 'skipped',
  PREPARING2: 'preparing',
  ERROR2: 'error',
} as const;

export type GithubComPortainerPortainerEeApiDockerImagesStatus =
  (typeof GithubComPortainerPortainerEeApiDockerImagesStatus)[keyof typeof GithubComPortainerPortainerEeApiDockerImagesStatus];

export type GithubComPortainerPortainerEeApiGitTypesGitAuthentication = {
  AuthorizationType?: Gittypes;
  GitCredentialID?: number;
  Password?: string;
  Provider?: Gittypes;
  Username?: string;
};

export const GithubComPortainerPortainerEeApiGitTypesGitCredentialAuthType = {
  /**
   * GitCredentialAuthType_Basic
   */
  GIT_CREDENTIAL_AUTH_TYPE_BASIC: 0 /**
   * GitCredentialAuthType_Token
   */,
  GIT_CREDENTIAL_AUTH_TYPE_TOKEN: 1,
} as const;

export type GithubComPortainerPortainerEeApiGitTypesGitCredentialAuthType =
  (typeof GithubComPortainerPortainerEeApiGitTypesGitCredentialAuthType)[keyof typeof GithubComPortainerPortainerEeApiGitTypesGitCredentialAuthType];

export const GithubComPortainerPortainerEeApiGitTypesGitProvider = {
  /**
   * GitProviderCustom
   */
  GIT_PROVIDER_CUSTOM: 0,
  /**
   * GitProviderGitHub
   */
  GIT_PROVIDER_GIT_HUB: 1,
  /**
   * GitProviderGitLab
   */
  GIT_PROVIDER_GIT_LAB: 2,
  /**
   * GitProviderBitbucketCloud
   */
  GIT_PROVIDER_BITBUCKET_CLOUD: 3,
  /**
   * GitProviderBitbucketDataCenter
   */
  GIT_PROVIDER_BITBUCKET_DATA_CENTER: 4,
  /**
   * GitProviderAzureDevOps
   */
  GIT_PROVIDER_AZURE_DEV_OPS: 5,
  /**
   * GitProviderGiteaForgejo
   */
  GIT_PROVIDER_GITEA_FORGEJO: 6,
} as const;

export type GithubComPortainerPortainerEeApiGitTypesGitProvider =
  (typeof GithubComPortainerPortainerEeApiGitTypesGitProvider)[keyof typeof GithubComPortainerPortainerEeApiGitTypesGitProvider];

export type GithubComPortainerPortainerEeApiGitTypesRepoConfig = {
  /**
   * Git credentials
   */
  Authentication?: GithubComPortainerPortainerApiGitTypesGitAuthentication;
  /**
   * ConfigFilePath is the path to the config file within the repository.
   * NOTE: For stacks, this mirrors Stack.EntryPoint and the two are kept in sync by stackUpdateGit.
   */
  ConfigFilePath?: string;
  /**
   * Repository hash
   */
  ConfigHash?: string;
  /**
   * The reference name
   */
  ReferenceName?: string;
  /**
   * TLSSkipVerify skips SSL verification when cloning the Git repository
   */
  TLSSkipVerify?: boolean;
  /**
   * The repo url
   */
  URL?: string;
};

export type GithubComPortainerPortainerEeApiHttpHandlerCustomtemplatesCustomTemplateFromFileContentPayload =
  {
    /**
     * Description of the template
     */
    Description: string;
    EdgeSettings?: PortainereeCustomTemplateEdgeSettings;
    /**
     * EdgeTemplate indicates if this template purpose for Edge Stack
     */
    EdgeTemplate?: boolean;
    /**
     * Content of stack file
     */
    FileContent: string;
    /**
     * URL of the template's logo
     */
    Logo?: string;
    /**
     * A note that will be displayed in the UI. Supports HTML content
     */
    Note?: string;
    /**
     * Platform associated to the template.
     * Valid values are: 1 - 'linux', 2 - 'windows'
     * Required for Docker stacks
     */
    Platform?: 1 | 2;
    /**
     * Title of the template
     */
    Title: string;
    /**
     * Type of created stack:
     * * 1 - swarm
     * * 2 - compose
     * * 3 - kubernetes
     */
    Type: 1 | 2 | 3;
    /**
     * Definitions of variables in the stack file
     */
    Variables?: Array<PortainerCustomTemplateVariableDefinition>;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerCustomtemplatesCustomTemplateFromGitRepositoryPayload =
  {
    /**
     * Path to the Stack file inside the Git repository
     */
    ComposeFilePathInRepository?: string;
    /**
     * Description of the template
     */
    Description: string;
    EdgeSettings?: PortainereeCustomTemplateEdgeSettings;
    /**
     * EdgeTemplate indicates if this template purpose for Edge Stack
     */
    EdgeTemplate?: boolean;
    /**
     * IsComposeFormat indicates if the Kubernetes template is created from a Docker Compose file
     */
    IsComposeFormat?: boolean;
    /**
     * URL of the template's logo
     */
    Logo?: string;
    /**
     * A note that will be displayed in the UI. Supports HTML content
     */
    Note?: string;
    /**
     * Platform associated to the template.
     * Valid values are: 1 - 'linux', 2 - 'windows'
     * Required for Docker stacks
     */
    Platform?: 1 | 2;
    /**
     * Deprecated: use SourceID instead. Use basic authentication to clone the Git repository.
     */
    RepositoryAuthentication?: boolean;
    /**
     * Deprecated: use SourceID instead. RepositoryAuthorizationType is the authorization type to use
     */
    RepositoryAuthorizationType?: GithubComPortainerPortainerEeApiGitTypesGitCredentialAuthType;
    /**
     * Deprecated: use SourceID instead. Password used in basic authentication. Required when RepositoryAuthentication is true.
     */
    RepositoryPassword?: string;
    /**
     * Deprecated: use SourceID instead. RepositoryProvider is the provider to use
     */
    RepositoryProvider?: GithubComPortainerPortainerEeApiGitTypesGitProvider;
    /**
     * Reference name of a Git repository hosting the Stack file
     */
    RepositoryReferenceName?: string;
    /**
     * Deprecated: use SourceID instead. URL of a Git repository hosting the Stack file.
     */
    RepositoryURL?: string;
    /**
     * Deprecated: use SourceID instead. Username used in basic authentication. Required when RepositoryAuthentication is true.
     */
    RepositoryUsername?: string;
    /**
     * SourceID references an existing Source for git credentials/URL.
     * When set, the inline URL and authentication fields are ignored.
     */
    SourceID: number;
    /**
     * Deprecated: use SourceID instead. TLSSkipVerify skips SSL verification when cloning the Git repository.
     */
    TLSSkipVerify?: boolean;
    /**
     * Title of the template
     */
    Title: string;
    /**
     * Type of created stack:
     * * 1 - swarm
     * * 2 - compose
     * * 3 - kubernetes
     */
    Type: 1 | 2;
    /**
     * Definitions of variables in the stack file
     */
    Variables?: Array<PortainerCustomTemplateVariableDefinition>;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerCustomtemplatesCustomTemplateUpdatePayload =
  {
    /**
     * Path to the Stack file inside the Git repository
     */
    ComposeFilePathInRepository?: string;
    /**
     * Description of the template
     */
    Description: string;
    EdgeSettings?: PortainereeCustomTemplateEdgeSettings;
    /**
     * EdgeTemplate indicates if this template purpose for Edge Stack
     */
    EdgeTemplate?: boolean;
    /**
     * Content of stack file
     */
    FileContent: string;
    /**
     * IsComposeFormat indicates if the Kubernetes template is created from a Docker Compose file
     */
    IsComposeFormat?: boolean;
    /**
     * URL of the template's logo
     */
    Logo?: string;
    /**
     * A note that will be displayed in the UI. Supports HTML content
     */
    Note?: string;
    /**
     * Platform associated to the template.
     * Valid values are: 1 - 'linux', 2 - 'windows'
     * Required for Docker stacks
     */
    Platform?: 1 | 2;
    /**
     * Deprecated: use SourceID instead. Use authentication to clone the Git repository.
     */
    RepositoryAuthentication?: boolean;
    /**
     * Deprecated: use SourceID instead. RepositoryAuthorizationType is the authorization type to use
     */
    RepositoryAuthorizationType?: GithubComPortainerPortainerEeApiGitTypesGitCredentialAuthType;
    /**
     * Deprecated: use SourceID instead. Password used in basic authentication or token used in token authentication. Required when RepositoryAuthentication is true.
     */
    RepositoryPassword?: string;
    /**
     * Deprecated: use SourceID instead. RepositoryProvider is the provider to use
     */
    RepositoryProvider?: GithubComPortainerPortainerEeApiGitTypesGitProvider;
    /**
     * Reference name of a Git repository hosting the Stack file
     */
    RepositoryReferenceName?: string;
    /**
     * Deprecated: use SourceID instead. URL of a Git repository hosting the Stack file.
     */
    RepositoryURL?: string;
    /**
     * Deprecated: use SourceID instead. Username used in basic authentication. Required when RepositoryAuthentication is true.
     */
    RepositoryUsername?: string;
    /**
     * SourceID references an existing Source for git credentials/URL.
     * When set, the inline URL and authentication fields are ignored.
     */
    SourceID?: number;
    /**
     * Deprecated: use SourceID instead. TLSSkipVerify skips SSL verification when cloning the Git repository.
     */
    TLSSkipVerify?: boolean;
    /**
     * Title of the template
     */
    Title: string;
    /**
     * Type of created stack (1 - swarm, 2 - compose, 3 - kubernetes)
     */
    Type: 1 | 2 | 3;
    /**
     * Definitions of variables in the stack file
     */
    Variables?: Array<PortainerCustomTemplateVariableDefinition>;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerCustomtemplatesFileResponse =
  {
    FileContent?: string;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerEdgestacksAggregatedStatusesMap =
  {
    [key: string]: number;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerEdgestacksEdgeStackFromGitRepositoryPayload =
  {
    /**
     * Whether the edge stack always clones the git repository for relative path
     */
    AlwaysCloneGitRepoForRelativePath?: boolean;
    /**
     * Optional GitOps update configuration
     */
    AutoUpdate?: PortainerAutoUpdateSettings;
    /**
     * CreatedFromCustomTemplateID used to determine whether the edge stack is created from a custom template
     */
    CreatedFromCustomTemplateID?: number;
    /**
     * Deployment type to deploy this stack
     * Valid values are: 0 - 'compose', 1 - 'kubernetes'
     * compose is enabled only for docker environments
     * kubernetes is enabled only for kubernetes environments
     */
    DeploymentType?: 0 | 1;
    /**
     * List of identifiers of EdgeGroups
     */
    EdgeGroups: Array<number>;
    /**
     * List of environment variables
     */
    EnvVars?: Array<PortainerPair>;
    /**
     * Path to the Stack file inside the Git repository
     */
    FilePathInRepository?: string;
    /**
     * Local filesystem path
     */
    FilesystemPath?: string;
    /**
     * HelmConfig is the optional Helm configuration for deploying a chart from within the git repository.
     * Omit this field for non-Helm git stacks.
     */
    HelmConfig?: EdgestacksGitHelmConfig;
    /**
     * Name of the stack
     * Max length: 255
     * Name must only contains lowercase characters, numbers, hyphens, or underscores
     * Name must start with a lowercase character or number
     * Example: stack-name or stack_123 or stackName
     */
    Name: string;
    /**
     * Per device configs group match type
     */
    PerDeviceConfigsGroupMatchType?: 'file' | ' dir';
    /**
     * Per device configs match type
     */
    PerDeviceConfigsMatchType?: 'file' | ' dir';
    /**
     * Per device configs path
     */
    PerDeviceConfigsPath?: string;
    /**
     * Pre Pull image
     */
    PrePullImage?: boolean;
    /**
     * List of Registries to use for this stack
     */
    Registries?: Array<number>;
    /**
     * Deprecated: Use SourceID instead. Use basic authentication to clone the Git repository.
     */
    RepositoryAuthentication?: boolean;
    /**
     * Deprecated: Use SourceID instead. RepositoryAuthorizationType is the authorization type to use
     */
    RepositoryAuthorizationType?: GithubComPortainerPortainerEeApiGitTypesGitCredentialAuthType;
    /**
     * Deprecated: Use SourceID instead. Password used in basic authentication. Required when RepositoryAuthentication is true.
     */
    RepositoryPassword?: string;
    /**
     * Deprecated: Use SourceID instead. RepositoryProvider is the provider to use
     */
    RepositoryProvider?: GithubComPortainerPortainerEeApiGitTypesGitProvider;
    /**
     * Reference name of a Git repository hosting the Stack file
     */
    RepositoryReferenceName?: string;
    /**
     * Deprecated: Use SourceID instead. URL of a Git repository hosting the Stack file.
     */
    RepositoryURL?: string;
    /**
     * Deprecated: Use SourceID instead. Username used in basic authentication. Required when RepositoryAuthentication is true.
     */
    RepositoryUsername?: string;
    /**
     * Retry deploy
     */
    RetryDeploy?: boolean;
    /**
     * Retry period specifies the duration, in seconds, for which the agent should continue attempting to deploy the stack after a failure
     */
    RetryPeriod?: number;
    /**
     * SourceID references an existing Source that provides the git repository URL and credentials.
     * When set, the inline URL and authentication fields below are ignored.
     */
    SourceID?: number;
    /**
     * Configuration for stagger updates
     */
    StaggerConfig?: PortainereeEdgeStaggerConfig;
    /**
     * Whether the edge stack supports per device configs
     */
    SupportPerDeviceConfigs?: boolean;
    /**
     * Whether the stack supports relative path volume
     */
    SupportRelativePath?: boolean;
    /**
     * Deprecated: Use SourceID instead. TLSSkipVerify skips SSL verification when cloning the Git repository
     */
    TLSSkipVerify?: boolean;
    /**
     * Uses the manifest's namespaces instead of the default one
     */
    UseManifestNamespaces?: boolean;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerEdgestacksEdgeStackFromStringPayload =
  {
    /**
     * Deployment type to deploy this stack
     * Valid values are: 0 - 'compose', 1 - 'kubernetes'
     * compose is enabled only for docker environments
     * kubernetes is enabled only for kubernetes environments
     */
    DeploymentType?: 0 | 1;
    /**
     * List of identifiers of EdgeGroups
     */
    EdgeGroups?: Array<number>;
    /**
     * List of environment variables
     */
    EnvVars?: Array<PortainerPair>;
    /**
     * Name of the stack
     * Max length: 255
     * Name must only contains lowercase characters, numbers, hyphens, or underscores
     * Name must start with a lowercase character or number
     * Example: stack-name or stack_123 or stackName
     */
    Name: string;
    /**
     * Pre Pull image
     */
    PrePullImage?: boolean;
    /**
     * List of Registries to use for this stack
     */
    Registries?: Array<number>;
    /**
     * Retry deploy
     */
    RetryDeploy?: boolean;
    /**
     * Retry period specifies the duration, in seconds, for which the agent should continue attempting to deploy the stack after a failure
     */
    RetryPeriod?: number;
    /**
     * Content of the Stack file
     */
    StackFileContent: string;
    /**
     * Configuration for stagger updates
     */
    StaggerConfig?: PortainereeEdgeStaggerConfig;
    /**
     * Uses the manifest's namespaces instead of the default one
     */
    UseManifestNamespaces?: boolean;
    /**
     * Optional webhook configuration
     */
    Webhook?: string;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerEdgestacksEdgeStackListResponseItem =
  {
    /**
     * Whether the edge stack always clones the git repository for relative path
     */
    AlwaysCloneGitRepoForRelativePath?: boolean;
    /**
     * The GitOps update settings of a git stack
     */
    AutoUpdate?: PortainerAutoUpdateSettings;
    /**
     * The username which created this stack
     */
    CreatedBy?: string;
    /**
     * The username id which created this stack
     */
    CreatedByUserId?: string;
    /**
     * StatusArray    map[EndpointID][]EdgeStackStatus `json:"StatusArray"`
     */
    CreationDate?: number;
    /**
     * CurrentDeploymentInfo records the git repository state at the time of the last actual deployment
     */
    CurrentDeploymentInfo?: PortainerStackDeploymentInfo;
    /**
     * Options to control the Deployer behaviour
     */
    DeployerOptions?: PortainereeEdgeStackDeployerOptions;
    DeploymentType?: PortainerEdgeStackDeploymentType;
    EdgeGroups?: Array<number>;
    /**
     * EdgeUpdateID represents the parent update ID, will be zero if this stack is not part of an update
     */
    EdgeUpdateID?: number;
    EntryPoint?: string;
    /**
     * EnvVars is a list of environment variables to inject into the stack
     */
    EnvVars?: Array<PortainerPair>;
    /**
     * Local filesystem path
     */
    FilesystemPath?: string;
    /**
     * GitConfig is the git repository configuration for git-backed edge stacks.
     * Deprecated: loaded from Source via WorkflowID; kept for DB backwards-compatibility only.
     * Non-migration code must not read or write this field; use Source records instead.
     */
    GitConfig?: GithubComPortainerPortainerEeApiGitTypesRepoConfig;
    GitSourceId?: number;
    /**
     * HelmConfig is the configuration for a Helm based deployment of an edge stack (Helm Chart)
     */
    HelmConfig?: PortainerHelmConfig;
    /**
     * EdgeStack Identifier
     */
    Id?: number;
    ManifestPath?: string;
    Name?: string;
    NumDeployments?: number;
    /**
     * Per device configs group match type
     */
    PerDeviceConfigsGroupMatchType?: 'file' | ' dir';
    /**
     * Per device configs match type
     */
    PerDeviceConfigsMatchType?: 'file' | ' dir';
    /**
     * Per device configs path
     */
    PerDeviceConfigsPath?: string;
    /**
     * Pre Pull Image
     */
    PrePullImage?: boolean;
    /**
     * PreviousDeploymentInfo represents the previous deployment info of the stack
     */
    PreviousDeploymentInfo?: PortainerStackDeploymentInfo;
    ProjectPath?: string;
    /**
     * Deprecated(2.36): keep it for backward compatibility. To remove in future versions (2.44+)
     * Re-Pull Image
     */
    RePullImage?: boolean;
    Registries?: Array<number>;
    /**
     * Retry deploy
     */
    RetryDeploy?: boolean;
    /**
     * RetryPeriod specifies the duration, in seconds, for which the agent should continue attempting to deploy the stack after a failure
     */
    RetryPeriod?: number;
    /**
     * Schedule represents the schedule of the Edge stack (optional, format - 'YYYY-MM-DD HH:mm:ss')
     */
    ScheduledTime?: string;
    /**
     * StackFileVersion represents the version of the stack file, such yaml, hcl, manifest file
     */
    StackFileVersion?: number;
    /**
     * StaggerConfig is the configuration for staggered update
     */
    StaggerConfig?: PortainereeEdgeStaggerConfig;
    Status?: {
      [key: string]: PortainerEdgeStackStatus;
    };
    StatusSummary?: GithubComPortainerPortainerEeApiHttpHandlerEdgestacksEdgeStackStatusSummary;
    /**
     * Whether the edge stack supports per device configs
     */
    SupportPerDeviceConfigs?: boolean;
    /**
     * Whether the stack supports relative path volume
     */
    SupportRelativePath?: boolean;
    /**
     * Uses the manifest's namespaces instead of the default one
     */
    UseManifestNamespaces?: boolean;
    Version?: number;
    /**
     * A UUID to identify a webhook. The stack will be force updated and pull the latest image when the webhook was invoked.
     */
    Webhook?: string;
    /**
     * WorkflowID is the ID of the Workflow that owns the Source for this edge stack.
     */
    WorkflowID?: number;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerEdgestacksEdgeStackStatusSummary =
  {
    AggregatedStatus?: GithubComPortainerPortainerEeApiHttpHandlerEdgestacksAggregatedStatusesMap;
    Reason?: string;
    Status?: EdgestacksSummarizedStatus;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerEdgestacksStackFileResponse =
  {
    StackFileContent?: string;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerEdgestacksUpdateEdgeStackPayload =
  {
    /**
     * Options to control the deployer behaviour
     */
    DeployerOptions?: EdgestacksDeployerOptionsPayload;
    DeploymentType?: PortainerEdgeStackDeploymentType;
    EdgeGroups?: Array<number>;
    /**
     * Environment variables to inject into the stack
     */
    EnvVars?: Array<PortainerPair>;
    PrePullImage?: boolean;
    /**
     * Deprecated(2.36): use RepullImageAndRedeploy instead
     */
    RePullImage?: boolean;
    Registries?: Array<number>;
    /**
     * RepullImageAndRedeploy indicates whether the edge stack is manually forced to redeploy
     */
    RepullImageAndRedeploy?: boolean;
    RetryDeploy?: boolean;
    /**
     * RetryPeriod specifies the duration, in seconds, for which the agent should continue attempting to deploy the stack after a failure
     */
    RetryPeriod?: number;
    /**
     * RollbackTo specifies the stack file version to rollback to (only support to rollback to the last version currently)
     */
    RollbackTo?: number;
    StackFileContent?: string;
    /**
     * Configuration for stagger updates
     */
    StaggerConfig?: PortainereeEdgeStaggerConfig;
    UpdateVersion?: boolean;
    /**
     * Uses the manifest's namespaces instead of the default one
     */
    UseManifestNamespaces?: boolean;
    /**
     * Optional webhook configuration
     */
    Webhook?: string;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerEdgestacksUpdateStatusPayload =
  {
    EndpointID?: number;
    Error?: string;
    /**
     * RollbackTo specifies the stack file version to rollback to (only support to rollback to the last version currently)
     */
    RollbackTo?: number;
    Status?: PortainerEdgeStackStatusType;
    Time?: number;
    Version?: number;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerEndpointgroupsEndpointGroupCreatePayload =
  {
    /**
     * List of environment(endpoint) identifiers that will be part of this group
     */
    AssociatedEndpoints?: Array<number>;
    /**
     * Environment(Endpoint) group description
     */
    Description?: string;
    /**
     * Environment(Endpoint) group name
     */
    Name: string;
    /**
     * List of tag identifiers to which this environment(endpoint) group is associated
     */
    TagIDs?: Array<number>;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerEndpointgroupsEndpointGroupUpdatePayload =
  {
    /**
     * List of environment(endpoint) identifiers that will be part of this group
     */
    AssociatedEndpoints?: Array<number>;
    /**
     * Environment(Endpoint) group description
     */
    Description?: string;
    /**
     * Environment(Endpoint) group name
     */
    Name?: string;
    /**
     * List of tag identifiers associated to the environment(endpoint) group
     */
    TagIDs?: Array<number>;
    TeamAccessPolicies?: PortainerTeamAccessPolicies;
    UserAccessPolicies?: PortainerUserAccessPolicies;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesCreateNamespaceResponse =
  {
    /**
     * APIVersion defines the versioned schema of this representation of an object.
     * Servers should convert recognized schemas to the latest internal value, and
     * may reject unrecognized values.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#resources
     * +optional
     */
    apiVersion?: string;
    /**
     * Kind is a string value representing the REST resource this object represents.
     * Servers may infer this from the endpoint the client submits requests to.
     * Cannot be updated.
     * In CamelCase.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
     * +optional
     */
    kind?: string;
    /**
     * Standard object's metadata.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#metadata
     * +optional
     */
    metadata?: V1ObjectMeta;
    /**
     * Spec defines the behavior of the Namespace.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#spec-and-status
     * +optional
     */
    spec?: V1NamespaceSpec;
    /**
     * Status describes the current status of a Namespace.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#spec-and-status
     * +optional
     */
    status?: V1NamespaceStatus;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesDeploymentListResponse =
  {
    /**
     * APIVersion defines the versioned schema of this representation of an object.
     * Servers should convert recognized schemas to the latest internal value, and
     * may reject unrecognized values.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#resources
     * +optional
     */
    apiVersion?: string;
    /**
     * Items is the list of Deployments.
     */
    items?: Array<V1Deployment>;
    /**
     * Kind is a string value representing the REST resource this object represents.
     * Servers may infer this from the endpoint the client submits requests to.
     * Cannot be updated.
     * In CamelCase.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
     * +optional
     */
    kind?: string;
    /**
     * Standard list metadata.
     * +optional
     */
    metadata?: V1ListMeta;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesDeploymentResponse =
  {
    /**
     * APIVersion defines the versioned schema of this representation of an object.
     * Servers should convert recognized schemas to the latest internal value, and
     * may reject unrecognized values.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#resources
     * +optional
     */
    apiVersion?: string;
    /**
     * Kind is a string value representing the REST resource this object represents.
     * Servers may infer this from the endpoint the client submits requests to.
     * Cannot be updated.
     * In CamelCase.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
     * +optional
     */
    kind?: string;
    /**
     * Standard object's metadata.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#metadata
     * +optional
     */
    metadata?: V1ObjectMeta;
    /**
     * Specification of the desired behavior of the Deployment.
     * +optional
     */
    spec?: V1DeploymentSpec;
    /**
     * Most recently observed status of the Deployment.
     * +optional
     */
    status?: V1DeploymentStatus;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesNodeResponse =
  {
    /**
     * APIVersion defines the versioned schema of this representation of an object.
     * Servers should convert recognized schemas to the latest internal value, and
     * may reject unrecognized values.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#resources
     * +optional
     */
    apiVersion?: string;
    /**
     * Kind is a string value representing the REST resource this object represents.
     * Servers may infer this from the endpoint the client submits requests to.
     * Cannot be updated.
     * In CamelCase.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
     * +optional
     */
    kind?: string;
    /**
     * Standard object's metadata.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#metadata
     * +optional
     */
    metadata?: V1ObjectMeta;
    /**
     * Spec defines the behavior of a node.
     * https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#spec-and-status
     * +optional
     */
    spec?: V1NodeSpec;
    /**
     * Most recently observed status of the node.
     * Populated by the system.
     * Read-only.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#spec-and-status
     * +optional
     */
    status?: V1NodeStatus;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesPodListResponse =
  {
    /**
     * APIVersion defines the versioned schema of this representation of an object.
     * Servers should convert recognized schemas to the latest internal value, and
     * may reject unrecognized values.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#resources
     * +optional
     */
    apiVersion?: string;
    /**
     * List of pods.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md
     */
    items?: Array<V1Pod>;
    /**
     * Kind is a string value representing the REST resource this object represents.
     * Servers may infer this from the endpoint the client submits requests to.
     * Cannot be updated.
     * In CamelCase.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
     * +optional
     */
    kind?: string;
    /**
     * Standard list metadata.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
     * +optional
     */
    metadata?: V1ListMeta;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesReplicaSetListResponse =
  {
    /**
     * APIVersion defines the versioned schema of this representation of an object.
     * Servers should convert recognized schemas to the latest internal value, and
     * may reject unrecognized values.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#resources
     * +optional
     */
    apiVersion?: string;
    /**
     * List of ReplicaSets.
     * More info: https://kubernetes.io/docs/concepts/workloads/controllers/replicaset
     */
    items?: Array<V1ReplicaSet>;
    /**
     * Kind is a string value representing the REST resource this object represents.
     * Servers may infer this from the endpoint the client submits requests to.
     * Cannot be updated.
     * In CamelCase.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
     * +optional
     */
    kind?: string;
    /**
     * Standard list metadata.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
     * +optional
     */
    metadata?: V1ListMeta;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesResourceQuotaListResponse =
  {
    /**
     * APIVersion defines the versioned schema of this representation of an object.
     * Servers should convert recognized schemas to the latest internal value, and
     * may reject unrecognized values.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#resources
     * +optional
     */
    apiVersion?: string;
    /**
     * Items is a list of ResourceQuota objects.
     * More info: https://kubernetes.io/docs/concepts/policy/resource-quotas/
     */
    items?: Array<V1ResourceQuota>;
    /**
     * Kind is a string value representing the REST resource this object represents.
     * Servers may infer this from the endpoint the client submits requests to.
     * Cannot be updated.
     * In CamelCase.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
     * +optional
     */
    kind?: string;
    /**
     * Standard list metadata.
     * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
     * +optional
     */
    metadata?: V1ListMeta;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerKubernetesDescribeResourceResponse =
  {
    describe?: string;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerKubernetesDrainNodePayload =
  {
    /**
     * DeleteEmptyDirData allows eviction of pods using emptyDir volumes, whose data is lost once the pod is deleted.
     */
    DeleteEmptyDirData?: boolean;
    /**
     * DisableEviction forces the use of direct pod deletion instead of the eviction API, ignoring any configured PodDisruptionBudgets.
     */
    DisableEviction?: boolean;
    /**
     * Force allows deletion of standalone pods not managed by a controller.
     */
    Force?: boolean;
    /**
     * GracePeriodSeconds overrides each pod's termination grace period. -1 uses the pod's own grace period.
     */
    GracePeriodSeconds?: number;
    /**
     * IgnoreDaemonSets skips DaemonSet-managed pods, which would otherwise block the drain.
     */
    IgnoreDaemonSets?: boolean;
    /**
     * TimeoutSeconds is the overall time to wait for the drain to complete. Defaults to 60 when omitted.
     */
    TimeoutSeconds?: number;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesVersionResponse =
  {
    buildDate?: string;
    compiler?: string;
    /**
     * EmulationMajor is the major version of the emulation version
     */
    emulationMajor?: string;
    /**
     * EmulationMinor is the minor version of the emulation version
     */
    emulationMinor?: string;
    gitCommit?: string;
    gitTreeState?: string;
    gitVersion?: string;
    goVersion?: string;
    /**
     * Major is the major version of the binary version
     */
    major?: string;
    /**
     * MinCompatibilityMajor is the major version of the minimum compatibility version
     */
    minCompatibilityMajor?: string;
    /**
     * MinCompatibilityMinor is the minor version of the minimum compatibility version
     */
    minCompatibilityMinor?: string;
    /**
     * Minor is the minor version of the binary version
     */
    minor?: string;
    platform?: string;
    /**
     * SupportsPodRestart is true when the cluster exposes the `pods/restart`
     * subresource via API discovery — i.e. the feature gate is enabled and
     * the cluster version is recent enough. This is the authoritative
     * signal for whether Portainer can call the pod-restart endpoint, and
     * is preferred over a raw Kubernetes-version comparison.
     */
    supportsPodRestart?: boolean;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerKubernetesManifestDryRunPayload =
  {
    /**
     * The manifests to validate. Each entry may hold several YAML documents separated by "---".
     */
    manifests?: Array<string>;
    /**
     * The namespace applied to namespaced resources that do not declare one. A resource declaring a different namespace is rejected.
     */
    namespace?: string;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerKubernetesManifestDryRunResponse =
  {
    results?: Array<GithubComPortainerPortainerEeApiHttpHandlerKubernetesManifestDryRunResult>;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerKubernetesManifestDryRunResult =
  {
    /**
     * The zero-based position of the document among the non-empty documents of all manifests. It identifies a document rejected before its resource could be named, such as malformed YAML.
     */
    documentIndex?: number;
    kind?: string;
    /**
     * The reason the resource was rejected. Empty when the resource passed.
     */
    message?: string;
    name?: string;
    namespace?: string;
    /**
     * Either "pass" or "fail".
     */
    status?: string;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerKubernetesNamespacesToggleSystemPayload =
  {
    /**
     * Toggle the system state of this namespace to true or false
     */
    System?: boolean;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerSystemNodesCountResponse =
  {
    nodes?: number;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerSystemStatus = {
  /**
   * Server Instance ID
   */
  InstanceID?: string;
  /**
   * Portainer API version
   */
  Version?: string;
};

export type GithubComPortainerPortainerEeApiHttpHandlerSystemSystemInfoResponse =
  {
    /**
     * AddonsAvailable reports whether the addon lifecycle can be used on this
     * deployment. Addons require Portainer itself to be running on Kubernetes and
     * are not supported in FIPS mode; this mirrors the gates that govern whether
     * the lifecycle routes exist, and lets the UI show a clear message when they
     * don't.
     */
    addonsAvailable?: boolean;
    agents?: number;
    edgeAgents?: number;
    edgeDevices?: number;
    platform?: PlatformContainerPlatform;
  };

export type GithubComPortainerPortainerEeApiHttpHandlerSystemVersionResponse = {
  Build?: BuildBuildInfo;
  DatabaseVersion?: string;
  Dependencies?: BuildDependenciesInfo;
  /**
   * The latest version available
   */
  LatestVersion?: string;
  Runtime?: BuildRuntimeInfo;
  ServerEdition?: string;
  ServerVersion?: string;
  /**
   * Whether portainer has an update available
   */
  UpdateAvailable?: boolean;
  VersionSupport?: string;
};

export type GithubComPortainerPortainerApiGitTypesGitAuthentication = {
  AuthorizationType?: Gittypes;
  GitCredentialID?: number;
  Password?: string;
  Provider?: Gittypes;
  Username?: string;
};

export type GithubComPortainerPortainerApiGitTypesGitSource = {
  Authentication?: GithubComPortainerPortainerApiGitTypesGitAuthentication;
  TLSSkipVerify?: boolean;
  URL?: string;
};

export type GithubComPortainerPortainerApiGitTypesRepoConfig = {
  /**
   * Git credentials
   */
  Authentication?: GithubComPortainerPortainerApiGitTypesGitAuthentication;
  /**
   * ConfigFilePath is the path to the config file within the repository.
   * NOTE: For stacks, this mirrors Stack.EntryPoint and the two are kept in sync by stackUpdateGit.
   */
  ConfigFilePath?: string;
  /**
   * Repository hash
   */
  ConfigHash?: string;
  /**
   * The reference name
   */
  ReferenceName?: string;
  /**
   * TLSSkipVerify skips SSL verification when cloning the Git repository
   */
  TLSSkipVerify?: boolean;
  /**
   * The repo url
   */
  URL?: string;
};

export const GithubComPortainerPortainerApiGitopsWorkflowsStatus = {
  /**
   * StatusHealthy
   */
  STATUS_HEALTHY: 'healthy',
  /**
   * StatusSyncing
   */
  STATUS_SYNCING: 'syncing',
  /**
   * StatusError
   */
  STATUS_ERROR: 'error',
  /**
   * StatusPaused
   */
  STATUS_PAUSED: 'paused',
  /**
   * StatusUnknown
   */
  STATUS_UNKNOWN: 'unknown',
} as const;

export type GithubComPortainerPortainerApiGitopsWorkflowsStatus =
  (typeof GithubComPortainerPortainerApiGitopsWorkflowsStatus)[keyof typeof GithubComPortainerPortainerApiGitopsWorkflowsStatus];

export type GithubComPortainerPortainerPkgLibhelmReleaseHook = {
  /**
   * DeletePolicies are the policies that indicate when to delete the hook
   */
  delete_policies?: Array<string>;
  /**
   * Events are the events that this hook fires on.
   */
  events?: Array<string>;
  /**
   * Kind is the Kubernetes kind.
   */
  kind?: string;
  /**
   * LastRun indicates the date/time this was last run.
   */
  last_run?: ReleaseHookExecution;
  /**
   * Manifest is the manifest contents.
   */
  manifest?: string;
  name?: string;
  /**
   * Path is the chart-relative path to the template.
   */
  path?: string;
  /**
   * Weight indicates the sort order for execution among similar Hook type
   */
  weight?: number;
};

export type GitopsFileResponse = {
  FileContent?: string;
};

export type GitopsHelmValuesPreviewPayload = {
  /**
   * TLSSkipVerify skips SSL verification when cloning the Git repository
   * Deprecated: use SourceID instead
   */
  TLSSkipVerify?: boolean;
  /**
   * Deprecated: use SourceID instead
   */
  authorizationType?: GithubComPortainerPortainerEeApiGitTypesGitCredentialAuthType;
  /**
   * CreatedFromCustomTemplateID will fetch auth from this custom template's git settings
   */
  createdFromCustomTemplateID?: number;
  fromEdgeStack?: boolean;
  /**
   * Deprecated: use SourceID instead
   */
  password?: string;
  /**
   * Deprecated: use SourceID instead
   */
  provider?: GithubComPortainerPortainerEeApiGitTypesGitProvider;
  reference?: string;
  /**
   * Deprecated: use SourceID instead
   */
  repository?: string;
  /**
   * SourceID resolves URL and auth from the stored source record
   */
  sourceId?: number;
  /**
   * StackID will fetch auth from this stack's git settings (edge stack if FromEdgeStack is true)
   */
  stackId?: number;
  /**
   * Deprecated: use SourceID instead
   */
  username?: string;
  /**
   * ValuesFiles is an array of paths to Helm values files (matches Stack.HelmValuesFiles)
   */
  valuesFiles: Array<string>;
};

export type GitopsHelmValuesPreviewResponse = {
  commitHash?: string;
  filesProcessed?: Array<string>;
  mergedValues?: string;
};

export type GitopsRepositoryFilePreviewPayload = {
  /**
   * TLSSkipVerify skips SSL verification when cloning the Git repository
   * Deprecated: use SourceID instead
   */
  TLSSkipVerify?: boolean;
  /**
   * Deprecated: use SourceID instead
   */
  authorizationType?: GithubComPortainerPortainerEeApiGitTypesGitCredentialAuthType;
  /**
   * CreatedFromCustomTemplateID will fetch auth from this custom template's git settings
   */
  createdFromCustomTemplateID?: number;
  fromEdgeStack?: boolean;
  /**
   * Deprecated: use SourceID instead
   */
  password?: string;
  /**
   * Deprecated: use SourceID instead
   */
  provider?: GithubComPortainerPortainerEeApiGitTypesGitProvider;
  reference?: string;
  /**
   * Deprecated: use SourceID instead
   */
  repository?: string;
  /**
   * SourceID resolves URL and auth from the stored source record
   */
  sourceId?: number;
  /**
   * StackID will fetch auth from this stack's git settings (edge stack if FromEdgeStack is true)
   */
  stackId?: number;
  /**
   * Path to file whose content will be read
   */
  targetFile?: string;
  /**
   * Deprecated: use SourceID instead
   */
  username?: string;
};

export type GitopsRepositoryFileSearchPayload = {
  /**
   * TLSSkipVerify skips SSL verification when cloning the Git repository
   * Deprecated: use SourceID instead
   */
  TLSSkipVerify?: boolean;
  /**
   * Deprecated: use SourceID instead
   */
  authorizationType?: GithubComPortainerPortainerEeApiGitTypesGitCredentialAuthType;
  /**
   * CreatedFromCustomTemplateID will fetch auth from this custom template's git settings
   */
  createdFromCustomTemplateID?: number;
  /**
   * DirOnly List directories only
   */
  dirOnly?: boolean;
  fromEdgeStack?: boolean;
  /**
   * Allow to provide specific file extension as the search result. If empty, the file extensions yml,yaml,hcl,json will be set by default
   */
  include?: string;
  /**
   * Partial or completed file name. If empty, all filenames with included extensions will be returned
   */
  keyword?: string;
  /**
   * Deprecated: use SourceID instead
   */
  password?: string;
  /**
   * Deprecated: use SourceID instead
   */
  provider?: GithubComPortainerPortainerEeApiGitTypesGitProvider;
  /**
   * Specific Git repository reference. If empty, the reference ref/heads/main will be set by default
   */
  reference?: string;
  /**
   * Deprecated: use SourceID instead
   */
  repository?: string;
  /**
   * SourceID resolves URL and auth from the stored source record
   */
  sourceId?: number;
  /**
   * StackID will fetch auth from this stack's git settings (edge stack if FromEdgeStack is true)
   */
  stackId?: number;
  /**
   * Deprecated: use SourceID instead
   */
  username?: string;
};

export type GitopsRepositoryReferenceListPayload = {
  /**
   * TLSSkipVerify skips SSL verification when cloning the Git repository
   * Deprecated: use SourceID instead
   */
  TLSSkipVerify?: boolean;
  /**
   * Deprecated: use SourceID instead
   */
  authorizationType?: GithubComPortainerPortainerEeApiGitTypesGitCredentialAuthType;
  /**
   * CreatedFromCustomTemplateID will fetch auth from this custom template's git settings
   */
  createdFromCustomTemplateID?: number;
  fromEdgeStack?: boolean;
  /**
   * Deprecated: use SourceID instead
   */
  password?: string;
  /**
   * Deprecated: use SourceID instead
   */
  provider?: GithubComPortainerPortainerEeApiGitTypesGitProvider;
  /**
   * Deprecated: use SourceID instead
   */
  repository?: string;
  /**
   * SourceID resolves URL and auth from the stored source record
   */
  sourceId?: number;
  /**
   * StackID will fetch auth from this stack's git settings (edge stack if FromEdgeStack is true)
   */
  stackId?: number;
  /**
   * Deprecated: use SourceID instead
   */
  username?: string;
};

export const Gittypes = {
  /**
   * GitCredentialAuthType_Basic
   */
  GIT_CREDENTIAL_AUTH_TYPE_BASIC: 0 /**
   * GitCredentialAuthType_Token
   */,
  GIT_CREDENTIAL_AUTH_TYPE_TOKEN: 1,
} as const;

export type Gittypes = (typeof Gittypes)[keyof typeof Gittypes];

export type HelmGitHelmDryRunPayload = {
  helmChartPath?: string;
  helmValuesFiles?: Array<string>;
  name?: string;
  namespace?: string;
  repositoryAuthentication?: boolean;
  repositoryAuthorizationType?: GithubComPortainerPortainerEeApiGitTypesGitCredentialAuthType;
  repositoryPassword?: string;
  repositoryProvider?: GithubComPortainerPortainerEeApiGitTypesGitProvider;
  repositoryReferenceName?: string;
  repositoryURL?: string;
  repositoryUsername?: string;
  /**
   * SourceID resolves URL and auth from the stored Source record.
   * When set, the inline Repository/auth/tls fields are ignored.
   */
  sourceId?: number;
  tlsSkipVerify?: boolean;
};

export type HelmInstallChartPayload = {
  atomic?: boolean;
  chart?: string;
  name?: string;
  namespace?: string;
  registryId?: number;
  repo?: string;
  values?: string;
  version?: string;
};

export type ImagesImageResponse = {
  created?: number;
  id?: string;
  nodeName?: string;
  size?: number;
  tags?: Array<string>;
  /**
   * Used is true if the image is used by at least one container
   * supplied only when withUsage is true
   */
  used?: boolean;
};

export type ImagesStatusResponse = {
  Message?: string;
  Status?: GithubComPortainerPortainerEeApiDockerImagesStatus;
};

export type IntstrIntOrString = {
  IntVal?: number;
  StrVal?: string;
  Type?: IntstrType;
};

export const IntstrType = {
  /**
   * Int
   *
   * The IntOrString holds an int.
   */
  INT: 0 /**
   * String
   *
   * The IntOrString holds a string.
   */,
  STRING: 1,
} as const;

export type IntstrType = (typeof IntstrType)[keyof typeof IntstrType];

export type K8sIoApiAppsV1DeploymentCondition = {
  /**
   * Last time the condition transitioned from one status to another.
   */
  lastTransitionTime?: string;
  /**
   * The last time this condition was updated.
   */
  lastUpdateTime?: string;
  /**
   * A human readable message indicating details about the transition.
   */
  message?: string;
  /**
   * The reason for the condition's last transition.
   */
  reason?: string;
  /**
   * Status of the condition, one of True, False, Unknown.
   */
  status?: K8sIoApiCoreV1ConditionStatus;
  /**
   * Type of deployment condition.
   */
  type?: K8sIoApiAppsV1DeploymentConditionType;
};

export const K8sIoApiAppsV1DeploymentConditionType = {
  /**
   * DeploymentAvailable
   */
  DEPLOYMENT_AVAILABLE: 'Available',
  /**
   * DeploymentProgressing
   */
  DEPLOYMENT_PROGRESSING: 'Progressing',
  /**
   * DeploymentReplicaFailure
   */
  DEPLOYMENT_REPLICA_FAILURE: 'ReplicaFailure',
} as const;

export type K8sIoApiAppsV1DeploymentConditionType =
  (typeof K8sIoApiAppsV1DeploymentConditionType)[keyof typeof K8sIoApiAppsV1DeploymentConditionType];

export type K8sIoApiAppsV1DeploymentStrategy = {
  /**
   * Rolling update config params. Present only if DeploymentStrategyType =
   * RollingUpdate.
   * ---
   * TODO: Update this to follow our convention for oneOf, whatever we decide it
   * to be.
   * +optional
   */
  rollingUpdate?: V1RollingUpdateDeployment;
  /**
   * Type of deployment. Can be "Recreate" or "RollingUpdate". Default is RollingUpdate.
   * +optional
   */
  type?: K8sIoApiAppsV1DeploymentStrategyType;
};

export const K8sIoApiAppsV1DeploymentStrategyType = {
  /**
   * RecreateDeploymentStrategyType
   */
  RECREATE_DEPLOYMENT_STRATEGY_TYPE: 'Recreate' /**
   * RollingUpdateDeploymentStrategyType
   */,
  ROLLING_UPDATE_DEPLOYMENT_STRATEGY_TYPE: 'RollingUpdate',
} as const;

export type K8sIoApiAppsV1DeploymentStrategyType =
  (typeof K8sIoApiAppsV1DeploymentStrategyType)[keyof typeof K8sIoApiAppsV1DeploymentStrategyType];

export const K8sIoApiCoreV1ConditionStatus = {
  /**
   * ConditionTrue
   */
  CONDITION_TRUE: 'True',
  /**
   * ConditionFalse
   */
  CONDITION_FALSE: 'False',
  /**
   * ConditionUnknown
   */
  CONDITION_UNKNOWN: 'Unknown',
} as const;

export type K8sIoApiCoreV1ConditionStatus =
  (typeof K8sIoApiCoreV1ConditionStatus)[keyof typeof K8sIoApiCoreV1ConditionStatus];

export type K8sIoApiCoreV1HttpHeader = {
  /**
   * The header field name.
   * This will be canonicalized upon output, so case-variant names will be understood as the same header.
   */
  name?: string;
  /**
   * The header field value
   */
  value?: string;
};

export type K8sIoApiCoreV1LocalObjectReference = {
  /**
   * Name of the referent.
   * This field is effectively required, but due to backwards compatibility is
   * allowed to be empty. Instances of this type with an empty value here are
   * almost certainly wrong.
   * More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/names/#names
   * +optional
   * +default=""
   * +kubebuilder:default=""
   * TODO: Drop `kubebuilder:default` when controller-gen doesn't need it https://github.com/kubernetes-sigs/kubebuilder/issues/3896.
   */
  name?: string;
};

export type K8sIoApiCoreV1ObjectReference = {
  /**
   * API version of the referent.
   * +optional
   */
  apiVersion?: string;
  /**
   * If referring to a piece of an object instead of an entire object, this string
   * should contain a valid JSON/Go field access statement, such as desiredState.manifest.containers[2].
   * For example, if the object reference is to a container within a pod, this would take on a value like:
   * "spec.containers{name}" (where "name" refers to the name of the container that triggered
   * the event) or if no container name is specified "spec.containers[2]" (container with
   * index 2 in this pod). This syntax is chosen only to have some well-defined way of
   * referencing a part of an object.
   * TODO: this design is not final and this field is subject to change in the future.
   * +optional
   */
  fieldPath?: string;
  /**
   * Kind of the referent.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
   * +optional
   */
  kind?: string;
  /**
   * Name of the referent.
   * More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/names/#names
   * +optional
   */
  name?: string;
  /**
   * Namespace of the referent.
   * More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/namespaces/
   * +optional
   */
  namespace?: string;
  /**
   * Specific resourceVersion to which this reference is made, if any.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#concurrency-control-and-consistency
   * +optional
   */
  resourceVersion?: string;
  /**
   * UID of the referent.
   * More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/names/#uids
   * +optional
   */
  uid?: string;
};

export type K8sIoApiCoreV1ResourceClaim = {
  /**
   * Name must match the name of one entry in pod.spec.resourceClaims of
   * the Pod where this field is used. It makes that resource available
   * inside a container.
   */
  name?: string;
  /**
   * Request is the name chosen for a request in the referenced claim.
   * If empty, everything from the claim is made available, otherwise
   * only the result of this request.
   *
   * +optional
   */
  request?: string;
};

export type K8sIoApiRbacV1Subject = {
  /**
   * APIGroup holds the API group of the referenced subject.
   * Defaults to "" for ServiceAccount subjects.
   * Defaults to "rbac.authorization.k8s.io" for User and Group subjects.
   * +optional
   */
  apiGroup?: string;
  /**
   * Kind of object being referenced. Values defined by this API group are "User", "Group", and "ServiceAccount".
   * If the Authorizer does not recognized the kind value, the Authorizer should report an error.
   */
  kind?: string;
  /**
   * Name of the object being referenced.
   * +required
   * +k8s:required
   */
  name?: string;
  /**
   * Namespace of the referenced object.  If the object kind is non-namespace, such as "User" or "Group", and this value is not empty
   * the Authorizer should report an error.
   * +optional
   */
  namespace?: string;
};

export type KubernetesConfiguration = {
  ConfigurationOwner?: string;
  Data?: {
    [key: string]: unknown;
  };
  Kind?: string;
};

export type KubernetesCustomResourceMetadata = {
  apiVersion?: string;
  kind?: string;
  name?: string;
  plural?: string;
  scope?: string;
};

export type KubernetesIngressRule = {
  Host?: string;
  IP?: string;
  Path?: string;
  TLS?: Array<KubernetesTlsInfo>;
};

export type KubernetesK8sApplication = {
  Annotations?: {
    [key: string]: string;
  };
  ApplicationOwner?: string;
  ApplicationType?: string;
  Configurations?: Array<KubernetesConfiguration>;
  Containers?: Array<unknown>;
  CreationDate?: string;
  CustomResourceMetadata?: KubernetesCustomResourceMetadata;
  DeploymentType?: string;
  Id?: string;
  Image?: string;
  Kind?: string;
  Labels?: {
    [key: string]: string;
  };
  LoadBalancerIPAddress?: string;
  MatchLabels?: {
    [key: string]: string;
  };
  Metadata?: KubernetesMetadata;
  Name?: string;
  Namespace?: string;
  Pods?: Array<KubernetesPod>;
  PublishedPorts?: Array<KubernetesPublishedPort>;
  Resource?: KubernetesK8sApplicationResource;
  ResourcePool?: string;
  RunningPodsCount?: number;
  ServiceId?: string;
  ServiceName?: string;
  ServiceType?: string;
  StackId?: string;
  StackKind?: string;
  StackName?: string;
  Status?: string;
  TotalPodsCount?: number;
  Uid?: string;
};

export type KubernetesK8sApplicationResource = {
  CpuLimit?: number;
  CpuRequest?: number;
  MemoryLimit?: number;
  MemoryRequest?: number;
};

export type KubernetesK8sClusterRole = {
  creationDate?: string;
  isSystem?: boolean;
  name?: string;
  uid?: string;
};

export type KubernetesK8sClusterRoleBinding = {
  creationDate?: string;
  isSystem?: boolean;
  name?: string;
  namespace?: string;
  roleRef?: V1RoleRef;
  subjects?: Array<K8sIoApiRbacV1Subject>;
  uid?: string;
};

export type KubernetesK8sConfigMapWriteRequest = {
  Annotations?: {
    [key: string]: string;
  };
  Data?: {
    [key: string]: string;
  };
  Labels?: {
    [key: string]: string;
  };
  Name?: string;
};

export type KubernetesK8sContainer = {
  args?: Array<string>;
  command?: Array<string>;
  env?: Array<KubernetesK8sEnvVar>;
  /**
   * EnvFromSecrets names secrets whose keys are all exposed as environment
   * variables.
   */
  envFromSecrets?: Array<string>;
  image?: string;
  imagePullPolicy?: string;
  name?: string;
  ports?: Array<KubernetesK8sContainerPort>;
  resources?: KubernetesK8sResourceRequirements;
  volumeMounts?: Array<KubernetesK8sVolumeMount>;
  workingDir?: string;
};

export type KubernetesK8sContainerPort = {
  containerPort?: number;
  name?: string;
  protocol?: string;
};

export type KubernetesK8sCronJob = {
  Command?: string;
  Id?: string;
  IsSystem?: boolean;
  Jobs?: Array<KubernetesK8sJob>;
  Name?: string;
  Namespace?: string;
  Schedule?: string;
  Suspend?: boolean;
  Timezone?: string;
};

export type KubernetesK8sCronJobDeleteRequests = {
  [key: string]: Array<string>;
};

export type KubernetesK8sDeploymentPatchRequest = {
  annotations?: {
    [key: string]: string;
  };
  podAnnotations?: {
    [key: string]: string;
  };
};

export type KubernetesK8sDeploymentRollbackRequest = {
  revision?: number;
};

export type KubernetesK8sDeploymentScaleRequest = {
  replicas?: number;
};

export type KubernetesK8sDeploymentWriteRequest = {
  annotations?: {
    [key: string]: string;
  };
  labels?: {
    [key: string]: string;
  };
  name?: string;
  pod?: KubernetesK8sPodTemplate;
  replicas?: number;
  /**
   * Selector matches the pods this deployment owns. It is immutable once the
   * deployment exists, so it is only read on create.
   */
  selector?: {
    [key: string]: string;
  };
};

export type KubernetesK8sEndpoint = {
  addresses?: Array<KubernetesK8sEndpointAddress>;
  name?: string;
  namespace?: string;
  ports?: Array<KubernetesK8sEndpointPort>;
  uid?: string;
};

export type KubernetesK8sEndpointAddress = {
  ip?: string;
  nodeName?: string;
};

export type KubernetesK8sEndpointPort = {
  name?: string;
  port?: number;
  protocol?: string;
};

export type KubernetesK8sEnvVar = {
  name?: string;
  secretRef?: KubernetesK8sSecretKeyRef;
  value?: string;
};

export type KubernetesK8sEvent = {
  count?: number;
  eventTime?: string;
  firstTimestamp?: string;
  involvedObject?: KubernetesK8sEventInvolvedObject;
  kind?: string;
  lastTimestamp?: string;
  message?: string;
  name?: string;
  namespace?: string;
  reason?: string;
  type?: string;
  uid?: string;
};

export type KubernetesK8sEventInvolvedObject = {
  kind?: string;
  name?: string;
  namespace?: string;
  uid?: string;
};

export type KubernetesK8sIngressClass = {
  Annotations?: {
    [key: string]: string;
  };
  Controller?: string;
  IsDefault?: boolean;
  Name?: string;
};

export type KubernetesK8sJob = {
  BackoffLimit?: number;
  Command?: string;
  Completions?: number;
  Container?: V1Container;
  Duration?: string;
  FailedReason?: string;
  FinishTime?: string;
  Id?: string;
  IsSystem?: boolean;
  Name?: string;
  Namespace?: string;
  PodName?: string;
  StartTime?: string;
  Status?: string;
};

export type KubernetesK8sJobDeleteRequests = {
  [key: string]: Array<string>;
};

export type KubernetesK8sPersistentVolume = {
  accessModes?: Array<string>;
  annotations?: {
    [key: string]: string;
  };
  capacity?: V1ResourceList;
  claimRef?: K8sIoApiCoreV1ObjectReference;
  creationDate?: string;
  csi?: V1CsiPersistentVolumeSource;
  humanReadableAccessModes?: Array<V1PersistentVolumeAccessMode>;
  labels?: {
    [key: string]: string;
  };
  name?: string;
  persistentVolumeReclaimPolicy?: V1PersistentVolumeReclaimPolicy;
  status?: V1PersistentVolumePhase;
  storageClassName?: string;
  volumeMode?: V1PersistentVolumeMode;
};

export type KubernetesK8sPersistentVolumeClaim = {
  accessModes?: Array<string>;
  allowVolumeExpansion?: boolean;
  creationDate?: string;
  humanReadableAccessModes?: Array<V1PersistentVolumeAccessMode>;
  id?: string;
  labels?: {
    [key: string]: string;
  };
  name?: string;
  namespace?: string;
  owningApplications?: Array<KubernetesK8sApplication>;
  phase?: V1PersistentVolumeClaimPhase;
  resourcesRequests?: V1ResourceList;
  storage?: number;
  storageClass?: string;
  storageRequest?: string;
  volumeMode?: V1PersistentVolumeMode;
  volumeName?: string;
};

export type KubernetesK8sPersistentVolumeClaimCreateRequest = {
  accessModes?: Array<V1PersistentVolumeAccessMode>;
  annotations?: {
    [key: string]: string;
  };
  labels?: {
    [key: string]: string;
  };
  name?: string;
  storage?: string;
  storageClass?: string;
  volumeMode?: V1PersistentVolumeMode;
};

export type KubernetesK8sPodTemplate = {
  annotations?: {
    [key: string]: string;
  };
  containers?: Array<KubernetesK8sContainer>;
  labels?: {
    [key: string]: string;
  };
  volumes?: Array<KubernetesK8sPodVolume>;
};

export type KubernetesK8sPodVolume = {
  claimName?: string;
  name?: string;
};

export type KubernetesK8sResourceRequirements = {
  limits?: {
    [key: string]: string;
  };
  requests?: {
    [key: string]: string;
  };
};

export type KubernetesK8sSecretKeyRef = {
  key?: string;
  name?: string;
};

export type KubernetesK8sSecretWriteRequest = {
  Annotations?: {
    [key: string]: string;
  };
  Data?: {
    [key: string]: string;
  };
  Labels?: {
    [key: string]: string;
  };
  Name?: string;
  SecretType?: string;
};

export type KubernetesK8sServiceAccount = {
  annotations?: {
    [key: string]: string;
  };
  automountServiceAccountToken?: boolean;
  creationDate?: string;
  imagePullSecrets?: Array<K8sIoApiCoreV1LocalObjectReference>;
  isSystem?: boolean;
  labels?: {
    [key: string]: string;
  };
  name?: string;
  namespace?: string;
  uid?: string;
};

export type KubernetesK8sServiceAccountDeleteRequests = {
  [key: string]: Array<string>;
};

export type KubernetesK8sServiceAccountImagePullSecretsUpdatePayload = {
  secretNames?: Array<string>;
};

export type KubernetesK8sStorageClass = {
  allowVolumeExpansion?: boolean;
  annotations?: {
    [key: string]: string;
  };
  creationDate?: string;
  isDefault?: boolean;
  labels?: {
    [key: string]: string;
  };
  mountOptions?: Array<string>;
  name?: string;
  parameters?: {
    [key: string]: string;
  };
  provisioner?: string;
  reclaimPolicy?: V1PersistentVolumeReclaimPolicy;
};

export type KubernetesK8sVolumeInfo = {
  persistentVolume?: KubernetesK8sPersistentVolume;
  persistentVolumeClaim?: KubernetesK8sPersistentVolumeClaim;
  storageClass?: KubernetesK8sStorageClass;
};

export type KubernetesK8sVolumeMount = {
  mountPath?: string;
  name?: string;
  readOnly?: boolean;
  subPath?: string;
};

export type KubernetesMetadata = {
  annotations?: {
    [key: string]: string;
  };
  labels?: {
    [key: string]: string;
  };
};

export type KubernetesPod = {
  ContainerName?: string;
  CreationDate?: string;
  Image?: string;
  ImagePullPolicy?: string;
  Name?: string;
  NodeName?: string;
  PodIP?: string;
  Resource?: KubernetesK8sApplicationResource;
  Status?: string;
  Uid?: string;
};

export type KubernetesPublishedPort = {
  IngressRules?: Array<KubernetesIngressRule>;
  Port?: number;
};

export type KubernetesTlsInfo = {
  hosts?: Array<string>;
};

export type LdapAdminGroupsPayload = {
  LDAPSettings?: PortainereeLdapSettings;
};

export type LdapCheckPayload = {
  LDAPSettings?: PortainereeLdapSettings;
};

export type LdapGroupsPayload = {
  LDAPSettings?: PortainereeLdapSettings;
};

export type LdapTestLoginPayload = {
  LDAPSettings?: PortainereeLdapSettings;
  Password?: string;
  Username?: string;
};

export type LdapTestLoginResponse = {
  valid?: boolean;
};

export type LdapUsersPayload = {
  LDAPSettings?: PortainereeLdapSettings;
};

export type LiblicensePortainerLicense = {
  company?: string;
  created?: number;
  email?: string;
  expiresAfter?: number;
  expiresAt?: number;
  firstCheckin?: number;
  id?: string;
  lastCheckin?: number;
  licenseKey?: string;
  multiCheckinCount?: number;
  multiuseInstancesCount?: number;
  nodes?: number;
  /**
   * ProductEdition was created originally with plans on having a
   * seperate portainer product for Enterprise users and Business users
   * with differing features. This didn't wind up coming about, but may
   * still serve useful in the future if we need to issue keys for a
   * different product entirely.
   * Originally, the ProductEdition was used as the prefix for generating
   * license keys, but in practice most people thought it was the
   * "version" due to us having the original extension licenses which can
   * be thought of as the true version 1 licenses.
   */
  productEdition?: LiblicenseProductEdition;
  redisRef?: string;
  reference?: string;
  revoked?: boolean;
  revokedAt?: number;
  /**
   * Type is used to distinguish different kinds of licenses, trial
   * licenses, enterprise subscriptions
   */
  type?: LiblicensePortainerLicenseType;
  uniqueId?: string;
  /**
   * Version indicates which key should be used to encode/decode the
   * license string.
   */
  version?: number;
};

export const LiblicensePortainerLicenseType = {
  /**
   * _
   */
  '': 0,
  /**
   * PortainerLicenseTrial
   */
  PORTAINER_LICENSE_TRIAL: 1,
  /**
   * PortainerLicenseSubscription
   */
  PORTAINER_LICENSE_SUBSCRIPTION: 2,
  /**
   * PortainerLicenseFree
   */
  PORTAINER_LICENSE_FREE: 3,
  /**
   * PortainerLicensePersonal
   */
  PORTAINER_LICENSE_PERSONAL: 4,
  /**
   * PortainerLicenseStarter
   */
  PORTAINER_LICENSE_STARTER: 5,
} as const;

export type LiblicensePortainerLicenseType =
  (typeof LiblicensePortainerLicenseType)[keyof typeof LiblicensePortainerLicenseType];

export const LiblicenseProductEdition = {
  /**
   * _
   */
  '': 0,
  /**
   * PortainerCE
   */
  PORTAINER_CE: 1,
  /**
   * PortainerBE
   */
  PORTAINER_BE: 2,
  /**
   * PortainerEE
   */
  PORTAINER_EE: 3,
} as const;

export type LiblicenseProductEdition =
  (typeof LiblicenseProductEdition)[keyof typeof LiblicenseProductEdition];

export type LicensesLicenseInfo = {
  company?: string;
  enforcedAt?: number;
  expiresAt?: number;
  nodes?: number;
  /**
   * unix timestamp when node usage exceeded available license limit
   */
  overuseStartedTimestamp?: number;
  type?: LiblicensePortainerLicenseType;
  valid?: boolean;
};

export type LicensesAttachData = {
  conflictingKeys?: Array<string>;
};

export type LicensesAttachPayload = {
  key?: string;
};

export type LicensesDeletePayload = {
  /**
   * List of license keys to remove
   */
  LicenseKeys?: Array<string>;
};

export type ModelsCloudCredential = {
  created?: number;
  credentials?: ModelsCloudCredentialMap;
  id?: number;
  name?: string;
  provider?: string;
};

export type ModelsCloudCredentialMap = {
  [key: string]: string;
};

export type ModelsConfiguration = {
  ConfigurationOwner?: string;
  Data?: {
    [key: string]: unknown;
  };
  Kind?: string;
};

export type ModelsIngressRule = {
  Host?: string;
  IP?: string;
  Path?: string;
  TLS?: Array<ModelsTlsInfo>;
};

export type ModelsK8sApplication = {
  Annotations?: {
    [key: string]: string;
  };
  ApplicationOwner?: string;
  ApplicationType?: string;
  Configurations?: Array<ModelsConfiguration>;
  Containers?: Array<unknown>;
  CreationDate?: string;
  DeploymentType?: string;
  Id?: string;
  Image?: string;
  Kind?: string;
  Labels?: {
    [key: string]: string;
  };
  LoadBalancerIPAddress?: string;
  MatchLabels?: {
    [key: string]: string;
  };
  Metadata?: ModelsMetadata;
  Name?: string;
  Namespace?: string;
  Pods?: Array<ModelsPod>;
  PublishedPorts?: Array<ModelsPublishedPort>;
  Resource?: ModelsK8sApplicationResource;
  ResourcePool?: string;
  RunningPodsCount?: number;
  ServiceId?: string;
  ServiceName?: string;
  ServiceType?: string;
  StackId?: string;
  StackName?: string;
  Status?: string;
  TotalPodsCount?: number;
  Uid?: string;
};

export type ModelsK8sApplicationResource = {
  CpuLimit?: number;
  CpuRequest?: number;
  MemoryLimit?: number;
  MemoryRequest?: number;
};

export type ModelsK8sConfigMap = {
  Annotations?: {
    [key: string]: string;
  };
  ConfigurationOwner?: string;
  ConfigurationOwnerId?: string;
  ConfigurationOwners?: Array<ModelsK8sConfigurationOwnerResource>;
  CreationDate?: string;
  Data?: {
    [key: string]: string;
  };
  IsUsed?: boolean;
  Labels?: {
    [key: string]: string;
  };
  Name?: string;
  Namespace?: string;
  UID?: string;
};

export type ModelsK8sConfigurationOwnerResource = {
  Id?: string;
  Name?: string;
  ResourceKind?: string;
};

export type ModelsK8sCustomResource = {
  creationDate?: string;
  definitionName?: string;
  name?: string;
  namespace?: string;
  uid?: string;
};

export type ModelsK8sCustomResourceDefinition = {
  creationDate?: string;
  group?: string;
  name?: string;
  releaseName?: string;
  releaseNamespace?: string;
  releaseVersion?: string;
  scope?: string;
};

export type ModelsK8sDashboard = {
  applicationsCount?: number;
  configMapsCount?: number;
  ingressesCount?: number;
  namespacesCount?: number;
  secretsCount?: number;
  servicesCount?: number;
  volumesCount?: number;
};

export type ModelsK8sGpuInfo = {
  Nodes?: Array<ModelsK8sGpuNode>;
  Summary?: ModelsK8sGpuSummary;
  Workloads?: Array<ModelsK8sGpuWorkload>;
};

export type ModelsK8sGpuNode = {
  Allocatable?: {
    [key: string]: number;
  };
  Allocated?: {
    [key: string]: number;
  };
  Capacity?: {
    [key: string]: number;
  };
  Name?: string;
  Status?: ModelsK8sGpuNodeStatus;
  StatusReason?: string;
};

export const ModelsK8sGpuNodeStatus = {
  /**
   * K8sGPUNodeStatusReady
   */
  K8S_GPU_NODE_STATUS_READY: 'Ready',
  /**
   * K8sGPUNodeStatusDegraded
   */
  K8S_GPU_NODE_STATUS_DEGRADED: 'Degraded',
  /**
   * K8sGPUNodeStatusNotAvailable
   */
  K8S_GPU_NODE_STATUS_NOT_AVAILABLE: 'Not Available',
} as const;

export type ModelsK8sGpuNodeStatus =
  (typeof ModelsK8sGpuNodeStatus)[keyof typeof ModelsK8sGpuNodeStatus];

export type ModelsK8sGpuSummary = {
  DegradedCount?: number;
  DevicePluginDetected?: boolean;
  GPUNodeCount?: number;
  OperatorDetected?: boolean;
  TotalAllocatable?: {
    [key: string]: number;
  };
  TotalAllocated?: {
    [key: string]: number;
  };
  TotalCapacity?: {
    [key: string]: number;
  };
};

export type ModelsK8sGpuWorkload = {
  GPURequests?: {
    [key: string]: number;
  };
  Namespace?: string;
  NodeName?: string;
  OwnerKind?: string;
  OwnerName?: string;
  PodName?: string;
  PodPhase?: string;
  SchedulingIssue?: string;
};

export type ModelsK8sIngressController = {
  Availability?: boolean;
  ClassName?: string;
  Name?: string;
  New?: boolean;
  Type?: string;
  Used?: boolean;
};

export type ModelsK8sIngressDeleteRequests = {
  [key: string]: Array<string>;
};

export type ModelsK8sIngressInfo = {
  Annotations?: {
    [key: string]: string;
  };
  ClassName?: string;
  CreationDate?: string;
  Hosts?: Array<string>;
  Labels?: {
    [key: string]: string;
  };
  Name?: string;
  Namespace?: string;
  Paths?: Array<ModelsK8sIngressPath>;
  TLS?: Array<ModelsK8sIngressTls>;
  Type?: string;
  UID?: string;
};

export type ModelsK8sIngressPath = {
  HasService?: boolean;
  Host?: string;
  IngressName?: string;
  Path?: string;
  PathType?: string;
  Port?: number;
  ServiceName?: string;
};

export type ModelsK8sIngressTls = {
  Hosts?: Array<string>;
  SecretName?: string;
};

export type ModelsK8sLoadBalancerQuota = {
  enabled?: boolean;
  limit?: number;
};

export type ModelsK8sNamespaceDetails = {
  Annotations?: {
    [key: string]: string;
  };
  LoadBalancerQuota?: ModelsK8sLoadBalancerQuota;
  Name?: string;
  Owner?: string;
  ResourceQuota?: ModelsK8sResourceQuota;
  StorageQuotas?: {
    [key: string]: ModelsK8sStorageQuota;
  };
};

export type ModelsK8sPvcResizeRequest = {
  name?: string;
  namespace?: string;
  newSize?: string;
};

export type ModelsK8sPvReclaimPolicyRequest = {
  name?: string;
  reclaimPolicy?: V1PersistentVolumeReclaimPolicy;
};

export type ModelsK8sPersistentVolume = {
  accessModes?: Array<string>;
  annotations?: {
    [key: string]: string;
  };
  capacity?: V1ResourceList;
  claimRef?: K8sIoApiCoreV1ObjectReference;
  creationDate?: string;
  csi?: V1CsiPersistentVolumeSource;
  humanReadableAccessModes?: Array<V1PersistentVolumeAccessMode>;
  labels?: {
    [key: string]: string;
  };
  name?: string;
  persistentVolumeReclaimPolicy?: V1PersistentVolumeReclaimPolicy;
  status?: V1PersistentVolumePhase;
  storageClassName?: string;
  volumeMode?: V1PersistentVolumeMode;
};

export type ModelsK8sPersistentVolumeClaim = {
  accessModes?: Array<string>;
  creationDate?: string;
  humanReadableAccessModes?: Array<V1PersistentVolumeAccessMode>;
  id?: string;
  labels?: {
    [key: string]: string;
  };
  name?: string;
  namespace?: string;
  owningApplications?: Array<ModelsK8sApplication>;
  phase?: V1PersistentVolumeClaimPhase;
  resourcesRequests?: V1ResourceList;
  storage?: number;
  storageClass?: string;
  storageRequest?: string;
  volumeMode?: V1PersistentVolumeMode;
  volumeName?: string;
};

export type ModelsK8sResourceQuota = {
  cpuLimit?: string;
  cpuRequest?: string;
  enabled?: boolean;
  memoryLimit?: string;
  memoryRequest?: string;
};

export type ModelsK8sRole = {
  annotations?: {
    [key: string]: string;
  };
  creationDate?: string;
  /**
   * isSystem is true if prefixed with "system:" or exists in the kube-system namespace
   * or is one of the portainer roles
   */
  isSystem?: boolean;
  /**
   * Unused is true if the role is not bound to any subject.
   */
  isUnused?: boolean;
  name?: string;
  namespace?: string;
  resourceVersion?: string;
  rules?: Array<V1PolicyRule>;
  uid?: string;
};

export type ModelsK8sRoleBinding = {
  annotations?: {
    [key: string]: string;
  };
  creationDate?: string;
  isSystem?: boolean;
  isUnused?: boolean;
  name?: string;
  namespace?: string;
  resourceVersion?: string;
  roleRef?: V1RoleRef;
  subjects?: Array<K8sIoApiRbacV1Subject>;
  uid?: string;
};

export type ModelsK8sRoleBindingDeleteRequests = {
  [key: string]: Array<string>;
};

export type ModelsK8sRoleDeleteRequests = {
  [key: string]: Array<string>;
};

export type ModelsK8sSecret = {
  Annotations?: {
    [key: string]: string;
  };
  ConfigurationOwner?: string;
  ConfigurationOwnerId?: string;
  ConfigurationOwners?: Array<ModelsK8sConfigurationOwnerResource>;
  CreationDate?: string;
  Data?: {
    [key: string]: string;
  };
  IsUsed?: boolean;
  Labels?: {
    [key: string]: string;
  };
  Name?: string;
  Namespace?: string;
  SecretType?: string;
  UID?: string;
};

export type ModelsK8sServiceDeleteRequests = {
  [key: string]: Array<string>;
};

export type ModelsK8sServiceInfo = {
  AllocateLoadBalancerNodePorts?: boolean;
  Annotations?: {
    [key: string]: string;
  };
  /**
   * serviceList screen
   */
  Applications?: Array<ModelsK8sApplication>;
  ClusterIPs?: Array<string>;
  CreationDate?: string;
  ExternalIPs?: Array<string>;
  ExternalName?: string;
  IngressStatus?: Array<ModelsK8sServiceIngress>;
  Labels?: {
    [key: string]: string;
  };
  Name?: string;
  Namespace?: string;
  Ports?: Array<ModelsK8sServicePort>;
  Selector?: {
    [key: string]: string;
  };
  Type?: string;
  UID?: string;
};

export type ModelsK8sServiceIngress = {
  Host?: string;
  IP?: string;
};

export type ModelsK8sServicePort = {
  Name?: string;
  NodePort?: number;
  Port?: number;
  Protocol?: string;
  TargetPort?: string;
};

export type ModelsK8sStorageClass = {
  allowVolumeExpansion?: boolean;
  annotations?: {
    [key: string]: string;
  };
  creationDate?: string;
  isDefault?: boolean;
  labels?: {
    [key: string]: string;
  };
  mountOptions?: Array<string>;
  name?: string;
  parameters?: {
    [key: string]: string;
  };
  provisioner?: string;
  reclaimPolicy?: V1PersistentVolumeReclaimPolicy;
};

export type ModelsK8sStorageQuota = {
  enabled?: boolean;
  limit?: string;
};

export type ModelsK8sVolumeDeleteRequest = {
  name?: string;
  namespace?: string;
};

export type ModelsMatcher = {
  /**
   * is equal
   */
  isEqual?: boolean;
  /**
   * is regex
   * Required: true
   */
  isRegex?: boolean;
  /**
   * name
   * Required: true
   */
  name?: string;
  /**
   * value
   * Required: true
   */
  value?: string;
};

export type ModelsMetadata = {
  annotations?: {
    [key: string]: string;
  };
  labels?: {
    [key: string]: string;
  };
};

export type ModelsPod = {
  ContainerName?: string;
  CreationDate?: string;
  Image?: string;
  ImagePullPolicy?: string;
  Name?: string;
  NodeName?: string;
  PodIP?: string;
  Status?: string;
  Uid?: string;
};

export type ModelsPostableSilence = {
  /**
   * comment
   * Required: true
   */
  comment?: string;
  /**
   * created by
   * Required: true
   */
  createdBy?: string;
  /**
   * ends at
   * Required: true
   * Format: date-time
   */
  endsAt?: string;
  /**
   * id
   */
  id?: string;
  /**
   * matchers
   * Required: true
   */
  matchers?: Array<ModelsMatcher>;
  /**
   * starts at
   * Required: true
   * Format: date-time
   */
  startsAt?: string;
};

export type ModelsPublishedPort = {
  IngressRules?: Array<ModelsIngressRule>;
  Port?: number;
};

export type ModelsSshKeyPair = {
  private?: string;
  public?: string;
};

export type ModelsTlsInfo = {
  hosts?: Array<string>;
};

export type MotdMotd = {
  ContentLayout?: {
    [key: string]: string;
  };
  Hash?: Array<number>;
  Message?: string;
  Style?: string;
  Title?: string;
};

export const MountPropagation = {
  /**
   * PropagationRPrivate
   */
  PROPAGATION_R_PRIVATE: 'rprivate',
  /**
   * PropagationPrivate
   */
  PROPAGATION_PRIVATE: 'private',
  /**
   * PropagationRShared
   */
  PROPAGATION_R_SHARED: 'rshared',
  /**
   * PropagationShared
   */
  PROPAGATION_SHARED: 'shared',
  /**
   * PropagationRSlave
   */
  PROPAGATION_R_SLAVE: 'rslave',
  /**
   * PropagationSlave
   */
  PROPAGATION_SLAVE: 'slave',
} as const;

export type MountPropagation =
  (typeof MountPropagation)[keyof typeof MountPropagation];

export const MountType = {
  /**
   * TypeBind
   */
  TYPE_BIND: 'bind',
  /**
   * TypeVolume
   */
  TYPE_VOLUME: 'volume',
  /**
   * TypeTmpfs
   */
  TYPE_TMPFS: 'tmpfs',
  /**
   * TypeNamedPipe
   */
  TYPE_NAMED_PIPE: 'npipe',
  /**
   * TypeCluster
   */
  TYPE_CLUSTER: 'cluster',
  /**
   * TypeImage
   */
  TYPE_IMAGE: 'image',
} as const;

export type MountType = (typeof MountType)[keyof typeof MountType];

export type NetworkEndpointIpamConfig = {
  IPv4Address?: string;
  IPv6Address?: string;
  LinkLocalIPs?: Array<string>;
};

export type NetworkEndpointSettings = {
  /**
   * Aliases holds the list of extra, user-specified DNS names for this endpoint.
   */
  Aliases?: Array<string>;
  /**
   * DNSNames holds all the (non fully qualified) DNS names associated to this endpoint. First entry is used to
   * generate PTR records.
   */
  DNSNames?: Array<string>;
  DriverOpts?: {
    [key: string]: string;
  };
  EndpointID?: string;
  Gateway?: string;
  GlobalIPv6Address?: string;
  GlobalIPv6PrefixLen?: number;
  /**
   * GwPriority determines which endpoint will provide the default gateway
   * for the container. The endpoint with the highest priority will be used.
   * If multiple endpoints have the same priority, they are lexicographically
   * sorted based on their network name, and the one that sorts first is picked.
   */
  GwPriority?: number;
  /**
   * Configurations
   */
  IPAMConfig?: NetworkEndpointIpamConfig;
  IPAddress?: string;
  IPPrefixLen?: number;
  IPv6Gateway?: string;
  Links?: Array<string>;
  /**
   * MacAddress may be used to specify a MAC address when the container is created.
   * Once the container is running, it becomes operational data (it may contain a
   * generated address).
   */
  MacAddress?: string;
  /**
   * Operational data
   */
  NetworkID?: string;
};

export const Oauth2AuthStyle = {
  /**
   * AuthStyleAutoDetect
   */
  AUTH_STYLE_AUTO_DETECT: 0,
  /**
   * AuthStyleInParams
   */
  AUTH_STYLE_IN_PARAMS: 1,
  /**
   * AuthStyleInHeader
   */
  AUTH_STYLE_IN_HEADER: 2,
} as const;

export type Oauth2AuthStyle =
  (typeof Oauth2AuthStyle)[keyof typeof Oauth2AuthStyle];

export type ObservabilityAlertRuleCreatePayload = {
  AlertingRule?: PortainereeAlertingRule;
};

export type ObservabilityAlertRuleEnvironmentGroupResponse = {
  endpointGroupId?: number;
  endpointGroupName?: string;
  environments?: Array<ObservabilityAlertRuleEnvironmentResponse>;
  size?: number;
};

export type ObservabilityAlertRuleEnvironmentResponse = {
  endpointId?: number;
  message?: string;
  name?: string;
  reasonCode?: PortainereeAlertRuleEnvReason;
  status?: PortainereeAlertRuleEnvStatus;
  updatedAt?: number;
};

export type ObservabilityAlertRuleEnvironmentsResponse = {
  active?: number;
  error?: number;
  excluded?: number;
  groups?: Array<ObservabilityAlertRuleEnvironmentGroupResponse>;
  notSupported?: number;
  pending?: number;
  total?: number;
};

export type ObservabilityAlertRuleGroupsUpdatePayload = {
  endpointGroupIds?: Array<number>;
};

export type ObservabilityAlertRuleTiersUpdatePayload = {
  conditionOperator?: PortainereeConditionOperator;
  description?: string;
  duration?: number;
  enabled?: boolean;
  tiers?: Array<PortainereeAlertingRuleTier>;
};

export type ObservabilityAlertRuleUpdatePayload = {
  AlertingRule?: PortainereeAlertingRule;
};

export type ObservabilityAlertingUpdatePayload = {
  AlertingSettings?: PortainereeAlertingSettings;
};

export type ObservabilityCreateSilencePayload = {
  alertManagerURL: string;
  silence: ModelsPostableSilence;
};

export type ObservabilityEnvironmentObservabilityResponse = {
  /**
   * ClusterID is OneUptime's internal cluster identifier, used by the
   * frontend to build deep links into the OneUptime dashboard. Omitted
   * when the cluster could not be resolved.
   */
  clusterId?: string;
  clusterName?: string;
  enabled?: boolean;
  projectId?: string;
  provider?: string;
  url?: string;
};

export type ObservabilityLogEntry = {
  labels?: {
    [key: string]: string;
  };
  message?: string;
  severity?: string;
  source?: string;
  time?: string;
};

export type ObservabilityLogsResponse = {
  logs?: Array<ObservabilityLogEntry>;
};

export type OmniCancelProvisioningPayload = {
  deleteCluster?: boolean;
  endpointId?: number;
};

export type OmniClusterData = {
  metadata?: OmniClusterSpec;
  status?: OmniClusterStatusSpec;
};

export type OmniClusterSpec = {
  backup_configuration?: OmniClusterSpecBackupConfiguration;
  features?: OmniClusterSpecFeatures;
  kubernetes_version?: string;
  talos_version?: string;
};

export type OmniClusterSpecBackupConfiguration = {
  enabled?: boolean;
  interval?: string;
};

export type OmniClusterSpecFeatures = {
  disk_encryption?: boolean;
  enable_workload_proxy?: boolean;
  use_embedded_discovery_service?: boolean;
};

export type OmniClusterStatusMachines = {
  connected?: number;
  healthy?: number;
  requested?: number;
  total?: number;
};

export type OmniClusterStatusSpec = {
  available?: boolean;
  controlplaneReady?: boolean;
  has_connected_control_planes?: boolean;
  kubernetesAPIReady?: boolean;
  machines?: OmniClusterStatusMachines;
  phase?: number;
  ready?: boolean;
  use_embedded_discovery_service?: boolean;
};

export type OmniCreateClusterPayload = {
  cluster?: PortainereeOmniCluster;
  clusterConfig?: string;
  controlPlane?: PortainereeOmniControlPlane;
  portainerUrl?: string;
  tunnelServerAddr?: string;
  worker?: PortainereeOmniWorker;
};

export type OmniMachineData = {
  labels?: {
    [key: string]: string;
  };
  machineName?: string;
  spec?: OmniMachineStatusSpec;
};

export type OmniMachineHardwareBlockDevice = {
  linux_name?: string;
  model?: string;
  size?: number;
  system_disk?: boolean;
  type?: string;
};

export type OmniMachineHardwareMemory = {
  description?: string;
  size_mb?: number;
};

export type OmniMachineHardwareProcessor = {
  core_count?: number;
  description?: string;
  frequency?: number;
  manufacturer?: string;
  thread_count?: number;
};

export type OmniMachineHardwareStatus = {
  arch?: string;
  blockdevices?: Array<OmniMachineHardwareBlockDevice>;
  memory_modules?: Array<OmniMachineHardwareMemory>;
  processors?: Array<OmniMachineHardwareProcessor>;
};

export type OmniMachineNetworkLink = {
  hardware_address?: string;
  link_up?: boolean;
  linux_name?: string;
  speed_mbps?: number;
};

export type OmniMachineNetworkStatus = {
  addresses?: Array<string>;
  default_gateways?: Array<string>;
  domainname?: string;
  hostname?: string;
  network_links?: Array<OmniMachineNetworkLink>;
};

export type OmniMachinePlatformMetadata = {
  hostname?: string;
  instance_id?: string;
  instance_type?: string;
  platform?: string;
  region?: string;
  zone?: string;
};

export type OmniMachineStatusSpec = {
  cluster?: string;
  connected?: boolean;
  hardware?: OmniMachineHardwareStatus;
  last_error?: string;
  maintenance?: boolean;
  management_address?: string;
  network?: OmniMachineNetworkStatus;
  platform_metadata?: OmniMachinePlatformMetadata;
  power_state?: string;
  role?: string;
  talos_version?: string;
};

export type OmniUpdateClusterPayload = {
  cluster?: PortainereeOmniCluster;
  controlPlanesToAdd?: PortainereeOmniControlPlane;
  controlPlanesToRemove?: Array<string>;
  workersToAdd?: PortainereeOmniWorker;
  workersToRemove?: Array<string>;
};

export type OmniUpgradeStatusResponse = {
  current_upgrade_version?: string;
  error?: string;
  last_upgrade_version?: string;
  phase?: number;
  status?: string;
  step?: string;
  upgrade_versions?: Array<string>;
};

export type OmniValidateClusterPayload = {
  cluster?: PortainereeOmniCluster;
  clusterConfig?: string;
  controlPlane?: PortainereeOmniControlPlane;
  worker?: PortainereeOmniWorker;
};

export type OneuptimeMetricsAggregateResponse = {
  data?: Array<{
    [key: string]: unknown;
  }>;
};

export const PlatformContainerPlatform = {
  /**
   * PlatformDocker
   */
  PLATFORM_DOCKER: 'Docker',
  /**
   * PlatformDockerStandalone
   */
  PLATFORM_DOCKER_STANDALONE: 'Docker Standalone',
  /**
   * PlatformDockerSwarm
   */
  PLATFORM_DOCKER_SWARM: 'Docker Swarm',
  /**
   * PlatformKubernetes
   */
  PLATFORM_KUBERNETES: 'Kubernetes',
  /**
   * PlatformPodman
   */
  PLATFORM_PODMAN: 'Podman',
} as const;

export type PlatformContainerPlatform =
  (typeof PlatformContainerPlatform)[keyof typeof PlatformContainerPlatform];

export const PodsecurityFsGroupStrategyType = {
  /**
   * FSGroupStrategyMayRunAs
   */
  FS_GROUP_STRATEGY_MAY_RUN_AS: 'MayRunAs',
  /**
   * FSGroupStrategyMustRunAs
   */
  FS_GROUP_STRATEGY_MUST_RUN_AS: 'MustRunAs',
  /**
   * FSGroupStrategyRunAsAny
   */
  FS_GROUP_STRATEGY_RUN_AS_ANY: 'RunAsAny',
} as const;

export type PodsecurityFsGroupStrategyType =
  (typeof PodsecurityFsGroupStrategyType)[keyof typeof PodsecurityFsGroupStrategyType];

export const PodsecurityFsType = {
  /**
   * AzureFile
   */
  AZURE_FILE: 'azureFile',
  /**
   * Flocker
   */
  FLOCKER: 'flocker',
  /**
   * FlexVolume
   */
  FLEX_VOLUME: 'flexVolume',
  /**
   * HostPath
   */
  HOST_PATH: 'hostPath',
  /**
   * EmptyDir
   */
  EMPTY_DIR: 'emptyDir',
  /**
   * GCEPersistentDisk
   */
  GCE_PERSISTENT_DISK: 'gcePersistentDisk',
  /**
   * AWSElasticBlockStore
   */
  AWS_ELASTIC_BLOCK_STORE: 'awsElasticBlockStore',
  /**
   * GitRepo
   */
  GIT_REPO: 'gitRepo',
  /**
   * Secret
   */
  SECRET: 'secret',
  /**
   * NFS
   */
  NFS: 'nfs',
  /**
   * ISCSI
   */
  ISCSI: 'iscsi',
  /**
   * Glusterfs
   */
  GLUSTERFS: 'glusterfs',
  /**
   * PersistentVolumeClaim
   */
  PERSISTENT_VOLUME_CLAIM: 'persistentVolumeClaim',
  /**
   * RBD
   */
  RBD: 'rbd',
  /**
   * Cinder
   */
  CINDER: 'cinder',
  /**
   * CephFS
   */
  CEPH_FS: 'cephFS',
  /**
   * DownwardAPI
   */
  DOWNWARD_API: 'downwardAPI',
  /**
   * FC
   */
  FC: 'fc',
  /**
   * ConfigMap
   */
  CONFIG_MAP: 'configMap',
  /**
   * VsphereVolume
   */
  VSPHERE_VOLUME: 'vsphereVolume',
  /**
   * Quobyte
   */
  QUOBYTE: 'quobyte',
  /**
   * AzureDisk
   */
  AZURE_DISK: 'azureDisk',
  /**
   * PhotonPersistentDisk
   */
  PHOTON_PERSISTENT_DISK: 'photonPersistentDisk',
  /**
   * StorageOS
   */
  STORAGE_OS: 'storageos',
  /**
   * Projected
   */
  PROJECTED: 'projected',
  /**
   * PortworxVolume
   */
  PORTWORX_VOLUME: 'portworxVolume',
  /**
   * ScaleIO
   */
  SCALE_IO: 'scaleIO',
  /**
   * CSI
   */
  CSI: 'csi',
  /**
   * Ephemeral
   */
  EPHEMERAL: 'ephemeral',
  /**
   * All
   */
  ALL: '*',
} as const;

export type PodsecurityFsType =
  (typeof PodsecurityFsType)[keyof typeof PodsecurityFsType];

export type PodsecurityPodSecurityAllowFlexVolumes = {
  allowedVolumes?: Array<string>;
  enabled?: boolean;
};

export type PodsecurityPodSecurityAllowPrivilegeEscalation = {
  enabled?: boolean;
};

export type PodsecurityPodSecurityAllowProcMount = {
  enabled?: boolean;
  procMountType?: string;
};

export type PodsecurityPodSecurityAllowedCapabilities = {
  level?: string;
  role?: string;
  type?: string;
  user?: string;
};

export type PodsecurityPodSecurityAllowedPaths = {
  pathPrefix?: string;
  readonly?: boolean;
};

export type PodsecurityPodSecurityAppArmour = {
  AppArmorType?: Array<string>;
  enabled?: boolean;
};

export type PodsecurityPodSecurityCapabilities = {
  allowedCapabilities?: Array<string>;
  enabled?: boolean;
  requiredDropCapabilities?: Array<string>;
};

export type PodsecurityPodSecurityForbiddenSysctlsList = {
  enabled?: boolean;
  requiredDropCapabilities?: Array<string>;
};

export type PodsecurityPodSecurityFsGroups = {
  idrange?: Array<PodsecurityPodSecurityIdrange>;
  type?: PodsecurityFsGroupStrategyType;
};

export type PodsecurityPodSecurityHostFilesystem = {
  allowedPaths?: Array<PodsecurityPodSecurityAllowedPaths>;
  enabled?: boolean;
};

export type PodsecurityPodSecurityHostNamespaces = {
  enabled?: boolean;
};

export type PodsecurityPodSecurityHostNetworkingPorts = {
  enabled?: boolean;
  hostNetwork?: boolean;
  max?: number;
  min?: number;
};

export type PodsecurityPodSecurityIdrange = {
  max?: number;
  min?: number;
};

export type PodsecurityPodSecurityPrivilegedContainers = {
  enabled?: boolean;
};

export type PodsecurityPodSecurityReadOnlyRootFileSystem = {
  enabled?: boolean;
};

export type PodsecurityPodSecurityRule = {
  allowFlexVolumes?: PodsecurityPodSecurityAllowFlexVolumes;
  allowPrivilegeEscalation?: PodsecurityPodSecurityAllowPrivilegeEscalation;
  allowProcMount?: PodsecurityPodSecurityAllowProcMount;
  appArmor?: PodsecurityPodSecurityAppArmour;
  capabilities?: PodsecurityPodSecurityCapabilities;
  enabled?: boolean;
  endPointID?: number;
  forbiddenSysctlsList?: PodsecurityPodSecurityForbiddenSysctlsList;
  hostFilesystem?: PodsecurityPodSecurityHostFilesystem;
  hostNamespaces?: PodsecurityPodSecurityHostNamespaces;
  hostPorts?: PodsecurityPodSecurityHostNetworkingPorts;
  id?: number;
  privilegedContainers?: PodsecurityPodSecurityPrivilegedContainers;
  readOnlyRootFileSystem?: PodsecurityPodSecurityReadOnlyRootFileSystem;
  restrictDefaultNamespace?: boolean;
  restrictSecrets?: boolean;
  secComp?: PodsecurityPodSecuritySecComp;
  selinux?: PodsecurityPodSecuritySelinux;
  users?: PodsecurityPodSecurityUsers;
  volumeTypes?: PodsecurityPodSecurityVolumeTypes;
};

export type PodsecurityPodSecurityRunAsGroup = {
  idrange?: Array<PodsecurityPodSecurityIdrange>;
  type?: PodsecurityRunAsGroupStrategy;
};

export type PodsecurityPodSecurityRunAsUser = {
  idrange?: Array<PodsecurityPodSecurityIdrange>;
  type?: PodsecurityRunAsUserStrategy;
};

export type PodsecurityPodSecuritySecComp = {
  enabled?: boolean;
  secCompType?: Array<string>;
};

export type PodsecurityPodSecuritySelinux = {
  allowedCapabilities?: Array<PodsecurityPodSecurityAllowedCapabilities>;
  enabled?: boolean;
};

export type PodsecurityPodSecuritySupplementalGroups = {
  idrange?: Array<PodsecurityPodSecurityIdrange>;
  type?: PodsecuritySupplementalGroupsStrategyType;
};

export type PodsecurityPodSecurityUsers = {
  enabled?: boolean;
  fsGroups?: PodsecurityPodSecurityFsGroups;
  runAsGroup?: PodsecurityPodSecurityRunAsGroup;
  runAsUser?: PodsecurityPodSecurityRunAsUser;
  supplementalGroups?: PodsecurityPodSecuritySupplementalGroups;
};

export type PodsecurityPodSecurityVolumeTypes = {
  allowedTypes?: Array<PodsecurityFsType>;
  enabled?: boolean;
};

export const PodsecurityRunAsGroupStrategy = {
  /**
   * RunAsGroupStrategyMayRunAs
   */
  RUN_AS_GROUP_STRATEGY_MAY_RUN_AS: 'MayRunAs',
  /**
   * RunAsGroupStrategyMustRunAs
   */
  RUN_AS_GROUP_STRATEGY_MUST_RUN_AS: 'MustRunAs',
  /**
   * RunAsGroupStrategyRunAsAny
   */
  RUN_AS_GROUP_STRATEGY_RUN_AS_ANY: 'RunAsAny',
} as const;

export type PodsecurityRunAsGroupStrategy =
  (typeof PodsecurityRunAsGroupStrategy)[keyof typeof PodsecurityRunAsGroupStrategy];

export const PodsecurityRunAsUserStrategy = {
  /**
   * RunAsUserStrategyMustRunAs
   */
  RUN_AS_USER_STRATEGY_MUST_RUN_AS: 'MustRunAs',
  /**
   * RunAsUserStrategyMustRunAsNonRoot
   */
  RUN_AS_USER_STRATEGY_MUST_RUN_AS_NON_ROOT: 'MustRunAsNonRoot',
  /**
   * RunAsUserStrategyRunAsAny
   */
  RUN_AS_USER_STRATEGY_RUN_AS_ANY: 'RunAsAny',
} as const;

export type PodsecurityRunAsUserStrategy =
  (typeof PodsecurityRunAsUserStrategy)[keyof typeof PodsecurityRunAsUserStrategy];

export const PodsecuritySupplementalGroupsStrategyType = {
  /**
   * SupplementalGroupsStrategyMayRunAs
   */
  SUPPLEMENTAL_GROUPS_STRATEGY_MAY_RUN_AS: 'MayRunAs',
  /**
   * SupplementalGroupsStrategyMustRunAs
   */
  SUPPLEMENTAL_GROUPS_STRATEGY_MUST_RUN_AS: 'MustRunAs',
  /**
   * SupplementalGroupsStrategyRunAsAny
   */
  SUPPLEMENTAL_GROUPS_STRATEGY_RUN_AS_ANY: 'RunAsAny',
} as const;

export type PodsecuritySupplementalGroupsStrategyType =
  (typeof PodsecuritySupplementalGroupsStrategyType)[keyof typeof PodsecuritySupplementalGroupsStrategyType];

export type PoliciesGenericPolicy = {
  CreatedAt: string;
  Data?: {
    [key: string]: unknown;
  };
  EnvironmentGroups?: Array<number>;
  EnvironmentTypes?: Array<'kubernetes' | 'docker' | 'podman' | 'swarm'>;
  Id: number;
  Name: string;
  /**
   * StatusBreakdown is computed on-demand and returned in API responses.
   * It is not persisted to the database.
   */
  StatusBreakdown?: PoliciesPolicyStatusBreakdown;
  Type:
    | 'rbac-k8s'
    | 'rbac-docker'
    | 'security-k8s'
    | 'security-docker'
    | 'setup-k8s'
    | 'setup-docker'
    | 'registry-k8s'
    | 'registry-docker'
    | 'change-confirmation'
    | 'cleanup-docker'
    | 'observability-k8s'
    | 'network-security-k8s'
    | 'pod-security-standards-k8s'
    | 'vulnerability-scanning-k8s';
  UpdatedAt: string;
};

export type PoliciesPolicy = {
  CreatedAt: string;
  EnvironmentGroups?: Array<number>;
  EnvironmentTypes?: Array<'kubernetes' | 'docker' | 'podman' | 'swarm'>;
  Id: number;
  Name: string;
  /**
   * StatusBreakdown is computed on-demand and returned in API responses.
   * It is not persisted to the database.
   */
  StatusBreakdown?: PoliciesPolicyStatusBreakdown;
  Type:
    | 'rbac-k8s'
    | 'rbac-docker'
    | 'security-k8s'
    | 'security-docker'
    | 'setup-k8s'
    | 'setup-docker'
    | 'registry-k8s'
    | 'registry-docker'
    | 'change-confirmation'
    | 'cleanup-docker'
    | 'observability-k8s'
    | 'network-security-k8s'
    | 'pod-security-standards-k8s'
    | 'vulnerability-scanning-k8s';
  UpdatedAt: string;
};

export const PoliciesPolicyCategory = {
  /**
   * CategoryRBAC
   */
  CATEGORY_RBAC: 'rbac',
  /**
   * CategorySecurity
   */
  CATEGORY_SECURITY: 'security',
  /**
   * CategorySetup
   */
  CATEGORY_SETUP: 'setup',
  /**
   * CategoryRegistry
   */
  CATEGORY_REGISTRY: 'registry',
  /**
   * CategoryCleanup
   */
  CATEGORY_CLEANUP: 'cleanup',
  /**
   * CategoryObservability
   */
  CATEGORY_OBSERVABILITY: 'observability',
} as const;

export type PoliciesPolicyCategory =
  (typeof PoliciesPolicyCategory)[keyof typeof PoliciesPolicyCategory];

export type PoliciesPolicyStatusBreakdown = {
  Applied?: number;
  Failed?: number;
  InProgress?: number;
  NotSupported?: number;
  Warning?: number;
};

export type PoliciesPolicyTemplate = {
  category?: PoliciesPolicyCategory;
  data?: {
    [key: string]: unknown;
  };
  description?: string;
  id?: string;
  name?: string;
  type?: PoliciesPolicyType;
};

export const PoliciesPolicyType = {
  /**
   * RbacK8s
   */
  RBAC_K8S: 'rbac-k8s',
  /**
   * SecurityK8s
   */
  SECURITY_K8S: 'security-k8s',
  /**
   * SetupK8s
   */
  SETUP_K8S: 'setup-k8s',
  /**
   * RegistryK8s
   */
  REGISTRY_K8S: 'registry-k8s',
  /**
   * RbacDocker
   */
  RBAC_DOCKER: 'rbac-docker',
  /**
   * SecurityDocker
   */
  SECURITY_DOCKER: 'security-docker',
  /**
   * SetupDocker
   */
  SETUP_DOCKER: 'setup-docker',
  /**
   * RegistryDocker
   */
  REGISTRY_DOCKER: 'registry-docker',
  /**
   * ChangeConfirmation
   */
  CHANGE_CONFIRMATION: 'change-confirmation',
  /**
   * CleanupDocker
   */
  CLEANUP_DOCKER: 'cleanup-docker',
  /**
   * ObservabilityK8s
   */
  OBSERVABILITY_K8S: 'observability-k8s',
  /**
   * PodSecurityStandardsK8s
   */
  POD_SECURITY_STANDARDS_K8S: 'pod-security-standards-k8s',
  /**
   * NetworkSecurityK8s
   */
  NETWORK_SECURITY_K8S: 'network-security-k8s',
  /**
   * VulnerabilityScanningK8s
   */
  VULNERABILITY_SCANNING_K8S: 'vulnerability-scanning-k8s',
  RBAC_K8S2: 'rbac-k8s',
  SECURITY_K8S2: 'security-k8s',
  SETUP_K8S2: 'setup-k8s',
  REGISTRY_K8S2: 'registry-k8s',
  RBAC_DOCKER2: 'rbac-docker',
  SECURITY_DOCKER2: 'security-docker',
  SETUP_DOCKER2: 'setup-docker',
  REGISTRY_DOCKER2: 'registry-docker',
  CHANGE_CONFIRMATION2: 'change-confirmation',
  CLEANUP_DOCKER2: 'cleanup-docker',
  OBSERVABILITY_K8S2: 'observability-k8s',
  POD_SECURITY_STANDARDS_K8S2: 'pod-security-standards-k8s',
  NETWORK_SECURITY_K8S2: 'network-security-k8s',
  VULNERABILITY_SCANNING_K8S2: 'vulnerability-scanning-k8s',
} as const;

export type PoliciesPolicyType =
  (typeof PoliciesPolicyType)[keyof typeof PoliciesPolicyType];

export type PoliciesConflictInfo = {
  environmentCount?: number;
  environmentGroupId?: number;
  environmentGroupName?: string;
  existingPolicyId?: number;
  existingPolicyName?: string;
  supportedEnvironments?: number;
  unsupportedEnvironments?: number;
};

export type PoliciesNewGroupInfo = {
  environmentCount?: number;
  environmentGroupId?: number;
  environmentGroupName?: string;
  supportedEnvironments?: number;
  unsupportedEnvironments?: number;
};

export type PoliciesPolicyConflictsPayload = {
  [key: string]: unknown;
};

export type PoliciesPolicyConflictsResponse = {
  conflicts?: Array<PoliciesConflictInfo>;
  newGroups?: Array<PoliciesNewGroupInfo>;
  supportedEnvironments?: number;
  totalEnvironments?: number;
  unsupportedEnvironments?: number;
};

export type PoliciesPolicyCreatePayload = {
  [key: string]: unknown;
};

export type PoliciesPolicyListResponse = {
  policies?: Array<PoliciesGenericPolicy>;
};

export type PoliciesPolicyMetadataResponse = {
  minimumAgentVersions?: {
    [key: string]: string;
  };
};

export type PoliciesPolicyUpdatePayload = {
  AllowOverride?: boolean;
  /**
   * Data contains the policy-type-specific configuration. Namespace fields on
   * RBAC/Registry/Network Security policies are lowercased automatically and
   * must be valid RFC 1123 DNS labels; values still invalid after lowercasing
   * are rejected with a 400.
   */
  Data?: {
    [key: string]: unknown;
  };
  EnvironmentGroups?: Array<number>;
  Name?: string;
  Type?:
    | 'rbac-k8s'
    | 'rbac-docker'
    | 'security-k8s'
    | 'security-docker'
    | 'setup-k8s'
    | 'setup-docker'
    | 'registry-k8s'
    | 'registry-docker'
    | 'change-confirmation'
    | 'observability-k8s';
};

export type PoliciesTemplateListResponse = {
  templates?: Array<PoliciesPolicyTemplate>;
};

export type PoliciesTestObservabilityConnectionPayload = {
  apiKey?: string;
  /**
   * AutoProvisionIngestionKey when true additionally checks that the key has
   * CreateTelemetryIngestionKey permission required to auto-provision ingestion keys.
   */
  autoProvisionIngestionKey?: boolean;
  /**
   * AutoRotateKey when true additionally checks that the key has rotation permissions.
   */
  autoRotateKey?: boolean;
  oneUptimeURL?: string;
  /**
   * PolicyID is optional; when set and APIKey is empty, the stored API key is used.
   */
  policyId?: number;
  projectID?: string;
  tlsSkipVerify?: boolean;
};

export type PoliciesTestObservabilityConnectionResponse = {
  message?: string;
  success?: boolean;
};

export type PortainerApiKey = {
  /**
   * Unix timestamp (UTC) when the API key was created
   */
  dateCreated?: number;
  description?: string;
  /**
   * Digest represents SHA256 hash of the raw API key
   */
  digest?: string;
  id?: number;
  /**
   * Unix timestamp (UTC) when the API key was last used
   */
  lastUsed?: number;
  /**
   * API key identifier (7 char prefix)
   */
  prefix?: string;
  userId?: number;
};

export type PortainerAccessPolicy = {
  /**
   * Namespaces is a list of namespaces that this access policy applies to. Only used for namespaced level roles
   */
  Namespaces?: Array<string>;
  /**
   * Role identifier. Reference the role that will be associated to this access policy
   */
  RoleId: number;
};

export type PortainerAllowList = {
  Entries?: Array<string>;
  Id?: PortainerAllowListKey;
  Mode?: PortainerSsrfMode;
};

export const PortainerAllowListKey = {
  /**
   * AllowListSSRF
   */
  ALLOW_LIST_SSRF: 0,
} as const;

export type PortainerAllowListKey =
  (typeof PortainerAllowListKey)[keyof typeof PortainerAllowListKey];

export type PortainerArtifact = {
  dependencies?: Array<PortainerArtifactRef>;
  edgeGroups?: Array<number>;
  edgeStackId?: number;
  envGroups?: Array<number>;
  envIds?: Array<number>;
  files?: Array<PortainerArtifactFile>;
  stackId?: number;
};

export type PortainerArtifactFile = {
  hash?: string;
  path?: string;
  pathError?: string;
  pathStatus?: PortainerSourceStatus;
  ref?: string;
  refError?: string;
  refStatus?: PortainerSourceStatus;
  sourceId?: number;
};

export type PortainerArtifactRef = {
  edgeStackId?: number;
};

export const PortainerAuthenticationMethod = {
  /**
   * _
   */
  '': 0,
  /**
   * AuthenticationInternal
   */
  AUTHENTICATION_INTERNAL: 1,
  /**
   * AuthenticationLDAP
   */
  AUTHENTICATION_LDAP: 2,
  /**
   * AuthenticationOAuth
   */
  AUTHENTICATION_O_AUTH: 3,
} as const;

export type PortainerAuthenticationMethod =
  (typeof PortainerAuthenticationMethod)[keyof typeof PortainerAuthenticationMethod];

export type PortainerAuthorizations = {
  [key: string]: boolean;
};

export type PortainerAutoUpdateSettings = {
  /**
   * Pull latest image
   */
  ForcePullImage?: boolean;
  /**
   * Force update ignores repo changes
   */
  ForceUpdate?: boolean;
  /**
   * Auto update interval
   * Deprecated: polling interval now lives on the associated Source (Source.Interval).
   * Kept for DB backwards-compatibility only; new code must not read or write this field.
   */
  Interval?: string;
  /**
   * Autoupdate job id
   */
  JobID?: string;
  /**
   * A UUID generated from client
   */
  Webhook?: string;
};

export type PortainerAzureCredentials = {
  /**
   * Azure application ID
   */
  ApplicationID: string;
  /**
   * Azure authentication key
   */
  AuthenticationKey: string;
  /**
   * Azure tenant ID
   */
  TenantID: string;
};

export const PortainerCustomTemplatePlatform = {
  /**
   * _
   */
  '': 0,
  /**
   * CustomTemplatePlatformLinux
   */
  CUSTOM_TEMPLATE_PLATFORM_LINUX: 1,
  /**
   * CustomTemplatePlatformWindows
   */
  CUSTOM_TEMPLATE_PLATFORM_WINDOWS: 2,
} as const;

export type PortainerCustomTemplatePlatform =
  (typeof PortainerCustomTemplatePlatform)[keyof typeof PortainerCustomTemplatePlatform];

export type PortainerCustomTemplateVariableDefinition = {
  defaultValue?: string;
  description?: string;
  label?: string;
  name?: string;
};

export type PortainerDiagnosticsData = {
  DNS?: {
    [key: string]: string;
  };
  Log?: string;
  Proxy?: {
    [key: string]: string;
  };
  Telnet?: {
    [key: string]: string;
  };
};

export type PortainerDockerContainerSnapshot = {
  Command?: string;
  Created?: number;
  /**
   * EE-5240
   */
  Env?: Array<string>;
  HostConfig?: {
    Annotations?: {
      [key: string]: string;
    };
    NetworkMode?: string;
  };
  Id?: string;
  Image?: string;
  ImageID?: string;
  ImageManifestDescriptor?: V1Descriptor;
  Labels?: {
    [key: string]: string;
  };
  Mounts?: Array<ContainerMountPoint>;
  Names?: Array<string>;
  NetworkSettings?: ContainerNetworkSettingsSummary;
  Ports?: Array<ContainerPort>;
  SizeRootFs?: number;
  SizeRw?: number;
  State?: ContainerContainerState;
  Status?: string;
};

export type PortainerDockerSnapshot = {
  ContainerCount: number;
  DiagnosticsData?: PortainerDiagnosticsData;
  DockerSnapshotRaw?: PortainerDockerSnapshotRaw;
  DockerVersion: string;
  GpuUseAll: boolean;
  GpuUseList?: Array<string>;
  HealthyContainerCount: number;
  ImageCount: number;
  IsPodman: boolean;
  NodeCount: number;
  PerformanceMetrics?: PortainerPerformanceMetrics;
  RunningContainerCount: number;
  ServiceCount: number;
  StackCount: number;
  StoppedContainerCount: number;
  Swarm: boolean;
  Time: number;
  TotalCPU: number;
  TotalMemory: number;
  UnhealthyContainerCount: number;
  VolumeCount: number;
};

export type PortainerDockerSnapshotRaw = {
  [key: string]: unknown;
};

export type PortainerEcrData = {
  Region?: string;
};

export type PortainerEdgeJob = {
  Created?: number;
  CronExpression?: string;
  EdgeGroups?: Array<number>;
  Endpoints?: {
    [key: string]: PortainerEdgeJobEndpointMeta;
  };
  /**
   * Field used for log collection of Endpoints belonging to EdgeGroups
   */
  GroupLogsCollection?: {
    [key: string]: PortainerEdgeJobEndpointMeta;
  };
  /**
   * EdgeJob Identifier
   */
  Id?: number;
  Name?: string;
  Recurring?: boolean;
  ScriptPath?: string;
  Version?: number;
};

export type PortainerEdgeJobEndpointMeta = {
  CollectLogs?: boolean;
  LogsStatus?: PortainerEdgeJobLogsStatus;
};

export const PortainerEdgeJobLogsStatus = {
  /**
   * _
   */
  '': 0,
  /**
   * EdgeJobLogsStatusIdle
   */
  EDGE_JOB_LOGS_STATUS_IDLE: 1,
  /**
   * EdgeJobLogsStatusPending
   */
  EDGE_JOB_LOGS_STATUS_PENDING: 2,
  /**
   * EdgeJobLogsStatusCollected
   */
  EDGE_JOB_LOGS_STATUS_COLLECTED: 3,
} as const;

export type PortainerEdgeJobLogsStatus =
  (typeof PortainerEdgeJobLogsStatus)[keyof typeof PortainerEdgeJobLogsStatus];

export type PortainerEdgeStackDeploymentStatus = {
  Error?: string;
  /**
   * EE only feature
   */
  RollbackTo?: number;
  Time?: number;
  Type?: PortainerEdgeStackStatusType;
  Version?: number;
};

export const PortainerEdgeStackDeploymentType = {
  /**
   * EdgeStackDeploymentCompose
   */
  EDGE_STACK_DEPLOYMENT_COMPOSE: 0 /**
   * EdgeStackDeploymentKubernetes
   */,
  EDGE_STACK_DEPLOYMENT_KUBERNETES: 1,
} as const;

export type PortainerEdgeStackDeploymentType =
  (typeof PortainerEdgeStackDeploymentType)[keyof typeof PortainerEdgeStackDeploymentType];

export type PortainerEdgeStackStatus = {
  /**
   * EE only feature
   */
  DeploymentInfo?: PortainerStackDeploymentInfo;
  /**
   * Deprecated
   */
  Details?: PortainerEdgeStackStatusDetails;
  EndpointID?: number;
  /**
   * Deprecated
   */
  Error?: string;
  /**
   * ReadyRePullImage is a flag to indicate whether the auto update is trigger to re-pull image
   */
  ReadyRePullImage?: boolean;
  Status?: Array<PortainerEdgeStackDeploymentStatus>;
  /**
   * Deprecated
   */
  Type?: PortainerEdgeStackStatusType;
};

export type PortainerEdgeStackStatusDetails = {
  Acknowledged?: boolean;
  Error?: boolean;
  ImagesPulled?: boolean;
  Ok?: boolean;
  Pending?: boolean;
  RemoteUpdateSuccess?: boolean;
  Remove?: boolean;
};

export const PortainerEdgeStackStatusType = {
  /**
   * EdgeStackStatusPending
   */
  EDGE_STACK_STATUS_PENDING: 0,
  /**
   * EdgeStackStatusDeploymentReceived
   */
  EDGE_STACK_STATUS_DEPLOYMENT_RECEIVED: 1,
  /**
   * EdgeStackStatusError
   */
  EDGE_STACK_STATUS_ERROR: 2,
  /**
   * EdgeStackStatusAcknowledged
   */
  EDGE_STACK_STATUS_ACKNOWLEDGED: 3,
  /**
   * EdgeStackStatusRemoved
   */
  EDGE_STACK_STATUS_REMOVED: 4,
  /**
   * EdgeStackStatusRemoteUpdateSuccess
   */
  EDGE_STACK_STATUS_REMOTE_UPDATE_SUCCESS: 5,
  /**
   * EdgeStackStatusImagesPulled
   */
  EDGE_STACK_STATUS_IMAGES_PULLED: 6,
  /**
   * EdgeStackStatusRunning
   */
  EDGE_STACK_STATUS_RUNNING: 7,
  /**
   * EdgeStackStatusDeploying
   */
  EDGE_STACK_STATUS_DEPLOYING: 8,
  /**
   * EdgeStackStatusRemoving
   */
  EDGE_STACK_STATUS_REMOVING: 9,
  /**
   * EdgeStackStatusPausedDeploying
   */
  EDGE_STACK_STATUS_PAUSED_DEPLOYING: 10,
  /**
   * EdgeStackStatusRollingBack
   */
  EDGE_STACK_STATUS_ROLLING_BACK: 11,
  /**
   * EdgeStackStatusRolledBack
   */
  EDGE_STACK_STATUS_ROLLED_BACK: 12,
  /**
   * EdgeStackStatusCompleted
   */
  EDGE_STACK_STATUS_COMPLETED: 13,
} as const;

export type PortainerEdgeStackStatusType =
  (typeof PortainerEdgeStackStatusType)[keyof typeof PortainerEdgeStackStatusType];

export type PortainerEndpointGroup = {
  /**
   * Description associated to the environment(endpoint) group
   */
  Description: string;
  /**
   * Environment(Endpoint) group Identifier
   */
  Id: number;
  /**
   * Environment(Endpoint) group name
   */
  Name: string;
  /**
   * List of tags associated to this environment(endpoint) group
   */
  TagIds?: Array<number>;
  TeamAccessPolicies?: PortainerTeamAccessPolicies;
  UserAccessPolicies?: PortainerUserAccessPolicies;
};

export type PortainerEndpointSecuritySettings = {
  /**
   * Whether non-administrator should be able to use bind mounts when creating containers
   */
  allowBindMountsForRegularUsers: boolean;
  /**
   * Whether non-administrator should be able to use container capabilities
   */
  allowContainerCapabilitiesForRegularUsers: boolean;
  /**
   * Whether non-administrator should be able to use device mapping
   */
  allowDeviceMappingForRegularUsers: boolean;
  /**
   * Whether non-administrator should be able to use the host pid
   */
  allowHostNamespaceForRegularUsers: boolean;
  /**
   * Whether non-administrator should be able to use privileged mode when creating containers
   */
  allowPrivilegedModeForRegularUsers: boolean;
  /**
   * Whether non-administrator should be able to use security-opt settings
   */
  allowSecurityOptForRegularUsers: boolean;
  /**
   * Whether non-administrator should be able to manage stacks
   */
  allowStackManagementForRegularUsers: boolean;
  /**
   * Whether non-administrator should be able to use sysctl settings
   */
  allowSysctlSettingForRegularUsers: boolean;
  /**
   * Whether non-administrator should be able to browse volumes
   */
  allowVolumeBrowserForRegularUsers: boolean;
  /**
   * Whether host management features are enabled
   */
  enableHostManagementFeatures: boolean;
};

export const PortainerEndpointStatus = {
  /**
   * _
   */
  '': 0,
  /**
   * EndpointStatusUp
   */
  ENDPOINT_STATUS_UP: 1,
  /**
   * EndpointStatusDown
   */
  ENDPOINT_STATUS_DOWN: 2,
} as const;

export type PortainerEndpointStatus =
  (typeof PortainerEndpointStatus)[keyof typeof PortainerEndpointStatus];

export const PortainerEndpointType = {
  /**
   * _
   */
  '': 0,
  /**
   * DockerEnvironment
   */
  DOCKER_ENVIRONMENT: 1,
  /**
   * AgentOnDockerEnvironment
   */
  AGENT_ON_DOCKER_ENVIRONMENT: 2,
  /**
   * AzureEnvironment
   */
  AZURE_ENVIRONMENT: 3,
  /**
   * EdgeAgentOnDockerEnvironment
   */
  EDGE_AGENT_ON_DOCKER_ENVIRONMENT: 4,
  /**
   * KubernetesLocalEnvironment
   */
  KUBERNETES_LOCAL_ENVIRONMENT: 5,
  /**
   * AgentOnKubernetesEnvironment
   */
  AGENT_ON_KUBERNETES_ENVIRONMENT: 6,
  /**
   * EdgeAgentOnKubernetesEnvironment
   */
  EDGE_AGENT_ON_KUBERNETES_ENVIRONMENT: 7,
} as const;

export type PortainerEndpointType =
  (typeof PortainerEndpointType)[keyof typeof PortainerEndpointType];

export type PortainerEnvironmentEdgeSettings = {
  /**
   * Whether the device has been started in edge async mode
   */
  AsyncMode: boolean;
  /**
   * The command list interval for edge agent - used in edge async mode [seconds]
   */
  CommandInterval: number;
  /**
   * The ping interval for edge agent - used in edge async mode [seconds]
   */
  PingInterval: number;
  /**
   * The snapshot interval for edge agent - used in edge async mode [seconds]
   */
  SnapshotInterval: number;
};

export type PortainerGithubRegistryData = {
  OrganisationName?: string;
  UseOrganisation?: boolean;
};

export type PortainerGitlabRegistryData = {
  InstanceURL?: string;
  ProjectId?: number;
  ProjectPath?: string;
};

export type PortainerHelmConfig = {
  /**
   * Atomic enables automatic rollback on deployment failure (equivalent to helm --atomic).
   * Used by both git repo and Helm repository deployments.
   */
  Atomic?: boolean;
  /**
   * ChartName is the name of the Helm chart within the repository.
   * Required for Helm repository deployments.
   */
  ChartName?: string;
  /**
   * ChartPath is the path to a Helm chart folder within the cloned git repository.
   * Used exclusively for git repo helm deployments. Mutually exclusive with ChartURL.
   */
  ChartPath?: string;
  /**
   * ChartURL is the URL of a Helm chart repository.
   * Used exclusively for Helm repository deployments. Mutually exclusive with ChartPath.
   */
  ChartURL?: string;
  /**
   * ChartVersion is the version of the Helm chart to deploy. Empty means latest.
   * Used exclusively for Helm repository deployments.
   */
  ChartVersion?: string;
  /**
   * Namespace is the Kubernetes namespace to deploy the Helm chart into.
   * Used by both git repo and Helm repository deployments.
   */
  Namespace?: string;
  /**
   * Timeout sets the deadline for Helm operations (equivalent to helm --timeout, e.g. "5m0s").
   * Used by both git repo and Helm repository deployments.
   */
  Timeout?: string;
  /**
   * ValuesFiles is a list of relative paths to Helm values YAML files within the cloned git repository.
   * Used exclusively for git repo helm deployments.
   */
  ValuesFiles?: Array<string>;
  /**
   * ValuesInline is the inline YAML string of Helm values.
   * Used exclusively for Helm repository deployments.
   */
  ValuesInline?: string;
};

export type PortainerHelmUserRepository = {
  /**
   * Membership Identifier
   */
  Id?: number;
  /**
   * Helm repository URL
   */
  URL?: string;
  /**
   * User identifier
   */
  UserId?: number;
};

export type PortainerInternalAuthSettings = {
  RequiredPasswordLength?: number;
};

export type PortainerK8sNamespaceInfo = {
  Annotations?: {
    [key: string]: string;
  };
  CreationDate?: string;
  Id?: string;
  IsDefault?: boolean;
  IsSystem?: boolean;
  Name?: string;
  NamespaceOwner?: string;
  ResourceQuota?: V1ResourceQuota;
  Status?: V1NamespaceStatus;
  UnhealthyEventCount?: number;
};

export type PortainerK8sNodeLimits = {
  CPU?: number;
  Memory?: number;
};

export type PortainerK8sNodesLimits = {
  [key: string]: PortainerK8sNodeLimits;
};

export type PortainerKubernetesFlags = {
  GPUOperator?: boolean;
  IsServerIngressClassDetected: boolean;
  IsServerMetricsDetected: boolean;
  IsServerStorageDetected: boolean;
};

export type PortainerKubernetesIngressClassConfig = {
  Blocked?: boolean;
  BlockedNamespaces?: Array<string>;
  Name: string;
  Type: string;
};

export type PortainerKubernetesSnapshot = {
  ClusterType?: string;
  DiagnosticsData?: PortainerDiagnosticsData;
  GPUNodeCount?: number;
  KubernetesVersion: string;
  NodeCount: number;
  PerformanceMetrics?: PortainerPerformanceMetrics;
  Time: number;
  TotalCPU: number;
  TotalGPU?: {
    [key: string]: number;
  };
  TotalMemory: number;
};

export type PortainerKubernetesStorageClassConfig = {
  AccessModes?: Array<string>;
  AllowVolumeExpansion: boolean;
  Name: string;
  Provisioner: string;
};

export type PortainerLdapGroupSearchSettings = {
  /**
   * LDAP attribute which denotes the group membership
   */
  GroupAttribute?: string;
  /**
   * The distinguished name of the element from which the LDAP server will search for groups
   */
  GroupBaseDN?: string;
  /**
   * The LDAP search filter used to select group elements, optional
   */
  GroupFilter?: string;
};

export type PortainerLdapSearchSettings = {
  /**
   * The distinguished name of the element from which the LDAP server will search for users
   */
  BaseDN?: string;
  /**
   * Optional LDAP search filter used to select user elements
   */
  Filter?: string;
  /**
   * LDAP attribute which denotes the username
   */
  UserNameAttribute?: string;
};

export type PortainerLdapUser = {
  Groups?: Array<string>;
  Name?: string;
};

export const PortainerMembershipRole = {
  /**
   * _
   */
  '': 0,
  /**
   * TeamLeader
   */
  TEAM_LEADER: 1,
  /**
   * TeamMember
   */
  TEAM_MEMBER: 2,
} as const;

export type PortainerMembershipRole =
  (typeof PortainerMembershipRole)[keyof typeof PortainerMembershipRole];

export type PortainerPair = {
  name: string;
  value: string;
};

export const PortainerPerDevConfigsFilterType = {
  /**
   * PerDevConfigsTypeFile
   */
  PER_DEV_CONFIGS_TYPE_FILE: 'file' /**
   * PerDevConfigsTypeDir
   */,
  PER_DEV_CONFIGS_TYPE_DIR: 'dir',
} as const;

export type PortainerPerDevConfigsFilterType =
  (typeof PortainerPerDevConfigsFilterType)[keyof typeof PortainerPerDevConfigsFilterType];

export type PortainerPerformanceMetrics = {
  CPUUsage?: number;
  DiskUsage?: number;
  MemoryUsage?: number;
  NetworkUsage?: number;
};

export type PortainerPolicyActualState = {
  fingerprint?: string;
  message?: string;
  policyID?: number;
  /**
   * applying|applied|failed|removing
   */
  status?: string;
  type?: string;
};

export type PortainerQuayRegistryData = {
  OrganisationName?: string;
  UseOrganisation?: boolean;
};

export type PortainerRegistry = {
  /**
   * Stores temporary access token
   */
  AccessToken?: string;
  AccessTokenExpiry?: number;
  /**
   * Is authentication against this registry enabled
   */
  Authentication?: boolean;
  /**
   * Deprecated in DBVersion == 18
   */
  AuthorizedTeams?: Array<number>;
  /**
   * Deprecated in DBVersion == 18
   */
  AuthorizedUsers?: Array<number>;
  /**
   * Base URL, introduced for ProGet registry
   */
  BaseURL?: string;
  Ecr?: PortainerEcrData;
  Github?: PortainerGithubRegistryData;
  Gitlab?: PortainerGitlabRegistryData;
  /**
   * Registry Identifier
   */
  Id?: number;
  ManagementConfiguration?: PortainerRegistryManagementConfiguration;
  /**
   * Registry Name
   */
  Name?: string;
  /**
   * Password or SecretAccessKey used to authenticate against this registry
   */
  Password?: string;
  Quay?: PortainerQuayRegistryData;
  RegistryAccesses?: PortainerRegistryAccesses;
  /**
   * Deprecated in DBVersion == 31
   */
  TeamAccessPolicies?: PortainerTeamAccessPolicies;
  /**
   * Registry Type (1 - Quay, 2 - Azure, 3 - Custom, 4 - Gitlab, 5 - ProGet, 6 - DockerHub, 7 - ECR)
   */
  Type?: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  /**
   * URL or IP address of the Docker registry
   */
  URL?: string;
  /**
   * Deprecated fields
   * Deprecated in DBVersion == 31
   */
  UserAccessPolicies?: PortainerUserAccessPolicies;
  /**
   * Username or AccessKeyID used to authenticate against this registry
   */
  Username?: string;
};

export type PortainerRegistryAccessPolicies = {
  /**
   * Kubernetes specific fields (with kubernetes, namespaces have access to a registry, if users/teams have access to the same namespace, they have access to the registry)
   */
  Namespaces?: Array<string>;
  TeamAccessPolicies?: PortainerTeamAccessPolicies;
  /**
   * Docker specific fields (with docker, users/teams have access to a registry)
   */
  UserAccessPolicies?: PortainerUserAccessPolicies;
};

export type PortainerRegistryAccesses = {
  [key: string]: PortainerRegistryAccessPolicies;
};

export type PortainerRegistryManagementConfiguration = {
  AccessToken?: string;
  AccessTokenExpiry?: number;
  Authentication?: boolean;
  Ecr?: PortainerEcrData;
  Password?: string;
  TLSConfig?: PortainerTlsConfiguration;
  Type?: PortainerRegistryType;
  Username?: string;
};

export const PortainerRegistryType = {
  /**
   * _
   */
  '': 0,
  /**
   * QuayRegistry
   */
  QUAY_REGISTRY: 1,
  /**
   * AzureRegistry
   */
  AZURE_REGISTRY: 2,
  /**
   * CustomRegistry
   */
  CUSTOM_REGISTRY: 3,
  /**
   * GitlabRegistry
   */
  GITLAB_REGISTRY: 4,
  /**
   * ProGetRegistry
   */
  PRO_GET_REGISTRY: 5,
  /**
   * DockerHubRegistry
   */
  DOCKER_HUB_REGISTRY: 6,
  /**
   * EcrRegistry
   */
  ECR_REGISTRY: 7,
  /**
   * GithubRegistry
   */
  GITHUB_REGISTRY: 8,
} as const;

export type PortainerRegistryType =
  (typeof PortainerRegistryType)[keyof typeof PortainerRegistryType];

export const PortainerResourceAccessLevel = {
  /**
   * _
   */
  '': 0 /**
   * ReadWriteAccessLevel
   */,
  READ_WRITE_ACCESS_LEVEL: 1,
} as const;

export type PortainerResourceAccessLevel =
  (typeof PortainerResourceAccessLevel)[keyof typeof PortainerResourceAccessLevel];

export type PortainerResourceControl = {
  AccessLevel?: PortainerResourceAccessLevel;
  /**
   * Permit access to resource only to admins
   */
  AdministratorsOnly?: boolean;
  /**
   * ResourceControl Identifier
   */
  Id?: number;
  /**
   * Deprecated fields
   * Deprecated in DBVersion == 2
   */
  OwnerId?: number;
  /**
   * Permit access to the associated resource to any user
   */
  Public?: boolean;
  /**
   * Docker resource identifier on which access control will be applied.\
   * In the case of a resource control applied to a stack, use the stack name as identifier
   */
  ResourceId?: string;
  /**
   * List of Docker resources that will inherit this access control
   */
  SubResourceIds?: Array<string>;
  System?: boolean;
  TeamAccesses?: Array<PortainerTeamResourceAccess>;
  /**
   * Type of Docker resource. Valid values are: 1- container, 2 -service
   * 3 - volume, 4 - secret, 5 - stack, 6 - config or 7 - custom template
   */
  Type?: PortainerResourceControlType;
  UserAccesses?: Array<PortainerUserResourceAccess>;
};

export const PortainerResourceControlType = {
  /**
   * _
   */
  '': 0,
  /**
   * ContainerResourceControl
   */
  CONTAINER_RESOURCE_CONTROL: 1,
  /**
   * ServiceResourceControl
   */
  SERVICE_RESOURCE_CONTROL: 2,
  /**
   * VolumeResourceControl
   */
  VOLUME_RESOURCE_CONTROL: 3,
  /**
   * NetworkResourceControl
   */
  NETWORK_RESOURCE_CONTROL: 4,
  /**
   * SecretResourceControl
   */
  SECRET_RESOURCE_CONTROL: 5,
  /**
   * StackResourceControl
   */
  STACK_RESOURCE_CONTROL: 6,
  /**
   * ConfigResourceControl
   */
  CONFIG_RESOURCE_CONTROL: 7,
  /**
   * CustomTemplateResourceControl
   */
  CUSTOM_TEMPLATE_RESOURCE_CONTROL: 8,
  /**
   * ContainerGroupResourceControl
   */
  CONTAINER_GROUP_RESOURCE_CONTROL: 9,
} as const;

export type PortainerResourceControlType =
  (typeof PortainerResourceControlType)[keyof typeof PortainerResourceControlType];

export const PortainerSsrfMode = {
  /**
   * SSRFModeOff
   */
  SSRF_MODE_OFF: 0,
  /**
   * SSRFModeAudit
   */
  SSRF_MODE_AUDIT: 1,
  /**
   * SSRFModeEnforce
   */
  SSRF_MODE_ENFORCE: 2,
} as const;

export type PortainerSsrfMode =
  (typeof PortainerSsrfMode)[keyof typeof PortainerSsrfMode];

export type PortainerSource = {
  administratorsOnly?: boolean;
  git?: GithubComPortainerPortainerApiGitTypesGitSource;
  helm?: PortainerHelmConfig;
  id?: number;
  interval?: string;
  lastSync?: number;
  name?: string;
  ownerID?: number;
  public?: boolean;
  registry?: PortainerRegistry;
  status?: PortainerSourceStatus;
  statusError?: string;
  teamAccesses?: Array<number>;
  type?: PortainerSourceType;
  userAccesses?: Array<number>;
};

export const PortainerSourceStatus = {
  /**
   * SourceStatusUnknown
   */
  SOURCE_STATUS_UNKNOWN: 0,
  /**
   * SourceStatusHealthy
   */
  SOURCE_STATUS_HEALTHY: 1,
  /**
   * SourceStatusError
   */
  SOURCE_STATUS_ERROR: 2,
} as const;

export type PortainerSourceStatus =
  (typeof PortainerSourceStatus)[keyof typeof PortainerSourceStatus];

export const PortainerSourceType = {
  /**
   * _
   */
  '': 0,
  /**
   * SourceTypeGit
   */
  SOURCE_TYPE_GIT: 1,
  /**
   * SourceTypeRegistry
   */
  SOURCE_TYPE_REGISTRY: 2,
  /**
   * SourceTypeHelm
   */
  SOURCE_TYPE_HELM: 3,
} as const;

export type PortainerSourceType =
  (typeof PortainerSourceType)[keyof typeof PortainerSourceType];

export type PortainerStackDeploymentInfo = {
  /**
   * AdditionalFiles are the additional files used for deploying the stack
   */
  AdditionalFiles?: Array<string>;
  /**
   * ConfigFilePath is the path to the config file in the git repository used for deploying the stack
   */
  ConfigFilePath?: string;
  /**
   * ConfigHash is the commit hash of the git repository used for deploying the stack
   */
  ConfigHash?: string;
  /**
   * FileVersion is the version of the stack file, used to detect changes
   */
  FileVersion?: number;
  /**
   * ReferenceName is the git reference (branch/tag) used for deploying the stack
   */
  ReferenceName?: string;
  /**
   * RepositoryURL is the git repository URL used for deploying the stack
   */
  RepositoryURL?: string;
  /**
   * SourceID is the Source used for deploying the stack
   */
  SourceID?: number;
  /**
   * Version is the version of the stack and also is the deployed version in edge agent
   */
  Version?: number;
};

export type PortainerStackDeploymentStatus = {
  /**
   * populated on Error entries
   */
  Message?: string;
  Status?: PortainerStackStatus;
  Time?: number;
};

export type PortainerStackOption = {
  /**
   * Enable atomic rollback on failure (Helm --atomic flag for Kubernetes Helm stacks)
   */
  HelmAtomic?: boolean;
  /**
   * Prune services that are no longer referenced
   */
  Prune?: boolean;
};

export const PortainerStackStatus = {
  /**
   * _
   */
  '': 0,
  /**
   * StackStatusActive
   *
   * 1 - deployed and running
   */
  STACK_STATUS_ACTIVE: 1,
  /**
   * StackStatusInactive
   *
   * 2 - intentionally stopped
   */
  STACK_STATUS_INACTIVE: 2,
  /**
   * StackStatusDeploying
   *
   * 3 - deployment in progress
   */
  STACK_STATUS_DEPLOYING: 3,
  /**
   * StackStatusError
   *
   * 4 - deployment failed
   */
  STACK_STATUS_ERROR: 4,
} as const;

export type PortainerStackStatus =
  (typeof PortainerStackStatus)[keyof typeof PortainerStackStatus];

export const PortainerStackType = {
  /**
   * _
   */
  '': 0,
  /**
   * DockerSwarmStack
   */
  DOCKER_SWARM_STACK: 1,
  /**
   * DockerComposeStack
   */
  DOCKER_COMPOSE_STACK: 2,
  /**
   * KubernetesStack
   */
  KUBERNETES_STACK: 3,
} as const;

export type PortainerStackType =
  (typeof PortainerStackType)[keyof typeof PortainerStackType];

export type PortainerTlsConfiguration = {
  /**
   * Use TLS
   */
  TLS: boolean;
  /**
   * Path to the TLS CA certificate file
   */
  TLSCACert?: string;
  /**
   * Path to the TLS client certificate file
   */
  TLSCert?: string;
  /**
   * Path to the TLS client key file
   */
  TLSKey?: string;
  /**
   * Skip the verification of the server TLS certificate
   */
  TLSSkipVerify: boolean;
};

export type PortainerTag = {
  /**
   * A set of environment(endpoint) group ids that have this tag
   */
  EndpointGroups?: {
    [key: string]: boolean;
  };
  /**
   * A set of environment(endpoint) ids that have this tag
   */
  Endpoints?: {
    [key: string]: boolean;
  };
  /**
   * Tag identifier
   */
  ID?: number;
  /**
   * Tag name
   */
  Name?: string;
};

export type PortainerTeam = {
  /**
   * Whether members of this team are denied access to Portainer itself (EE only)
   */
  DenyPortainerAccess?: boolean;
  /**
   * Team Identifier
   */
  Id?: number;
  /**
   * Team name
   */
  Name?: string;
};

export type PortainerTeamAccessPolicies = {
  [key: string]: PortainerAccessPolicy;
};

export type PortainerTeamMembership = {
  /**
   * Membership Identifier
   */
  Id?: number;
  /**
   * Team role (1 for team leader and 2 for team member)
   */
  Role?: PortainerMembershipRole;
  /**
   * Team identifier
   */
  TeamID?: number;
  /**
   * User identifier
   */
  UserID?: number;
};

export type PortainerTeamResourceAccess = {
  AccessLevel?: PortainerResourceAccessLevel;
  TeamId?: number;
};

export type PortainerTemplate = {
  /**
   * Whether the template should be available to administrators only
   */
  administrator_only?: boolean;
  /**
   * A list of categories associated to the template
   */
  categories?: Array<string>;
  /**
   * The command that will be executed in a container template
   */
  command?: string;
  /**
   * Description of the template
   */
  description?: string;
  /**
   * A list of environment(endpoint) variables used during the template deployment
   */
  env?: Array<PortainerTemplateEnv>;
  /**
   * Container hostname
   */
  hostname?: string;
  /**
   * Mandatory container/stack fields
   * Template Identifier
   */
  id?: number;
  /**
   * Mandatory container fields
   * Image associated to a container template. Mandatory for a container template
   */
  image?: string;
  /**
   * Whether the container should be started in
   * interactive mode (-i -t equivalent on the CLI)
   */
  interactive?: boolean;
  /**
   * Container labels
   */
  labels?: Array<PortainerPair>;
  /**
   * URL of the template's logo
   */
  logo?: string;
  /**
   * Optional stack/container fields
   * Default name for the stack/container to be used on deployment
   */
  name?: string;
  /**
   * Name of a network that will be used on container deployment if it exists inside the environment(endpoint)
   */
  network?: string;
  /**
   * A note that will be displayed in the UI. Supports HTML content
   */
  note?: string;
  /**
   * Platform associated to the template.
   * Valid values are: 'linux', 'windows' or leave empty for multi-platform
   */
  platform?: string;
  /**
   * A list of ports exposed by the container
   */
  ports?: Array<string>;
  /**
   * Whether the container should be started in privileged mode
   */
  privileged?: boolean;
  /**
   * Optional container fields
   * The URL of a registry associated to the image for a container template
   */
  registry?: string;
  /**
   * Mandatory stack fields
   */
  repository?: PortainerTemplateRepository;
  /**
   * Container restart policy
   */
  restart_policy?: string;
  /**
   * Mandatory Edge stack fields
   * Stack file used for this template
   */
  stackFile?: string;
  /**
   * Title of the template
   */
  title?: string;
  /**
   * Template type. Valid values are: 1 (container), 2 (Swarm stack), 3 (Compose stack), 4 (Compose edge stack)
   */
  type?: PortainerTemplateType;
  /**
   * A list of volumes used during the container template deployment
   */
  volumes?: Array<PortainerTemplateVolume>;
};

export type PortainerTemplateEnv = {
  /**
   * Default value that will be set for the variable
   */
  default?: string;
  /**
   * Content of the tooltip that will be generated in the UI
   */
  description?: string;
  /**
   * Text for the label that will be generated in the UI
   */
  label?: string;
  /**
   * name of the environment(endpoint) variable
   */
  name?: string;
  /**
   * If set to true, will not generate any input for this variable in the UI
   */
  preset?: boolean;
  /**
   * A list of name/value that will be used to generate a dropdown in the UI
   */
  select?: Array<PortainerTemplateEnvSelect>;
};

export type PortainerTemplateEnvSelect = {
  /**
   * Will set this choice as the default choice
   */
  default?: boolean;
  /**
   * Some text that will displayed as a choice
   */
  text?: string;
  /**
   * A value that will be associated to the choice
   */
  value?: string;
};

export type PortainerTemplateRepository = {
  /**
   * Path to the stack file inside the git repository
   */
  stackfile?: string;
  /**
   * URL of a git repository used to deploy a stack template. Mandatory for a Swarm/Compose stack template
   */
  url?: string;
};

export const PortainerTemplateType = {
  /**
   * _
   */
  '': 0,
  /**
   * ContainerTemplate
   */
  CONTAINER_TEMPLATE: 1,
  /**
   * SwarmStackTemplate
   */
  SWARM_STACK_TEMPLATE: 2,
  /**
   * ComposeStackTemplate
   */
  COMPOSE_STACK_TEMPLATE: 3,
} as const;

export type PortainerTemplateType =
  (typeof PortainerTemplateType)[keyof typeof PortainerTemplateType];

export type PortainerTemplateVolume = {
  /**
   * Path on the host
   */
  bind?: string;
  /**
   * Path inside the container
   */
  container?: string;
  /**
   * Whether the volume used should be readonly
   */
  readonly?: boolean;
};

export type PortainerUserAccessPolicies = {
  [key: string]: PortainerAccessPolicy;
};

export type PortainerUserResourceAccess = {
  AccessLevel?: PortainerResourceAccessLevel;
  UserId?: number;
};

export const PortainerUserRole = {
  /**
   * _
   */
  '': 0,
  /**
   * AdministratorRole
   */
  ADMINISTRATOR_ROLE: 1,
  /**
   * StandardUserRole
   */
  STANDARD_USER_ROLE: 2,
} as const;

export type PortainerUserRole =
  (typeof PortainerUserRole)[keyof typeof PortainerUserRole];

export type PortainerWebhook = {
  EndpointId?: number;
  /**
   * Webhook Identifier
   */
  Id?: number;
  RegistryId?: number;
  ResourceId?: string;
  Token?: string;
  /**
   * Type of webhook (1 - service)
   */
  Type?: PortainerWebhookType;
};

export const PortainerWebhookType = {
  /**
   * _
   */
  '': 0 /**
   * ServiceWebhook
   */,
  SERVICE_WEBHOOK: 1,
} as const;

export type PortainerWebhookType =
  (typeof PortainerWebhookType)[keyof typeof PortainerWebhookType];

export type PortainerWorkflow = {
  artifacts?: Array<PortainerArtifact>;
  id?: number;
  name?: string;
};

export type PortainereeAdditionalFunctionality = {
  [key: string]: unknown;
};

export const PortainereeAlertRuleCategory = {
  /**
   * AlertRuleCategoryPortainer
   */
  ALERT_RULE_CATEGORY_PORTAINER: 'portainer',
  /**
   * AlertRuleCategorySecurity
   */
  ALERT_RULE_CATEGORY_SECURITY: 'security',
  /**
   * AlertRuleCategoryEnvironment
   */
  ALERT_RULE_CATEGORY_ENVIRONMENT: 'environment',
  /**
   * AlertRuleCategoryBackup
   */
  ALERT_RULE_CATEGORY_BACKUP: 'backup',
} as const;

export type PortainereeAlertRuleCategory =
  (typeof PortainereeAlertRuleCategory)[keyof typeof PortainereeAlertRuleCategory];

export const PortainereeAlertRuleEnvReason = {
  /**
   * AlertRuleEnvReasonIncluded
   */
  ALERT_RULE_ENV_REASON_INCLUDED: 'included',
  /**
   * AlertRuleEnvReasonEnvironmentTypeMismatch
   */
  ALERT_RULE_ENV_REASON_ENVIRONMENT_TYPE_MISMATCH: 'environment_type_mismatch',
  /**
   * AlertRuleEnvReasonAgentVersionMismatch
   */
  ALERT_RULE_ENV_REASON_AGENT_VERSION_MISMATCH: 'agent_version_mismatch',
  /**
   * AlertRuleEnvReasonEndpointGroupMismatch
   */
  ALERT_RULE_ENV_REASON_ENDPOINT_GROUP_MISMATCH: 'endpoint_group_mismatch',
  /**
   * AlertRuleEnvReasonTagMismatch
   */
  ALERT_RULE_ENV_REASON_TAG_MISMATCH: 'tag_mismatch',
  /**
   * AlertRuleEnvReasonEmptyScope
   */
  ALERT_RULE_ENV_REASON_EMPTY_SCOPE: 'empty_scope',
  /**
   * AlertRuleEnvReasonRetryRequested
   */
  ALERT_RULE_ENV_REASON_RETRY_REQUESTED: 'retry_requested',
  /**
   * AlertRuleEnvReasonAgentLastError
   */
  ALERT_RULE_ENV_REASON_AGENT_LAST_ERROR: 'agent_last_error',
} as const;

export type PortainereeAlertRuleEnvReason =
  (typeof PortainereeAlertRuleEnvReason)[keyof typeof PortainereeAlertRuleEnvReason];

export const PortainereeAlertRuleEnvStatus = {
  /**
   * AlertRuleEnvStatusActive
   */
  ALERT_RULE_ENV_STATUS_ACTIVE: 'active',
  /**
   * AlertRuleEnvStatusExcluded
   */
  ALERT_RULE_ENV_STATUS_EXCLUDED: 'excluded',
  /**
   * AlertRuleEnvStatusNotSupported
   */
  ALERT_RULE_ENV_STATUS_NOT_SUPPORTED: 'not_supported',
  /**
   * AlertRuleEnvStatusError
   */
  ALERT_RULE_ENV_STATUS_ERROR: 'error',
  /**
   * AlertRuleEnvStatusPending
   */
  ALERT_RULE_ENV_STATUS_PENDING: 'pending',
} as const;

export type PortainereeAlertRuleEnvStatus =
  (typeof PortainereeAlertRuleEnvStatus)[keyof typeof PortainereeAlertRuleEnvStatus];

export type PortainereeAlertingNotificationChannel = {
  config?: {
    [key: string]: unknown;
  };
  enabled?: boolean;
  id?: number;
  name?: string;
  type?:
    | 'slack'
    | 'webhook'
    | 'teams'
    | 'discord'
    | 'email'
    | 'pagerduty'
    | 'opsgenie';
};

export type PortainereeAlertingRule = {
  alertManagerID?: number;
  annotationTemplate?: string;
  category?: 'portainer' | 'security' | 'environment';
  conditionOperator?: string;
  createdAt?: string;
  createdBy?: string;
  description?: string;
  duration?: number;
  enabled?: boolean;
  endpointGroupIds?: Array<number>;
  forDurationMinutes?: number;
  id?: number;
  independentTiers?: boolean;
  isEditable?: boolean;
  isInternal?: boolean;
  labels?: {
    [key: string]: string;
  };
  metricType?: 'percentage' | 'bytes' | 'raw' | 'days';
  name?: string;
  operatorFixed?: boolean;
  promqlExpr?: string;
  promqlScalarTemplate?: string;
  promqlTemplate?: string;
  severity?: 'critical' | 'warning' | 'info';
  summary?: string;
  supportedAgentVersion?: string;
  supportedEnvironmentTypes?: 'docker' | 'kubernetes' | 'podman' | 'all';
  tagIds?: Array<number>;
  threshold?: number;
  tiers?: Array<PortainereeAlertingRuleTier>;
  updatedAt?: string;
  useDurationAsFor?: boolean;
  useTiers?: boolean;
};

export type PortainereeAlertingRuleTier = {
  enabled?: boolean;
  /**
   * Selector is substituted into PromqlScalarTemplate as {{.TierSelector}},
   * letting each tier measure a different series. It is part of the built-in
   * rule definition and is never accepted from a client payload, because it
   * is interpolated directly into PromQL. Only used when IndependentTiers
   * is true.
   */
  selector?: string;
  severity?: 'critical' | 'warning' | 'info';
  threshold?: number;
};

export type PortainereeAlertingSettings = {
  createdAt?: string;
  createdBy?: string;
  enabled?: boolean;
  id?: number;
  isInternal?: boolean;
  name?: string;
  notificationChannels?: Array<PortainereeAlertingNotificationChannel>;
  portainerURL?: string;
  status?: 'disabled' | 'connected' | 'disconnected' | 'error';
  uptime?: string;
  url?: string;
};

export type PortainereeAppliedPolicy = {
  policyID?: number;
  policyName?: string;
  policyStatus?: PortainereePolicyStatus;
};

export type PortainereeAuthActivityLog = {
  context?: PortainerAuthenticationMethod;
  id?: number;
  origin?: string;
  timestamp?: number;
  type?: PortainereeAuthenticationActivityType;
  username?: string;
};

export const PortainereeAuthenticationActivityType = {
  /**
   * _
   */
  '': 0,
  /**
   * AuthenticationActivitySuccess
   */
  AUTHENTICATION_ACTIVITY_SUCCESS: 1,
  /**
   * AuthenticationActivityFailure
   */
  AUTHENTICATION_ACTIVITY_FAILURE: 2,
  /**
   * AuthenticationActivityLogOut
   */
  AUTHENTICATION_ACTIVITY_LOG_OUT: 3,
} as const;

export type PortainereeAuthenticationActivityType =
  (typeof PortainereeAuthenticationActivityType)[keyof typeof PortainereeAuthenticationActivityType];

export type PortainereeAutoPatchSettings = {
  Enabled?: boolean;
  PatchCron?: string;
  PortainerRepository?: string;
  RegistryID?: number;
  UpdaterRepository?: string;
};

export const PortainereeAutoUpdateStatus = {
  /**
   * AutoUpdateStatusInProgress
   */
  AUTO_UPDATE_STATUS_IN_PROGRESS: 'inProgress',
  /**
   * AutoUpdateStatusCompleted
   */
  AUTO_UPDATE_STATUS_COMPLETED: 'completed',
  /**
   * AutoUpdateStatusFailed
   */
  AUTO_UPDATE_STATUS_FAILED: 'failed',
} as const;

export type PortainereeAutoUpdateStatus =
  (typeof PortainereeAutoUpdateStatus)[keyof typeof PortainereeAutoUpdateStatus];

export const PortainereeAzureAuthMethod = {
  /**
   * AzureAuthServicePrincipalSecret
   */
  AZURE_AUTH_SERVICE_PRINCIPAL_SECRET: 'servicePrincipalSecret',
  /**
   * AzureAuthServicePrincipalCertificate
   */
  AZURE_AUTH_SERVICE_PRINCIPAL_CERTIFICATE: 'servicePrincipalCertificate',
  /**
   * AzureAuthManagedIdentity
   */
  AZURE_AUTH_MANAGED_IDENTITY: 'managedIdentity',
  /**
   * AzureAuthStorageAccountKey
   */
  AZURE_AUTH_STORAGE_ACCOUNT_KEY: 'storageAccountKey',
} as const;

export type PortainereeAzureAuthMethod =
  (typeof PortainereeAzureAuthMethod)[keyof typeof PortainereeAzureAuthMethod];

export type PortainereeAzureBlobBackupSettings = {
  authMethod?: PortainereeAzureAuthMethod;
  /**
   * Service Principal — certificate (base64-encoded PEM/PFX bytes)
   */
  clientCertificate?: string;
  clientCertificatePassword?: string;
  clientId?: string;
  /**
   * Service Principal — secret
   */
  clientSecret?: string;
  containerName?: string;
  cronRule?: string;
  /**
   * Managed Identity (empty = system-assigned; non-empty = user-assigned client ID)
   */
  managedIdentityClientId?: string;
  password?: string;
  /**
   * Optional: override blob service URL for sovereign clouds or Azurite testing
   */
  serviceUrl?: string;
  /**
   * Storage account access key (base64-encoded, copied from the Azure Portal Keys blade)
   */
  storageAccountKey?: string;
  storageAccountName?: string;
  /**
   * Service Principal common
   */
  tenantId?: string;
};

export type PortainereeCloudApiKeys = {
  CivoApiKey?: string;
  DigitalOceanToken?: string;
  GKEApiKey?: string;
  LinodeToken?: string;
};

export type PortainereeCloudProvider = {
  /**
   * Amazon specific fields
   */
  AmiType?: string;
  CPU?: number;
  /**
   * CredentialID holds an ID of the credential used to create the cluster
   */
  CredentialID?: number;
  DNSPrefix?: string;
  HDD?: number;
  InstanceType?: string;
  KubernetesVersion?: string;
  Name?: string;
  /**
   * Pointer will hide this field for providers other than civo which do
   * not use this field.
   */
  NetworkID?: string;
  NodeCount?: number;
  NodeVolumeSize?: number;
  PoolName?: string;
  Provider?: string;
  RAM?: number;
  Region?: string;
  /**
   * Azure specific fields
   */
  ResourceGroup?: string;
  Size?: string;
  Tier?: string;
  URL?: string;
};

export const PortainereeConditionOperator = {
  /**
   * OperatorGreaterThan
   */
  OPERATOR_GREATER_THAN: '>',
  /**
   * OperatorLessThan
   */
  OPERATOR_LESS_THAN: '<',
  /**
   * OperatorEqual
   */
  OPERATOR_EQUAL: '=',
  /**
   * OperatorGreaterThanOrEqual
   */
  OPERATOR_GREATER_THAN_OR_EQUAL: '>=',
  /**
   * OperatorLessThanOrEqual
   */
  OPERATOR_LESS_THAN_OR_EQUAL: '<=',
} as const;

export type PortainereeConditionOperator =
  (typeof PortainereeConditionOperator)[keyof typeof PortainereeConditionOperator];

export type PortainereeCustomTemplate = {
  /**
   * User identifier who created this template
   */
  CreatedByUserId?: number;
  /**
   * Description of the template
   */
  Description?: string;
  EdgeSettings?: PortainereeCustomTemplateEdgeSettings;
  /**
   * EdgeTemplate indicates if this template purpose for Edge Stack
   */
  EdgeTemplate?: boolean;
  /**
   * Path to the Stack file
   */
  EntryPoint?: string;
  GitConfig?: GithubComPortainerPortainerEeApiGitTypesRepoConfig;
  /**
   * CustomTemplate Identifier
   */
  Id?: number;
  /**
   * IsComposeFormat indicates if the Kubernetes template is created from a Docker Compose file
   */
  IsComposeFormat?: boolean;
  /**
   * URL of the template's logo
   */
  Logo?: string;
  /**
   * A note that will be displayed in the UI. Supports HTML content
   */
  Note?: string;
  /**
   * Platform associated to the template.
   * Valid values are: 1 - 'linux', 2 - 'windows'
   */
  Platform?: 1 | 2;
  /**
   * Path on disk to the repository hosting the Stack file
   */
  ProjectPath?: string;
  ResourceControl?: PortainerResourceControl;
  /**
   * Title of the template
   */
  Title?: string;
  /**
   * Type of created stack:
   * * 1 - swarm
   * * 2 - compose
   * * 3 - kubernetes
   */
  Type?: 1 | 2 | 3;
  Variables?: Array<PortainerCustomTemplateVariableDefinition>;
  artifact?: PortainerArtifact;
};

export type PortainereeCustomTemplateEdgeSettings = {
  /**
   * Pre Pull Image
   */
  PrePullImage?: boolean;
  PrivateRegistryId?: number;
  RelativePathSettings?: PortainereeCustomTemplateRelativePathSettings;
  /**
   * Retry deploy
   */
  RetryDeploy?: boolean;
  RetryPeriod?: number;
  /**
   * StaggerConfig is the configuration for staggered update
   */
  StaggerConfig?: PortainereeEdgeStaggerConfig;
};

export type PortainereeCustomTemplateRelativePathSettings = {
  /**
   * Local filesystem path
   */
  FilesystemPath?: string;
  /**
   * Per device configs group match type
   */
  PerDeviceConfigsGroupMatchType?: 'file' | ' dir';
  /**
   * Per device configs match type
   */
  PerDeviceConfigsMatchType?: 'file' | ' dir';
  /**
   * Per device configs path
   */
  PerDeviceConfigsPath?: string;
  /**
   * Whether the edge stack supports per device configs
   */
  SupportPerDeviceConfigs?: boolean;
  /**
   * Whether the stack supports relative path volume
   */
  SupportRelativePath?: boolean;
};

export type PortainereeDeploymentOptions = {
  /**
   * Hide manual deploy forms in portainer
   */
  hideAddWithForm: boolean;
  /**
   * Hide the file upload option in the remaining visible forms
   */
  hideFileUpload: boolean;
  /**
   * Hide the webeditor in the remaining visible forms
   */
  hideWebEditor: boolean;
  overrideGlobalOptions: boolean;
};

export type PortainereeEdge = {
  /**
   * Deprecated 2.18
   */
  AsyncMode?: boolean;
  /**
   * The command list interval for edge agent - used in edge async mode (in seconds)
   */
  CommandInterval?: number;
  MTLS?: PortainereeMtlsSettings;
  /**
   * The ping interval for edge agent - used in edge async mode (in seconds)
   */
  PingInterval?: number;
  /**
   * The snapshot interval for edge agent - used in edge async mode (in seconds)
   */
  SnapshotInterval?: number;
  /**
   * The address where the tunneling server can be reached by Edge agents
   */
  TunnelServerAddress?: string;
};

export type PortainereeEdgeConfig = {
  baseDir?: string;
  category?: PortainereeEdgeConfigCategory;
  created?: number;
  createdBy?: number;
  edgeGroupIDs?: Array<number>;
  id?: number;
  name?: string;
  prev?: PortainereeEdgeConfig;
  progress?: PortainereeEdgeConfigProgress;
  state?: PortainereeEdgeConfigStateType;
  type?: number;
  updated?: number;
  updatedBy?: number;
};

export const PortainereeEdgeConfigCategory = {
  /**
   * EdgeConfigCategoryConfig
   */
  EDGE_CONFIG_CATEGORY_CONFIG: 'configuration' /**
   * EdgeConfigCategorySecret
   */,
  EDGE_CONFIG_CATEGORY_SECRET: 'secret',
} as const;

export type PortainereeEdgeConfigCategory =
  (typeof PortainereeEdgeConfigCategory)[keyof typeof PortainereeEdgeConfigCategory];

export type PortainereeEdgeConfigProgress = {
  success?: number;
  total?: number;
};

export const PortainereeEdgeConfigStateType = {
  /**
   * EdgeConfigIdleState
   */
  EDGE_CONFIG_IDLE_STATE: 0,
  /**
   * EdgeConfigFailureState
   */
  EDGE_CONFIG_FAILURE_STATE: 1,
  /**
   * EdgeConfigSavingState
   */
  EDGE_CONFIG_SAVING_STATE: 2,
  /**
   * EdgeConfigDeletingState
   */
  EDGE_CONFIG_DELETING_STATE: 3,
  /**
   * EdgeConfigUpdatingState
   */
  EDGE_CONFIG_UPDATING_STATE: 4,
} as const;

export type PortainereeEdgeConfigStateType =
  (typeof PortainereeEdgeConfigStateType)[keyof typeof PortainereeEdgeConfigStateType];

export type PortainereeEdgeGroup = {
  Dynamic?: boolean;
  EdgeUpdateID?: number;
  EndpointIds?: RoarRoarPortainerEndpointId;
  /**
   * Deprecated: only used for API responses
   */
  Endpoints?: Array<number>;
  /**
   * EdgeGroup Identifier
   */
  Id?: number;
  Name?: string;
  PartialMatch?: boolean;
  TagIds?: Array<number>;
};

export type PortainereeEdgeStack = {
  /**
   * Whether the edge stack always clones the git repository for relative path
   */
  AlwaysCloneGitRepoForRelativePath?: boolean;
  /**
   * The GitOps update settings of a git stack
   */
  AutoUpdate?: PortainerAutoUpdateSettings;
  /**
   * The username which created this stack
   */
  CreatedBy?: string;
  /**
   * The username id which created this stack
   */
  CreatedByUserId?: string;
  /**
   * StatusArray    map[EndpointID][]EdgeStackStatus `json:"StatusArray"`
   */
  CreationDate?: number;
  /**
   * CurrentDeploymentInfo records the git repository state at the time of the last actual deployment
   */
  CurrentDeploymentInfo?: PortainerStackDeploymentInfo;
  /**
   * Options to control the Deployer behaviour
   */
  DeployerOptions?: PortainereeEdgeStackDeployerOptions;
  DeploymentType?: PortainerEdgeStackDeploymentType;
  EdgeGroups?: Array<number>;
  /**
   * EdgeUpdateID represents the parent update ID, will be zero if this stack is not part of an update
   */
  EdgeUpdateID?: number;
  EntryPoint?: string;
  /**
   * EnvVars is a list of environment variables to inject into the stack
   */
  EnvVars?: Array<PortainerPair>;
  /**
   * Local filesystem path
   */
  FilesystemPath?: string;
  /**
   * GitConfig is the git repository configuration for git-backed edge stacks.
   * Deprecated: loaded from Source via WorkflowID; kept for DB backwards-compatibility only.
   * Non-migration code must not read or write this field; use Source records instead.
   */
  GitConfig?: GithubComPortainerPortainerEeApiGitTypesRepoConfig;
  /**
   * HelmConfig is the configuration for a Helm based deployment of an edge stack (Helm Chart)
   */
  HelmConfig?: PortainerHelmConfig;
  /**
   * EdgeStack Identifier
   */
  Id?: number;
  ManifestPath?: string;
  Name?: string;
  NumDeployments?: number;
  /**
   * Per device configs group match type
   */
  PerDeviceConfigsGroupMatchType?: 'file' | ' dir';
  /**
   * Per device configs match type
   */
  PerDeviceConfigsMatchType?: 'file' | ' dir';
  /**
   * Per device configs path
   */
  PerDeviceConfigsPath?: string;
  /**
   * Pre Pull Image
   */
  PrePullImage?: boolean;
  /**
   * PreviousDeploymentInfo represents the previous deployment info of the stack
   */
  PreviousDeploymentInfo?: PortainerStackDeploymentInfo;
  ProjectPath?: string;
  /**
   * Deprecated(2.36): keep it for backward compatibility. To remove in future versions (2.44+)
   * Re-Pull Image
   */
  RePullImage?: boolean;
  Registries?: Array<number>;
  /**
   * Retry deploy
   */
  RetryDeploy?: boolean;
  /**
   * RetryPeriod specifies the duration, in seconds, for which the agent should continue attempting to deploy the stack after a failure
   */
  RetryPeriod?: number;
  /**
   * Schedule represents the schedule of the Edge stack (optional, format - 'YYYY-MM-DD HH:mm:ss')
   */
  ScheduledTime?: string;
  /**
   * StackFileVersion represents the version of the stack file, such yaml, hcl, manifest file
   */
  StackFileVersion?: number;
  /**
   * StaggerConfig is the configuration for staggered update
   */
  StaggerConfig?: PortainereeEdgeStaggerConfig;
  Status?: {
    [key: string]: PortainerEdgeStackStatus;
  };
  /**
   * Whether the edge stack supports per device configs
   */
  SupportPerDeviceConfigs?: boolean;
  /**
   * Whether the stack supports relative path volume
   */
  SupportRelativePath?: boolean;
  /**
   * Uses the manifest's namespaces instead of the default one
   */
  UseManifestNamespaces?: boolean;
  Version?: number;
  /**
   * A UUID to identify a webhook. The stack will be force updated and pull the latest image when the webhook was invoked.
   */
  Webhook?: string;
  /**
   * WorkflowID is the ID of the Workflow that owns the Source for this edge stack.
   */
  WorkflowID?: number;
};

export type PortainereeEdgeStackDeployerOptions = {
  /**
   * Drives `compose up --remove-orphans` and `stack up --prune` options
   */
  Prune?: boolean;
  /**
   * Drives `compose down --volumes` option
   */
  RemoveVolumes?: boolean;
};

export type PortainereeEdgeStaggerConfig = {
  DeviceNumber?: number;
  DeviceNumberIncrementBy?: number;
  DeviceNumberStartFrom?: number;
  StaggerOption?: PortainereeEdgeStaggerOption;
  StaggerParallelOption?: PortainereeEdgeStaggerParallelOption;
  /**
   * Timeout unit is minute
   */
  Timeout?: string;
  /**
   * UpdateDelay unit is minute
   */
  UpdateDelay?: string;
  UpdateFailureAction?: PortainereeEdgeUpdateFailureAction;
};

export const PortainereeEdgeStaggerOption = {
  /**
   * _
   */
  '': 0,
  /**
   * EdgeStaggerOptionAllAtOnce
   */
  EDGE_STAGGER_OPTION_ALL_AT_ONCE: 1,
  /**
   * EdgeStaggerOptionParallel
   */
  EDGE_STAGGER_OPTION_PARALLEL: 2,
} as const;

export type PortainereeEdgeStaggerOption =
  (typeof PortainereeEdgeStaggerOption)[keyof typeof PortainereeEdgeStaggerOption];

export const PortainereeEdgeStaggerParallelOption = {
  /**
   * _
   */
  '': 0,
  /**
   * EdgeStaggerParallelOptionFixed
   */
  EDGE_STAGGER_PARALLEL_OPTION_FIXED: 1,
  /**
   * EdgeStaggerParallelOptionIncremental
   */
  EDGE_STAGGER_PARALLEL_OPTION_INCREMENTAL: 2,
} as const;

export type PortainereeEdgeStaggerParallelOption =
  (typeof PortainereeEdgeStaggerParallelOption)[keyof typeof PortainereeEdgeStaggerParallelOption];

export const PortainereeEdgeUpdateFailureAction = {
  /**
   * _
   */
  '': 0,
  /**
   * EdgeUpdateFailureActionContinue
   */
  EDGE_UPDATE_FAILURE_ACTION_CONTINUE: 1,
  /**
   * EdgeUpdateFailureActionPause
   */
  EDGE_UPDATE_FAILURE_ACTION_PAUSE: 2,
  /**
   * EdgeUpdateFailureActionRollback
   */
  EDGE_UPDATE_FAILURE_ACTION_ROLLBACK: 3,
} as const;

export type PortainereeEdgeUpdateFailureAction =
  (typeof PortainereeEdgeUpdateFailureAction)[keyof typeof PortainereeEdgeUpdateFailureAction];

export type PortainereeEndpoint = {
  Agent: PortainereeEnvironmentAgentData;
  AzureCredentials?: PortainerAzureCredentials;
  /**
   * GitOps update change window restriction for stacks and apps
   */
  ChangeWindow: PortainereeEndpointChangeWindow;
  /**
   * A Kubernetes as a service cloud provider. Only included if this
   * endpoint was created using KaaS provisioning.
   */
  CloudProvider?: PortainereeCloudProvider;
  /**
   * Maximum version of docker-compose
   */
  ComposeSyntaxMaxVersion: string;
  /**
   * ContainerEngine represents the container engine type. This can be 'docker' or 'podman' when interacting directly with these environments, otherwise '' for kubernetes environments.
   */
  ContainerEngine: string;
  /**
   * Hide manual deployment forms for an environment
   */
  DeploymentOptions?: PortainereeDeploymentOptions;
  Edge: PortainerEnvironmentEdgeSettings;
  /**
   * The check in interval for edge agent (in seconds)
   */
  EdgeCheckinInterval: number;
  /**
   * The identifier of the edge agent associated with this environment(endpoint)
   */
  EdgeID?: string;
  /**
   * The key which is used to map the agent to Portainer
   */
  EdgeKey: string;
  EnableGPUManagement?: boolean;
  EnableImageNotification: boolean;
  Gpus?: Array<PortainerPair>;
  /**
   * Environment(Endpoint) group identifier
   */
  GroupId: number;
  /**
   * Heartbeat indicates the heartbeat status of an edge environment
   */
  Heartbeat?: boolean;
  /**
   * Environment(Endpoint) Identifier
   */
  Id: number;
  /**
   * Associated Kubernetes data
   */
  Kubernetes: PortainereeKubernetesData;
  /**
   * LastCheckInDate mark last check-in date on checkin
   */
  LastCheckInDate: number;
  /**
   * LocalTimeZone is the local time zone of the endpoint
   */
  LocalTimeZone: string;
  MTLSStatus?: PortainereeMtlsStatus;
  /**
   * Environment(Endpoint) name
   */
  Name: string;
  Policies?: PortainereeEndpointPolicies;
  /**
   * URL or IP address where exposed containers will be reachable
   */
  PublicURL: string;
  /**
   * Environment(Endpoint) specific security settings
   */
  SecuritySettings: PortainerEndpointSecuritySettings;
  /**
   * List of snapshots
   */
  Snapshots?: Array<PortainerDockerSnapshot>;
  /**
   * The status of the environment(endpoint) (1 - up, 2 - down, 3 - provisioning, 4 - error)
   */
  Status?: 1 | 2 | 3 | 4;
  /**
   * A message that describes the status. Should be included for Status 3
   * or 4.
   */
  StatusMessage: PortainereeEndpointStatusMessage;
  TLSConfig: PortainerTlsConfiguration;
  /**
   * List of tag identifiers to which this environment(endpoint) is associated
   */
  TagIds?: Array<number>;
  /**
   * List of team identifiers authorized to connect to this environment(endpoint)
   */
  TeamAccessPolicies?: PortainerTeamAccessPolicies;
  /**
   * Environment(Endpoint) environment(endpoint) type. 1 for a Docker environment(endpoint), 2 for an agent on Docker environment(endpoint) or 3 for an Azure environment(endpoint).
   */
  Type: PortainerEndpointType;
  /**
   * URL or IP address of the Docker host associated to this environment(endpoint)
   */
  URL: string;
  /**
   * List of user identifiers authorized to connect to this environment(endpoint)
   */
  UserAccessPolicies?: PortainerUserAccessPolicies;
  /**
   * Whether the device has been trusted or not by the user
   */
  UserTrusted?: boolean;
};

export type PortainereeEndpointChangeWindow = {
  Enabled: boolean;
  EndTime: string;
  StartTime: string;
};

export const PortainereeEndpointOperationStatus = {
  /**
   * EndpointOperationStatusProcessing
   */
  ENDPOINT_OPERATION_STATUS_PROCESSING: 'processing',
  /**
   * EndpointOperationStatusWarning
   */
  ENDPOINT_OPERATION_STATUS_WARNING: 'warning',
  /**
   * EndpointOperationStatusError
   */
  ENDPOINT_OPERATION_STATUS_ERROR: 'error',
  /**
   * EndpointOperationStatusDone
   */
  ENDPOINT_OPERATION_STATUS_DONE: '',
} as const;

export type PortainereeEndpointOperationStatus =
  (typeof PortainereeEndpointOperationStatus)[keyof typeof PortainereeEndpointOperationStatus];

export type PortainereeEndpointPolicies = {
  changeConfirmationPolicy?: PortainereeAppliedPolicy;
  dockerCleanupPolicy?: PortainereeAppliedPolicy;
  dockerRBACPolicy?: PortainereeAppliedPolicy;
  dockerRegistryPolicy?: PortainereeAppliedPolicy;
  dockerSecurityPolicy?: PortainereeAppliedPolicy;
  dockerSetupPolicy?: PortainereeAppliedPolicy;
  k8sNetworkSecurityPolicy?: PortainereeAppliedPolicy;
  k8sObservabilityPolicy?: PortainereeAppliedPolicy;
  k8sPodSecurityStandardsPolicy?: PortainereeAppliedPolicy;
  k8sRBACPolicy?: PortainereeAppliedPolicy;
  k8sRegistryPolicy?: PortainereeAppliedPolicy;
  k8sSecurityPolicy?: PortainereeAppliedPolicy;
  k8sSetupPolicy?: PortainereeAppliedPolicy;
  k8sVulnerabilityScanningPolicy?: PortainereeAppliedPolicy;
};

export type PortainereeEndpointStatusMessage = {
  detail?: string;
  /**
   * TODO: in future versions, we should think about removing these fields and
   * create a separate bucket to store cluster operation messages instead or try to find a better way.
   * Operation/OperationStatus blank means, nothing is happening
   */
  operation?: string;
  /**
   * ,processing,error
   */
  operationStatus?: PortainereeEndpointOperationStatus;
  summary?: string;
  warnings?: Array<string>;
};

export type PortainereeEnvironmentAgentData = {
  PreviousVersion?: string;
  Version?: string;
};

export type PortainereeExperimentalFeatures = {
  [key: string]: unknown;
};

export type PortainereeGlobalDeploymentOptions = {
  /**
   * Hide manual deploy forms in portainer
   */
  hideAddWithForm?: boolean;
  /**
   * Hide the file upload option in the remaining visible forms
   */
  hideFileUpload?: boolean;
  hideStacksFunctionality?: boolean;
  /**
   * Hide the webeditor in the remaining visible forms
   */
  hideWebEditor?: boolean;
  minApplicationNoteLength?: number;
  /**
   * Configure this per environment or globally
   */
  perEnvOverride?: boolean;
  /**
   * Make note field mandatory if enabled
   */
  requireNoteOnApplications?: boolean;
};

export type PortainereeKubernetesConfiguration = {
  AllowNoneIngressClass: boolean;
  EnableNodeShell?: boolean;
  EnableResourceOverCommit?: boolean;
  IngressAvailabilityPerNamespace: boolean;
  IngressClasses?: Array<PortainerKubernetesIngressClassConfig>;
  ResourceOverCommitPercentage?: number;
  RestrictDefaultNamespace?: boolean;
  RestrictSecrets?: boolean;
  RestrictStandardUserIngressW?: boolean;
  StorageClasses?: Array<PortainerKubernetesStorageClassConfig>;
  UseLoadBalancer?: boolean;
  UseServerMetrics?: boolean;
};

export type PortainereeKubernetesData = {
  Configuration: PortainereeKubernetesConfiguration;
  Flags: PortainerKubernetesFlags;
  Snapshots?: Array<PortainerKubernetesSnapshot>;
};

export type PortainereeLdapKerberosSettings = {
  Configuration?: string;
  Password?: string;
  Realm?: string;
  Service?: string;
  Username?: string;
};

export const PortainereeLdapServerType = {
  /**
   * LDAPServerCustom
   */
  LDAP_SERVER_CUSTOM: 0,
  /**
   * LDAPServerOpenLDAP
   */
  LDAP_SERVER_OPEN_LDAP: 1,
  /**
   * LDAPServerAD
   */
  LDAP_SERVER_AD: 2,
} as const;

export type PortainereeLdapServerType =
  (typeof PortainereeLdapServerType)[keyof typeof PortainereeLdapServerType];

export type PortainereeLdapSettings = {
  /**
   * Whether auto admin population is switched on or not
   */
  AdminAutoPopulate?: boolean;
  AdminGroupSearchSettings?: Array<PortainerLdapGroupSearchSettings>;
  /**
   * Saved admin group list, the user will be populated as an admin role if any user group matches the record in the list
   */
  AdminGroups?: Array<string>;
  /**
   * Enable this option if the server is configured for Anonymous access. When enabled, ReaderDN and Password will not be used
   */
  AnonymousMode?: boolean;
  /**
   * Automatically provision users and assign them to matching LDAP group names
   */
  AutoCreateUsers?: boolean;
  GroupSearchSettings?: Array<PortainerLdapGroupSearchSettings>;
  /**
   * Kerberos settings
   */
  Kerberos?: PortainereeLdapKerberosSettings;
  /**
   * Password of the account that will be used to search users
   */
  Password?: string;
  /**
   * Account that will be used to search for users
   */
  ReaderDN?: string;
  SearchSettings?: Array<PortainerLdapSearchSettings>;
  ServerType?: PortainereeLdapServerType;
  /**
   * Whether LDAP connection should use StartTLS
   */
  StartTLS?: boolean;
  TLSConfig?: PortainerTlsConfiguration;
  /**
   * Deprecated
   */
  URL?: string;
  /**
   * URLs or IP addresses of the LDAP server
   */
  URLs?: Array<string>;
};

export type PortainereeLocalBackupSettings = {
  /**
   * Crontab rule to make periodical backups
   */
  cronRule?: string;
  /**
   * Number of days to retain backups (0 means keep forever)
   */
  retentionDays?: number;
};

export type PortainereeMtlsSettings = {
  /**
   * CaCertFile is the path to the mTLS CA certificate file
   */
  CaCertFile?: string;
  /**
   * CertFile is the path to the mTLS certificate file
   */
  CertFile?: string;
  /**
   * KeyFile is the path to the mTLS key file
   */
  KeyFile?: string;
  UseSeparateCert?: boolean;
};

export type PortainereeMtlsStatus = {
  enabled: boolean;
  ok: boolean;
};

export type PortainereeOAuthClaimMappings = {
  ClaimValRegex?: string;
  Team?: number;
};

export type PortainereeOAuthSettings = {
  AccessTokenURI?: string;
  AuthStyle?: Oauth2AuthStyle;
  AuthorizationURI?: string;
  ClientID?: string;
  ClientSecret?: string;
  DefaultTeamID?: number;
  HideInternalAuth?: boolean;
  KubeSecretKey?: Array<number>;
  LogoutURI?: string;
  MicrosoftTenantID?: string;
  OAuthAutoCreateUsers?: boolean;
  OAuthAutoMapTeamMemberships?: boolean;
  RedirectURI?: string;
  ResourceURI?: string;
  SSO?: boolean;
  Scopes?: string;
  TeamMemberships?: PortainereeTeamMemberships;
  UserIdentifier?: string;
};

export type PortainereeOmniCluster = {
  kind?: string;
  kubernetes?: {
    version?: string;
  };
  labels?: {
    [key: string]: string;
  };
  name?: string;
  patches?: Array<PortainereeOmniClusterPatch>;
  talos?: {
    version?: string;
  };
};

export type PortainereeOmniClusterPatch = {
  annotations?: PortainereeOmniClusterPatchAnnotations;
  idOverride?: string;
  inline?: {
    [key: string]: unknown;
  };
};

export type PortainereeOmniClusterPatchAnnotations = {
  name?: string;
};

export type PortainereeOmniControlPlane = {
  kind?: string;
  machines?: Array<PortainereeOmniMachine>;
};

export type PortainereeOmniInterface = {
  addresses?: Array<string>;
  dhcp?: boolean;
  interface?: string;
  routes?: Array<PortainereeOmniInterfaceRoute>;
};

export type PortainereeOmniInterfaceRoute = {
  gateway?: string;
  network?: string;
};

export type PortainereeOmniMachine = {
  hostname?: string;
  install?: PortainereeOmniMachineInstall;
  interfaces?: Array<PortainereeOmniInterface>;
  kind?: string;
  name?: string;
  nameservers?: Array<string>;
  patches?: Array<PortainereeOmniMachinePatch>;
  /**
   * in GiB; drives VolumeConfig EPHEMERAL patch generation, not serialized to YAML directly
   */
  systemDiskSize?: number;
  /**
   * drives user VolumeConfig patch generation, not serialized to YAML directly
   */
  userDisk?: PortainereeOmniUserDisk;
};

export type PortainereeOmniMachineInstall = {
  disk?: string;
};

export type PortainereeOmniMachinePatch = {
  idOverride?: string;
  inline?: PortainereeOmniMachinePatchInline;
};

export type PortainereeOmniMachinePatchInline = {
  machine?: PortainereeOmniMachinePatchMachine;
};

export type PortainereeOmniMachinePatchInterface = {
  addresses?: Array<string>;
  dhcp?: boolean;
  interface?: string;
  routes?: Array<PortainereeOmniMachinePatchRoute>;
};

export type PortainereeOmniMachinePatchMachine = {
  network?: PortainereeOmniMachinePatchNetwork;
};

export type PortainereeOmniMachinePatchNetwork = {
  hostname?: string;
  interfaces?: Array<PortainereeOmniMachinePatchInterface>;
  nameservers?: Array<string>;
};

export type PortainereeOmniMachinePatchRoute = {
  gateway?: string;
  network?: string;
};

export type PortainereeOmniUserDisk = {
  /**
   * in GiB; 0 means use all remaining space (grow: true)
   */
  size?: number;
  volumeName?: string;
};

export type PortainereeOmniWorker = {
  kind?: string;
  machines?: Array<PortainereeOmniMachine>;
  name?: string;
};

export type PortainereePolicyStatus = {
  /**
   * Unix timestamp
   */
  LastAttemptTime?: number;
  Message?: string;
  Status?: PortainereePolicyStatusType;
};

export const PortainereePolicyStatusType = {
  /**
   * PolicyStatusTypeApplied
   */
  POLICY_STATUS_TYPE_APPLIED: 'applied',
  /**
   * PolicyStatusTypeFailed
   */
  POLICY_STATUS_TYPE_FAILED: 'failed',
  /**
   * PolicyStatusTypeInProgress
   */
  POLICY_STATUS_TYPE_IN_PROGRESS: 'in_progress',
  /**
   * PolicyStatusTypeWarning
   */
  POLICY_STATUS_TYPE_WARNING: 'warning',
  /**
   * PolicyStatusTypeNotSupported
   */
  POLICY_STATUS_TYPE_NOT_SUPPORTED: 'not_supported',
} as const;

export type PortainereePolicyStatusType =
  (typeof PortainereePolicyStatusType)[keyof typeof PortainereePolicyStatusType];

export const PortainereeRecommendationCategory = {
  /**
   * RecommendationCategoryGovernance
   */
  RECOMMENDATION_CATEGORY_GOVERNANCE: 'governance',
  /**
   * RecommendationCategorySecurity
   */
  RECOMMENDATION_CATEGORY_SECURITY: 'security',
  /**
   * RecommendationCategoryOperations
   */
  RECOMMENDATION_CATEGORY_OPERATIONS: 'operations',
} as const;

export type PortainereeRecommendationCategory =
  (typeof PortainereeRecommendationCategory)[keyof typeof PortainereeRecommendationCategory];

export const PortainereeRecommendationSeverity = {
  /**
   * RecommendationSeverityError
   */
  RECOMMENDATION_SEVERITY_ERROR: 'error',
  /**
   * RecommendationSeverityWarning
   */
  RECOMMENDATION_SEVERITY_WARNING: 'warning',
  /**
   * RecommendationSeverityInfo
   */
  RECOMMENDATION_SEVERITY_INFO: 'info',
} as const;

export type PortainereeRecommendationSeverity =
  (typeof PortainereeRecommendationSeverity)[keyof typeof PortainereeRecommendationSeverity];

export type PortainereeRegistry = {
  /**
   * Stores temporary access token
   */
  AccessToken?: string;
  AccessTokenExpiry?: number;
  /**
   * Is authentication against this registry enabled
   */
  Authentication?: boolean;
  /**
   * Deprecated in DBVersion == 18
   */
  AuthorizedTeams?: Array<number>;
  /**
   * Deprecated in DBVersion == 18
   */
  AuthorizedUsers?: Array<number>;
  /**
   * Base URL, introduced for ProGet registry
   */
  BaseURL?: string;
  Ecr?: PortainerEcrData;
  Github?: PortainerGithubRegistryData;
  Gitlab?: PortainerGitlabRegistryData;
  /**
   * Registry Identifier
   */
  Id?: number;
  ManagementConfiguration?: PortainerRegistryManagementConfiguration;
  /**
   * Registry Name
   */
  Name?: string;
  /**
   * Password or SecretAccessKey used to authenticate against this registry
   */
  Password?: string;
  Quay?: PortainerQuayRegistryData;
  RegistryAccesses?: PortainerRegistryAccesses;
  /**
   * Deprecated in DBVersion == 31
   */
  TeamAccessPolicies?: PortainerTeamAccessPolicies;
  /**
   * Registry Type (1 - Quay, 2 - Azure, 3 - Custom, 4 - Gitlab, 5 - ProGet, 6 - DockerHub, 7 - ECR)
   */
  Type?: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  /**
   * URL or IP address of the Docker registry
   */
  URL?: string;
  /**
   * Deprecated fields
   * Deprecated in DBVersion == 31
   */
  UserAccessPolicies?: PortainerUserAccessPolicies;
  /**
   * Username or AccessKeyID used to authenticate against this registry
   */
  Username?: string;
};

export type PortainereeRole = {
  /**
   * Authorizations associated to a role
   */
  Authorizations: PortainerAuthorizations;
  /**
   * Role description
   */
  Description: string;
  /**
   * Role Identifier
   */
  Id: number;
  /**
   * Role name
   */
  Name: string;
  Priority: number;
  Scope: {
    [key: string]: Array<string>;
  };
};

export type PortainereeS3BackupSettings = {
  /**
   * AWS access key id
   */
  accessKeyID?: string;
  /**
   * AWS S3 bucket name
   */
  bucketName?: string;
  /**
   * Crontab rule to make periodical backups
   */
  cronRule?: string;
  /**
   * Password to encrypt the backup with
   */
  password?: string;
  /**
   * AWS S3 region. Default to "us-east-1"
   */
  region?: string;
  /**
   * S3 compatible host
   */
  s3CompatibleHost?: string;
  /**
   * AWS secret access key
   */
  secretAccessKey?: string;
};

export type PortainereeSslSettings = {
  caCertPath?: string;
  certPath?: string;
  httpEnabled?: boolean;
  keyPath?: string;
  selfSigned?: boolean;
};

export type PortainereeSettings = {
  /**
   * Additional Functionality
   */
  AdditionalFunctionality?: PortainereeAdditionalFunctionality;
  /**
   * AddonsCatalogURL is where the add-on catalog is fetched from. Empty means
   * the built-in default, so an operator can pin or mirror the catalog.
   */
  AddonsCatalogURL?: string;
  /**
   * Container environment parameter AGENT_SECRET
   */
  AgentSecret?: string;
  AllowBindMountsForRegularUsers?: boolean;
  AllowContainerCapabilitiesForRegularUsers?: boolean;
  AllowDeviceMappingForRegularUsers?: boolean;
  AllowHostNamespaceForRegularUsers?: boolean;
  AllowPrivilegedModeForRegularUsers?: boolean;
  AllowStackManagementForRegularUsers?: boolean;
  AllowVolumeBrowserForRegularUsers?: boolean;
  /**
   * Active authentication method for the Portainer instance. Valid values are: 1 for internal, 2 for LDAP, or 3 for oauth
   */
  AuthenticationMethod?: PortainerAuthenticationMethod;
  AutoPatchSettings?: PortainereeAutoPatchSettings;
  /**
   * A list of label name & value that will be used to hide containers when querying containers
   */
  BlackListedLabels?: Array<PortainerPair>;
  /**
   * CloudAPIKeys
   */
  CloudApiKeys?: PortainereeCloudApiKeys;
  /**
   * The content in plaintext used to display in the login page. Will hide when value is empty string
   */
  CustomLoginBanner?: string;
  /**
   * The default builtin registry now is anonymous docker hub registry
   */
  DefaultRegistry?: {
    Hide?: boolean;
  };
  /**
   * DisableKubeRolesSync will disable the sync of kube roles for all built-in roles
   */
  DisableKubeRolesSync?: boolean;
  /**
   * DisableKubeShell will disable the kube shell feature for non-admin users
   */
  DisableKubeShell?: boolean;
  /**
   * DisableKubeconfigDownload will disable the download of kubeconfig files for non-admin users
   */
  DisableKubeconfigDownload?: boolean;
  /**
   * Deprecated fields
   */
  DisplayDonationHeader?: boolean;
  DisplayExternalContributors?: boolean;
  Edge?: PortainereeEdge;
  /**
   * The default check in interval for edge agent (in seconds)
   */
  EdgeAgentCheckinInterval?: number;
  /**
   * EdgePortainerURL is the URL that is exposed to edge agents
   */
  EdgePortainerUrl?: string;
  /**
   * Whether edge compute features are enabled
   */
  EnableEdgeComputeFeatures?: boolean;
  /**
   * Deprecated fields v26
   */
  EnableHostManagementFeatures?: boolean;
  /**
   * EnforceEdgeID makes Portainer store the Edge ID instead of accepting anyone
   */
  EnforceEdgeID?: boolean;
  /**
   * Experimental features
   */
  ExperimentalFeatures?: PortainereeExperimentalFeatures;
  FeatureFlagSettings?: {
    [key: string]: boolean;
  };
  /**
   * ForceSecureCookies forces the Secure attribute on auth cookies regardless of detected scheme.
   * Enable when Portainer runs behind a TLS-terminating proxy.
   */
  ForceSecureCookies?: boolean;
  /**
   * Deployment options for encouraging git ops workflows
   */
  GlobalDeploymentOptions?: PortainereeGlobalDeploymentOptions;
  /**
   * Helm repository URL, defaults to "https://charts.bitnami.com/bitnami"
   */
  HelmRepositoryURL?: string;
  InternalAuthSettings?: PortainerInternalAuthSettings;
  IsDockerDesktopExtension?: boolean;
  /**
   * The expiry of a Kubeconfig
   */
  KubeconfigExpiry?: string;
  /**
   * KubectlImage, defaults to portainer/kubectl-shell
   */
  KubectlShellImage?: string;
  LDAPSettings?: PortainereeLdapSettings;
  /**
   * URL to a logo that will be displayed on the login page as well as on top of the sidebar. Will use default Portainer logo when value is empty string
   */
  LogoURL?: string;
  OAuthSettings?: PortainereeOAuthSettings;
  /**
   * The interval in which environment(endpoint) snapshots are created
   */
  SnapshotInterval?: string;
  /**
   * URL to the templates that will be displayed in the UI when navigating to App Templates
   */
  TemplatesURL?: string;
  /**
   * TrustOnFirstConnect makes Portainer accepting edge agent connection by default
   */
  TrustOnFirstConnect?: boolean;
  /**
   * The duration of a user session
   */
  UserSessionTimeout?: string;
};

export type PortainereeStack = {
  /**
   * Only applies when deploying stack with multiple files
   */
  AdditionalFiles?: Array<string>;
  /**
   * The GitOps update settings of a git stack
   */
  AutoUpdate?: PortainerAutoUpdateSettings;
  /**
   * The username which created this stack
   */
  CreatedBy?: string;
  /**
   * The username id which created this stack
   */
  CreatedByUserId?: string;
  /**
   * The date in unix time when stack was created
   */
  CreationDate?: number;
  /**
   * CurrentDeploymentInfo records the git repository state at the time of the last actual deployment.
   */
  CurrentDeploymentInfo?: PortainerStackDeploymentInfo;
  /**
   * DeploymentStartStatus is the stack status captured when the current
   * deployment starts. It is used by deployment logic during the current
   * deployment attempt and is cleared/replaced when a new deployment begins.
   */
  DeploymentStartStatus?: PortainerStackStatus;
  /**
   * DeploymentStatus records the status progression of the current deployment.
   * Cleared when a new deployment starts.
   */
  DeploymentStatus?: Array<PortainerStackDeploymentStatus>;
  /**
   * Environment(Endpoint) identifier. Reference the environment(endpoint) that will be used for deployment
   */
  EndpointId?: number;
  /**
   * EntryPoint is the path to the config file relative to the project root.
   * NOTE: For git stacks this mirrors GitConfig.ConfigFilePath and the two are kept in sync
   * by stackUpdateGit. The deploy command builder (compose_unpacker_cmd_builder) uses this
   * field directly; Kubernetes deploy and git clone operations use GitConfig.ConfigFilePath.
   */
  EntryPoint?: string;
  /**
   * A list of environment(endpoint) variables used during stack deployment
   */
  Env?: Array<PortainerPair>;
  /**
   * Network(Swarm) or local(Standalone) filesystem path
   */
  FilesystemPath?: string;
  /**
   * Whether the stack is from a app template
   */
  FromAppTemplate?: boolean;
  /**
   * GitConfig is the git repository configuration for git-backed stacks.
   * Deprecated: loaded from Source via WorkflowID; kept for DB backwards-compatibility only.
   * Non-migration code must not read or write this field; use Source records instead.
   */
  GitConfig?: GithubComPortainerPortainerEeApiGitTypesRepoConfig;
  /**
   * Path to a Helm chart folder for Helm git deployments
   */
  HelmChartPath?: string;
  /**
   * Array of paths to Helm values YAML files for Helm git deployments
   */
  HelmValuesFiles?: Array<string>;
  /**
   * Stack Identifier
   */
  Id?: number;
  /**
   * Whether the stack is detached from git
   */
  IsDetachedFromGit?: boolean;
  /**
   * Stack name
   */
  Name?: string;
  /**
   * Kubernetes namespace if stack is a kube application
   */
  Namespace?: string;
  /**
   * The stack deployment option
   */
  Option?: PortainerStackOption;
  /**
   * The previous deployment info of the stack
   */
  PreviousDeploymentInfo?: PortainerStackDeploymentInfo;
  /**
   * Path on disk to the repository hosting the Stack file
   */
  ProjectPath?: string;
  /**
   * List of Registries to use for this stack
   */
  Registries?: Array<number>;
  ResourceControl?: PortainerResourceControl;
  /**
   * StackFileVersion indicates the stack file version, such as yaml, hcl, and manifest
   */
  StackFileVersion?: number;
  /**
   * Stack status (1 - active, 2 - inactive, 3 - deploying, 4 - error)
   */
  Status?: PortainerStackStatus;
  /**
   * If stack support relative path volume
   */
  SupportRelativePath?: boolean;
  /**
   * Cluster identifier of the Swarm cluster where the stack is deployed
   */
  SwarmId?: string;
  /**
   * Stack type. 1 for a Swarm stack, 2 for a Compose stack
   */
  Type?: PortainerStackType;
  /**
   * The date in unix time when stack was last updated
   */
  UpdateDate?: number;
  /**
   * The username which last updated this stack
   */
  UpdatedBy?: string;
  /**
   * A UUID to identify a webhook. The stack will be force updated and pull the latest image when the webhook was invoked.
   */
  Webhook?: string;
  /**
   * WorkflowID is the ID of the Workflow that owns the Source for this stack.
   */
  WorkflowID?: number;
};

export type PortainereeTalosVersions = {
  [key: string]: Array<string>;
};

export type PortainereeTeamMemberships = {
  AdminAutoPopulate?: boolean;
  AdminGroupClaimsRegexList?: Array<string>;
  OAuthClaimMappings?: Array<PortainereeOAuthClaimMappings>;
  OAuthClaimName?: string;
};

export type PortainereeUser = {
  /**
   * User Identifier
   */
  Id: number;
  PortainerAuthorizations?: PortainerAuthorizations;
  /**
   * User role (1 for administrator account and 2 for regular account)
   */
  Role: PortainerUserRole;
  ThemeSettings?: PortainereeUserThemeSettings;
  TokenIssueAt?: number;
  UseCache?: boolean;
  Username: string;
};

export type PortainereeUserActivityLog = {
  action?: string;
  context?: string;
  id?: number;
  payload?: Array<number>;
  timestamp?: number;
  username?: string;
};

export type PortainereeUserThemeSettings = {
  /**
   * Color represents the color theme of the UI
   */
  color?: 'dark' | 'light' | 'highcontrast' | 'auto' | '';
  /**
   * SubtleUpgradeButton indicates if the upgrade banner should be displayed in a subtle way
   */
  subtleUpgradeButton?: boolean;
};

export type RecommendationsRecommendationResponse = {
  actionLabel?: string;
  actionUrl?: string;
  category?: PortainereeRecommendationCategory;
  description?: string;
  severity?: PortainereeRecommendationSeverity;
  title?: string;
  typeId?: string;
};

export type RecommendationsSummaryResponse = {
  categoryCounts?: {
    [key: string]: number;
  };
  severityCounts?: {
    [key: string]: number;
  };
  total?: number;
  totalTypes?: number;
};

export type RegistriesRepositoryPlatform = {
  Architecture?: string;
  Os?: string;
};

export type RegistriesRepositoryTagDetails = {
  Created?: string;
  ImageId?: string;
  Name?: string;
  Platforms?: Array<RegistriesRepositoryPlatform>;
  /**
   * Size is the compressed layer size a pull of this tag would download. It
   * is only populated for single-platform tags: a multi-platform index has
   * no single size, since a pull only ever fetches one platform's layers.
   */
  Size?: number;
};

export type RegistriesDeleteTagsPayload = {
  Tags?: Array<string>;
};

export type RegistriesRegistryConfigurePayload = {
  /**
   * Is authentication against this registry enabled
   */
  Authentication: boolean;
  /**
   * Password used to authenticate against this registry. required when Authentication is true
   */
  Password?: string;
  /**
   * ECR region
   */
  Region?: string;
  /**
   * Use TLS
   */
  TLS?: boolean;
  /**
   * The TLS CA certificate file
   */
  TLSCACertFile?: Array<number>;
  /**
   * The TLS client certificate file
   */
  TLSCertFile?: Array<number>;
  /**
   * The TLS client key file
   */
  TLSKeyFile?: Array<number>;
  /**
   * Skip the verification of the server TLS certificate
   */
  TLSSkipVerify?: boolean;
  /**
   * Username used to authenticate against this registry. Required when Authentication is true
   */
  Username?: string;
};

export type RegistriesRegistryCreatePayload = {
  /**
   * Is authentication against this registry enabled
   */
  Authentication: boolean;
  /**
   * BaseURL required for ProGet registry
   */
  BaseURL?: string;
  /**
   * ECR specific details, required when type = 7
   */
  Ecr?: PortainerEcrData;
  /**
   * Github specific details, required when type = 8
   */
  Github?: PortainerGithubRegistryData;
  /**
   * Gitlab specific details, required when type = 4
   */
  Gitlab?: PortainerGitlabRegistryData;
  /**
   * Name that will be used to identify this registry
   */
  Name: string;
  /**
   * Password used to authenticate against this registry. required when Authentication is true
   */
  Password?: string;
  /**
   * Quay specific details, required when type = 1
   */
  Quay?: PortainerQuayRegistryData;
  /**
   * Use TLS
   */
  TLS?: boolean;
  /**
   * Registry Type. Valid values are:
   * 1 (Quay.io),
   * 2 (Azure container registry),
   * 3 (custom registry),
   * 4 (Gitlab registry),
   * 5 (ProGet registry),
   * 6 (DockerHub)
   * 7 (ECR)
   * 8 (Github registry)
   */
  Type: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  /**
   * URL or IP address of the Docker registry
   */
  URL: string;
  /**
   * Username used to authenticate against this registry. Required when Authentication is true
   */
  Username?: string;
};

export type RegistriesRegistryPingPayload = {
  /**
   * Password used to authenticate against this registry
   */
  Password?: string;
  /**
   * Use TLS
   */
  TLS?: boolean;
  /**
   * Registry Type. Valid values are:
   * 1 (Quay.io),
   * 2 (Azure container registry),
   * 3 (custom registry),
   * 4 (Gitlab registry),
   * 5 (ProGet registry),
   * 6 (DockerHub)
   * 7 (ECR)
   * 8 (Github registry)
   */
  Type: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  /**
   * URL or IP address of the Docker registry
   */
  URL: string;
  /**
   * Username used to authenticate against this registry
   */
  Username?: string;
};

export type RegistriesRegistryPingResponse = {
  /**
   * Message provides details about the connection test result
   */
  message?: string;
  /**
   * Success indicates if the registry connection was successful
   */
  success?: boolean;
};

export type RegistriesRegistryUpdatePayload = {
  Authentication: boolean;
  BaseURL?: string;
  Ecr?: PortainerEcrData;
  Github?: PortainerGithubRegistryData;
  Name: string;
  Password?: string;
  Quay?: PortainerQuayRegistryData;
  RegistryAccesses?: PortainerRegistryAccesses;
  URL: string;
  Username?: string;
};

export type RegistriesRepositoryTagsDeletePayload = {
  tags?: Array<string>;
};

export type ReleaseChart = {
  /**
   * Files are miscellaneous files in a chart archive,
   * e.g. README, LICENSE, etc.
   */
  files?: Array<ReleaseFile>;
  /**
   * Lock is the contents of Chart.lock.
   */
  lock?: ReleaseLock;
  /**
   * Metadata is the contents of the Chartfile.
   */
  metadata?: ReleaseMetadata;
  /**
   * Schema is an optional JSON schema for imposing structure on Values
   */
  schema?: Array<number>;
  /**
   * Templates for this chart.
   */
  templates?: Array<ReleaseFile>;
  /**
   * Values are default config for this chart.
   */
  values?: {
    [key: string]: unknown;
  };
};

export type ReleaseChartReference = {
  chartPath?: string;
  registryID?: number;
  repoURL?: string;
};

export type ReleaseDependency = {
  /**
   * Alias usable alias to be used for the chart
   */
  alias?: string;
  /**
   * A yaml path that resolves to a boolean, used for enabling/disabling charts (e.g. subchart1.enabled )
   */
  condition?: string;
  /**
   * Enabled bool determines if chart should be loaded
   */
  enabled?: boolean;
  /**
   * ImportValues holds the mapping of source values to parent key to be imported. Each item can be a
   * string or pair of child/parent sublist items.
   */
  'import-values'?: Array<unknown>;
  /**
   * Name is the name of the dependency.
   *
   * This must mach the name in the dependency's Chart.yaml.
   */
  name?: string;
  /**
   * The URL to the repository.
   *
   * Appending `index.yaml` to this string should result in a URL that can be
   * used to fetch the repository index.
   */
  repository?: string;
  /**
   * Tags can be used to group charts for enabling/disabling together
   */
  tags?: Array<string>;
  /**
   * Version is the version (range) of this chart.
   *
   * A lock file will always produce a single version, while a dependency
   * may contain a semantic version range.
   */
  version?: string;
};

export type ReleaseFile = {
  /**
   * Data is the template as byte data.
   */
  data?: Array<number>;
  /**
   * Name is the path-like name of the template.
   */
  name?: string;
};

export type ReleaseHookExecution = {
  /**
   * CompletedAt indicates the date/time this hook was completed.
   */
  completed_at?: string;
  /**
   * Phase indicates whether the hook completed successfully
   */
  phase?: string;
  /**
   * StartedAt indicates the date/time this hook was started
   */
  started_at?: string;
};

export type ReleaseInfo = {
  /**
   * Deleted tracks when this object was deleted.
   */
  deleted?: string;
  /**
   * Description is human-friendly "log entry" about this release.
   */
  description?: string;
  /**
   * FirstDeployed is when the release was first deployed.
   */
  first_deployed?: string;
  /**
   * LastDeployed is when the release was last deployed.
   */
  last_deployed?: string;
  /**
   * Contains the rendered templates/NOTES.txt if available
   */
  notes?: string;
  /**
   * Resources is the list of resources that are part of the release
   */
  resources?: Array<UnstructuredUnstructured>;
  /**
   * Status is the current state of the release
   */
  status?: string;
};

export type ReleaseLock = {
  /**
   * Dependencies is the list of dependencies that this lock file has locked.
   */
  dependencies?: Array<ReleaseDependency>;
  /**
   * Digest is a hash of the dependencies in Chart.yaml.
   */
  digest?: string;
  /**
   * Generated is the date the lock file was last generated.
   */
  generated?: string;
};

export type ReleaseMaintainer = {
  /**
   * Email is an optional email address to contact the named maintainer
   */
  email?: string;
  /**
   * Name is a user name or organization name
   */
  name?: string;
  /**
   * URL is an optional URL to an address for the named maintainer
   */
  url?: string;
};

export type ReleaseMetadata = {
  /**
   * Annotations are additional mappings uninterpreted by Helm,
   * made available for inspection by other applications.
   */
  annotations?: {
    [key: string]: string;
  };
  /**
   * The API Version of this chart. Required.
   */
  apiVersion?: string;
  /**
   * The version of the application enclosed inside of this chart.
   */
  appVersion?: string;
  /**
   * The condition to check to enable chart
   */
  condition?: string;
  /**
   * Dependencies are a list of dependencies for a chart.
   */
  dependencies?: Array<ReleaseDependency>;
  /**
   * Whether or not this chart is deprecated
   */
  deprecated?: boolean;
  /**
   * A one-sentence description of the chart
   */
  description?: string;
  /**
   * The URL to a relevant project page, git repo, or contact person
   */
  home?: string;
  /**
   * The URL to an icon file.
   */
  icon?: string;
  /**
   * A list of string keywords
   */
  keywords?: Array<string>;
  /**
   * KubeVersion is a SemVer constraint specifying the version of Kubernetes required.
   */
  kubeVersion?: string;
  /**
   * A list of name and URL/email address combinations for the maintainer(s)
   */
  maintainers?: Array<ReleaseMaintainer>;
  /**
   * The name of the chart. Required.
   */
  name?: string;
  /**
   * Source is the URL to the source code of this chart
   */
  sources?: Array<string>;
  /**
   * The tags to check to enable chart
   */
  tags?: string;
  /**
   * Specifies the chart type: application or library
   */
  type?: string;
  /**
   * A SemVer 2 conformant version string of the chart. Required.
   */
  version?: string;
};

export type ReleaseRelease = {
  /**
   * AppVersion is the app version of the release.
   */
  appVersion?: string;
  /**
   * Chart is the chart that was released.
   */
  chart?: ReleaseChart;
  /**
   * ChartReference are the labels that are used to identify the chart source.
   */
  chartReference?: ReleaseChartReference;
  /**
   * Config is the set of extra Values added to the chart.
   * These values override the default values inside of the chart.
   */
  config?: {
    [key: string]: unknown;
  };
  /**
   * Hooks are all of the hooks declared for this release.
   */
  hooks?: Array<GithubComPortainerPortainerPkgLibhelmReleaseHook>;
  /**
   * Info provides information about a release
   */
  info?: ReleaseInfo;
  /**
   * Manifest is the string representation of the rendered template.
   */
  manifest?: string;
  /**
   * Name is the name of the release
   */
  name?: string;
  /**
   * Namespace is the kubernetes namespace of the release.
   */
  namespace?: string;
  /**
   * StackID is the ID of the Portainer stack associated with this release (if using GitOps)
   */
  stackID?: number;
  /**
   * Values are the values used to deploy the chart.
   */
  values?: ReleaseValues;
  /**
   * Version is an int which represents the revision of the release.
   */
  version?: number;
};

export type ReleaseReleaseElement = {
  appVersion?: string;
  chart?: string;
  name?: string;
  namespace?: string;
  revision?: string;
  status?: string;
  updated?: string;
};

export type ReleaseValues = {
  computedValues?: string;
  userSuppliedValues?: string;
};

export type ResourceQuantity = {
  Format?: 'DecimalExponent' | 'BinarySI' | 'DecimalSI';
};

export type ResourcecontrolsResourceControlCreatePayload = {
  /**
   * Permit access to resource only to admins
   */
  AdministratorsOnly?: boolean;
  /**
   * Permit access to the associated resource to any user
   */
  Public?: boolean;
  ResourceID: string;
  /**
   * List of Docker resources that will inherit this access control
   */
  SubResourceIDs?: Array<string>;
  /**
   * List of team identifiers with access to the associated resource
   */
  Teams?: Array<number>;
  /**
   * Type of Resource. Valid values are: 1 - container, 2 - service
   * 3 - volume, 4 - network, 5 - secret, 6 - stack, 7 - config, 8 - custom template, 9 - azure-container-group
   */
  Type: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
  /**
   * List of user identifiers with access to the associated resource
   */
  Users?: Array<number>;
};

export type ResourcecontrolsResourceControlUpdatePayload = {
  /**
   * Permit access to resource only to admins
   */
  AdministratorsOnly?: boolean;
  /**
   * Permit access to the associated resource to any user
   */
  Public?: boolean;
  /**
   * List of team identifiers with access to the associated resource
   */
  Teams?: Array<number>;
  /**
   * List of user identifiers with access to the associated resource
   */
  Users?: Array<number>;
};

export type RoarRoarPortainerEndpointId = {
  [key: string]: unknown;
};

export type SettingsAutoPatchResponse = {
  Enabled?: boolean;
  PatchCron?: string;
};

export type SettingsAutoPatchSettingsPayload = {
  Enabled?: boolean;
  PatchCron?: string;
  PortainerRepository?: string;
  RegistryID?: number;
  UpdaterRepository?: string;
};

export type SettingsDefaultRegistryUpdatePayload = {
  Hide?: boolean;
};

export type SettingsMTlsPayload = {
  CaCert?: string;
  Cert?: string;
  Key?: string;
  UseSeparateCert?: boolean;
};

export type SettingsPublicSettingsResponse = {
  /**
   * Active authentication method for the Portainer instance. Valid values are: 1 for internal, 2 for LDAP, or 3 for oauth
   */
  AuthenticationMethod?: PortainerAuthenticationMethod;
  AutoPatchSettings?: SettingsAutoPatchResponse;
  /**
   * The content in plaintext used to display in the login page. Will hide when value is empty string
   */
  CustomLoginBanner?: string;
  DefaultRegistry?: {
    Hide?: boolean;
  };
  /**
   * DisableKubeShell will disable the kube shell feature for non-admin users
   */
  DisableKubeShell?: boolean;
  /**
   * DisableKubeconfigDownload will disable the download of kubeconfig files for non-admin users
   */
  DisableKubeconfigDownload?: boolean;
  Edge?: {
    /**
     * The check in interval for edge agent (in seconds) - used in non async mode [seconds]
     */
    CheckinInterval?: number;
    /**
     * The command list interval for edge agent - used in edge async mode [seconds]
     */
    CommandInterval?: number;
    MTLS?: PortainereeMtlsSettings;
    /**
     * The ping interval for edge agent - used in edge async mode [seconds]
     */
    PingInterval?: number;
    /**
     * The snapshot interval for edge agent - used in edge async mode [seconds]
     */
    SnapshotInterval?: number;
  };
  /**
   * Whether edge compute features are enabled
   */
  EnableEdgeComputeFeatures?: boolean;
  /**
   * Whether FIPS mode is enabled
   */
  FIPSMode?: boolean;
  /**
   * Supported feature flags
   */
  Features?: {
    [key: string]: boolean;
  };
  /**
   * Deployment options for encouraging deployment as code
   */
  GlobalDeploymentOptions?: PortainereeGlobalDeploymentOptions;
  /**
   * The expiry of a Kubeconfig
   */
  KubeconfigExpiry?: string;
  /**
   * URL to a logo that will be displayed on the login page as well as on top of the sidebar. Will use default Portainer logo when value is empty string
   */
  LogoURL?: string;
  /**
   * Whether portainer internal auth view will be hidden
   */
  OAuthHideInternalAuth?: boolean;
  /**
   * The URL used for oauth login
   */
  OAuthLoginURI?: string;
  /**
   * The URL used for oauth logout
   */
  OAuthLogoutURI?: string;
  /**
   * The minimum required length for a password of any user when using internal auth mode
   */
  RequiredPasswordLength?: number;
  /**
   * Whether the setup wizard must send the X-Setup-Token header for admin init / restore
   */
  RequiresSetupToken?: boolean;
  /**
   * Whether team sync is enabled
   */
  TeamSync?: boolean;
  /**
   * The display name of the app
   */
  Whitelabel?: string;
};

export type SettingsSettingsAdditionalFunctionalityInspectResponse = {
  additionalFunctionality?: PortainereeAdditionalFunctionality;
};

export type SettingsSettingsAdditionalFunctionalityUpdatePayload = {
  Policies: boolean;
};

export type SettingsSettingsCaCertResponse = {
  /**
   * MTLSCACertificate is the X.509 Certificate of the MTLS CA Certificate
   */
  MTLSCACertificate?: SslCertificate;
};

export type SettingsSettingsCertResponse = {
  /**
   * MTLSCertificate is the X.509 Certificate of the MTLS Certificate
   */
  MTLSCertificate?: SslCertificate;
};

export type SettingsSettingsExperimentalInspectResponse = {
  experimentalFeatures?: PortainereeExperimentalFeatures;
};

export type SettingsSettingsExperimentalUpdatePayload = {
  fleetManagement?: boolean;
};

export type SettingsSettingsUpdatePayload = {
  /**
   * URL to the add-on catalog. Empty string restores the built-in default.
   */
  AddonsCatalogURL?: string;
  /**
   * Active authentication method for the Portainer instance. Valid values are: 1 for internal, 2 for LDAP, or 3 for oauth
   */
  AuthenticationMethod?: number;
  /**
   * Whether to enable automatic patches
   */
  AutoPatchSettings?: SettingsAutoPatchSettingsPayload;
  /**
   * A list of label name & value that will be used to hide containers when querying containers
   */
  BlackListedLabels?: Array<PortainerPair>;
  /**
   * The content in plaintext used to display in the login page. Will hide when value is empty string
   */
  CustomLoginBanner?: string;
  /**
   * Whether to disable kube roles sync for all built-in roles
   */
  DisableKubeRolesSync?: boolean;
  /**
   * Whether to disable kubeshell access for non-admin users
   */
  DisableKubeShell?: boolean;
  /**
   * Whether to disable kubeconfig download for non-admin users
   */
  DisableKubeconfigDownload?: boolean;
  Edge?: {
    /**
     * AsyncMode enables edge agent to run in async mode by default
     */
    AsyncMode?: boolean;
    /**
     * The command list interval for edge agent - used in edge async mode (in seconds)
     */
    CommandInterval?: number;
    MTLS?: SettingsMTlsPayload;
    /**
     * The ping interval for edge agent - used in edge async mode (in seconds)
     */
    PingInterval?: number;
    /**
     * The snapshot interval for edge agent - used in edge async mode (in seconds)
     */
    SnapshotInterval?: number;
    /**
     * The address where the tunneling server can be reached by Edge agents
     */
    TunnelServerAddress?: string;
  };
  /**
   * The default check in interval for edge agent (in seconds)
   */
  EdgeAgentCheckinInterval?: number;
  /**
   * EdgePortainerURL is the URL that is exposed to edge agents
   */
  EdgePortainerURL?: string;
  /**
   * Whether edge compute features are enabled
   */
  EnableEdgeComputeFeatures?: boolean;
  /**
   * EnforceEdgeID makes Portainer store the Edge ID instead of accepting anyone
   */
  EnforceEdgeID?: boolean;
  /**
   * ForceSecureCookies forces the Secure attribute on auth cookies regardless of the detected scheme
   */
  ForceSecureCookies?: boolean;
  /**
   * Deployment options for encouraging deployment as code
   */
  GlobalDeploymentOptions?: PortainereeGlobalDeploymentOptions;
  /**
   * Helm repository URL
   */
  HelmRepositoryURL?: string;
  InternalAuthSettings?: PortainerInternalAuthSettings;
  /**
   * The expiry of a Kubeconfig
   */
  KubeconfigExpiry?: string;
  /**
   * Kubectl Shell Image Name/Tag
   */
  KubectlShellImage?: string;
  LDAPSettings?: PortainereeLdapSettings;
  /**
   * URL to a logo that will be displayed on the login page as well as on top of the sidebar. Will use default Portainer logo when value is empty string
   */
  LogoURL?: string;
  OAuthSettings?: PortainereeOAuthSettings;
  /**
   * The interval in which environment(endpoint) snapshots are created
   */
  SnapshotInterval?: string;
  /**
   * URL to the templates that will be displayed in the UI when navigating to App Templates
   */
  TemplatesURL?: string;
  /**
   * TrustOnFirstConnect makes Portainer accepting edge agent connection by default
   */
  TrustOnFirstConnect?: boolean;
  /**
   * The duration of a user session
   */
  UserSessionTimeout?: string;
};

export const SourcesAuthType = {
  /**
   * AuthTypeBasic
   */
  AUTH_TYPE_BASIC: 'basic' /**
   * AuthTypeToken
   */,
  AUTH_TYPE_TOKEN: 'token',
} as const;

export type SourcesAuthType =
  (typeof SourcesAuthType)[keyof typeof SourcesAuthType];

export type SourcesConnectionTestResult = {
  error?: string;
  success?: boolean;
};

export type SourcesGitAuthenticationPayload = {
  password?: string;
  provider?: SourcesProvider;
  sharedCredentialId?: number;
  type?: SourcesAuthType;
  username?: string;
};

export type SourcesGitAuthenticationUpdatePayload = {
  password?: string;
  provider?: SourcesProvider;
  sharedCredentialId?: number;
  type?: SourcesAuthType;
  username?: string;
};

export type SourcesGitSourceCreatePayload = {
  administratorsOnly?: boolean;
  authentication?: SourcesGitAuthenticationPayload;
  interval?: string;
  name?: string;
  public?: boolean;
  teamAccesses?: Array<number>;
  tlsSkipVerify?: boolean;
  url: string;
  userAccesses?: Array<number>;
};

export type SourcesGitSourceUpdatePayload = {
  authentication?: SourcesGitAuthenticationUpdatePayload;
  interval?: string;
  name?: string;
  tlsSkipVerify?: boolean;
  url?: string;
};

export const SourcesProvider = {
  /**
   * ProviderCustom
   */
  PROVIDER_CUSTOM: 'custom',
  /**
   * ProviderGitHub
   */
  PROVIDER_GIT_HUB: 'github',
  /**
   * ProviderGitLab
   */
  PROVIDER_GIT_LAB: 'gitlab',
  /**
   * ProviderBitbucketCloud
   */
  PROVIDER_BITBUCKET_CLOUD: 'bitbucket-cloud',
  /**
   * ProviderBitbucketDataCenter
   */
  PROVIDER_BITBUCKET_DATA_CENTER: 'bitbucket-datacenter',
  /**
   * ProviderAzureDevOps
   */
  PROVIDER_AZURE_DEV_OPS: 'azure-devops',
  /**
   * ProviderGiteaForgejo
   */
  PROVIDER_GITEA_FORGEJO: 'gitea-forgejo',
} as const;

export type SourcesProvider =
  (typeof SourcesProvider)[keyof typeof SourcesProvider];

export type SourcesSource = {
  environments?: number;
  error?: string;
  id: number;
  interval?: string;
  lastSync?: number;
  name: string;
  provider?: SourcesProvider;
  status: GithubComPortainerPortainerApiGitopsWorkflowsStatus;
  type: SourcesSourceType;
  url: string;
  usedBy?: number;
};

export type SourcesSourceAccess = {
  public?: boolean;
  teams?: Array<number>;
  users?: Array<number>;
};

export type SourcesSourceAccessUpdatePayload = {
  public?: boolean;
  teams?: Array<number>;
  users?: Array<number>;
};

export type SourcesSourceDetail = {
  access?: SourcesSourceAccess;
  connection: SourcesConnectionInfo;
  error?: string;
  id: number;
  interval?: string;
  lastSync?: number;
  name: string;
  provider?: SourcesProvider;
  status: GithubComPortainerPortainerApiGitopsWorkflowsStatus;
  type: SourcesSourceType;
  url: string;
};

export const SourcesSourceType = {
  /**
   * SourceTypeGit
   */
  SOURCE_TYPE_GIT: 'git',
  /**
   * SourceTypeHelm
   */
  SOURCE_TYPE_HELM: 'helm',
  /**
   * SourceTypeOCI
   */
  SOURCE_TYPE_OCI: 'oci',
} as const;

export type SourcesSourceType =
  (typeof SourcesSourceType)[keyof typeof SourcesSourceType];

export const SourcesStatus = {
  /**
   * SourceStatusUnknown
   */
  SOURCE_STATUS_UNKNOWN: 'unknown',
  /**
   * SourceStatusHealthy
   */
  SOURCE_STATUS_HEALTHY: 'healthy',
  /**
   * SourceStatusError
   */
  SOURCE_STATUS_ERROR: 'error',
} as const;

export type SourcesStatus = (typeof SourcesStatus)[keyof typeof SourcesStatus];

export type SourcesWorkflow = {
  creationDate?: number;
  gitConfig?: GithubComPortainerPortainerApiGitTypesRepoConfig;
  id: number;
  lastSyncDate?: number;
  name: string;
  platform: WorkflowsDeploymentPlatform;
  sourceId?: number;
  status: WorkflowsWorkflowStatusObject;
  target: WorkflowsTarget;
  type: WorkflowsType;
};

export type SourcesConnectionInfo = {
  authentication?: SourcesGitAuthInfo;
  tlsSkipVerify?: boolean;
};

export type SourcesGitAuthInfo = {
  sharedCredentialId?: number;
  type?: SourcesAuthType;
  username?: string;
};

export type SslCertificate = {
  AuthorityKeyId?: string;
  CRLDistributionPoints?: Array<string>;
  ExtendedKeyUsages?: Array<string>;
  IsCertificateAuthority?: boolean;
  Issuer?: SslName;
  IssuingCertificateURLs?: Array<string>;
  KeyUsages?: Array<string>;
  OCSPServers?: Array<string>;
  Policies?: Array<string>;
  PublicKey?: SslPublicKey;
  SHA256Fingerprint?: string;
  SerialNumber?: string;
  SignatureAlgorithm?: string;
  Subject?: SslName;
  SubjectAltDNSNames?: Array<string>;
  SubjectAltEmailAddresses?: Array<string>;
  SubjectAltIpAddresses?: Array<string>;
  SubjectAltURIs?: Array<string>;
  SubjectKeyId?: string;
  ValidNotAfter?: string;
  ValidNotBefore?: string;
  Version?: number;
};

export type SslName = {
  CommonName?: string;
  Country?: Array<string>;
  Locality?: Array<string>;
  Organization?: Array<string>;
  OrganizationalUnit?: Array<string>;
  PostalCode?: Array<string>;
  Province?: Array<string>;
  SerialNumber?: string;
  StreetAddress?: Array<string>;
};

export type SslPublicKey = {
  Algorithm?: string;
  Exponent?: string;
  Modulus?: string;
  Size?: number;
  Value?: string;
};

export type SslSslUpdatePayload = {
  /**
   * SSL Certificates
   */
  Cert?: string;
  HTTPEnabled?: boolean;
  Key?: string;
  /**
   * SSL Client Certificates
   */
  clientCert?: string;
};

export type StacksComposeStackFromFileContentPayload = {
  /**
   * A list of environment variables used during stack deployment
   */
  Env?: Array<PortainerPair>;
  /**
   * Whether the stack is from a app template
   */
  FromAppTemplate?: boolean;
  /**
   * Name of the stack
   */
  Name: string;
  /**
   * List of Registries to use for this stack
   */
  Registries?: Array<number>;
  /**
   * Content of the Stack file
   */
  StackFileContent: string;
  /**
   * A UUID to identify a webhook. The stack will be force updated and pull the latest image when the webhook was invoked.
   */
  Webhook?: string;
};

export type StacksComposeStackFromGitRepositoryPayload = {
  /**
   * Applicable when deploying with multiple stack files
   */
  AdditionalFiles?: Array<string>;
  /**
   * Optional GitOps update configuration
   */
  AutoUpdate?: PortainerAutoUpdateSettings;
  /**
   * Path to the Stack file inside the Git repository
   */
  ComposeFile?: string;
  /**
   * A list of environment variables used during stack deployment
   */
  Env?: Array<PortainerPair>;
  /**
   * Local filesystem path
   */
  FilesystemPath?: string;
  /**
   * Whether the stack is from a app template
   */
  FromAppTemplate?: boolean;
  /**
   * Name of the stack
   */
  Name: string;
  /**
   * List of Registries to use for this stack
   */
  Registries?: Array<number>;
  /**
   * Deprecated: use SourceID instead. Use basic authentication to clone the Git repository.
   */
  RepositoryAuthentication?: boolean;
  /**
   * Deprecated: use SourceID instead. RepositoryAuthorizationType is the authorization type to use.
   */
  RepositoryAuthorizationType?: GithubComPortainerPortainerEeApiGitTypesGitCredentialAuthType;
  /**
   * Deprecated: use SourceID instead.
   * Password used in basic authentication. Required when RepositoryAuthentication is true
   */
  RepositoryPassword?: string;
  /**
   * Deprecated: use SourceID instead. RepositoryProvider is the provider to use.
   */
  RepositoryProvider?: GithubComPortainerPortainerEeApiGitTypesGitProvider;
  /**
   * Reference name of a Git repository hosting the Stack file
   */
  RepositoryReferenceName?: string;
  /**
   * Deprecated: use SourceID instead. URL of a Git repository hosting the Stack file.
   */
  RepositoryURL?: string;
  /**
   * Deprecated: use SourceID instead.
   * Username used in basic authentication. Required when RepositoryAuthentication is true
   */
  RepositoryUsername?: string;
  /**
   * SourceID references an existing Source for git credentials/URL.
   * When set, the inline URL and authentication fields are ignored.
   */
  SourceID?: number;
  /**
   * Whether the stack supports relative path volume
   */
  SupportRelativePath?: boolean;
  /**
   * Deprecated: use SourceID instead. TLSSkipVerify skips SSL verification when cloning the Git repository.
   */
  TLSSkipVerify?: boolean;
};

export type StacksCreateKubernetesStackResponse = {
  Output?: string;
};

export type StacksKubernetesGitDeploymentPayload = {
  AdditionalFiles?: Array<string>;
  AutoUpdate?: PortainerAutoUpdateSettings;
  ComposeFormat?: boolean;
  /**
   * Helm-specific fields
   */
  HelmChartPath?: string;
  /**
   * Array of paths to values YAML files in Git repo
   */
  HelmValuesFiles?: Array<string>;
  ManifestFile?: string;
  Namespace?: string;
  /**
   * Deprecated: use SourceID instead. Use authentication to clone the Git repository.
   */
  RepositoryAuthentication?: boolean;
  /**
   * Deprecated: use SourceID instead. Authorization type used to clone the Git repository.
   */
  RepositoryAuthorizationType?: GithubComPortainerPortainerEeApiGitTypesGitCredentialAuthType;
  /**
   * Deprecated: use SourceID instead. Password used in authentication.
   */
  RepositoryPassword?: string;
  /**
   * Deprecated: use SourceID instead. Provider used to clone the Git repository.
   */
  RepositoryProvider?: GithubComPortainerPortainerEeApiGitTypesGitProvider;
  /**
   * Deprecated: use SourceID instead. Reference name of a Git repository hosting the Stack file.
   */
  RepositoryReferenceName?: string;
  /**
   * Deprecated: use SourceID instead. URL of a Git repository hosting the Stack file.
   */
  RepositoryURL?: string;
  /**
   * Deprecated: use SourceID instead. Username used in authentication.
   */
  RepositoryUsername?: string;
  /**
   * SourceID references an existing Source for git credentials/URL.
   * When set, the inline URL and authentication fields are ignored.
   */
  SourceID?: number;
  StackName?: string;
  /**
   * Deprecated: use SourceID instead. TLSSkipVerify skips SSL verification when cloning the Git repository.
   */
  TLSSkipVerify?: boolean;
};

export type StacksKubernetesManifestUrlDeploymentPayload = {
  ComposeFormat?: boolean;
  ManifestURL?: string;
  Namespace?: string;
  StackName?: string;
};

export type StacksKubernetesStringDeploymentPayload = {
  ComposeFormat?: boolean;
  /**
   * Whether the stack is from a app template
   */
  FromAppTemplate?: boolean;
  Namespace?: string;
  StackFileContent?: string;
  StackName?: string;
};

export type StacksStackConvertPayload = {
  /**
   * Namespace for the Kubernetes stack
   */
  namespace?: string;
  /**
   * Target format: "kubernetes" for manifests or "helm" for helm chart
   */
  targetFormat: string;
};

export type StacksStackConvertResponse = {
  /**
   * Converted files for preview
   */
  files?: {
    [key: string]: string;
  };
};

export type StacksStackFileResponse = {
  /**
   * Content of the Stack file
   */
  StackFileContent?: string;
};

export type StacksStackGitRedeployPayload = {
  Env?: Array<PortainerPair>;
  Prune?: boolean;
  /**
   * Deprecated(2.36): use RepullImageAndRedeploy instead for cleaner responsibility
   * Force a pulling to current image with the original tag though the image is already the latest
   */
  PullImage?: boolean;
  /**
   * When true and RepositoryPassword is non-empty, stored credentials are replaced.
   */
  RepositoryAuthentication?: boolean;
  RepositoryAuthorizationType?: GithubComPortainerPortainerEeApiGitTypesGitCredentialAuthType;
  /**
   * Non-empty value (with RepositoryAuthentication=true) replaces stored credentials; leave blank to keep them.
   */
  RepositoryPassword?: string;
  RepositoryProvider?: GithubComPortainerPortainerEeApiGitTypesGitProvider;
  RepositoryReferenceName?: string;
  RepositoryUsername?: string;
  /**
   * RepullImageAndRedeploy indicates whether to force repulling images and redeploying the stack
   */
  RepullImageAndRedeploy?: boolean;
  StackName?: string;
};

export type StacksStackGitUpdatePayload = {
  AdditionalFiles?: Array<string>;
  /**
   * Enable atomic rollback on failure (Helm --atomic flag)
   */
  Atomic?: boolean;
  AutoUpdate?: PortainerAutoUpdateSettings;
  ConfigFilePath?: string;
  Env?: Array<PortainerPair>;
  /**
   * Helm chart folder path in Git repo (for Helm stacks)
   */
  HelmChartPath?: string;
  /**
   * Helm values files paths in Git repo (for Helm stacks)
   */
  HelmValuesFiles?: Array<string>;
  Prune?: boolean;
  Registries?: Array<number>;
  /**
   * Deprecated: use SourceID instead. Use basic authentication to clone the Git repository.
   */
  RepositoryAuthentication?: boolean;
  /**
   * Deprecated: use SourceID instead. Authorization type for git authentication.
   */
  RepositoryAuthorizationType?: GithubComPortainerPortainerEeApiGitTypesGitCredentialAuthType;
  /**
   * Deprecated: use SourceID instead. Password used in basic authentication.
   */
  RepositoryPassword?: string;
  /**
   * Deprecated: use SourceID instead. Git provider for OAuth-based authentication.
   */
  RepositoryProvider?: GithubComPortainerPortainerEeApiGitTypesGitProvider;
  RepositoryReferenceName?: string;
  /**
   * Deprecated: use SourceID instead. URL of a Git repository hosting the Stack file.
   */
  RepositoryURL?: string;
  /**
   * Deprecated: use SourceID instead. Username used in basic authentication.
   */
  RepositoryUsername?: string;
  /**
   * SourceID references an existing Source for git credentials/URL.
   * When set, the inline URL and authentication fields are ignored.
   */
  SourceID?: number;
  TLSSkipVerify?: boolean;
};

export type StacksStackMigratePayload = {
  EndpointID: number;
  IsHelm?: boolean;
  Name?: string;
  Namespace?: string;
  SwarmID?: string;
};

export type StacksStackResponse = {
  /**
   * Only applies when deploying stack with multiple files
   */
  AdditionalFiles?: Array<string>;
  /**
   * The GitOps update settings of a git stack
   */
  AutoUpdate?: PortainerAutoUpdateSettings;
  /**
   * The username which created this stack
   */
  CreatedBy?: string;
  /**
   * The username id which created this stack
   */
  CreatedByUserId?: string;
  /**
   * The date in unix time when stack was created
   */
  CreationDate?: number;
  /**
   * CurrentDeploymentInfo records the git repository state at the time of the last actual deployment.
   */
  CurrentDeploymentInfo?: PortainerStackDeploymentInfo;
  /**
   * DeploymentStartStatus is the stack status captured when the current
   * deployment starts. It is used by deployment logic during the current
   * deployment attempt and is cleared/replaced when a new deployment begins.
   */
  DeploymentStartStatus?: PortainerStackStatus;
  /**
   * DeploymentStatus records the status progression of the current deployment.
   * Cleared when a new deployment starts.
   */
  DeploymentStatus?: Array<PortainerStackDeploymentStatus>;
  /**
   * Environment(Endpoint) identifier. Reference the environment(endpoint) that will be used for deployment
   */
  EndpointId?: number;
  /**
   * EntryPoint is the path to the config file relative to the project root.
   * NOTE: For git stacks this mirrors GitConfig.ConfigFilePath and the two are kept in sync
   * by stackUpdateGit. The deploy command builder (compose_unpacker_cmd_builder) uses this
   * field directly; Kubernetes deploy and git clone operations use GitConfig.ConfigFilePath.
   */
  EntryPoint?: string;
  /**
   * A list of environment(endpoint) variables used during stack deployment
   */
  Env?: Array<PortainerPair>;
  /**
   * Network(Swarm) or local(Standalone) filesystem path
   */
  FilesystemPath?: string;
  /**
   * Whether the stack is from a app template
   */
  FromAppTemplate?: boolean;
  /**
   * GitConfig is the git repository configuration for git-backed stacks.
   * Deprecated: loaded from Source via WorkflowID; kept for DB backwards-compatibility only.
   * Non-migration code must not read or write this field; use Source records instead.
   */
  GitConfig?: GithubComPortainerPortainerEeApiGitTypesRepoConfig;
  GitSourceId?: number;
  /**
   * Path to a Helm chart folder for Helm git deployments
   */
  HelmChartPath?: string;
  /**
   * Array of paths to Helm values YAML files for Helm git deployments
   */
  HelmValuesFiles?: Array<string>;
  /**
   * Stack Identifier
   */
  Id?: number;
  /**
   * Whether the stack is detached from git
   */
  IsDetachedFromGit?: boolean;
  /**
   * Stack name
   */
  Name?: string;
  /**
   * Kubernetes namespace if stack is a kube application
   */
  Namespace?: string;
  /**
   * The stack deployment option
   */
  Option?: PortainerStackOption;
  /**
   * The previous deployment info of the stack
   */
  PreviousDeploymentInfo?: PortainerStackDeploymentInfo;
  /**
   * Path on disk to the repository hosting the Stack file
   */
  ProjectPath?: string;
  /**
   * List of Registries to use for this stack
   */
  Registries?: Array<number>;
  ResourceControl?: PortainerResourceControl;
  /**
   * StackFileVersion indicates the stack file version, such as yaml, hcl, and manifest
   */
  StackFileVersion?: number;
  /**
   * Stack status (1 - active, 2 - inactive, 3 - deploying, 4 - error)
   */
  Status?: PortainerStackStatus;
  /**
   * If stack support relative path volume
   */
  SupportRelativePath?: boolean;
  /**
   * Cluster identifier of the Swarm cluster where the stack is deployed
   */
  SwarmId?: string;
  /**
   * Stack type. 1 for a Swarm stack, 2 for a Compose stack
   */
  Type?: PortainerStackType;
  /**
   * The date in unix time when stack was last updated
   */
  UpdateDate?: number;
  /**
   * The username which last updated this stack
   */
  UpdatedBy?: string;
  /**
   * A UUID to identify a webhook. The stack will be force updated and pull the latest image when the webhook was invoked.
   */
  Webhook?: string;
  /**
   * WorkflowID is the ID of the Workflow that owns the Source for this stack.
   */
  WorkflowID?: number;
};

export type StacksSwarmStackFromFileContentPayload = {
  /**
   * A list of environment variables used during stack deployment
   */
  Env?: Array<PortainerPair>;
  /**
   * Whether the stack is from a app template
   */
  FromAppTemplate?: boolean;
  /**
   * Name of the stack
   */
  Name: string;
  /**
   * List of Registries to use for this stack
   */
  Registries?: Array<number>;
  /**
   * Content of the Stack file
   */
  StackFileContent: string;
  /**
   * Swarm cluster identifier
   */
  SwarmID: string;
  /**
   * A UUID to identify a webhook. The stack will be force updated and pull the latest image when the webhook was invoked.
   */
  Webhook?: string;
};

export type StacksSwarmStackFromGitRepositoryPayload = {
  /**
   * Applicable when deploying with multiple stack files
   */
  AdditionalFiles?: Array<string>;
  /**
   * Optional GitOps update configuration
   */
  AutoUpdate?: PortainerAutoUpdateSettings;
  /**
   * Path to the Stack file inside the Git repository
   */
  ComposeFile?: string;
  /**
   * A list of environment variables used during stack deployment
   */
  Env?: Array<PortainerPair>;
  /**
   * Network filesystem path
   */
  FilesystemPath?: string;
  /**
   * Whether the stack is from a app template
   */
  FromAppTemplate?: boolean;
  /**
   * Name of the stack
   */
  Name: string;
  /**
   * List of Registries to use for this stack
   */
  Registries?: Array<number>;
  /**
   * Deprecated: use SourceID instead. Use basic authentication to clone the Git repository.
   */
  RepositoryAuthentication?: boolean;
  /**
   * Deprecated: use SourceID instead. RepositoryAuthorizationType is the authorization type to use.
   */
  RepositoryAuthorizationType?: GithubComPortainerPortainerEeApiGitTypesGitCredentialAuthType;
  /**
   * Deprecated: use SourceID instead.
   * Password used in basic authentication. Required when RepositoryAuthentication is true
   */
  RepositoryPassword?: string;
  /**
   * Deprecated: use SourceID instead. RepositoryProvider is the provider to use.
   */
  RepositoryProvider?: GithubComPortainerPortainerEeApiGitTypesGitProvider;
  /**
   * Reference name of a Git repository hosting the Stack file
   */
  RepositoryReferenceName?: string;
  /**
   * Deprecated: use SourceID instead. URL of a Git repository hosting the Stack file.
   */
  RepositoryURL?: string;
  /**
   * Deprecated: use SourceID instead.
   * Username used in basic authentication. Required when RepositoryAuthentication is true
   */
  RepositoryUsername?: string;
  /**
   * SourceID references an existing Source for git credentials/URL.
   * When set, the inline URL and authentication fields are ignored.
   */
  SourceID?: number;
  /**
   * Whether the stack suppors relative path volume
   */
  SupportRelativePath?: boolean;
  /**
   * Swarm cluster identifier
   */
  SwarmID: string;
  /**
   * Deprecated: use SourceID instead. TLSSkipVerify skips SSL verification when cloning the Git repository.
   */
  TLSSkipVerify?: boolean;
};

export type StacksUpdateStackPayload = {
  /**
   * A list of environment(endpoint) variables used during stack deployment
   */
  Env?: Array<PortainerPair>;
  /**
   * Prune services that are no longer referenced (only available for Swarm stacks)
   */
  Prune?: boolean;
  /**
   * Deprecated(2.36): use RepullImageAndRedeploy instead for cleaner responsibility
   * Force a pulling to current image with the original tag though the image is already the latest
   */
  PullImage?: boolean;
  /**
   * List of Registries to use for this stack
   */
  Registries?: Array<number>;
  /**
   * RepullImageAndRedeploy indicates whether to force repulling images and redeploying the stack
   */
  RepullImageAndRedeploy?: boolean;
  /**
   * RollbackTo specifies the stack file version to rollback to (only support to rollback to the last version currently)
   */
  RollbackTo?: number;
  /**
   * New content of the Stack file
   */
  StackFileContent?: string;
  /**
   * A UUID to identify a webhook. The stack will be force updated and pull the latest image when the webhook was invoked.
   */
  Webhook?: string;
};

export type StacksWebhookInvokeResponse = {
  message?: string;
  stackId?: number;
};

export type StatsContainerStats = {
  healthy?: number;
  running?: number;
  stopped?: number;
  total?: number;
  unhealthy?: number;
};

export type SupportDebugLogPayload = {
  debugLogEnabled?: boolean;
};

export type SwarmServiceUpdateResponse = {
  /**
   * Optional warning messages
   */
  Warnings?: Array<string>;
};

export type TagsTagCreatePayload = {
  Name: string;
};

export type TeammembershipsTeamMembershipCreatePayload = {
  /**
   * Role for the user inside the team (1 for leader and 2 for regular member)
   */
  Role: 1 | 2;
  /**
   * Team identifier
   */
  TeamID: number;
  /**
   * User identifier
   */
  UserID: number;
};

export type TeammembershipsTeamMembershipUpdatePayload = {
  /**
   * Role for the user inside the team (1 for leader and 2 for regular member)
   */
  Role: 1 | 2;
  /**
   * Team identifier
   */
  TeamID: number;
  /**
   * User identifier
   */
  UserID: number;
};

export type TeamsTeamCreatePayload = {
  /**
   * DenyPortainerAccess denies members of this team access to Portainer itself
   */
  DenyPortainerAccess?: boolean;
  /**
   * Name
   */
  Name: string;
  /**
   * TeamLeaders
   */
  TeamLeaders?: Array<number>;
};

export type TeamsTeamUpdatePayload = {
  /**
   * DenyPortainerAccess denies members of this team access to Portainer itself
   */
  DenyPortainerAccess?: boolean;
  /**
   * Name
   */
  Name?: string;
};

export type TemplatesFileResponse = {
  /**
   * The requested file content
   */
  FileContent?: string;
};

export type TemplatesListResponse = {
  templates?: Array<PortainerTemplate>;
  version?: string;
};

export type TypesEndpointUpdateScheduleRelation = {
  edgeStackId?: number;
  environmentId?: number;
  scheduleId?: number;
  targetVersion?: string;
};

export type TypesUpdateSchedule = {
  /**
   * Name of the agent image, does not include the registry
   */
  agentImage?: string;
  /**
   * Created timestamp
   */
  created?: number;
  /**
   * Created by user id
   */
  createdBy?: number;
  /**
   * EdgeGroups to be updated
   *
   * There is some duplication here with EdgeStack.EdgeGroups
   * EdgeStack.EdgeGroup should have only one group which a temporary group, used only for the update
   * This field is saved only to show which groups the user chose when creating the schedule
   */
  edgeGroupIds?: Array<number>;
  edgeStackId?: number;
  /**
   * Deprecated - use Environment.Agent.PreviousVersion instead
   */
  environmentsPreviousVersions?: {
    [key: string]: string;
  };
  /**
   * EdgeUpdateSchedule Identifier
   */
  id?: number;
  /**
   * Name of the schedule
   */
  name?: string;
  /**
   * ID of registry
   */
  registryId?: number;
  /**
   * Type of the update (1 - update, 2 - rollback)
   */
  type?: 1 | 2;
  /**
   * Updated timestamp
   */
  updated?: number;
  /**
   * Name of the updater image, does not include the registry but must include a tag
   */
  updaterImage?: string;
  /**
   * Deprecated
   */
  version?: string;
};

export const TypesUpdateScheduleStatusType = {
  /**
   * UpdateScheduleStatusPending
   */
  UPDATE_SCHEDULE_STATUS_PENDING: 0,
  /**
   * UpdateScheduleStatusError
   */
  UPDATE_SCHEDULE_STATUS_ERROR: 1,
  /**
   * UpdateScheduleStatusSuccess
   */
  UPDATE_SCHEDULE_STATUS_SUCCESS: 2,
  /**
   * UpdateScheduleStatusSent
   */
  UPDATE_SCHEDULE_STATUS_SENT: 3,
  /**
   * UpdateScheduleStatusInProgress
   */
  UPDATE_SCHEDULE_STATUS_IN_PROGRESS: 4,
} as const;

export type TypesUpdateScheduleStatusType =
  (typeof TypesUpdateScheduleStatusType)[keyof typeof TypesUpdateScheduleStatusType];

export const TypesUpdateScheduleType = {
  /**
   * _
   */
  '': 0,
  /**
   * UpdateScheduleUpdate
   */
  UPDATE_SCHEDULE_UPDATE: 1,
  /**
   * UpdateScheduleRollback
   */
  UPDATE_SCHEDULE_ROLLBACK: 2,
} as const;

export type TypesUpdateScheduleType =
  (typeof TypesUpdateScheduleType)[keyof typeof TypesUpdateScheduleType];

export type UnstructuredUnstructured = {
  /**
   * Object is a JSON compatible map with string, float, int, bool, []interface{}, or
   * map[string]interface{}
   * children.
   */
  Object?: {
    [key: string]: unknown;
  };
};

export type UseractivityLogsListResponse = {
  logs?: Array<PortainereeUserActivityLog>;
  totalCount?: number;
};

export const UsersAccessLocation = {
  /**
   * AccessLocationEnvironment
   */
  ACCESS_LOCATION_ENVIRONMENT: 'environment',
  /**
   * AccessLocationEnvironmentGroup
   */
  ACCESS_LOCATION_ENVIRONMENT_GROUP: 'environmentGroup',
  /**
   * AccessLocationPolicy
   */
  ACCESS_LOCATION_POLICY: 'policy',
} as const;

export type UsersAccessLocation =
  (typeof UsersAccessLocation)[keyof typeof UsersAccessLocation];

export type UsersCurrentUserEndpointAuthResponse = {
  Authorizations?: PortainerAuthorizations;
};

export type UsersCurrentUserEndpointNamespaceAuthResponse = {
  namespaceAuthorizations?: {
    [key: string]: PortainerAuthorizations;
  };
};

export type UsersCurrentUserInspectResponse = {
  /**
   * User Identifier
   */
  Id: number;
  /**
   * PortainerAccessDenied is true when a team the user belongs to denies
   * Portainer access, steering them to an add-on.
   */
  PortainerAccessDenied?: boolean;
  PortainerAuthorizations?: PortainerAuthorizations;
  /**
   * User role (1 for administrator account and 2 for regular account)
   */
  Role: PortainerUserRole;
  ThemeSettings?: PortainereeUserThemeSettings;
  TokenIssueAt?: number;
  UseCache?: boolean;
  Username: string;
  forceChangePassword?: boolean;
};

export type UsersEffectiveAccessEntry = {
  accessLocation?: UsersAccessLocation;
  endpointId?: number;
  endpointName?: string;
  groupId?: number;
  groupName?: string;
  policyId?: number;
  policyName?: string;
  roleId?: number;
  roleName?: string;
  rolePriority?: number;
  teamId?: number;
  teamName?: string;
};

export type UsersUser = {
  Id?: number;
  /**
   * User role (1 for administrator account and 2 for regular account)
   */
  Role?: PortainerUserRole;
  Username?: string;
};

export type UsersAccessTokenResponse = {
  apiKey?: PortainerApiKey;
  rawAPIKey?: string;
};

export type UsersAddHelmRepoUrlPayload = {
  url?: string;
};

export type UsersAdminInitPayload = {
  /**
   * Password for the admin user
   */
  Password: string;
  /**
   * Username for the admin user
   */
  Username: string;
};

export type UsersHelmUserRepositoryResponse = {
  GlobalRepository?: string;
  UserRepositories?: Array<PortainerHelmUserRepository>;
};

export type UsersNamespaceMapping = {
  [key: string]: {
    [key: string]: PortainerAuthorizations;
  };
};

export type UsersThemePayload = {
  /**
   * Color represents the color theme of the UI
   */
  color?: 'dark' | 'light' | 'highcontrast' | 'auto';
  /**
   * SubtleUpgradeButton indicates if the upgrade banner should be displayed in a subtle way
   */
  subtleUpgradeButton?: boolean;
};

export type UsersUserAccessTokenCreatePayload = {
  description: string;
  password: string;
};

export type UsersUserCreatePayload = {
  Password: string;
  /**
   * User role
   * 1 = administrator account
   * 2 = regular account
   */
  Role: 1 | 2;
  Username: string;
};

export type UsersUserUpdatePasswordPayload = {
  /**
   * New Password
   */
  NewPassword: string;
  /**
   * Current Password
   */
  Password: string;
};

export type UsersUserUpdatePayload = {
  NewPassword: string;
  Password: string;
  /**
   * User role
   * 1 = administrator account
   * 2 = regular account
   * 3 = edge administrator account
   */
  Role: 1 | 2 | 3;
  Theme?: UsersThemePayload;
  UseCache: boolean;
  Username: string;
};

export type V1AwsElasticBlockStoreVolumeSource = {
  /**
   * fsType is the filesystem type of the volume that you want to mount.
   * Tip: Ensure that the filesystem type is supported by the host operating system.
   * Examples: "ext4", "xfs", "ntfs". Implicitly inferred to be "ext4" if unspecified.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#awselasticblockstore
   * TODO: how do we prevent errors in the filesystem from compromising the machine
   * +optional
   */
  fsType?: string;
  /**
   * partition is the partition in the volume that you want to mount.
   * If omitted, the default is to mount by volume name.
   * Examples: For volume /dev/sda1, you specify the partition as "1".
   * Similarly, the volume partition for /dev/sda is "0" (or you can leave the property empty).
   * +optional
   */
  partition?: number;
  /**
   * readOnly value true will force the readOnly setting in VolumeMounts.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#awselasticblockstore
   * +optional
   */
  readOnly?: boolean;
  /**
   * volumeID is unique ID of the persistent disk resource in AWS (Amazon EBS volume).
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#awselasticblockstore
   */
  volumeID?: string;
};

export type V1Affinity = {
  /**
   * Describes node affinity scheduling rules for the pod.
   * +optional
   */
  nodeAffinity?: V1NodeAffinity;
  /**
   * Describes pod affinity scheduling rules (e.g. co-locate this pod in the same node, zone, etc. as some other pod(s)).
   * +optional
   */
  podAffinity?: V1PodAffinity;
  /**
   * Describes pod anti-affinity scheduling rules (e.g. avoid putting this pod in the same node, zone, etc. as some other pod(s)).
   * +optional
   */
  podAntiAffinity?: V1PodAntiAffinity;
};

export type V1AppArmorProfile = {
  /**
   * localhostProfile indicates a profile loaded on the node that should be used.
   * The profile must be preconfigured on the node to work.
   * Must match the loaded name of the profile.
   * Must be set if and only if type is "Localhost".
   * +optional
   */
  localhostProfile?: string;
  /**
   * type indicates which kind of AppArmor profile will be applied.
   * Valid options are:
   * Localhost - a profile pre-loaded on the node.
   * RuntimeDefault - the container runtime's default profile.
   * Unconfined - no AppArmor enforcement.
   * +unionDiscriminator
   */
  type?: V1AppArmorProfileType;
};

export const V1AppArmorProfileType = {
  /**
   * AppArmorProfileTypeUnconfined
   */
  APP_ARMOR_PROFILE_TYPE_UNCONFINED: 'Unconfined',
  /**
   * AppArmorProfileTypeRuntimeDefault
   */
  APP_ARMOR_PROFILE_TYPE_RUNTIME_DEFAULT: 'RuntimeDefault',
  /**
   * AppArmorProfileTypeLocalhost
   */
  APP_ARMOR_PROFILE_TYPE_LOCALHOST: 'Localhost',
} as const;

export type V1AppArmorProfileType =
  (typeof V1AppArmorProfileType)[keyof typeof V1AppArmorProfileType];

export type V1AttachedVolume = {
  /**
   * DevicePath represents the device path where the volume should be available
   */
  devicePath?: string;
  /**
   * Name of the attached volume
   */
  name?: string;
};

export const V1AzureDataDiskCachingMode = {
  /**
   * AzureDataDiskCachingNone
   */
  AZURE_DATA_DISK_CACHING_NONE: 'None',
  /**
   * AzureDataDiskCachingReadOnly
   */
  AZURE_DATA_DISK_CACHING_READ_ONLY: 'ReadOnly',
  /**
   * AzureDataDiskCachingReadWrite
   */
  AZURE_DATA_DISK_CACHING_READ_WRITE: 'ReadWrite',
} as const;

export type V1AzureDataDiskCachingMode =
  (typeof V1AzureDataDiskCachingMode)[keyof typeof V1AzureDataDiskCachingMode];

export const V1AzureDataDiskKind = {
  /**
   * AzureSharedBlobDisk
   */
  AZURE_SHARED_BLOB_DISK: 'Shared',
  /**
   * AzureDedicatedBlobDisk
   */
  AZURE_DEDICATED_BLOB_DISK: 'Dedicated',
  /**
   * AzureManagedDisk
   */
  AZURE_MANAGED_DISK: 'Managed',
} as const;

export type V1AzureDataDiskKind =
  (typeof V1AzureDataDiskKind)[keyof typeof V1AzureDataDiskKind];

export type V1AzureDiskVolumeSource = {
  /**
   * cachingMode is the Host Caching mode: None, Read Only, Read Write.
   * +optional
   * +default=ref(AzureDataDiskCachingReadWrite)
   */
  cachingMode?: V1AzureDataDiskCachingMode;
  /**
   * diskName is the Name of the data disk in the blob storage
   */
  diskName?: string;
  /**
   * diskURI is the URI of data disk in the blob storage
   */
  diskURI?: string;
  /**
   * fsType is Filesystem type to mount.
   * Must be a filesystem type supported by the host operating system.
   * Ex. "ext4", "xfs", "ntfs". Implicitly inferred to be "ext4" if unspecified.
   * +optional
   * +default="ext4"
   */
  fsType?: string;
  /**
   * kind expected values are Shared: multiple blob disks per storage account  Dedicated: single blob disk per storage account  Managed: azure managed data disk (only in managed availability set). defaults to shared
   * +default=ref(AzureSharedBlobDisk)
   */
  kind?: V1AzureDataDiskKind;
  /**
   * readOnly Defaults to false (read/write). ReadOnly here will force
   * the ReadOnly setting in VolumeMounts.
   * +optional
   * +default=false
   */
  readOnly?: boolean;
};

export type V1AzureFileVolumeSource = {
  /**
   * readOnly defaults to false (read/write). ReadOnly here will force
   * the ReadOnly setting in VolumeMounts.
   * +optional
   */
  readOnly?: boolean;
  /**
   * secretName is the  name of secret that contains Azure Storage Account Name and Key
   */
  secretName?: string;
  /**
   * shareName is the azure share Name
   */
  shareName?: string;
};

export type V1CsiPersistentVolumeSource = {
  /**
   * controllerExpandSecretRef is a reference to the secret object containing
   * sensitive information to pass to the CSI driver to complete the CSI
   * ControllerExpandVolume call.
   * This field is optional, and may be empty if no secret is required. If the
   * secret object contains more than one secret, all secrets are passed.
   * +optional
   */
  controllerExpandSecretRef?: V1SecretReference;
  /**
   * controllerPublishSecretRef is a reference to the secret object containing
   * sensitive information to pass to the CSI driver to complete the CSI
   * ControllerPublishVolume and ControllerUnpublishVolume calls.
   * This field is optional, and may be empty if no secret is required. If the
   * secret object contains more than one secret, all secrets are passed.
   * +optional
   */
  controllerPublishSecretRef?: V1SecretReference;
  /**
   * driver is the name of the driver to use for this volume.
   * Required.
   */
  driver?: string;
  /**
   * fsType to mount. Must be a filesystem type supported by the host operating system.
   * Ex. "ext4", "xfs", "ntfs".
   * +optional
   */
  fsType?: string;
  /**
   * nodeExpandSecretRef is a reference to the secret object containing
   * sensitive information to pass to the CSI driver to complete the CSI
   * NodeExpandVolume call.
   * This field is optional, may be omitted if no secret is required. If the
   * secret object contains more than one secret, all secrets are passed.
   * +optional
   */
  nodeExpandSecretRef?: V1SecretReference;
  /**
   * nodePublishSecretRef is a reference to the secret object containing
   * sensitive information to pass to the CSI driver to complete the CSI
   * NodePublishVolume and NodeUnpublishVolume calls.
   * This field is optional, and may be empty if no secret is required. If the
   * secret object contains more than one secret, all secrets are passed.
   * +optional
   */
  nodePublishSecretRef?: V1SecretReference;
  /**
   * nodeStageSecretRef is a reference to the secret object containing sensitive
   * information to pass to the CSI driver to complete the CSI NodeStageVolume
   * and NodeStageVolume and NodeUnstageVolume calls.
   * This field is optional, and may be empty if no secret is required. If the
   * secret object contains more than one secret, all secrets are passed.
   * +optional
   */
  nodeStageSecretRef?: V1SecretReference;
  /**
   * readOnly value to pass to ControllerPublishVolumeRequest.
   * Defaults to false (read/write).
   * +optional
   */
  readOnly?: boolean;
  /**
   * volumeAttributes of the volume to publish.
   * +optional
   */
  volumeAttributes?: {
    [key: string]: string;
  };
  /**
   * volumeHandle is the unique volume name returned by the CSI volume
   * plugin’s CreateVolume to refer to the volume on all subsequent calls.
   * Required.
   */
  volumeHandle?: string;
};

export type V1CsiVolumeSource = {
  /**
   * driver is the name of the CSI driver that handles this volume.
   * Consult with your admin for the correct name as registered in the cluster.
   */
  driver?: string;
  /**
   * fsType to mount. Ex. "ext4", "xfs", "ntfs".
   * If not provided, the empty value is passed to the associated CSI driver
   * which will determine the default filesystem to apply.
   * +optional
   */
  fsType?: string;
  /**
   * nodePublishSecretRef is a reference to the secret object containing
   * sensitive information to pass to the CSI driver to complete the CSI
   * NodePublishVolume and NodeUnpublishVolume calls.
   * This field is optional, and  may be empty if no secret is required. If the
   * secret object contains more than one secret, all secret references are passed.
   * +optional
   */
  nodePublishSecretRef?: K8sIoApiCoreV1LocalObjectReference;
  /**
   * readOnly specifies a read-only configuration for the volume.
   * Defaults to false (read/write).
   * +optional
   */
  readOnly?: boolean;
  /**
   * volumeAttributes stores driver-specific properties that are passed to the CSI
   * driver. Consult your driver's documentation for supported values.
   * +optional
   */
  volumeAttributes?: {
    [key: string]: string;
  };
};

export type V1Capabilities = {
  /**
   * Added capabilities
   * +optional
   * +listType=atomic
   */
  add?: Array<string>;
  /**
   * Removed capabilities
   * +optional
   * +listType=atomic
   */
  drop?: Array<string>;
};

export type V1CephFsVolumeSource = {
  /**
   * monitors is Required: Monitors is a collection of Ceph monitors
   * More info: https://examples.k8s.io/volumes/cephfs/README.md#how-to-use-it
   * +listType=atomic
   */
  monitors?: Array<string>;
  /**
   * path is Optional: Used as the mounted root, rather than the full Ceph tree, default is /
   * +optional
   */
  path?: string;
  /**
   * readOnly is Optional: Defaults to false (read/write). ReadOnly here will force
   * the ReadOnly setting in VolumeMounts.
   * More info: https://examples.k8s.io/volumes/cephfs/README.md#how-to-use-it
   * +optional
   */
  readOnly?: boolean;
  /**
   * secretFile is Optional: SecretFile is the path to key ring for User, default is /etc/ceph/user.secret
   * More info: https://examples.k8s.io/volumes/cephfs/README.md#how-to-use-it
   * +optional
   */
  secretFile?: string;
  /**
   * secretRef is Optional: SecretRef is reference to the authentication secret for User, default is empty.
   * More info: https://examples.k8s.io/volumes/cephfs/README.md#how-to-use-it
   * +optional
   */
  secretRef?: K8sIoApiCoreV1LocalObjectReference;
  /**
   * user is optional: User is the rados user name, default is admin
   * More info: https://examples.k8s.io/volumes/cephfs/README.md#how-to-use-it
   * +optional
   */
  user?: string;
};

export type V1CinderVolumeSource = {
  /**
   * fsType is the filesystem type to mount.
   * Must be a filesystem type supported by the host operating system.
   * Examples: "ext4", "xfs", "ntfs". Implicitly inferred to be "ext4" if unspecified.
   * More info: https://examples.k8s.io/mysql-cinder-pd/README.md
   * +optional
   */
  fsType?: string;
  /**
   * readOnly defaults to false (read/write). ReadOnly here will force
   * the ReadOnly setting in VolumeMounts.
   * More info: https://examples.k8s.io/mysql-cinder-pd/README.md
   * +optional
   */
  readOnly?: boolean;
  /**
   * secretRef is optional: points to a secret object containing parameters used to connect
   * to OpenStack.
   * +optional
   */
  secretRef?: K8sIoApiCoreV1LocalObjectReference;
  /**
   * volumeID used to identify the volume in cinder.
   * More info: https://examples.k8s.io/mysql-cinder-pd/README.md
   */
  volumeID?: string;
};

export type V1ClusterTrustBundleProjection = {
  /**
   * Select all ClusterTrustBundles that match this label selector.  Only has
   * effect if signerName is set.  Mutually-exclusive with name.  If unset,
   * interpreted as "match nothing".  If set but empty, interpreted as "match
   * everything".
   * +optional
   */
  labelSelector?: V1LabelSelector;
  /**
   * Select a single ClusterTrustBundle by object name.  Mutually-exclusive
   * with signerName and labelSelector.
   * +optional
   */
  name?: string;
  /**
   * If true, don't block pod startup if the referenced ClusterTrustBundle(s)
   * aren't available.  If using name, then the named ClusterTrustBundle is
   * allowed not to exist.  If using signerName, then the combination of
   * signerName and labelSelector is allowed to match zero
   * ClusterTrustBundles.
   * +optional
   */
  optional?: boolean;
  /**
   * Relative path from the volume root to write the bundle.
   */
  path?: string;
  /**
   * Select all ClusterTrustBundles that match this signer name.
   * Mutually-exclusive with name.  The contents of all selected
   * ClusterTrustBundles will be unified and deduplicated.
   * +optional
   */
  signerName?: string;
};

export type V1ConfigMapEnvSource = {
  /**
   * Name of the referent.
   * This field is effectively required, but due to backwards compatibility is
   * allowed to be empty. Instances of this type with an empty value here are
   * almost certainly wrong.
   * More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/names/#names
   * +optional
   * +default=""
   * +kubebuilder:default=""
   * TODO: Drop `kubebuilder:default` when controller-gen doesn't need it https://github.com/kubernetes-sigs/kubebuilder/issues/3896.
   */
  name?: string;
  /**
   * Specify whether the ConfigMap must be defined
   * +optional
   */
  optional?: boolean;
};

export type V1ConfigMapKeySelector = {
  /**
   * The key to select.
   */
  key?: string;
  /**
   * Name of the referent.
   * This field is effectively required, but due to backwards compatibility is
   * allowed to be empty. Instances of this type with an empty value here are
   * almost certainly wrong.
   * More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/names/#names
   * +optional
   * +default=""
   * +kubebuilder:default=""
   * TODO: Drop `kubebuilder:default` when controller-gen doesn't need it https://github.com/kubernetes-sigs/kubebuilder/issues/3896.
   */
  name?: string;
  /**
   * Specify whether the ConfigMap or its key must be defined
   * +optional
   */
  optional?: boolean;
};

export type V1ConfigMapNodeConfigSource = {
  /**
   * KubeletConfigKey declares which key of the referenced ConfigMap corresponds to the KubeletConfiguration structure
   * This field is required in all cases.
   */
  kubeletConfigKey?: string;
  /**
   * Name is the metadata.name of the referenced ConfigMap.
   * This field is required in all cases.
   */
  name?: string;
  /**
   * Namespace is the metadata.namespace of the referenced ConfigMap.
   * This field is required in all cases.
   */
  namespace?: string;
  /**
   * ResourceVersion is the metadata.ResourceVersion of the referenced ConfigMap.
   * This field is forbidden in Node.Spec, and required in Node.Status.
   * +optional
   */
  resourceVersion?: string;
  /**
   * UID is the metadata.UID of the referenced ConfigMap.
   * This field is forbidden in Node.Spec, and required in Node.Status.
   * +optional
   */
  uid?: string;
};

export type V1ConfigMapProjection = {
  /**
   * items if unspecified, each key-value pair in the Data field of the referenced
   * ConfigMap will be projected into the volume as a file whose name is the
   * key and content is the value. If specified, the listed keys will be
   * projected into the specified paths, and unlisted keys will not be
   * present. If a key is specified which is not present in the ConfigMap,
   * the volume setup will error unless it is marked optional. Paths must be
   * relative and may not contain the '..' path or start with '..'.
   * +optional
   * +listType=atomic
   */
  items?: Array<V1KeyToPath>;
  /**
   * Name of the referent.
   * This field is effectively required, but due to backwards compatibility is
   * allowed to be empty. Instances of this type with an empty value here are
   * almost certainly wrong.
   * More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/names/#names
   * +optional
   * +default=""
   * +kubebuilder:default=""
   * TODO: Drop `kubebuilder:default` when controller-gen doesn't need it https://github.com/kubernetes-sigs/kubebuilder/issues/3896.
   */
  name?: string;
  /**
   * optional specify whether the ConfigMap or its keys must be defined
   * +optional
   */
  optional?: boolean;
};

export type V1ConfigMapVolumeSource = {
  /**
   * defaultMode is optional: mode bits used to set permissions on created files by default.
   * Must be an octal value between 0000 and 0777 or a decimal value between 0 and 511.
   * YAML accepts both octal and decimal values, JSON requires decimal values for mode bits.
   * Defaults to 0644.
   * Directories within the path are not affected by this setting.
   * This might be in conflict with other options that affect the file
   * mode, like fsGroup, and the result can be other mode bits set.
   * +optional
   */
  defaultMode?: number;
  /**
   * items if unspecified, each key-value pair in the Data field of the referenced
   * ConfigMap will be projected into the volume as a file whose name is the
   * key and content is the value. If specified, the listed keys will be
   * projected into the specified paths, and unlisted keys will not be
   * present. If a key is specified which is not present in the ConfigMap,
   * the volume setup will error unless it is marked optional. Paths must be
   * relative and may not contain the '..' path or start with '..'.
   * +optional
   * +listType=atomic
   */
  items?: Array<V1KeyToPath>;
  /**
   * Name of the referent.
   * This field is effectively required, but due to backwards compatibility is
   * allowed to be empty. Instances of this type with an empty value here are
   * almost certainly wrong.
   * More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/names/#names
   * +optional
   * +default=""
   * +kubebuilder:default=""
   * TODO: Drop `kubebuilder:default` when controller-gen doesn't need it https://github.com/kubernetes-sigs/kubebuilder/issues/3896.
   */
  name?: string;
  /**
   * optional specify whether the ConfigMap or its keys must be defined
   * +optional
   */
  optional?: boolean;
};

export type V1Container = {
  /**
   * Arguments to the entrypoint.
   * The container image's CMD is used if this is not provided.
   * Variable references $(VAR_NAME) are expanded using the container's environment. If a variable
   * cannot be resolved, the reference in the input string will be unchanged. Double $$ are reduced
   * to a single $, which allows for escaping the $(VAR_NAME) syntax: i.e. "$$(VAR_NAME)" will
   * produce the string literal "$(VAR_NAME)". Escaped references will never be expanded, regardless
   * of whether the variable exists or not. Cannot be updated.
   * More info: https://kubernetes.io/docs/tasks/inject-data-application/define-command-argument-container/#running-a-command-in-a-shell
   * +optional
   * +listType=atomic
   */
  args?: Array<string>;
  /**
   * Entrypoint array. Not executed within a shell.
   * The container image's ENTRYPOINT is used if this is not provided.
   * Variable references $(VAR_NAME) are expanded using the container's environment. If a variable
   * cannot be resolved, the reference in the input string will be unchanged. Double $$ are reduced
   * to a single $, which allows for escaping the $(VAR_NAME) syntax: i.e. "$$(VAR_NAME)" will
   * produce the string literal "$(VAR_NAME)". Escaped references will never be expanded, regardless
   * of whether the variable exists or not. Cannot be updated.
   * More info: https://kubernetes.io/docs/tasks/inject-data-application/define-command-argument-container/#running-a-command-in-a-shell
   * +optional
   * +listType=atomic
   */
  command?: Array<string>;
  /**
   * List of environment variables to set in the container.
   * Cannot be updated.
   * +optional
   * +patchMergeKey=name
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=name
   */
  env?: Array<V1EnvVar>;
  /**
   * List of sources to populate environment variables in the container.
   * The keys defined within a source may consist of any printable ASCII characters except '='.
   * When a key exists in multiple
   * sources, the value associated with the last source will take precedence.
   * Values defined by an Env with a duplicate key will take precedence.
   * Cannot be updated.
   * +optional
   * +listType=atomic
   */
  envFrom?: Array<V1EnvFromSource>;
  /**
   * Container image name.
   * More info: https://kubernetes.io/docs/concepts/containers/images
   * This field is optional to allow higher level config management to default or override
   * container images in workload controllers like Deployments and StatefulSets.
   * +optional
   */
  image?: string;
  /**
   * Image pull policy.
   * One of Always, Never, IfNotPresent.
   * Defaults to Always if :latest tag is specified, or IfNotPresent otherwise.
   * Cannot be updated.
   * More info: https://kubernetes.io/docs/concepts/containers/images#updating-images
   * +optional
   */
  imagePullPolicy?: V1PullPolicy;
  /**
   * Actions that the management system should take in response to container lifecycle events.
   * Cannot be updated.
   * +optional
   */
  lifecycle?: V1Lifecycle;
  /**
   * Periodic probe of container liveness.
   * Container will be restarted if the probe fails.
   * Cannot be updated.
   * More info: https://kubernetes.io/docs/concepts/workloads/pods/pod-lifecycle#container-probes
   * +optional
   */
  livenessProbe?: V1Probe;
  /**
   * Name of the container specified as a DNS_LABEL.
   * Each container in a pod must have a unique name (DNS_LABEL).
   * Cannot be updated.
   */
  name?: string;
  /**
   * List of ports to expose from the container. Not specifying a port here
   * DOES NOT prevent that port from being exposed. Any port which is
   * listening on the default "0.0.0.0" address inside a container will be
   * accessible from the network.
   * Modifying this array with strategic merge patch may corrupt the data.
   * For more information See https://github.com/kubernetes/kubernetes/issues/108255.
   * Cannot be updated.
   * +optional
   * +patchMergeKey=containerPort
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=containerPort
   * +listMapKey=protocol
   */
  ports?: Array<V1ContainerPort>;
  /**
   * Periodic probe of container service readiness.
   * Container will be removed from service endpoints if the probe fails.
   * Cannot be updated.
   * More info: https://kubernetes.io/docs/concepts/workloads/pods/pod-lifecycle#container-probes
   * +optional
   */
  readinessProbe?: V1Probe;
  /**
   * Resources resize policy for the container.
   * This field cannot be set on ephemeral containers.
   * +featureGate=InPlacePodVerticalScaling
   * +optional
   * +listType=atomic
   */
  resizePolicy?: Array<V1ContainerResizePolicy>;
  /**
   * Compute Resources required by this container.
   * Cannot be updated.
   * More info: https://kubernetes.io/docs/concepts/configuration/manage-resources-containers/
   * +optional
   */
  resources?: V1ResourceRequirements;
  /**
   * RestartPolicy defines the restart behavior of individual containers in a pod.
   * This overrides the pod-level restart policy. When this field is not specified,
   * the restart behavior is defined by the Pod's restart policy and the container type.
   * Additionally, setting the RestartPolicy as "Always" for the init container will
   * have the following effect:
   * this init container will be continually restarted on
   * exit until all regular containers have terminated. Once all regular
   * containers have completed, all init containers with restartPolicy "Always"
   * will be shut down. This lifecycle differs from normal init containers and
   * is often referred to as a "sidecar" container. Although this init
   * container still starts in the init container sequence, it does not wait
   * for the container to complete before proceeding to the next init
   * container. Instead, the next init container starts immediately after this
   * init container is started, or after any startupProbe has successfully
   * completed.
   * +optional
   */
  restartPolicy?: V1ContainerRestartPolicy;
  /**
   * Represents a list of rules to be checked to determine if the
   * container should be restarted on exit. The rules are evaluated in
   * order. Once a rule matches a container exit condition, the remaining
   * rules are ignored. If no rule matches the container exit condition,
   * the Container-level restart policy determines the whether the container
   * is restarted or not. Constraints on the rules:
   * - At most 20 rules are allowed.
   * - Rules can have the same action.
   * - Identical rules are not forbidden in validations.
   * When rules are specified, container MUST set RestartPolicy explicitly
   * even it if matches the Pod's RestartPolicy.
   * +featureGate=ContainerRestartRules
   * +optional
   * +listType=atomic
   */
  restartPolicyRules?: Array<V1ContainerRestartRule>;
  /**
   * SecurityContext defines the security options the container should be run with.
   * If set, the fields of SecurityContext override the equivalent fields of PodSecurityContext.
   * More info: https://kubernetes.io/docs/tasks/configure-pod-container/security-context/
   * +optional
   */
  securityContext?: V1SecurityContext;
  /**
   * StartupProbe indicates that the Pod has successfully initialized.
   * If specified, no other probes are executed until this completes successfully.
   * If this probe fails, the Pod will be restarted, just as if the livenessProbe failed.
   * This can be used to provide different probe parameters at the beginning of a Pod's lifecycle,
   * when it might take a long time to load data or warm a cache, than during steady-state operation.
   * This cannot be updated.
   * More info: https://kubernetes.io/docs/concepts/workloads/pods/pod-lifecycle#container-probes
   * +optional
   */
  startupProbe?: V1Probe;
  /**
   * Whether this container should allocate a buffer for stdin in the container runtime. If this
   * is not set, reads from stdin in the container will always result in EOF.
   * Default is false.
   * +optional
   */
  stdin?: boolean;
  /**
   * Whether the container runtime should close the stdin channel after it has been opened by
   * a single attach. When stdin is true the stdin stream will remain open across multiple attach
   * sessions. If stdinOnce is set to true, stdin is opened on container start, is empty until the
   * first client attaches to stdin, and then remains open and accepts data until the client disconnects,
   * at which time stdin is closed and remains closed until the container is restarted. If this
   * flag is false, a container processes that reads from stdin will never receive an EOF.
   * Default is false
   * +optional
   */
  stdinOnce?: boolean;
  /**
   * Optional: Path at which the file to which the container's termination message
   * will be written is mounted into the container's filesystem.
   * Message written is intended to be brief final status, such as an assertion failure message.
   * Will be truncated by the node if greater than 4096 bytes. The total message length across
   * all containers will be limited to 12kb.
   * Defaults to /dev/termination-log.
   * Cannot be updated.
   * +optional
   */
  terminationMessagePath?: string;
  /**
   * Indicate how the termination message should be populated. File will use the contents of
   * terminationMessagePath to populate the container status message on both success and failure.
   * FallbackToLogsOnError will use the last chunk of container log output if the termination
   * message file is empty and the container exited with an error.
   * The log output is limited to 2048 bytes or 80 lines, whichever is smaller.
   * Defaults to File.
   * Cannot be updated.
   * +optional
   */
  terminationMessagePolicy?: V1TerminationMessagePolicy;
  /**
   * Whether this container should allocate a TTY for itself, also requires 'stdin' to be true.
   * Default is false.
   * +optional
   */
  tty?: boolean;
  /**
   * volumeDevices is the list of block devices to be used by the container.
   * +patchMergeKey=devicePath
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=devicePath
   * +optional
   */
  volumeDevices?: Array<V1VolumeDevice>;
  /**
   * Pod volumes to mount into the container's filesystem.
   * Cannot be updated.
   * +optional
   * +patchMergeKey=mountPath
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=mountPath
   */
  volumeMounts?: Array<V1VolumeMount>;
  /**
   * Container's working directory.
   * If not specified, the container runtime's default will be used, which
   * might be configured in the container image.
   * Cannot be updated.
   * +optional
   */
  workingDir?: string;
};

export type V1ContainerExtendedResourceRequest = {
  /**
   * The name of the container requesting resources.
   */
  containerName?: string;
  /**
   * The name of the request in the special ResourceClaim which corresponds to the extended resource.
   */
  requestName?: string;
  /**
   * The name of the extended resource in that container which gets backed by DRA.
   */
  resourceName?: string;
};

export type V1ContainerImage = {
  /**
   * Names by which this image is known.
   * e.g. ["kubernetes.example/hyperkube:v1.0.7", "cloud-vendor.registry.example/cloud-vendor/hyperkube:v1.0.7"]
   * +optional
   * +listType=atomic
   */
  names?: Array<string>;
  /**
   * The size of the image in bytes.
   * +optional
   */
  sizeBytes?: number;
};

export type V1ContainerPort = {
  /**
   * Number of port to expose on the pod's IP address.
   * This must be a valid port number, 0 < x < 65536.
   */
  containerPort?: number;
  /**
   * What host IP to bind the external port to.
   * +optional
   */
  hostIP?: string;
  /**
   * Number of port to expose on the host.
   * If specified, this must be a valid port number, 0 < x < 65536.
   * If HostNetwork is specified, this must match ContainerPort.
   * Most containers do not need this.
   * +optional
   */
  hostPort?: number;
  /**
   * If specified, this must be an IANA_SVC_NAME and unique within the pod. Each
   * named port in a pod must have a unique name. Name for the port that can be
   * referred to by services.
   * +optional
   */
  name?: string;
  /**
   * Protocol for port. Must be UDP, TCP, or SCTP.
   * Defaults to "TCP".
   * +optional
   * +default="TCP"
   */
  protocol?: V1Protocol;
};

export type V1ContainerResizePolicy = {
  /**
   * Name of the resource to which this resource resize policy applies.
   * Supported values: cpu, memory.
   */
  resourceName?: V1ResourceName;
  /**
   * Restart policy to apply when specified resource is resized.
   * If not specified, it defaults to NotRequired.
   */
  restartPolicy?: V1ResourceResizeRestartPolicy;
};

export const V1ContainerRestartPolicy = {
  /**
   * ContainerRestartPolicyAlways
   */
  CONTAINER_RESTART_POLICY_ALWAYS: 'Always',
  /**
   * ContainerRestartPolicyNever
   */
  CONTAINER_RESTART_POLICY_NEVER: 'Never',
  /**
   * ContainerRestartPolicyOnFailure
   */
  CONTAINER_RESTART_POLICY_ON_FAILURE: 'OnFailure',
} as const;

export type V1ContainerRestartPolicy =
  (typeof V1ContainerRestartPolicy)[keyof typeof V1ContainerRestartPolicy];

export type V1ContainerRestartRule = {
  /**
   * Specifies the action taken on a container exit if the requirements
   * are satisfied. The only possible value is "Restart" to restart the
   * container.
   * +required
   */
  action?: V1ContainerRestartRuleAction;
  /**
   * Represents the exit codes to check on container exits.
   * +optional
   * +oneOf=when
   */
  exitCodes?: V1ContainerRestartRuleOnExitCodes;
};

export const V1ContainerRestartRuleAction = {
  /**
   * ContainerRestartRuleActionRestart
   */
  CONTAINER_RESTART_RULE_ACTION_RESTART: 'Restart' /**
   * ContainerRestartRuleActionRestartAllContainers
   */,
  CONTAINER_RESTART_RULE_ACTION_RESTART_ALL_CONTAINERS: 'RestartAllContainers',
} as const;

export type V1ContainerRestartRuleAction =
  (typeof V1ContainerRestartRuleAction)[keyof typeof V1ContainerRestartRuleAction];

export type V1ContainerRestartRuleOnExitCodes = {
  /**
   * Represents the relationship between the container exit code(s) and the
   * specified values. Possible values are:
   * - In: the requirement is satisfied if the container exit code is in the
   * set of specified values.
   * - NotIn: the requirement is satisfied if the container exit code is
   * not in the set of specified values.
   * +required
   */
  operator?: V1ContainerRestartRuleOnExitCodesOperator;
  /**
   * Specifies the set of values to check for container exit codes.
   * At most 255 elements are allowed.
   * +optional
   * +listType=set
   */
  values?: Array<number>;
};

export const V1ContainerRestartRuleOnExitCodesOperator = {
  /**
   * ContainerRestartRuleOnExitCodesOpIn
   */
  CONTAINER_RESTART_RULE_ON_EXIT_CODES_OP_IN: 'In' /**
   * ContainerRestartRuleOnExitCodesOpNotIn
   */,
  CONTAINER_RESTART_RULE_ON_EXIT_CODES_OP_NOT_IN: 'NotIn',
} as const;

export type V1ContainerRestartRuleOnExitCodesOperator =
  (typeof V1ContainerRestartRuleOnExitCodesOperator)[keyof typeof V1ContainerRestartRuleOnExitCodesOperator];

export type V1ContainerState = {
  /**
   * Details about a running container
   * +optional
   */
  running?: V1ContainerStateRunning;
  /**
   * Details about a terminated container
   * +optional
   */
  terminated?: V1ContainerStateTerminated;
  /**
   * Details about a waiting container
   * +optional
   */
  waiting?: V1ContainerStateWaiting;
};

export type V1ContainerStateRunning = {
  /**
   * Time at which the container was last (re-)started
   * +optional
   */
  startedAt?: string;
};

export type V1ContainerStateTerminated = {
  /**
   * Container's ID in the format '<type>://<container_id>'
   * +optional
   */
  containerID?: string;
  /**
   * Exit status from the last termination of the container
   */
  exitCode?: number;
  /**
   * Time at which the container last terminated
   * +optional
   */
  finishedAt?: string;
  /**
   * Message regarding the last termination of the container
   * +optional
   */
  message?: string;
  /**
   * (brief) reason from the last termination of the container
   * +optional
   */
  reason?: string;
  /**
   * Signal from the last termination of the container
   * +optional
   */
  signal?: number;
  /**
   * Time at which previous execution of the container started
   * +optional
   */
  startedAt?: string;
};

export type V1ContainerStateWaiting = {
  /**
   * Message regarding why the container is not yet running.
   * +optional
   */
  message?: string;
  /**
   * (brief) reason the container is not yet running.
   * +optional
   */
  reason?: string;
};

export type V1ContainerStatus = {
  /**
   * AllocatedResources represents the compute resources allocated for this container by the
   * node. Kubelet sets this value to Container.Resources.Requests upon successful pod admission
   * and after successfully admitting desired pod resize.
   * +featureGate=InPlacePodVerticalScalingAllocatedStatus
   * +optional
   */
  allocatedResources?: V1ResourceList;
  /**
   * AllocatedResourcesStatus represents the status of various resources
   * allocated for this Pod.
   * +featureGate=ResourceHealthStatus
   * +optional
   * +patchMergeKey=name
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=name
   */
  allocatedResourcesStatus?: Array<V1ResourceStatus>;
  /**
   * ContainerID is the ID of the container in the format '<type>://<container_id>'.
   * Where type is a container runtime identifier, returned from Version call of CRI API
   * (for example "containerd").
   * +optional
   */
  containerID?: string;
  /**
   * Image is the name of container image that the container is running.
   * The container image may not match the image used in the PodSpec,
   * as it may have been resolved by the runtime.
   * More info: https://kubernetes.io/docs/concepts/containers/images.
   */
  image?: string;
  /**
   * ImageID is the image ID of the container's image. The image ID may not
   * match the image ID of the image used in the PodSpec, as it may have been
   * resolved by the runtime.
   */
  imageID?: string;
  /**
   * LastTerminationState holds the last termination state of the container to
   * help debug container crashes and restarts. This field is not
   * populated if the container is still running and RestartCount is 0.
   * +optional
   */
  lastState?: V1ContainerState;
  /**
   * Name is a DNS_LABEL representing the unique name of the container.
   * Each container in a pod must have a unique name across all container types.
   * Cannot be updated.
   */
  name?: string;
  /**
   * Ready specifies whether the container is currently passing its readiness check.
   * The value will change as readiness probes keep executing. If no readiness
   * probes are specified, this field defaults to true once the container is
   * fully started (see Started field).
   *
   * The value is typically used to determine whether a container is ready to
   * accept traffic.
   */
  ready?: boolean;
  /**
   * Resources represents the compute resource requests and limits that have been successfully
   * enacted on the running container after it has been started or has been successfully resized.
   * +featureGate=InPlacePodVerticalScaling
   * +optional
   */
  resources?: V1ResourceRequirements;
  /**
   * RestartCount holds the number of times the container has been restarted.
   * Kubelet makes an effort to always increment the value, but there
   * are cases when the state may be lost due to node restarts and then the value
   * may be reset to 0. The value is never negative.
   */
  restartCount?: number;
  /**
   * Started indicates whether the container has finished its postStart lifecycle hook
   * and passed its startup probe.
   * Initialized as false, becomes true after startupProbe is considered
   * successful. Resets to false when the container is restarted, or if kubelet
   * loses state temporarily. In both cases, startup probes will run again.
   * Is always true when no startupProbe is defined and container is running and
   * has passed the postStart lifecycle hook. The null value must be treated the
   * same as false.
   * +optional
   */
  started?: boolean;
  /**
   * State holds details about the container's current condition.
   * +optional
   */
  state?: V1ContainerState;
  /**
   * StopSignal reports the effective stop signal for this container
   * +featureGate=ContainerStopSignals
   * +optional
   */
  stopSignal?: V1Signal;
  /**
   * User represents user identity information initially attached to the first process of the container
   * +featureGate=SupplementalGroupsPolicy
   * +optional
   */
  user?: V1ContainerUser;
  /**
   * Status of volume mounts.
   * +optional
   * +patchMergeKey=mountPath
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=mountPath
   */
  volumeMounts?: Array<V1VolumeMountStatus>;
};

export type V1ContainerUser = {
  /**
   * Linux holds user identity information initially attached to the first process of the containers in Linux.
   * Note that the actual running identity can be changed if the process has enough privilege to do so.
   * +optional
   */
  linux?: V1LinuxContainerUser;
};

export const V1DnsPolicy = {
  /**
   * DNSClusterFirstWithHostNet
   */
  DNS_CLUSTER_FIRST_WITH_HOST_NET: 'ClusterFirstWithHostNet',
  /**
   * DNSClusterFirst
   */
  DNS_CLUSTER_FIRST: 'ClusterFirst',
  /**
   * DNSDefault
   */
  DNS_DEFAULT: 'Default',
  /**
   * DNSNone
   */
  DNS_NONE: 'None',
} as const;

export type V1DnsPolicy = (typeof V1DnsPolicy)[keyof typeof V1DnsPolicy];

export type V1DaemonEndpoint = {
  /**
   * Port number of the given endpoint.
   */
  Port?: number;
};

export type V1Deployment = {
  /**
   * APIVersion defines the versioned schema of this representation of an object.
   * Servers should convert recognized schemas to the latest internal value, and
   * may reject unrecognized values.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#resources
   * +optional
   */
  apiVersion?: string;
  /**
   * Kind is a string value representing the REST resource this object represents.
   * Servers may infer this from the endpoint the client submits requests to.
   * Cannot be updated.
   * In CamelCase.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
   * +optional
   */
  kind?: string;
  /**
   * Standard object's metadata.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#metadata
   * +optional
   */
  metadata?: V1ObjectMeta;
  /**
   * Specification of the desired behavior of the Deployment.
   * +optional
   */
  spec?: V1DeploymentSpec;
  /**
   * Most recently observed status of the Deployment.
   * +optional
   */
  status?: V1DeploymentStatus;
};

export type V1DeploymentSpec = {
  /**
   * Minimum number of seconds for which a newly created pod should be ready
   * without any of its container crashing, for it to be considered available.
   * Defaults to 0 (pod will be considered available as soon as it is ready)
   * +optional
   */
  minReadySeconds?: number;
  /**
   * Indicates that the deployment is paused.
   * +optional
   */
  paused?: boolean;
  /**
   * The maximum time in seconds for a deployment to make progress before it
   * is considered to be failed. The deployment controller will continue to
   * process failed deployments and a condition with a ProgressDeadlineExceeded
   * reason will be surfaced in the deployment status. Note that progress will
   * not be estimated during the time a deployment is paused. Defaults to 600s.
   */
  progressDeadlineSeconds?: number;
  /**
   * Number of desired pods. This is a pointer to distinguish between explicit
   * zero and not specified. Defaults to 1.
   * +optional
   */
  replicas?: number;
  /**
   * The number of old ReplicaSets to retain to allow rollback.
   * This is a pointer to distinguish between explicit zero and not specified.
   * Defaults to 10.
   * +optional
   */
  revisionHistoryLimit?: number;
  /**
   * Label selector for pods. Existing ReplicaSets whose pods are
   * selected by this will be the ones affected by this deployment.
   * It must match the pod template's labels.
   */
  selector?: V1LabelSelector;
  /**
   * The deployment strategy to use to replace existing pods with new ones.
   * +optional
   * +patchStrategy=retainKeys
   */
  strategy?: K8sIoApiAppsV1DeploymentStrategy;
  /**
   * Template describes the pods that will be created.
   * The only allowed template.spec.restartPolicy value is "Always".
   */
  template?: V1PodTemplateSpec;
};

export type V1DeploymentStatus = {
  /**
   * Total number of available non-terminating pods (ready for at least minReadySeconds) targeted by this deployment.
   * +optional
   */
  availableReplicas?: number;
  /**
   * Count of hash collisions for the Deployment. The Deployment controller uses this
   * field as a collision avoidance mechanism when it needs to create the name for the
   * newest ReplicaSet.
   * +optional
   */
  collisionCount?: number;
  /**
   * Represents the latest available observations of a deployment's current state.
   * +patchMergeKey=type
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=type
   */
  conditions?: Array<K8sIoApiAppsV1DeploymentCondition>;
  /**
   * The generation observed by the deployment controller.
   * +optional
   */
  observedGeneration?: number;
  /**
   * Total number of non-terminating pods targeted by this Deployment with a Ready Condition.
   * +optional
   */
  readyReplicas?: number;
  /**
   * Total number of non-terminating pods targeted by this deployment (their labels match the selector).
   * +optional
   */
  replicas?: number;
  /**
   * Total number of terminating pods targeted by this deployment. Terminating pods have a non-null
   * .metadata.deletionTimestamp and have not yet reached the Failed or Succeeded .status.phase.
   *
   * This is a beta field and requires enabling DeploymentReplicaSetTerminatingReplicas feature (enabled by default).
   * +optional
   */
  terminatingReplicas?: number;
  /**
   * Total number of unavailable pods targeted by this deployment. This is the total number of
   * pods that are still required for the deployment to have 100% available capacity. They may
   * either be pods that are running but not yet available or pods that still have not been created.
   * +optional
   */
  unavailableReplicas?: number;
  /**
   * Total number of non-terminating pods targeted by this deployment that have the desired template spec.
   * +optional
   */
  updatedReplicas?: number;
};

export type V1Descriptor = {
  /**
   * Annotations contains arbitrary metadata relating to the targeted content.
   */
  annotations?: {
    [key: string]: string;
  };
  /**
   * ArtifactType is the IANA media type of this artifact.
   */
  artifactType?: string;
  /**
   * Data is an embedding of the targeted content. This is encoded as a base64
   * string when marshalled to JSON (automatically, by encoding/json). If
   * present, Data can be used directly to avoid fetching the targeted content.
   */
  data?: Array<number>;
  /**
   * Digest is the digest of the targeted content.
   */
  digest?: string;
  /**
   * MediaType is the media type of the object this schema refers to.
   */
  mediaType?: string;
  /**
   * Platform describes the platform which the image in the manifest runs on.
   *
   * This should only be used when referring to a manifest.
   */
  platform?: V1Platform;
  /**
   * Size specifies the size in bytes of the blob.
   */
  size?: number;
  /**
   * URLs specifies a list of URLs from which this object MAY be downloaded
   */
  urls?: Array<string>;
};

export type V1DownwardApiProjection = {
  /**
   * Items is a list of DownwardAPIVolume file
   * +optional
   * +listType=atomic
   */
  items?: Array<V1DownwardApiVolumeFile>;
};

export type V1DownwardApiVolumeFile = {
  /**
   * Required: Selects a field of the pod: only annotations, labels, name, namespace and uid are supported.
   * +optional
   */
  fieldRef?: V1ObjectFieldSelector;
  /**
   * Optional: mode bits used to set permissions on this file, must be an octal value
   * between 0000 and 0777 or a decimal value between 0 and 511.
   * YAML accepts both octal and decimal values, JSON requires decimal values for mode bits.
   * If not specified, the volume defaultMode will be used.
   * This might be in conflict with other options that affect the file
   * mode, like fsGroup, and the result can be other mode bits set.
   * +optional
   */
  mode?: number;
  /**
   * Required: Path is  the relative path name of the file to be created. Must not be absolute or contain the '..' path. Must be utf-8 encoded. The first item of the relative path must not start with '..'
   */
  path?: string;
  /**
   * Selects a resource of the container: only resources limits and requests
   * (limits.cpu, limits.memory, requests.cpu and requests.memory) are currently supported.
   * +optional
   */
  resourceFieldRef?: V1ResourceFieldSelector;
};

export type V1DownwardApiVolumeSource = {
  /**
   * Optional: mode bits to use on created files by default. Must be a
   * Optional: mode bits used to set permissions on created files by default.
   * Must be an octal value between 0000 and 0777 or a decimal value between 0 and 511.
   * YAML accepts both octal and decimal values, JSON requires decimal values for mode bits.
   * Defaults to 0644.
   * Directories within the path are not affected by this setting.
   * This might be in conflict with other options that affect the file
   * mode, like fsGroup, and the result can be other mode bits set.
   * +optional
   */
  defaultMode?: number;
  /**
   * Items is a list of downward API volume file
   * +optional
   * +listType=atomic
   */
  items?: Array<V1DownwardApiVolumeFile>;
};

export type V1EmptyDirVolumeSource = {
  /**
   * medium represents what type of storage medium should back this directory.
   * The default is "" which means to use the node's default medium.
   * Must be an empty string (default) or Memory.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#emptydir
   * +optional
   */
  medium?: V1StorageMedium;
  /**
   * sizeLimit is the total amount of local storage required for this EmptyDir volume.
   * The size limit is also applicable for memory medium.
   * The maximum usage on memory medium EmptyDir would be the minimum value between
   * the SizeLimit specified here and the sum of memory limits of all containers in a pod.
   * The default is nil which means that the limit is undefined.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#emptydir
   * +optional
   */
  sizeLimit?: ResourceQuantity;
};

export type V1EnvFromSource = {
  /**
   * The ConfigMap to select from
   * +optional
   */
  configMapRef?: V1ConfigMapEnvSource;
  /**
   * Optional text to prepend to the name of each environment variable.
   * May consist of any printable ASCII characters except '='.
   * +optional
   */
  prefix?: string;
  /**
   * The Secret to select from
   * +optional
   */
  secretRef?: V1SecretEnvSource;
};

export type V1EnvVar = {
  /**
   * Name of the environment variable.
   * May consist of any printable ASCII characters except '='.
   */
  name?: string;
  /**
   * Variable references $(VAR_NAME) are expanded
   * using the previously defined environment variables in the container and
   * any service environment variables. If a variable cannot be resolved,
   * the reference in the input string will be unchanged. Double $$ are reduced
   * to a single $, which allows for escaping the $(VAR_NAME) syntax: i.e.
   * "$$(VAR_NAME)" will produce the string literal "$(VAR_NAME)".
   * Escaped references will never be expanded, regardless of whether the variable
   * exists or not.
   * Defaults to "".
   * +optional
   */
  value?: string;
  /**
   * Source for the environment variable's value. Cannot be used if value is not empty.
   * +optional
   */
  valueFrom?: V1EnvVarSource;
};

export type V1EnvVarSource = {
  /**
   * Selects a key of a ConfigMap.
   * +optional
   */
  configMapKeyRef?: V1ConfigMapKeySelector;
  /**
   * Selects a field of the pod: supports metadata.name, metadata.namespace, `metadata.labels['<KEY>']`, `metadata.annotations['<KEY>']`,
   * spec.nodeName, spec.serviceAccountName, status.hostIP, status.podIP, status.podIPs.
   * +optional
   */
  fieldRef?: V1ObjectFieldSelector;
  /**
   * FileKeyRef selects a key of the env file.
   * Requires the EnvFiles feature gate to be enabled.
   *
   * +featureGate=EnvFiles
   * +optional
   */
  fileKeyRef?: V1FileKeySelector;
  /**
   * Selects a resource of the container: only resources limits and requests
   * (limits.cpu, limits.memory, limits.ephemeral-storage, requests.cpu, requests.memory and requests.ephemeral-storage) are currently supported.
   * +optional
   */
  resourceFieldRef?: V1ResourceFieldSelector;
  /**
   * Selects a key of a secret in the pod's namespace
   * +optional
   */
  secretKeyRef?: V1SecretKeySelector;
};

export type V1EphemeralContainer = {
  /**
   * Arguments to the entrypoint.
   * The image's CMD is used if this is not provided.
   * Variable references $(VAR_NAME) are expanded using the container's environment. If a variable
   * cannot be resolved, the reference in the input string will be unchanged. Double $$ are reduced
   * to a single $, which allows for escaping the $(VAR_NAME) syntax: i.e. "$$(VAR_NAME)" will
   * produce the string literal "$(VAR_NAME)". Escaped references will never be expanded, regardless
   * of whether the variable exists or not. Cannot be updated.
   * More info: https://kubernetes.io/docs/tasks/inject-data-application/define-command-argument-container/#running-a-command-in-a-shell
   * +optional
   * +listType=atomic
   */
  args?: Array<string>;
  /**
   * Entrypoint array. Not executed within a shell.
   * The image's ENTRYPOINT is used if this is not provided.
   * Variable references $(VAR_NAME) are expanded using the container's environment. If a variable
   * cannot be resolved, the reference in the input string will be unchanged. Double $$ are reduced
   * to a single $, which allows for escaping the $(VAR_NAME) syntax: i.e. "$$(VAR_NAME)" will
   * produce the string literal "$(VAR_NAME)". Escaped references will never be expanded, regardless
   * of whether the variable exists or not. Cannot be updated.
   * More info: https://kubernetes.io/docs/tasks/inject-data-application/define-command-argument-container/#running-a-command-in-a-shell
   * +optional
   * +listType=atomic
   */
  command?: Array<string>;
  /**
   * List of environment variables to set in the container.
   * Cannot be updated.
   * +optional
   * +patchMergeKey=name
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=name
   */
  env?: Array<V1EnvVar>;
  /**
   * List of sources to populate environment variables in the container.
   * The keys defined within a source may consist of any printable ASCII characters except '='.
   * When a key exists in multiple
   * sources, the value associated with the last source will take precedence.
   * Values defined by an Env with a duplicate key will take precedence.
   * Cannot be updated.
   * +optional
   * +listType=atomic
   */
  envFrom?: Array<V1EnvFromSource>;
  /**
   * Container image name.
   * More info: https://kubernetes.io/docs/concepts/containers/images
   */
  image?: string;
  /**
   * Image pull policy.
   * One of Always, Never, IfNotPresent.
   * Defaults to Always if :latest tag is specified, or IfNotPresent otherwise.
   * Cannot be updated.
   * More info: https://kubernetes.io/docs/concepts/containers/images#updating-images
   * +optional
   */
  imagePullPolicy?: V1PullPolicy;
  /**
   * Lifecycle is not allowed for ephemeral containers.
   * +optional
   */
  lifecycle?: V1Lifecycle;
  /**
   * Probes are not allowed for ephemeral containers.
   * +optional
   */
  livenessProbe?: V1Probe;
  /**
   * Name of the ephemeral container specified as a DNS_LABEL.
   * This name must be unique among all containers, init containers and ephemeral containers.
   */
  name?: string;
  /**
   * Ports are not allowed for ephemeral containers.
   * +optional
   * +patchMergeKey=containerPort
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=containerPort
   * +listMapKey=protocol
   */
  ports?: Array<V1ContainerPort>;
  /**
   * Probes are not allowed for ephemeral containers.
   * +optional
   */
  readinessProbe?: V1Probe;
  /**
   * Resources resize policy for the container.
   * +featureGate=InPlacePodVerticalScaling
   * +optional
   * +listType=atomic
   */
  resizePolicy?: Array<V1ContainerResizePolicy>;
  /**
   * Resources are not allowed for ephemeral containers. Ephemeral containers use spare resources
   * already allocated to the pod.
   * +optional
   */
  resources?: V1ResourceRequirements;
  /**
   * Restart policy for the container to manage the restart behavior of each
   * container within a pod.
   * You cannot set this field on ephemeral containers.
   * +optional
   */
  restartPolicy?: V1ContainerRestartPolicy;
  /**
   * Represents a list of rules to be checked to determine if the
   * container should be restarted on exit. You cannot set this field on
   * ephemeral containers.
   * +featureGate=ContainerRestartRules
   * +optional
   * +listType=atomic
   */
  restartPolicyRules?: Array<V1ContainerRestartRule>;
  /**
   * Optional: SecurityContext defines the security options the ephemeral container should be run with.
   * If set, the fields of SecurityContext override the equivalent fields of PodSecurityContext.
   * +optional
   */
  securityContext?: V1SecurityContext;
  /**
   * Probes are not allowed for ephemeral containers.
   * +optional
   */
  startupProbe?: V1Probe;
  /**
   * Whether this container should allocate a buffer for stdin in the container runtime. If this
   * is not set, reads from stdin in the container will always result in EOF.
   * Default is false.
   * +optional
   */
  stdin?: boolean;
  /**
   * Whether the container runtime should close the stdin channel after it has been opened by
   * a single attach. When stdin is true the stdin stream will remain open across multiple attach
   * sessions. If stdinOnce is set to true, stdin is opened on container start, is empty until the
   * first client attaches to stdin, and then remains open and accepts data until the client disconnects,
   * at which time stdin is closed and remains closed until the container is restarted. If this
   * flag is false, a container processes that reads from stdin will never receive an EOF.
   * Default is false
   * +optional
   */
  stdinOnce?: boolean;
  /**
   * If set, the name of the container from PodSpec that this ephemeral container targets.
   * The ephemeral container will be run in the namespaces (IPC, PID, etc) of this container.
   * If not set then the ephemeral container uses the namespaces configured in the Pod spec.
   *
   * The container runtime must implement support for this feature. If the runtime does not
   * support namespace targeting then the result of setting this field is undefined.
   * +optional
   */
  targetContainerName?: string;
  /**
   * Optional: Path at which the file to which the container's termination message
   * will be written is mounted into the container's filesystem.
   * Message written is intended to be brief final status, such as an assertion failure message.
   * Will be truncated by the node if greater than 4096 bytes. The total message length across
   * all containers will be limited to 12kb.
   * Defaults to /dev/termination-log.
   * Cannot be updated.
   * +optional
   */
  terminationMessagePath?: string;
  /**
   * Indicate how the termination message should be populated. File will use the contents of
   * terminationMessagePath to populate the container status message on both success and failure.
   * FallbackToLogsOnError will use the last chunk of container log output if the termination
   * message file is empty and the container exited with an error.
   * The log output is limited to 2048 bytes or 80 lines, whichever is smaller.
   * Defaults to File.
   * Cannot be updated.
   * +optional
   */
  terminationMessagePolicy?: V1TerminationMessagePolicy;
  /**
   * Whether this container should allocate a TTY for itself, also requires 'stdin' to be true.
   * Default is false.
   * +optional
   */
  tty?: boolean;
  /**
   * volumeDevices is the list of block devices to be used by the container.
   * +patchMergeKey=devicePath
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=devicePath
   * +optional
   */
  volumeDevices?: Array<V1VolumeDevice>;
  /**
   * Pod volumes to mount into the container's filesystem. Subpath mounts are not allowed for ephemeral containers.
   * Cannot be updated.
   * +optional
   * +patchMergeKey=mountPath
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=mountPath
   */
  volumeMounts?: Array<V1VolumeMount>;
  /**
   * Container's working directory.
   * If not specified, the container runtime's default will be used, which
   * might be configured in the container image.
   * Cannot be updated.
   * +optional
   */
  workingDir?: string;
};

export type V1EphemeralVolumeSource = {
  /**
   * Will be used to create a stand-alone PVC to provision the volume.
   * The pod in which this EphemeralVolumeSource is embedded will be the
   * owner of the PVC, i.e. the PVC will be deleted together with the
   * pod.  The name of the PVC will be `<pod name>-<volume name>` where
   * `<volume name>` is the name from the `PodSpec.Volumes` array
   * entry. Pod validation will reject the pod if the concatenated name
   * is not valid for a PVC (for example, too long).
   *
   * An existing PVC with that name that is not owned by the pod
   * will *not* be used for the pod to avoid using an unrelated
   * volume by mistake. Starting the pod is then blocked until
   * the unrelated PVC is removed. If such a pre-created PVC is
   * meant to be used by the pod, the PVC has to updated with an
   * owner reference to the pod once the pod exists. Normally
   * this should not be necessary, but it may be useful when
   * manually reconstructing a broken cluster.
   *
   * This field is read-only and no changes will be made by Kubernetes
   * to the PVC after it has been created.
   *
   * Required, must not be nil.
   */
  volumeClaimTemplate?: V1PersistentVolumeClaimTemplate;
};

export type V1ExecAction = {
  /**
   * Command is the command line to execute inside the container, the working directory for the
   * command  is root ('/') in the container's filesystem. The command is simply exec'd, it is
   * not run inside a shell, so traditional shell instructions ('|', etc) won't work. To use
   * a shell, you need to explicitly call out to that shell.
   * Exit status of 0 is treated as live/healthy and non-zero is unhealthy.
   * +optional
   * +listType=atomic
   */
  command?: Array<string>;
};

export type V1FcVolumeSource = {
  /**
   * fsType is the filesystem type to mount.
   * Must be a filesystem type supported by the host operating system.
   * Ex. "ext4", "xfs", "ntfs". Implicitly inferred to be "ext4" if unspecified.
   * TODO: how do we prevent errors in the filesystem from compromising the machine
   * +optional
   */
  fsType?: string;
  /**
   * lun is Optional: FC target lun number
   * +optional
   */
  lun?: number;
  /**
   * readOnly is Optional: Defaults to false (read/write). ReadOnly here will force
   * the ReadOnly setting in VolumeMounts.
   * +optional
   */
  readOnly?: boolean;
  /**
   * targetWWNs is Optional: FC target worldwide names (WWNs)
   * +optional
   * +listType=atomic
   */
  targetWWNs?: Array<string>;
  /**
   * wwids Optional: FC volume world wide identifiers (wwids)
   * Either wwids or combination of targetWWNs and lun must be set, but not both simultaneously.
   * +optional
   * +listType=atomic
   */
  wwids?: Array<string>;
};

export type V1FieldsV1 = {
  [key: string]: unknown;
};

export type V1FileKeySelector = {
  /**
   * The key within the env file. An invalid key will prevent the pod from starting.
   * The keys defined within a source may consist of any printable ASCII characters except '='.
   * During Alpha stage of the EnvFiles feature gate, the key size is limited to 128 characters.
   * +required
   */
  key?: string;
  /**
   * Specify whether the file or its key must be defined. If the file or key
   * does not exist, then the env var is not published.
   * If optional is set to true and the specified key does not exist,
   * the environment variable will not be set in the Pod's containers.
   *
   * If optional is set to false and the specified key does not exist,
   * an error will be returned during Pod creation.
   * +optional
   * +default=false
   */
  optional?: boolean;
  /**
   * The path within the volume from which to select the file.
   * Must be relative and may not contain the '..' path or start with '..'.
   * +required
   */
  path?: string;
  /**
   * The name of the volume mount containing the env file.
   * +required
   */
  volumeName?: string;
};

export const V1FinalizerName = {
  /**
   * FinalizerKubernetes
   */
  FINALIZER_KUBERNETES: 'kubernetes',
} as const;

export type V1FinalizerName =
  (typeof V1FinalizerName)[keyof typeof V1FinalizerName];

export type V1FlexVolumeSource = {
  /**
   * driver is the name of the driver to use for this volume.
   */
  driver?: string;
  /**
   * fsType is the filesystem type to mount.
   * Must be a filesystem type supported by the host operating system.
   * Ex. "ext4", "xfs", "ntfs". The default filesystem depends on FlexVolume script.
   * +optional
   */
  fsType?: string;
  /**
   * options is Optional: this field holds extra command options if any.
   * +optional
   */
  options?: {
    [key: string]: string;
  };
  /**
   * readOnly is Optional: defaults to false (read/write). ReadOnly here will force
   * the ReadOnly setting in VolumeMounts.
   * +optional
   */
  readOnly?: boolean;
  /**
   * secretRef is Optional: secretRef is reference to the secret object containing
   * sensitive information to pass to the plugin scripts. This may be
   * empty if no secret object is specified. If the secret object
   * contains more than one secret, all secrets are passed to the plugin
   * scripts.
   * +optional
   */
  secretRef?: K8sIoApiCoreV1LocalObjectReference;
};

export type V1FlockerVolumeSource = {
  /**
   * datasetName is Name of the dataset stored as metadata -> name on the dataset for Flocker
   * should be considered as deprecated
   * +optional
   */
  datasetName?: string;
  /**
   * datasetUUID is the UUID of the dataset. This is unique identifier of a Flocker dataset
   * +optional
   */
  datasetUUID?: string;
};

export type V1GcePersistentDiskVolumeSource = {
  /**
   * fsType is filesystem type of the volume that you want to mount.
   * Tip: Ensure that the filesystem type is supported by the host operating system.
   * Examples: "ext4", "xfs", "ntfs". Implicitly inferred to be "ext4" if unspecified.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#gcepersistentdisk
   * TODO: how do we prevent errors in the filesystem from compromising the machine
   * +optional
   */
  fsType?: string;
  /**
   * partition is the partition in the volume that you want to mount.
   * If omitted, the default is to mount by volume name.
   * Examples: For volume /dev/sda1, you specify the partition as "1".
   * Similarly, the volume partition for /dev/sda is "0" (or you can leave the property empty).
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#gcepersistentdisk
   * +optional
   */
  partition?: number;
  /**
   * pdName is unique name of the PD resource in GCE. Used to identify the disk in GCE.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#gcepersistentdisk
   */
  pdName?: string;
  /**
   * readOnly here will force the ReadOnly setting in VolumeMounts.
   * Defaults to false.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#gcepersistentdisk
   * +optional
   */
  readOnly?: boolean;
};

export type V1GrpcAction = {
  /**
   * Port number of the gRPC service. Number must be in the range 1 to 65535.
   */
  port?: number;
  /**
   * Service is the name of the service to place in the gRPC HealthCheckRequest
   * (see https://github.com/grpc/grpc/blob/master/doc/health-checking.md).
   *
   * If this is not specified, the default behavior is defined by gRPC.
   * +optional
   * +default=""
   */
  service?: string;
};

export type V1GitRepoVolumeSource = {
  /**
   * directory is the target directory name.
   * Must not contain or start with '..'.  If '.' is supplied, the volume directory will be the
   * git repository.  Otherwise, if specified, the volume will contain the git repository in
   * the subdirectory with the given name.
   * +optional
   */
  directory?: string;
  /**
   * repository is the URL
   */
  repository?: string;
  /**
   * revision is the commit hash for the specified revision.
   * +optional
   */
  revision?: string;
};

export type V1GlusterfsVolumeSource = {
  /**
   * endpoints is the endpoint name that details Glusterfs topology.
   */
  endpoints?: string;
  /**
   * path is the Glusterfs volume path.
   * More info: https://examples.k8s.io/volumes/glusterfs/README.md#create-a-pod
   */
  path?: string;
  /**
   * readOnly here will force the Glusterfs volume to be mounted with read-only permissions.
   * Defaults to false.
   * More info: https://examples.k8s.io/volumes/glusterfs/README.md#create-a-pod
   * +optional
   */
  readOnly?: boolean;
};

export type V1HttpGetAction = {
  /**
   * Host name to connect to, defaults to the pod IP. You probably want to set
   * "Host" in httpHeaders instead.
   * +optional
   */
  host?: string;
  /**
   * Custom headers to set in the request. HTTP allows repeated headers.
   * +optional
   * +listType=atomic
   */
  httpHeaders?: Array<K8sIoApiCoreV1HttpHeader>;
  /**
   * Path to access on the HTTP server.
   * +optional
   */
  path?: string;
  /**
   * Name or number of the port to access on the container.
   * Number must be in the range 1 to 65535.
   * Name must be an IANA_SVC_NAME.
   */
  port?: IntstrIntOrString;
  /**
   * Scheme to use for connecting to the host.
   * Defaults to HTTP.
   * +optional
   */
  scheme?: V1UriScheme;
};

export type V1HostAlias = {
  /**
   * Hostnames for the above IP address.
   * +listType=atomic
   */
  hostnames?: Array<string>;
  /**
   * IP address of the host file entry.
   * +required
   */
  ip?: string;
};

export type V1HostIp = {
  /**
   * IP is the IP address assigned to the host
   * +required
   */
  ip?: string;
};

export const V1HostPathType = {
  /**
   * HostPathUnset
   */
  HOST_PATH_UNSET: '',
  /**
   * HostPathDirectoryOrCreate
   */
  HOST_PATH_DIRECTORY_OR_CREATE: 'DirectoryOrCreate',
  /**
   * HostPathDirectory
   */
  HOST_PATH_DIRECTORY: 'Directory',
  /**
   * HostPathFileOrCreate
   */
  HOST_PATH_FILE_OR_CREATE: 'FileOrCreate',
  /**
   * HostPathFile
   */
  HOST_PATH_FILE: 'File',
  /**
   * HostPathSocket
   */
  HOST_PATH_SOCKET: 'Socket',
  /**
   * HostPathCharDev
   */
  HOST_PATH_CHAR_DEV: 'CharDevice',
  /**
   * HostPathBlockDev
   */
  HOST_PATH_BLOCK_DEV: 'BlockDevice',
} as const;

export type V1HostPathType =
  (typeof V1HostPathType)[keyof typeof V1HostPathType];

export type V1HostPathVolumeSource = {
  /**
   * path of the directory on the host.
   * If the path is a symlink, it will follow the link to the real path.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#hostpath
   */
  path?: string;
  /**
   * type for HostPath Volume
   * Defaults to ""
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#hostpath
   * +optional
   */
  type?: V1HostPathType;
};

export type V1IscsiVolumeSource = {
  /**
   * chapAuthDiscovery defines whether support iSCSI Discovery CHAP authentication
   * +optional
   */
  chapAuthDiscovery?: boolean;
  /**
   * chapAuthSession defines whether support iSCSI Session CHAP authentication
   * +optional
   */
  chapAuthSession?: boolean;
  /**
   * fsType is the filesystem type of the volume that you want to mount.
   * Tip: Ensure that the filesystem type is supported by the host operating system.
   * Examples: "ext4", "xfs", "ntfs". Implicitly inferred to be "ext4" if unspecified.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#iscsi
   * TODO: how do we prevent errors in the filesystem from compromising the machine
   * +optional
   */
  fsType?: string;
  /**
   * initiatorName is the custom iSCSI Initiator Name.
   * If initiatorName is specified with iscsiInterface simultaneously, new iSCSI interface
   * <target portal>:<volume name> will be created for the connection.
   * +optional
   */
  initiatorName?: string;
  /**
   * iqn is the target iSCSI Qualified Name.
   */
  iqn?: string;
  /**
   * iscsiInterface is the interface Name that uses an iSCSI transport.
   * Defaults to 'default' (tcp).
   * +optional
   * +default="default"
   */
  iscsiInterface?: string;
  /**
   * lun represents iSCSI Target Lun number.
   */
  lun?: number;
  /**
   * portals is the iSCSI Target Portal List. The portal is either an IP or ip_addr:port if the port
   * is other than default (typically TCP ports 860 and 3260).
   * +optional
   * +listType=atomic
   */
  portals?: Array<string>;
  /**
   * readOnly here will force the ReadOnly setting in VolumeMounts.
   * Defaults to false.
   * +optional
   */
  readOnly?: boolean;
  /**
   * secretRef is the CHAP Secret for iSCSI target and initiator authentication
   * +optional
   */
  secretRef?: K8sIoApiCoreV1LocalObjectReference;
  /**
   * targetPortal is iSCSI Target Portal. The Portal is either an IP or ip_addr:port if the port
   * is other than default (typically TCP ports 860 and 3260).
   */
  targetPortal?: string;
};

export type V1ImageVolumeSource = {
  /**
   * Policy for pulling OCI objects. Possible values are:
   * Always: the kubelet always attempts to pull the reference. Container creation will fail If the pull fails.
   * Never: the kubelet never pulls the reference and only uses a local image or artifact. Container creation will fail if the reference isn't present.
   * IfNotPresent: the kubelet pulls if the reference isn't already present on disk. Container creation will fail if the reference isn't present and the pull fails.
   * Defaults to Always if :latest tag is specified, or IfNotPresent otherwise.
   * +optional
   */
  pullPolicy?: V1PullPolicy;
  /**
   * Required: Image or artifact reference to be used.
   * Behaves in the same way as pod.spec.containers[*].image.
   * Pull secrets will be assembled in the same way as for the container image by looking up node credentials, SA image pull secrets, and pod spec image pull secrets.
   * More info: https://kubernetes.io/docs/concepts/containers/images
   * This field is optional to allow higher level config management to default or override
   * container images in workload controllers like Deployments and StatefulSets.
   * +optional
   */
  reference?: string;
};

export type V1KeyToPath = {
  /**
   * key is the key to project.
   */
  key?: string;
  /**
   * mode is Optional: mode bits used to set permissions on this file.
   * Must be an octal value between 0000 and 0777 or a decimal value between 0 and 511.
   * YAML accepts both octal and decimal values, JSON requires decimal values for mode bits.
   * If not specified, the volume defaultMode will be used.
   * This might be in conflict with other options that affect the file
   * mode, like fsGroup, and the result can be other mode bits set.
   * +optional
   */
  mode?: number;
  /**
   * path is the relative path of the file to map the key to.
   * May not be an absolute path.
   * May not contain the path element '..'.
   * May not start with the string '..'.
   */
  path?: string;
};

export type V1LabelSelector = {
  /**
   * matchExpressions is a list of label selector requirements. The requirements are ANDed.
   * +optional
   * +listType=atomic
   */
  matchExpressions?: Array<V1LabelSelectorRequirement>;
  /**
   * matchLabels is a map of {key,value} pairs. A single {key,value} in the matchLabels
   * map is equivalent to an element of matchExpressions, whose key field is "key", the
   * operator is "In", and the values array contains only "value". The requirements are ANDed.
   * +optional
   */
  matchLabels?: {
    [key: string]: string;
  };
};

export const V1LabelSelectorOperator = {
  /**
   * LabelSelectorOpIn
   */
  LABEL_SELECTOR_OP_IN: 'In',
  /**
   * LabelSelectorOpNotIn
   */
  LABEL_SELECTOR_OP_NOT_IN: 'NotIn',
  /**
   * LabelSelectorOpExists
   */
  LABEL_SELECTOR_OP_EXISTS: 'Exists',
  /**
   * LabelSelectorOpDoesNotExist
   */
  LABEL_SELECTOR_OP_DOES_NOT_EXIST: 'DoesNotExist',
} as const;

export type V1LabelSelectorOperator =
  (typeof V1LabelSelectorOperator)[keyof typeof V1LabelSelectorOperator];

export type V1LabelSelectorRequirement = {
  /**
   * key is the label key that the selector applies to.
   */
  key?: string;
  /**
   * operator represents a key's relationship to a set of values.
   * Valid operators are In, NotIn, Exists and DoesNotExist.
   */
  operator?: V1LabelSelectorOperator;
  /**
   * values is an array of string values. If the operator is In or NotIn,
   * the values array must be non-empty. If the operator is Exists or DoesNotExist,
   * the values array must be empty. This array is replaced during a strategic
   * merge patch.
   * +optional
   * +listType=atomic
   */
  values?: Array<string>;
};

export type V1Lifecycle = {
  /**
   * PostStart is called immediately after a container is created. If the handler fails,
   * the container is terminated and restarted according to its restart policy.
   * Other management of the container blocks until the hook completes.
   * More info: https://kubernetes.io/docs/concepts/containers/container-lifecycle-hooks/#container-hooks
   * +optional
   */
  postStart?: V1LifecycleHandler;
  /**
   * PreStop is called immediately before a container is terminated due to an
   * API request or management event such as liveness/startup probe failure,
   * preemption, resource contention, etc. The handler is not called if the
   * container crashes or exits. The Pod's termination grace period countdown begins before the
   * PreStop hook is executed. Regardless of the outcome of the handler, the
   * container will eventually terminate within the Pod's termination grace
   * period (unless delayed by finalizers). Other management of the container blocks until the hook completes
   * or until the termination grace period is reached.
   * More info: https://kubernetes.io/docs/concepts/containers/container-lifecycle-hooks/#container-hooks
   * +optional
   */
  preStop?: V1LifecycleHandler;
  /**
   * StopSignal defines which signal will be sent to a container when it is being stopped.
   * If not specified, the default is defined by the container runtime in use.
   * StopSignal can only be set for Pods with a non-empty .spec.os.name
   * +optional
   */
  stopSignal?: V1Signal;
};

export type V1LifecycleHandler = {
  /**
   * Exec specifies a command to execute in the container.
   * +optional
   */
  exec?: V1ExecAction;
  /**
   * HTTPGet specifies an HTTP GET request to perform.
   * +optional
   */
  httpGet?: V1HttpGetAction;
  /**
   * Sleep represents a duration that the container should sleep.
   * +optional
   */
  sleep?: V1SleepAction;
  /**
   * Deprecated. TCPSocket is NOT supported as a LifecycleHandler and kept
   * for backward compatibility. There is no validation of this field and
   * lifecycle hooks will fail at runtime when it is specified.
   * +optional
   */
  tcpSocket?: V1TcpSocketAction;
};

export type V1LinuxContainerUser = {
  /**
   * GID is the primary gid initially attached to the first process in the container
   */
  gid?: number;
  /**
   * SupplementalGroups are the supplemental groups initially attached to the first process in the container
   * +optional
   * +listType=atomic
   */
  supplementalGroups?: Array<number>;
  /**
   * UID is the primary uid initially attached to the first process in the container
   */
  uid?: number;
};

export type V1ListMeta = {
  /**
   * continue may be set if the user set a limit on the number of items returned, and indicates that
   * the server has more data available. The value is opaque and may be used to issue another request
   * to the endpoint that served this list to retrieve the next set of available objects. Continuing a
   * consistent list may not be possible if the server configuration has changed or more than a few
   * minutes have passed. The resourceVersion field returned when using this continue value will be
   * identical to the value in the first response, unless you have received this token from an error
   * message.
   */
  continue?: string;
  /**
   * remainingItemCount is the number of subsequent items in the list which are not included in this
   * list response. If the list request contained label or field selectors, then the number of
   * remaining items is unknown and the field will be left unset and omitted during serialization.
   * If the list is complete (either because it is not chunking or because this is the last chunk),
   * then there are no more remaining items and this field will be left unset and omitted during
   * serialization.
   * Servers older than v1.15 do not set this field.
   * The intended use of the remainingItemCount is *estimating* the size of a collection. Clients
   * should not rely on the remainingItemCount to be set or to be exact.
   * +optional
   */
  remainingItemCount?: number;
  /**
   * String that identifies the server's internal version of this object that
   * can be used by clients to determine when objects have changed.
   * Value must be treated as opaque by clients and passed unmodified back to the server.
   * Populated by the system.
   * Read-only.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#concurrency-control-and-consistency
   * +optional
   */
  resourceVersion?: string;
  /**
   * Deprecated: selfLink is a legacy read-only field that is no longer populated by the system.
   * +optional
   */
  selfLink?: string;
};

export type V1ManagedFieldsEntry = {
  /**
   * APIVersion defines the version of this resource that this field set
   * applies to. The format is "group/version" just like the top-level
   * APIVersion field. It is necessary to track the version of a field
   * set because it cannot be automatically converted.
   */
  apiVersion?: string;
  /**
   * FieldsType is the discriminator for the different fields format and version.
   * There is currently only one possible value: "FieldsV1"
   */
  fieldsType?: string;
  /**
   * FieldsV1 holds the first JSON version format as described in the "FieldsV1" type.
   * +optional
   */
  fieldsV1?: V1FieldsV1;
  /**
   * Manager is an identifier of the workflow managing these fields.
   */
  manager?: string;
  /**
   * Operation is the type of operation which lead to this ManagedFieldsEntry being created.
   * The only valid values for this field are 'Apply' and 'Update'.
   */
  operation?: V1ManagedFieldsOperationType;
  /**
   * Subresource is the name of the subresource used to update that object, or
   * empty string if the object was updated through the main resource. The
   * value of this field is used to distinguish between managers, even if they
   * share the same name. For example, a status update will be distinct from a
   * regular update using the same manager name.
   * Note that the APIVersion field is not related to the Subresource field and
   * it always corresponds to the version of the main resource.
   */
  subresource?: string;
  /**
   * Time is the timestamp of when the ManagedFields entry was added. The
   * timestamp will also be updated if a field is added, the manager
   * changes any of the owned fields value or removes a field. The
   * timestamp does not update when a field is removed from the entry
   * because another manager took it over.
   * +optional
   */
  time?: string;
};

export const V1ManagedFieldsOperationType = {
  /**
   * ManagedFieldsOperationApply
   */
  MANAGED_FIELDS_OPERATION_APPLY: 'Apply' /**
   * ManagedFieldsOperationUpdate
   */,
  MANAGED_FIELDS_OPERATION_UPDATE: 'Update',
} as const;

export type V1ManagedFieldsOperationType =
  (typeof V1ManagedFieldsOperationType)[keyof typeof V1ManagedFieldsOperationType];

export const V1MountPropagationMode = {
  /**
   * MountPropagationNone
   */
  MOUNT_PROPAGATION_NONE: 'None',
  /**
   * MountPropagationHostToContainer
   */
  MOUNT_PROPAGATION_HOST_TO_CONTAINER: 'HostToContainer',
  /**
   * MountPropagationBidirectional
   */
  MOUNT_PROPAGATION_BIDIRECTIONAL: 'Bidirectional',
} as const;

export type V1MountPropagationMode =
  (typeof V1MountPropagationMode)[keyof typeof V1MountPropagationMode];

export type V1NfsVolumeSource = {
  /**
   * path that is exported by the NFS server.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#nfs
   */
  path?: string;
  /**
   * readOnly here will force the NFS export to be mounted with read-only permissions.
   * Defaults to false.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#nfs
   * +optional
   */
  readOnly?: boolean;
  /**
   * server is the hostname or IP address of the NFS server.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#nfs
   */
  server?: string;
};

export type V1NamespaceCondition = {
  /**
   * Last time the condition transitioned from one status to another.
   * +optional
   */
  lastTransitionTime?: string;
  /**
   * Human-readable message indicating details about last transition.
   * +optional
   */
  message?: string;
  /**
   * Unique, one-word, CamelCase reason for the condition's last transition.
   * +optional
   */
  reason?: string;
  /**
   * Status of the condition, one of True, False, Unknown.
   */
  status?: K8sIoApiCoreV1ConditionStatus;
  /**
   * Type of namespace controller condition.
   */
  type?: V1NamespaceConditionType;
};

export const V1NamespaceConditionType = {
  /**
   * NamespaceDeletionDiscoveryFailure
   */
  NAMESPACE_DELETION_DISCOVERY_FAILURE: 'NamespaceDeletionDiscoveryFailure',
  /**
   * NamespaceDeletionContentFailure
   */
  NAMESPACE_DELETION_CONTENT_FAILURE: 'NamespaceDeletionContentFailure',
  /**
   * NamespaceDeletionGVParsingFailure
   */
  NAMESPACE_DELETION_GV_PARSING_FAILURE:
    'NamespaceDeletionGroupVersionParsingFailure',
  /**
   * NamespaceContentRemaining
   */
  NAMESPACE_CONTENT_REMAINING: 'NamespaceContentRemaining',
  /**
   * NamespaceFinalizersRemaining
   */
  NAMESPACE_FINALIZERS_REMAINING: 'NamespaceFinalizersRemaining',
} as const;

export type V1NamespaceConditionType =
  (typeof V1NamespaceConditionType)[keyof typeof V1NamespaceConditionType];

export const V1NamespacePhase = {
  /**
   * NamespaceActive
   */
  NAMESPACE_ACTIVE: 'Active' /**
   * NamespaceTerminating
   */,
  NAMESPACE_TERMINATING: 'Terminating',
} as const;

export type V1NamespacePhase =
  (typeof V1NamespacePhase)[keyof typeof V1NamespacePhase];

export type V1NamespaceSpec = {
  /**
   * Finalizers is an opaque list of values that must be empty to permanently remove object from storage.
   * More info: https://kubernetes.io/docs/tasks/administer-cluster/namespaces/
   * +optional
   * +listType=atomic
   */
  finalizers?: Array<V1FinalizerName>;
};

export type V1NamespaceStatus = {
  /**
   * Represents the latest available observations of a namespace's current state.
   * +optional
   * +patchMergeKey=type
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=type
   */
  conditions?: Array<V1NamespaceCondition>;
  /**
   * Phase is the current lifecycle phase of the namespace.
   * More info: https://kubernetes.io/docs/tasks/administer-cluster/namespaces/
   * +optional
   */
  phase?: V1NamespacePhase;
};

export type V1NodeAddress = {
  /**
   * The node address.
   */
  address?: string;
  /**
   * Node address type, one of Hostname, ExternalIP or InternalIP.
   */
  type?: V1NodeAddressType;
};

export const V1NodeAddressType = {
  /**
   * NodeHostName
   */
  NODE_HOST_NAME: 'Hostname',
  /**
   * NodeInternalIP
   */
  NODE_INTERNAL_IP: 'InternalIP',
  /**
   * NodeExternalIP
   */
  NODE_EXTERNAL_IP: 'ExternalIP',
  /**
   * NodeInternalDNS
   */
  NODE_INTERNAL_DNS: 'InternalDNS',
  /**
   * NodeExternalDNS
   */
  NODE_EXTERNAL_DNS: 'ExternalDNS',
} as const;

export type V1NodeAddressType =
  (typeof V1NodeAddressType)[keyof typeof V1NodeAddressType];

export type V1NodeAffinity = {
  /**
   * The scheduler will prefer to schedule pods to nodes that satisfy
   * the affinity expressions specified by this field, but it may choose
   * a node that violates one or more of the expressions. The node that is
   * most preferred is the one with the greatest sum of weights, i.e.
   * for each node that meets all of the scheduling requirements (resource
   * request, requiredDuringScheduling affinity expressions, etc.),
   * compute a sum by iterating through the elements of this field and adding
   * "weight" to the sum if the node matches the corresponding matchExpressions; the
   * node(s) with the highest sum are the most preferred.
   * +optional
   * +listType=atomic
   */
  preferredDuringSchedulingIgnoredDuringExecution?: Array<V1PreferredSchedulingTerm>;
  /**
   * If the affinity requirements specified by this field are not met at
   * scheduling time, the pod will not be scheduled onto the node.
   * If the affinity requirements specified by this field cease to be met
   * at some point during pod execution (e.g. due to an update), the system
   * may or may not try to eventually evict the pod from its node.
   * +optional
   */
  requiredDuringSchedulingIgnoredDuringExecution?: V1NodeSelector;
};

export type V1NodeCondition = {
  /**
   * Last time we got an update on a given condition.
   * +optional
   */
  lastHeartbeatTime?: string;
  /**
   * Last time the condition transit from one status to another.
   * +optional
   */
  lastTransitionTime?: string;
  /**
   * Human readable message indicating details about last transition.
   * +optional
   */
  message?: string;
  /**
   * (brief) reason for the condition's last transition.
   * +optional
   */
  reason?: string;
  /**
   * Status of the condition, one of True, False, Unknown.
   */
  status?: K8sIoApiCoreV1ConditionStatus;
  /**
   * Type of node condition.
   */
  type?: V1NodeConditionType;
};

export const V1NodeConditionType = {
  /**
   * NodeReady
   */
  NODE_READY: 'Ready',
  /**
   * NodeMemoryPressure
   */
  NODE_MEMORY_PRESSURE: 'MemoryPressure',
  /**
   * NodeDiskPressure
   */
  NODE_DISK_PRESSURE: 'DiskPressure',
  /**
   * NodePIDPressure
   */
  NODE_PID_PRESSURE: 'PIDPressure',
  /**
   * NodeNetworkUnavailable
   */
  NODE_NETWORK_UNAVAILABLE: 'NetworkUnavailable',
} as const;

export type V1NodeConditionType =
  (typeof V1NodeConditionType)[keyof typeof V1NodeConditionType];

export type V1NodeConfigSource = {
  /**
   * ConfigMap is a reference to a Node's ConfigMap
   */
  configMap?: V1ConfigMapNodeConfigSource;
};

export type V1NodeConfigStatus = {
  /**
   * Active reports the checkpointed config the node is actively using.
   * Active will represent either the current version of the Assigned config,
   * or the current LastKnownGood config, depending on whether attempting to use the
   * Assigned config results in an error.
   * +optional
   */
  active?: V1NodeConfigSource;
  /**
   * Assigned reports the checkpointed config the node will try to use.
   * When Node.Spec.ConfigSource is updated, the node checkpoints the associated
   * config payload to local disk, along with a record indicating intended
   * config. The node refers to this record to choose its config checkpoint, and
   * reports this record in Assigned. Assigned only updates in the status after
   * the record has been checkpointed to disk. When the Kubelet is restarted,
   * it tries to make the Assigned config the Active config by loading and
   * validating the checkpointed payload identified by Assigned.
   * +optional
   */
  assigned?: V1NodeConfigSource;
  /**
   * Error describes any problems reconciling the Spec.ConfigSource to the Active config.
   * Errors may occur, for example, attempting to checkpoint Spec.ConfigSource to the local Assigned
   * record, attempting to checkpoint the payload associated with Spec.ConfigSource, attempting
   * to load or validate the Assigned config, etc.
   * Errors may occur at different points while syncing config. Earlier errors (e.g. download or
   * checkpointing errors) will not result in a rollback to LastKnownGood, and may resolve across
   * Kubelet retries. Later errors (e.g. loading or validating a checkpointed config) will result in
   * a rollback to LastKnownGood. In the latter case, it is usually possible to resolve the error
   * by fixing the config assigned in Spec.ConfigSource.
   * You can find additional information for debugging by searching the error message in the Kubelet log.
   * Error is a human-readable description of the error state; machines can check whether or not Error
   * is empty, but should not rely on the stability of the Error text across Kubelet versions.
   * +optional
   */
  error?: string;
  /**
   * LastKnownGood reports the checkpointed config the node will fall back to
   * when it encounters an error attempting to use the Assigned config.
   * The Assigned config becomes the LastKnownGood config when the node determines
   * that the Assigned config is stable and correct.
   * This is currently implemented as a 10-minute soak period starting when the local
   * record of Assigned config is updated. If the Assigned config is Active at the end
   * of this period, it becomes the LastKnownGood. Note that if Spec.ConfigSource is
   * reset to nil (use local defaults), the LastKnownGood is also immediately reset to nil,
   * because the local default config is always assumed good.
   * You should not make assumptions about the node's method of determining config stability
   * and correctness, as this may change or become configurable in the future.
   * +optional
   */
  lastKnownGood?: V1NodeConfigSource;
};

export type V1NodeDaemonEndpoints = {
  /**
   * Endpoint on which Kubelet is listening.
   * +optional
   */
  kubeletEndpoint?: V1DaemonEndpoint;
};

export type V1NodeFeatures = {
  /**
   * SupplementalGroupsPolicy is set to true if the runtime supports SupplementalGroupsPolicy and ContainerUser.
   * +optional
   */
  supplementalGroupsPolicy?: boolean;
};

export const V1NodeInclusionPolicy = {
  /**
   * NodeInclusionPolicyIgnore
   */
  NODE_INCLUSION_POLICY_IGNORE: 'Ignore' /**
   * NodeInclusionPolicyHonor
   */,
  NODE_INCLUSION_POLICY_HONOR: 'Honor',
} as const;

export type V1NodeInclusionPolicy =
  (typeof V1NodeInclusionPolicy)[keyof typeof V1NodeInclusionPolicy];

export const V1NodePhase = {
  /**
   * NodePending
   */
  NODE_PENDING: 'Pending',
  /**
   * NodeRunning
   */
  NODE_RUNNING: 'Running',
  /**
   * NodeTerminated
   */
  NODE_TERMINATED: 'Terminated',
} as const;

export type V1NodePhase = (typeof V1NodePhase)[keyof typeof V1NodePhase];

export type V1NodeRuntimeHandler = {
  /**
   * Supported features.
   * +optional
   */
  features?: V1NodeRuntimeHandlerFeatures;
  /**
   * Runtime handler name.
   * Empty for the default runtime handler.
   * +optional
   */
  name?: string;
};

export type V1NodeRuntimeHandlerFeatures = {
  /**
   * RecursiveReadOnlyMounts is set to true if the runtime handler supports RecursiveReadOnlyMounts.
   * +optional
   */
  recursiveReadOnlyMounts?: boolean;
  /**
   * UserNamespaces is set to true if the runtime handler supports UserNamespaces, including for volumes.
   * +featureGate=UserNamespacesSupport
   * +optional
   */
  userNamespaces?: boolean;
};

export type V1NodeSelector = {
  /**
   * Required. A list of node selector terms. The terms are ORed.
   * +listType=atomic
   */
  nodeSelectorTerms?: Array<V1NodeSelectorTerm>;
};

export const V1NodeSelectorOperator = {
  /**
   * NodeSelectorOpIn
   */
  NODE_SELECTOR_OP_IN: 'In',
  /**
   * NodeSelectorOpNotIn
   */
  NODE_SELECTOR_OP_NOT_IN: 'NotIn',
  /**
   * NodeSelectorOpExists
   */
  NODE_SELECTOR_OP_EXISTS: 'Exists',
  /**
   * NodeSelectorOpDoesNotExist
   */
  NODE_SELECTOR_OP_DOES_NOT_EXIST: 'DoesNotExist',
  /**
   * NodeSelectorOpGt
   */
  NODE_SELECTOR_OP_GT: 'Gt',
  /**
   * NodeSelectorOpLt
   */
  NODE_SELECTOR_OP_LT: 'Lt',
} as const;

export type V1NodeSelectorOperator =
  (typeof V1NodeSelectorOperator)[keyof typeof V1NodeSelectorOperator];

export type V1NodeSelectorRequirement = {
  /**
   * The label key that the selector applies to.
   */
  key?: string;
  /**
   * Represents a key's relationship to a set of values.
   * Valid operators are In, NotIn, Exists, DoesNotExist. Gt, and Lt.
   */
  operator?: V1NodeSelectorOperator;
  /**
   * An array of string values. If the operator is In or NotIn,
   * the values array must be non-empty. If the operator is Exists or DoesNotExist,
   * the values array must be empty. If the operator is Gt or Lt, the values
   * array must have a single element, which will be interpreted as an integer.
   * This array is replaced during a strategic merge patch.
   * +optional
   * +listType=atomic
   */
  values?: Array<string>;
};

export type V1NodeSelectorTerm = {
  /**
   * A list of node selector requirements by node's labels.
   * +optional
   * +listType=atomic
   */
  matchExpressions?: Array<V1NodeSelectorRequirement>;
  /**
   * A list of node selector requirements by node's fields.
   * +optional
   * +listType=atomic
   */
  matchFields?: Array<V1NodeSelectorRequirement>;
};

export type V1NodeSpec = {
  /**
   * Deprecated: Previously used to specify the source of the node's configuration for the DynamicKubeletConfig feature. This feature is removed.
   * +optional
   */
  configSource?: V1NodeConfigSource;
  /**
   * Deprecated. Not all kubelets will set this field. Remove field after 1.13.
   * see: https://issues.k8s.io/61966
   * +optional
   */
  externalID?: string;
  /**
   * PodCIDR represents the pod IP range assigned to the node.
   * +optional
   */
  podCIDR?: string;
  /**
   * podCIDRs represents the IP ranges assigned to the node for usage by Pods on that node. If this
   * field is specified, the 0th entry must match the podCIDR field. It may contain at most 1 value for
   * each of IPv4 and IPv6.
   * +optional
   * +patchStrategy=merge
   * +listType=set
   */
  podCIDRs?: Array<string>;
  /**
   * ID of the node assigned by the cloud provider in the format: <ProviderName>://<ProviderSpecificNodeID>
   * +optional
   */
  providerID?: string;
  /**
   * If specified, the node's taints.
   * +optional
   * +listType=atomic
   */
  taints?: Array<V1Taint>;
  /**
   * Unschedulable controls node schedulability of new pods. By default, node is schedulable.
   * More info: https://kubernetes.io/docs/concepts/nodes/node/#manual-node-administration
   * +optional
   */
  unschedulable?: boolean;
};

export type V1NodeStatus = {
  /**
   * List of addresses reachable to the node.
   * Queried from cloud provider, if available.
   * More info: https://kubernetes.io/docs/reference/node/node-status/#addresses
   * Note: This field is declared as mergeable, but the merge key is not sufficiently
   * unique, which can cause data corruption when it is merged. Callers should instead
   * use a full-replacement patch. See https://pr.k8s.io/79391 for an example.
   * Consumers should assume that addresses can change during the
   * lifetime of a Node. However, there are some exceptions where this may not
   * be possible, such as Pods that inherit a Node's address in its own status or
   * consumers of the downward API (status.hostIP).
   * +optional
   * +patchMergeKey=type
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=type
   */
  addresses?: Array<V1NodeAddress>;
  /**
   * Allocatable represents the resources of a node that are available for scheduling.
   * Defaults to Capacity.
   * +optional
   */
  allocatable?: V1ResourceList;
  /**
   * Capacity represents the total resources of a node.
   * More info: https://kubernetes.io/docs/reference/node/node-status/#capacity
   * +optional
   */
  capacity?: V1ResourceList;
  /**
   * Conditions is an array of current observed node conditions.
   * More info: https://kubernetes.io/docs/reference/node/node-status/#condition
   * +optional
   * +patchMergeKey=type
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=type
   */
  conditions?: Array<V1NodeCondition>;
  /**
   * Status of the config assigned to the node via the dynamic Kubelet config feature.
   * +optional
   */
  config?: V1NodeConfigStatus;
  /**
   * Endpoints of daemons running on the Node.
   * +optional
   */
  daemonEndpoints?: V1NodeDaemonEndpoints;
  /**
   * DeclaredFeatures represents the features related to feature gates that are declared by the node.
   * +featureGate=NodeDeclaredFeatures
   * +optional
   * +listType=atomic
   */
  declaredFeatures?: Array<string>;
  /**
   * Features describes the set of features implemented by the CRI implementation.
   * +featureGate=SupplementalGroupsPolicy
   * +optional
   */
  features?: V1NodeFeatures;
  /**
   * List of container images on this node
   * +optional
   * +listType=atomic
   */
  images?: Array<V1ContainerImage>;
  /**
   * Set of ids/uuids to uniquely identify the node.
   * More info: https://kubernetes.io/docs/reference/node/node-status/#info
   * +optional
   */
  nodeInfo?: V1NodeSystemInfo;
  /**
   * NodePhase is the recently observed lifecycle phase of the node.
   * More info: https://kubernetes.io/docs/concepts/nodes/node/#phase
   * The field is never populated, and now is deprecated.
   * +optional
   */
  phase?: V1NodePhase;
  /**
   * The available runtime handlers.
   * +featureGate=UserNamespacesSupport
   * +optional
   * +listType=atomic
   */
  runtimeHandlers?: Array<V1NodeRuntimeHandler>;
  /**
   * List of volumes that are attached to the node.
   * +optional
   * +listType=atomic
   */
  volumesAttached?: Array<V1AttachedVolume>;
  /**
   * List of attachable volumes in use (mounted) by the node.
   * +optional
   * +listType=atomic
   */
  volumesInUse?: Array<string>;
};

export type V1NodeSwapStatus = {
  /**
   * Total amount of swap memory in bytes.
   * +optional
   */
  capacity?: number;
};

export type V1NodeSystemInfo = {
  /**
   * The Architecture reported by the node
   */
  architecture?: string;
  /**
   * Boot ID reported by the node.
   */
  bootID?: string;
  /**
   * ContainerRuntime Version reported by the node through runtime remote API (e.g. containerd://1.4.2).
   */
  containerRuntimeVersion?: string;
  /**
   * Kernel Version reported by the node from 'uname -r' (e.g. 3.16.0-0.bpo.4-amd64).
   */
  kernelVersion?: string;
  /**
   * Deprecated: KubeProxy Version reported by the node.
   */
  kubeProxyVersion?: string;
  /**
   * Kubelet Version reported by the node.
   */
  kubeletVersion?: string;
  /**
   * MachineID reported by the node. For unique machine identification
   * in the cluster this field is preferred. Learn more from man(5)
   * machine-id: http://man7.org/linux/man-pages/man5/machine-id.5.html
   */
  machineID?: string;
  /**
   * The Operating System reported by the node
   */
  operatingSystem?: string;
  /**
   * OS Image reported by the node from /etc/os-release (e.g. Debian GNU/Linux 7 (wheezy)).
   */
  osImage?: string;
  /**
   * Swap Info reported by the node.
   */
  swap?: V1NodeSwapStatus;
  /**
   * SystemUUID reported by the node. For unique machine identification
   * MachineID is preferred. This field is specific to Red Hat hosts
   * https://access.redhat.com/documentation/en-us/red_hat_subscription_management/1/html/rhsm/uuid
   */
  systemUUID?: string;
};

export const V1OsName = {
  /**
   * Linux
   */
  LINUX: 'linux' /**
   * Windows
   */,
  WINDOWS: 'windows',
} as const;

export type V1OsName = (typeof V1OsName)[keyof typeof V1OsName];

export type V1ObjectFieldSelector = {
  /**
   * Version of the schema the FieldPath is written in terms of, defaults to "v1".
   * +optional
   */
  apiVersion?: string;
  /**
   * Path of the field to select in the specified API version.
   */
  fieldPath?: string;
};

export type V1ObjectMeta = {
  /**
   * Annotations is an unstructured key value map stored with a resource that may be
   * set by external tools to store and retrieve arbitrary metadata. They are not
   * queryable and should be preserved when modifying objects.
   * More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/annotations
   * +optional
   */
  annotations?: {
    [key: string]: string;
  };
  /**
   * CreationTimestamp is a timestamp representing the server time when this object was
   * created. It is not guaranteed to be set in happens-before order across separate operations.
   * Clients may not set this value. It is represented in RFC3339 form and is in UTC.
   *
   * Populated by the system.
   * Read-only.
   * Null for lists.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#metadata
   * +optional
   */
  creationTimestamp?: string;
  /**
   * Number of seconds allowed for this object to gracefully terminate before
   * it will be removed from the system. Only set when deletionTimestamp is also set.
   * May only be shortened.
   * Read-only.
   * +optional
   */
  deletionGracePeriodSeconds?: number;
  /**
   * DeletionTimestamp is RFC 3339 date and time at which this resource will be deleted. This
   * field is set by the server when a graceful deletion is requested by the user, and is not
   * directly settable by a client. The resource is expected to be deleted (no longer visible
   * from resource lists, and not reachable by name) after the time in this field, once the
   * finalizers list is empty. As long as the finalizers list contains items, deletion is blocked.
   * Once the deletionTimestamp is set, this value may not be unset or be set further into the
   * future, although it may be shortened or the resource may be deleted prior to this time.
   * For example, a user may request that a pod is deleted in 30 seconds. The Kubelet will react
   * by sending a graceful termination signal to the containers in the pod. After that 30 seconds,
   * the Kubelet will send a hard termination signal (SIGKILL) to the container and after cleanup,
   * remove the pod from the API. In the presence of network partitions, this object may still
   * exist after this timestamp, until an administrator or automated process can determine the
   * resource is fully terminated.
   * If not set, graceful deletion of the object has not been requested.
   *
   * Populated by the system when a graceful deletion is requested.
   * Read-only.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#metadata
   * +optional
   */
  deletionTimestamp?: string;
  /**
   * Must be empty before the object is deleted from the registry. Each entry
   * is an identifier for the responsible component that will remove the entry
   * from the list. If the deletionTimestamp of the object is non-nil, entries
   * in this list can only be removed.
   * Finalizers may be processed and removed in any order.  Order is NOT enforced
   * because it introduces significant risk of stuck finalizers.
   * finalizers is a shared field, any actor with permission can reorder it.
   * If the finalizer list is processed in order, then this can lead to a situation
   * in which the component responsible for the first finalizer in the list is
   * waiting for a signal (field value, external system, or other) produced by a
   * component responsible for a finalizer later in the list, resulting in a deadlock.
   * Without enforced ordering finalizers are free to order amongst themselves and
   * are not vulnerable to ordering changes in the list.
   * +optional
   * +patchStrategy=merge
   * +listType=set
   */
  finalizers?: Array<string>;
  /**
   * GenerateName is an optional prefix, used by the server, to generate a unique
   * name ONLY IF the Name field has not been provided.
   * If this field is used, the name returned to the client will be different
   * than the name passed. This value will also be combined with a unique suffix.
   * The provided value has the same validation rules as the Name field,
   * and may be truncated by the length of the suffix required to make the value
   * unique on the server.
   *
   * If this field is specified and the generated name exists, the server will return a 409.
   *
   * Applied only if Name is not specified.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#idempotency
   * +optional
   */
  generateName?: string;
  /**
   * A sequence number representing a specific generation of the desired state.
   * Populated by the system. Read-only.
   * +optional
   */
  generation?: number;
  /**
   * Map of string keys and values that can be used to organize and categorize
   * (scope and select) objects. May match selectors of replication controllers
   * and services.
   * More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/labels
   * +optional
   */
  labels?: {
    [key: string]: string;
  };
  /**
   * ManagedFields maps workflow-id and version to the set of fields
   * that are managed by that workflow. This is mostly for internal
   * housekeeping, and users typically shouldn't need to set or
   * understand this field. A workflow can be the user's name, a
   * controller's name, or the name of a specific apply path like
   * "ci-cd". The set of fields is always in the version that the
   * workflow used when modifying the object.
   *
   * +optional
   * +listType=atomic
   */
  managedFields?: Array<V1ManagedFieldsEntry>;
  /**
   * Name must be unique within a namespace. Is required when creating resources, although
   * some resources may allow a client to request the generation of an appropriate name
   * automatically. Name is primarily intended for creation idempotence and configuration
   * definition.
   * Cannot be updated.
   * More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/names#names
   * +optional
   */
  name?: string;
  /**
   * Namespace defines the space within which each name must be unique. An empty namespace is
   * equivalent to the "default" namespace, but "default" is the canonical representation.
   * Not all objects are required to be scoped to a namespace - the value of this field for
   * those objects will be empty.
   *
   * Must be a DNS_LABEL.
   * Cannot be updated.
   * More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/namespaces
   * +optional
   */
  namespace?: string;
  /**
   * List of objects depended by this object. If ALL objects in the list have
   * been deleted, this object will be garbage collected. If this object is managed by a controller,
   * then an entry in this list will point to this controller, with the controller field set to true.
   * There cannot be more than one managing controller.
   * +optional
   * +patchMergeKey=uid
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=uid
   */
  ownerReferences?: Array<V1OwnerReference>;
  /**
   * An opaque value that represents the internal version of this object that can
   * be used by clients to determine when objects have changed. May be used for optimistic
   * concurrency, change detection, and the watch operation on a resource or set of resources.
   * Clients must treat these values as opaque and passed unmodified back to the server.
   * They may only be valid for a particular resource or set of resources.
   *
   * Populated by the system.
   * Read-only.
   * Value must be treated as opaque by clients and .
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#concurrency-control-and-consistency
   * +optional
   */
  resourceVersion?: string;
  /**
   * Deprecated: selfLink is a legacy read-only field that is no longer populated by the system.
   * +optional
   */
  selfLink?: string;
  /**
   * UID is the unique in time and space value for this object. It is typically generated by
   * the server on successful creation of a resource and is not allowed to change on PUT
   * operations.
   *
   * Populated by the system.
   * Read-only.
   * More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/names#uids
   * +optional
   */
  uid?: string;
};

export type V1OwnerReference = {
  /**
   * API version of the referent.
   */
  apiVersion?: string;
  /**
   * If true, AND if the owner has the "foregroundDeletion" finalizer, then
   * the owner cannot be deleted from the key-value store until this
   * reference is removed.
   * See https://kubernetes.io/docs/concepts/architecture/garbage-collection/#foreground-deletion
   * for how the garbage collector interacts with this field and enforces the foreground deletion.
   * Defaults to false.
   * To set this field, a user needs "delete" permission of the owner,
   * otherwise 422 (Unprocessable Entity) will be returned.
   * +optional
   */
  blockOwnerDeletion?: boolean;
  /**
   * If true, this reference points to the managing controller.
   * +optional
   */
  controller?: boolean;
  /**
   * Kind of the referent.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
   */
  kind?: string;
  /**
   * Name of the referent.
   * More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/names#names
   */
  name?: string;
  /**
   * UID of the referent.
   * More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/names#uids
   */
  uid?: string;
};

export const V1PersistentVolumeAccessMode = {
  /**
   * ReadWriteOnce
   */
  READ_WRITE_ONCE: 'ReadWriteOnce',
  /**
   * ReadOnlyMany
   */
  READ_ONLY_MANY: 'ReadOnlyMany',
  /**
   * ReadWriteMany
   */
  READ_WRITE_MANY: 'ReadWriteMany',
  /**
   * ReadWriteOncePod
   */
  READ_WRITE_ONCE_POD: 'ReadWriteOncePod',
} as const;

export type V1PersistentVolumeAccessMode =
  (typeof V1PersistentVolumeAccessMode)[keyof typeof V1PersistentVolumeAccessMode];

export const V1PersistentVolumeClaimPhase = {
  /**
   * ClaimPending
   */
  CLAIM_PENDING: 'Pending',
  /**
   * ClaimBound
   */
  CLAIM_BOUND: 'Bound',
  /**
   * ClaimLost
   */
  CLAIM_LOST: 'Lost',
} as const;

export type V1PersistentVolumeClaimPhase =
  (typeof V1PersistentVolumeClaimPhase)[keyof typeof V1PersistentVolumeClaimPhase];

export type V1PersistentVolumeClaimSpec = {
  /**
   * accessModes contains the desired access modes the volume should have.
   * More info: https://kubernetes.io/docs/concepts/storage/persistent-volumes#access-modes-1
   * +optional
   * +listType=atomic
   */
  accessModes?: Array<V1PersistentVolumeAccessMode>;
  /**
   * dataSource field can be used to specify either:
   * * An existing VolumeSnapshot object (snapshot.storage.k8s.io/VolumeSnapshot)
   * * An existing PVC (PersistentVolumeClaim)
   * If the provisioner or an external controller can support the specified data source,
   * it will create a new volume based on the contents of the specified data source.
   * When the AnyVolumeDataSource feature gate is enabled, dataSource contents will be copied to dataSourceRef,
   * and dataSourceRef contents will be copied to dataSource when dataSourceRef.namespace is not specified.
   * If the namespace is specified, then dataSourceRef will not be copied to dataSource.
   * +optional
   */
  dataSource?: V1TypedLocalObjectReference;
  /**
   * dataSourceRef specifies the object from which to populate the volume with data, if a non-empty
   * volume is desired. This may be any object from a non-empty API group (non
   * core object) or a PersistentVolumeClaim object.
   * When this field is specified, volume binding will only succeed if the type of
   * the specified object matches some installed volume populator or dynamic
   * provisioner.
   * This field will replace the functionality of the dataSource field and as such
   * if both fields are non-empty, they must have the same value. For backwards
   * compatibility, when namespace isn't specified in dataSourceRef,
   * both fields (dataSource and dataSourceRef) will be set to the same
   * value automatically if one of them is empty and the other is non-empty.
   * When namespace is specified in dataSourceRef,
   * dataSource isn't set to the same value and must be empty.
   * There are three important differences between dataSource and dataSourceRef:
   * * While dataSource only allows two specific types of objects, dataSourceRef
   * allows any non-core object, as well as PersistentVolumeClaim objects.
   * * While dataSource ignores disallowed values (dropping them), dataSourceRef
   * preserves all values, and generates an error if a disallowed value is
   * specified.
   * * While dataSource only allows local objects, dataSourceRef allows objects
   * in any namespaces.
   * (Beta) Using this field requires the AnyVolumeDataSource feature gate to be enabled.
   * (Alpha) Using the namespace field of dataSourceRef requires the CrossNamespaceVolumeDataSource feature gate to be enabled.
   * +optional
   */
  dataSourceRef?: V1TypedObjectReference;
  /**
   * resources represents the minimum resources the volume should have.
   * Users are allowed to specify resource requirements
   * that are lower than previous value but must still be higher than capacity recorded in the
   * status field of the claim.
   * More info: https://kubernetes.io/docs/concepts/storage/persistent-volumes#resources
   * +optional
   */
  resources?: V1VolumeResourceRequirements;
  /**
   * selector is a label query over volumes to consider for binding.
   * +optional
   */
  selector?: V1LabelSelector;
  /**
   * storageClassName is the name of the StorageClass required by the claim.
   * More info: https://kubernetes.io/docs/concepts/storage/persistent-volumes#class-1
   * +optional
   */
  storageClassName?: string;
  /**
   * volumeAttributesClassName may be used to set the VolumeAttributesClass used by this claim.
   * If specified, the CSI driver will create or update the volume with the attributes defined
   * in the corresponding VolumeAttributesClass. This has a different purpose than storageClassName,
   * it can be changed after the claim is created. An empty string or nil value indicates that no
   * VolumeAttributesClass will be applied to the claim. If the claim enters an Infeasible error state,
   * this field can be reset to its previous value (including nil) to cancel the modification.
   * If the resource referred to by volumeAttributesClass does not exist, this PersistentVolumeClaim will be
   * set to a Pending state, as reflected by the modifyVolumeStatus field, until such as a resource
   * exists.
   * More info: https://kubernetes.io/docs/concepts/storage/volume-attributes-classes/
   * +featureGate=VolumeAttributesClass
   * +optional
   */
  volumeAttributesClassName?: string;
  /**
   * volumeMode defines what type of volume is required by the claim.
   * Value of Filesystem is implied when not included in claim spec.
   * +optional
   */
  volumeMode?: V1PersistentVolumeMode;
  /**
   * volumeName is the binding reference to the PersistentVolume backing this claim.
   * +optional
   */
  volumeName?: string;
};

export type V1PersistentVolumeClaimTemplate = {
  /**
   * May contain labels and annotations that will be copied into the PVC
   * when creating it. No other fields are allowed and will be rejected during
   * validation.
   *
   * +optional
   */
  metadata?: V1ObjectMeta;
  /**
   * The specification for the PersistentVolumeClaim. The entire content is
   * copied unchanged into the PVC that gets created from this
   * template. The same fields as in a PersistentVolumeClaim
   * are also valid here.
   */
  spec?: V1PersistentVolumeClaimSpec;
};

export type V1PersistentVolumeClaimVolumeSource = {
  /**
   * claimName is the name of a PersistentVolumeClaim in the same namespace as the pod using this volume.
   * More info: https://kubernetes.io/docs/concepts/storage/persistent-volumes#persistentvolumeclaims
   */
  claimName?: string;
  /**
   * readOnly Will force the ReadOnly setting in VolumeMounts.
   * Default false.
   * +optional
   */
  readOnly?: boolean;
};

export const V1PersistentVolumeMode = {
  /**
   * PersistentVolumeBlock
   */
  PERSISTENT_VOLUME_BLOCK: 'Block' /**
   * PersistentVolumeFilesystem
   */,
  PERSISTENT_VOLUME_FILESYSTEM: 'Filesystem',
} as const;

export type V1PersistentVolumeMode =
  (typeof V1PersistentVolumeMode)[keyof typeof V1PersistentVolumeMode];

export const V1PersistentVolumePhase = {
  /**
   * VolumePending
   */
  VOLUME_PENDING: 'Pending',
  /**
   * VolumeAvailable
   */
  VOLUME_AVAILABLE: 'Available',
  /**
   * VolumeBound
   */
  VOLUME_BOUND: 'Bound',
  /**
   * VolumeReleased
   */
  VOLUME_RELEASED: 'Released',
  /**
   * VolumeFailed
   */
  VOLUME_FAILED: 'Failed',
} as const;

export type V1PersistentVolumePhase =
  (typeof V1PersistentVolumePhase)[keyof typeof V1PersistentVolumePhase];

export const V1PersistentVolumeReclaimPolicy = {
  /**
   * PersistentVolumeReclaimRecycle
   */
  PERSISTENT_VOLUME_RECLAIM_RECYCLE: 'Recycle',
  /**
   * PersistentVolumeReclaimDelete
   */
  PERSISTENT_VOLUME_RECLAIM_DELETE: 'Delete',
  /**
   * PersistentVolumeReclaimRetain
   */
  PERSISTENT_VOLUME_RECLAIM_RETAIN: 'Retain',
} as const;

export type V1PersistentVolumeReclaimPolicy =
  (typeof V1PersistentVolumeReclaimPolicy)[keyof typeof V1PersistentVolumeReclaimPolicy];

export type V1PhotonPersistentDiskVolumeSource = {
  /**
   * fsType is the filesystem type to mount.
   * Must be a filesystem type supported by the host operating system.
   * Ex. "ext4", "xfs", "ntfs". Implicitly inferred to be "ext4" if unspecified.
   */
  fsType?: string;
  /**
   * pdID is the ID that identifies Photon Controller persistent disk
   */
  pdID?: string;
};

export type V1Platform = {
  /**
   * Architecture field specifies the CPU architecture, for example
   * `amd64` or `ppc64le`.
   */
  architecture?: string;
  /**
   * OS specifies the operating system, for example `linux` or `windows`.
   */
  os?: string;
  /**
   * OSFeatures is an optional field specifying an array of strings,
   * each listing a required OS feature (for example on Windows `win32k`).
   */
  'os.features'?: Array<string>;
  /**
   * OSVersion is an optional field specifying the operating system
   * version, for example on Windows `10.0.14393.1066`.
   */
  'os.version'?: string;
  /**
   * Variant is an optional field specifying a variant of the CPU, for
   * example `v7` to specify ARMv7 when architecture is `arm`.
   */
  variant?: string;
};

export type V1Pod = {
  /**
   * APIVersion defines the versioned schema of this representation of an object.
   * Servers should convert recognized schemas to the latest internal value, and
   * may reject unrecognized values.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#resources
   * +optional
   */
  apiVersion?: string;
  /**
   * Kind is a string value representing the REST resource this object represents.
   * Servers may infer this from the endpoint the client submits requests to.
   * Cannot be updated.
   * In CamelCase.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
   * +optional
   */
  kind?: string;
  /**
   * Standard object's metadata.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#metadata
   * +optional
   */
  metadata?: V1ObjectMeta;
  /**
   * Specification of the desired behavior of the pod.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#spec-and-status
   * +optional
   */
  spec?: V1PodSpec;
  /**
   * Most recently observed status of the pod.
   * This data may not be up to date.
   * Populated by the system.
   * Read-only.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#spec-and-status
   * +optional
   */
  status?: V1PodStatus;
};

export type V1PodAffinity = {
  /**
   * The scheduler will prefer to schedule pods to nodes that satisfy
   * the affinity expressions specified by this field, but it may choose
   * a node that violates one or more of the expressions. The node that is
   * most preferred is the one with the greatest sum of weights, i.e.
   * for each node that meets all of the scheduling requirements (resource
   * request, requiredDuringScheduling affinity expressions, etc.),
   * compute a sum by iterating through the elements of this field and adding
   * "weight" to the sum if the node has pods which matches the corresponding podAffinityTerm; the
   * node(s) with the highest sum are the most preferred.
   * +optional
   * +listType=atomic
   */
  preferredDuringSchedulingIgnoredDuringExecution?: Array<V1WeightedPodAffinityTerm>;
  /**
   * If the affinity requirements specified by this field are not met at
   * scheduling time, the pod will not be scheduled onto the node.
   * If the affinity requirements specified by this field cease to be met
   * at some point during pod execution (e.g. due to a pod label update), the
   * system may or may not try to eventually evict the pod from its node.
   * When there are multiple elements, the lists of nodes corresponding to each
   * podAffinityTerm are intersected, i.e. all terms must be satisfied.
   * +optional
   * +listType=atomic
   */
  requiredDuringSchedulingIgnoredDuringExecution?: Array<V1PodAffinityTerm>;
};

export type V1PodAffinityTerm = {
  /**
   * A label query over a set of resources, in this case pods.
   * If it's null, this PodAffinityTerm matches with no Pods.
   * +optional
   */
  labelSelector?: V1LabelSelector;
  /**
   * MatchLabelKeys is a set of pod label keys to select which pods will
   * be taken into consideration. The keys are used to lookup values from the
   * incoming pod labels, those key-value labels are merged with `labelSelector` as `key in (value)`
   * to select the group of existing pods which pods will be taken into consideration
   * for the incoming pod's pod (anti) affinity. Keys that don't exist in the incoming
   * pod labels will be ignored. The default value is empty.
   * The same key is forbidden to exist in both matchLabelKeys and labelSelector.
   * Also, matchLabelKeys cannot be set when labelSelector isn't set.
   *
   * +listType=atomic
   * +optional
   */
  matchLabelKeys?: Array<string>;
  /**
   * MismatchLabelKeys is a set of pod label keys to select which pods will
   * be taken into consideration. The keys are used to lookup values from the
   * incoming pod labels, those key-value labels are merged with `labelSelector` as `key notin (value)`
   * to select the group of existing pods which pods will be taken into consideration
   * for the incoming pod's pod (anti) affinity. Keys that don't exist in the incoming
   * pod labels will be ignored. The default value is empty.
   * The same key is forbidden to exist in both mismatchLabelKeys and labelSelector.
   * Also, mismatchLabelKeys cannot be set when labelSelector isn't set.
   *
   * +listType=atomic
   * +optional
   */
  mismatchLabelKeys?: Array<string>;
  /**
   * A label query over the set of namespaces that the term applies to.
   * The term is applied to the union of the namespaces selected by this field
   * and the ones listed in the namespaces field.
   * null selector and null or empty namespaces list means "this pod's namespace".
   * An empty selector ({}) matches all namespaces.
   * +optional
   */
  namespaceSelector?: V1LabelSelector;
  /**
   * namespaces specifies a static list of namespace names that the term applies to.
   * The term is applied to the union of the namespaces listed in this field
   * and the ones selected by namespaceSelector.
   * null or empty namespaces list and null namespaceSelector means "this pod's namespace".
   * +optional
   * +listType=atomic
   */
  namespaces?: Array<string>;
  /**
   * This pod should be co-located (affinity) or not co-located (anti-affinity) with the pods matching
   * the labelSelector in the specified namespaces, where co-located is defined as running on a node
   * whose value of the label with key topologyKey matches that of any node on which any of the
   * selected pods is running.
   * Empty topologyKey is not allowed.
   */
  topologyKey?: string;
};

export type V1PodAntiAffinity = {
  /**
   * The scheduler will prefer to schedule pods to nodes that satisfy
   * the anti-affinity expressions specified by this field, but it may choose
   * a node that violates one or more of the expressions. The node that is
   * most preferred is the one with the greatest sum of weights, i.e.
   * for each node that meets all of the scheduling requirements (resource
   * request, requiredDuringScheduling anti-affinity expressions, etc.),
   * compute a sum by iterating through the elements of this field and subtracting
   * "weight" from the sum if the node has pods which matches the corresponding podAffinityTerm; the
   * node(s) with the highest sum are the most preferred.
   * +optional
   * +listType=atomic
   */
  preferredDuringSchedulingIgnoredDuringExecution?: Array<V1WeightedPodAffinityTerm>;
  /**
   * If the anti-affinity requirements specified by this field are not met at
   * scheduling time, the pod will not be scheduled onto the node.
   * If the anti-affinity requirements specified by this field cease to be met
   * at some point during pod execution (e.g. due to a pod label update), the
   * system may or may not try to eventually evict the pod from its node.
   * When there are multiple elements, the lists of nodes corresponding to each
   * podAffinityTerm are intersected, i.e. all terms must be satisfied.
   * +optional
   * +listType=atomic
   */
  requiredDuringSchedulingIgnoredDuringExecution?: Array<V1PodAffinityTerm>;
};

export type V1PodCertificateProjection = {
  /**
   * Write the certificate chain at this path in the projected volume.
   *
   * Most applications should use credentialBundlePath.  When using keyPath
   * and certificateChainPath, your application needs to check that the key
   * and leaf certificate are consistent, because it is possible to read the
   * files mid-rotation.
   *
   * +optional
   */
  certificateChainPath?: string;
  /**
   * Write the credential bundle at this path in the projected volume.
   *
   * The credential bundle is a single file that contains multiple PEM blocks.
   * The first PEM block is a PRIVATE KEY block, containing a PKCS#8 private
   * key.
   *
   * The remaining blocks are CERTIFICATE blocks, containing the issued
   * certificate chain from the signer (leaf and any intermediates).
   *
   * Using credentialBundlePath lets your Pod's application code make a single
   * atomic read that retrieves a consistent key and certificate chain.  If you
   * project them to separate files, your application code will need to
   * additionally check that the leaf certificate was issued to the key.
   *
   * +optional
   */
  credentialBundlePath?: string;
  /**
   * Write the key at this path in the projected volume.
   *
   * Most applications should use credentialBundlePath.  When using keyPath
   * and certificateChainPath, your application needs to check that the key
   * and leaf certificate are consistent, because it is possible to read the
   * files mid-rotation.
   *
   * +optional
   */
  keyPath?: string;
  /**
   * The type of keypair Kubelet will generate for the pod.
   *
   * Valid values are "RSA3072", "RSA4096", "ECDSAP256", "ECDSAP384",
   * "ECDSAP521", and "ED25519".
   *
   * +required
   */
  keyType?: string;
  /**
   * maxExpirationSeconds is the maximum lifetime permitted for the
   * certificate.
   *
   * Kubelet copies this value verbatim into the PodCertificateRequests it
   * generates for this projection.
   *
   * If omitted, kube-apiserver will set it to 86400(24 hours). kube-apiserver
   * will reject values shorter than 3600 (1 hour).  The maximum allowable
   * value is 7862400 (91 days).
   *
   * The signer implementation is then free to issue a certificate with any
   * lifetime *shorter* than MaxExpirationSeconds, but no shorter than 3600
   * seconds (1 hour).  This constraint is enforced by kube-apiserver.
   * `kubernetes.io` signers will never issue certificates with a lifetime
   * longer than 24 hours.
   *
   * +optional
   */
  maxExpirationSeconds?: number;
  /**
   * Kubelet's generated CSRs will be addressed to this signer.
   *
   * +required
   */
  signerName?: string;
  /**
   * userAnnotations allow pod authors to pass additional information to
   * the signer implementation.  Kubernetes does not restrict or validate this
   * metadata in any way.
   *
   * These values are copied verbatim into the `spec.unverifiedUserAnnotations` field of
   * the PodCertificateRequest objects that Kubelet creates.
   *
   * Entries are subject to the same validation as object metadata annotations,
   * with the addition that all keys must be domain-prefixed. No restrictions
   * are placed on values, except an overall size limitation on the entire field.
   *
   * Signers should document the keys and values they support. Signers should
   * deny requests that contain keys they do not recognize.
   */
  userAnnotations?: {
    [key: string]: string;
  };
};

export type V1PodCondition = {
  /**
   * Last time we probed the condition.
   * +optional
   */
  lastProbeTime?: string;
  /**
   * Last time the condition transitioned from one status to another.
   * +optional
   */
  lastTransitionTime?: string;
  /**
   * Human-readable message indicating details about last transition.
   * +optional
   */
  message?: string;
  /**
   * If set, this represents the .metadata.generation that the pod condition was set based upon.
   * The PodObservedGenerationTracking feature gate must be enabled to use this field.
   * +featureGate=PodObservedGenerationTracking
   * +optional
   */
  observedGeneration?: number;
  /**
   * Unique, one-word, CamelCase reason for the condition's last transition.
   * +optional
   */
  reason?: string;
  /**
   * Status is the status of the condition.
   * Can be True, False, Unknown.
   * More info: https://kubernetes.io/docs/concepts/workloads/pods/pod-lifecycle#pod-conditions
   */
  status?: K8sIoApiCoreV1ConditionStatus;
  /**
   * Type is the type of the condition.
   * More info: https://kubernetes.io/docs/concepts/workloads/pods/pod-lifecycle#pod-conditions
   */
  type?: V1PodConditionType;
};

export const V1PodConditionType = {
  /**
   * ContainersReady
   */
  CONTAINERS_READY: 'ContainersReady',
  /**
   * PodInitialized
   */
  POD_INITIALIZED: 'Initialized',
  /**
   * PodReady
   */
  POD_READY: 'Ready',
  /**
   * PodScheduled
   */
  POD_SCHEDULED: 'PodScheduled',
  /**
   * DisruptionTarget
   */
  DISRUPTION_TARGET: 'DisruptionTarget',
  /**
   * PodReadyToStartContainers
   */
  POD_READY_TO_START_CONTAINERS: 'PodReadyToStartContainers',
  /**
   * PodResizePending
   */
  POD_RESIZE_PENDING: 'PodResizePending',
  /**
   * PodResizeInProgress
   */
  POD_RESIZE_IN_PROGRESS: 'PodResizeInProgress',
  /**
   * AllContainersRestarting
   */
  ALL_CONTAINERS_RESTARTING: 'AllContainersRestarting',
} as const;

export type V1PodConditionType =
  (typeof V1PodConditionType)[keyof typeof V1PodConditionType];

export type V1PodDnsConfig = {
  /**
   * A list of DNS name server IP addresses.
   * This will be appended to the base nameservers generated from DNSPolicy.
   * Duplicated nameservers will be removed.
   * +optional
   * +listType=atomic
   */
  nameservers?: Array<string>;
  /**
   * A list of DNS resolver options.
   * This will be merged with the base options generated from DNSPolicy.
   * Duplicated entries will be removed. Resolution options given in Options
   * will override those that appear in the base DNSPolicy.
   * +optional
   * +listType=atomic
   */
  options?: Array<V1PodDnsConfigOption>;
  /**
   * A list of DNS search domains for host-name lookup.
   * This will be appended to the base search paths generated from DNSPolicy.
   * Duplicated search paths will be removed.
   * +optional
   * +listType=atomic
   */
  searches?: Array<string>;
};

export type V1PodDnsConfigOption = {
  /**
   * Name is this DNS resolver option's name.
   * Required.
   */
  name?: string;
  /**
   * Value is this DNS resolver option's value.
   * +optional
   */
  value?: string;
};

export type V1PodExtendedResourceClaimStatus = {
  /**
   * RequestMappings identifies the mapping of <container, extended resource backed by DRA> to  device request
   * in the generated ResourceClaim.
   * +listType=atomic
   */
  requestMappings?: Array<V1ContainerExtendedResourceRequest>;
  /**
   * ResourceClaimName is the name of the ResourceClaim that was
   * generated for the Pod in the namespace of the Pod.
   */
  resourceClaimName?: string;
};

export const V1PodFsGroupChangePolicy = {
  /**
   * FSGroupChangeOnRootMismatch
   */
  FS_GROUP_CHANGE_ON_ROOT_MISMATCH: 'OnRootMismatch' /**
   * FSGroupChangeAlways
   */,
  FS_GROUP_CHANGE_ALWAYS: 'Always',
} as const;

export type V1PodFsGroupChangePolicy =
  (typeof V1PodFsGroupChangePolicy)[keyof typeof V1PodFsGroupChangePolicy];

export type V1PodIp = {
  /**
   * IP is the IP address assigned to the pod
   * +required
   */
  ip?: string;
};

export type V1PodOs = {
  /**
   * Name is the name of the operating system. The currently supported values are linux and windows.
   * Additional value may be defined in future and can be one of:
   * https://github.com/opencontainers/runtime-spec/blob/master/config.md#platform-specific-configuration
   * Clients should expect to handle additional values and treat unrecognized values in this field as os: null
   */
  name?: V1OsName;
};

export const V1PodPhase = {
  /**
   * PodPending
   */
  POD_PENDING: 'Pending',
  /**
   * PodRunning
   */
  POD_RUNNING: 'Running',
  /**
   * PodSucceeded
   */
  POD_SUCCEEDED: 'Succeeded',
  /**
   * PodFailed
   */
  POD_FAILED: 'Failed',
  /**
   * PodUnknown
   */
  POD_UNKNOWN: 'Unknown',
} as const;

export type V1PodPhase = (typeof V1PodPhase)[keyof typeof V1PodPhase];

export const V1PodQosClass = {
  /**
   * PodQOSGuaranteed
   */
  POD_QOS_GUARANTEED: 'Guaranteed',
  /**
   * PodQOSBurstable
   */
  POD_QOS_BURSTABLE: 'Burstable',
  /**
   * PodQOSBestEffort
   */
  POD_QOS_BEST_EFFORT: 'BestEffort',
} as const;

export type V1PodQosClass = (typeof V1PodQosClass)[keyof typeof V1PodQosClass];

export type V1PodReadinessGate = {
  /**
   * ConditionType refers to a condition in the pod's condition list with matching type.
   */
  conditionType?: V1PodConditionType;
};

export const V1PodResizeStatus = {
  /**
   * PodResizeStatusInProgress
   */
  POD_RESIZE_STATUS_IN_PROGRESS: 'InProgress',
  /**
   * PodResizeStatusDeferred
   */
  POD_RESIZE_STATUS_DEFERRED: 'Deferred',
  /**
   * PodResizeStatusInfeasible
   */
  POD_RESIZE_STATUS_INFEASIBLE: 'Infeasible',
} as const;

export type V1PodResizeStatus =
  (typeof V1PodResizeStatus)[keyof typeof V1PodResizeStatus];

export type V1PodResourceClaim = {
  /**
   * Name uniquely identifies this resource claim inside the pod.
   * This must be a DNS_LABEL.
   */
  name?: string;
  /**
   * ResourceClaimName is the name of a ResourceClaim object in the same
   * namespace as this pod.
   *
   * Exactly one of ResourceClaimName and ResourceClaimTemplateName must
   * be set.
   */
  resourceClaimName?: string;
  /**
   * ResourceClaimTemplateName is the name of a ResourceClaimTemplate
   * object in the same namespace as this pod.
   *
   * The template will be used to create a new ResourceClaim, which will
   * be bound to this pod. When this pod is deleted, the ResourceClaim
   * will also be deleted. The pod name and resource name, along with a
   * generated component, will be used to form a unique name for the
   * ResourceClaim, which will be recorded in pod.status.resourceClaimStatuses.
   *
   * This field is immutable and no changes will be made to the
   * corresponding ResourceClaim by the control plane after creating the
   * ResourceClaim.
   *
   * Exactly one of ResourceClaimName and ResourceClaimTemplateName must
   * be set.
   */
  resourceClaimTemplateName?: string;
};

export type V1PodResourceClaimStatus = {
  /**
   * Name uniquely identifies this resource claim inside the pod.
   * This must match the name of an entry in pod.spec.resourceClaims,
   * which implies that the string must be a DNS_LABEL.
   */
  name?: string;
  /**
   * ResourceClaimName is the name of the ResourceClaim that was
   * generated for the Pod in the namespace of the Pod. If this is
   * unset, then generating a ResourceClaim was not necessary. The
   * pod.spec.resourceClaims entry can be ignored in this case.
   *
   * +optional
   */
  resourceClaimName?: string;
};

export const V1PodSeLinuxChangePolicy = {
  /**
   * SELinuxChangePolicyRecursive
   */
  SE_LINUX_CHANGE_POLICY_RECURSIVE: 'Recursive' /**
   * SELinuxChangePolicyMountOption
   */,
  SE_LINUX_CHANGE_POLICY_MOUNT_OPTION: 'MountOption',
} as const;

export type V1PodSeLinuxChangePolicy =
  (typeof V1PodSeLinuxChangePolicy)[keyof typeof V1PodSeLinuxChangePolicy];

export type V1PodSchedulingGate = {
  /**
   * Name of the scheduling gate.
   * Each scheduling gate must have a unique name field.
   */
  name?: string;
};

export type V1PodSecurityContext = {
  /**
   * appArmorProfile is the AppArmor options to use by the containers in this pod.
   * Note that this field cannot be set when spec.os.name is windows.
   * +optional
   */
  appArmorProfile?: V1AppArmorProfile;
  /**
   * A special supplemental group that applies to all containers in a pod.
   * Some volume types allow the Kubelet to change the ownership of that volume
   * to be owned by the pod:
   *
   * 1. The owning GID will be the FSGroup
   * 2. The setgid bit is set (new files created in the volume will be owned by FSGroup)
   * 3. The permission bits are OR'd with rw-rw----
   *
   * If unset, the Kubelet will not modify the ownership and permissions of any volume.
   * Note that this field cannot be set when spec.os.name is windows.
   * +optional
   */
  fsGroup?: number;
  /**
   * fsGroupChangePolicy defines behavior of changing ownership and permission of the volume
   * before being exposed inside Pod. This field will only apply to
   * volume types which support fsGroup based ownership(and permissions).
   * It will have no effect on ephemeral volume types such as: secret, configmaps
   * and emptydir.
   * Valid values are "OnRootMismatch" and "Always". If not specified, "Always" is used.
   * Note that this field cannot be set when spec.os.name is windows.
   * +optional
   */
  fsGroupChangePolicy?: V1PodFsGroupChangePolicy;
  /**
   * The GID to run the entrypoint of the container process.
   * Uses runtime default if unset.
   * May also be set in SecurityContext.  If set in both SecurityContext and
   * PodSecurityContext, the value specified in SecurityContext takes precedence
   * for that container.
   * Note that this field cannot be set when spec.os.name is windows.
   * +optional
   */
  runAsGroup?: number;
  /**
   * Indicates that the container must run as a non-root user.
   * If true, the Kubelet will validate the image at runtime to ensure that it
   * does not run as UID 0 (root) and fail to start the container if it does.
   * If unset or false, no such validation will be performed.
   * May also be set in SecurityContext.  If set in both SecurityContext and
   * PodSecurityContext, the value specified in SecurityContext takes precedence.
   * +optional
   */
  runAsNonRoot?: boolean;
  /**
   * The UID to run the entrypoint of the container process.
   * Defaults to user specified in image metadata if unspecified.
   * May also be set in SecurityContext.  If set in both SecurityContext and
   * PodSecurityContext, the value specified in SecurityContext takes precedence
   * for that container.
   * Note that this field cannot be set when spec.os.name is windows.
   * +optional
   */
  runAsUser?: number;
  /**
   * seLinuxChangePolicy defines how the container's SELinux label is applied to all volumes used by the Pod.
   * It has no effect on nodes that do not support SELinux or to volumes does not support SELinux.
   * Valid values are "MountOption" and "Recursive".
   *
   * "Recursive" means relabeling of all files on all Pod volumes by the container runtime.
   * This may be slow for large volumes, but allows mixing privileged and unprivileged Pods sharing the same volume on the same node.
   *
   * "MountOption" mounts all eligible Pod volumes with `-o context` mount option.
   * This requires all Pods that share the same volume to use the same SELinux label.
   * It is not possible to share the same volume among privileged and unprivileged Pods.
   * Eligible volumes are in-tree FibreChannel and iSCSI volumes, and all CSI volumes
   * whose CSI driver announces SELinux support by setting spec.seLinuxMount: true in their
   * CSIDriver instance. Other volumes are always re-labelled recursively.
   * "MountOption" value is allowed only when SELinuxMount feature gate is enabled.
   *
   * If not specified and SELinuxMount feature gate is enabled, "MountOption" is used.
   * If not specified and SELinuxMount feature gate is disabled, "MountOption" is used for ReadWriteOncePod volumes
   * and "Recursive" for all other volumes.
   *
   * This field affects only Pods that have SELinux label set, either in PodSecurityContext or in SecurityContext of all containers.
   *
   * All Pods that use the same volume should use the same seLinuxChangePolicy, otherwise some pods can get stuck in ContainerCreating state.
   * Note that this field cannot be set when spec.os.name is windows.
   * +featureGate=SELinuxChangePolicy
   * +optional
   */
  seLinuxChangePolicy?: V1PodSeLinuxChangePolicy;
  /**
   * The SELinux context to be applied to all containers.
   * If unspecified, the container runtime will allocate a random SELinux context for each
   * container.  May also be set in SecurityContext.  If set in
   * both SecurityContext and PodSecurityContext, the value specified in SecurityContext
   * takes precedence for that container.
   * Note that this field cannot be set when spec.os.name is windows.
   * +optional
   */
  seLinuxOptions?: V1SeLinuxOptions;
  /**
   * The seccomp options to use by the containers in this pod.
   * Note that this field cannot be set when spec.os.name is windows.
   * +optional
   */
  seccompProfile?: V1SeccompProfile;
  /**
   * A list of groups applied to the first process run in each container, in
   * addition to the container's primary GID and fsGroup (if specified).  If
   * the SupplementalGroupsPolicy feature is enabled, the
   * supplementalGroupsPolicy field determines whether these are in addition
   * to or instead of any group memberships defined in the container image.
   * If unspecified, no additional groups are added, though group memberships
   * defined in the container image may still be used, depending on the
   * supplementalGroupsPolicy field.
   * Note that this field cannot be set when spec.os.name is windows.
   * +optional
   * +listType=atomic
   */
  supplementalGroups?: Array<number>;
  /**
   * Defines how supplemental groups of the first container processes are calculated.
   * Valid values are "Merge" and "Strict". If not specified, "Merge" is used.
   * (Alpha) Using the field requires the SupplementalGroupsPolicy feature gate to be enabled
   * and the container runtime must implement support for this feature.
   * Note that this field cannot be set when spec.os.name is windows.
   * TODO: update the default value to "Merge" when spec.os.name is not windows in v1.34
   * +featureGate=SupplementalGroupsPolicy
   * +optional
   */
  supplementalGroupsPolicy?: V1SupplementalGroupsPolicy;
  /**
   * Sysctls hold a list of namespaced sysctls used for the pod. Pods with unsupported
   * sysctls (by the container runtime) might fail to launch.
   * Note that this field cannot be set when spec.os.name is windows.
   * +optional
   * +listType=atomic
   */
  sysctls?: Array<V1Sysctl>;
  /**
   * The Windows specific settings applied to all containers.
   * If unspecified, the options within a container's SecurityContext will be used.
   * If set in both SecurityContext and PodSecurityContext, the value specified in SecurityContext takes precedence.
   * Note that this field cannot be set when spec.os.name is linux.
   * +optional
   */
  windowsOptions?: V1WindowsSecurityContextOptions;
};

export type V1PodSpec = {
  /**
   * Optional duration in seconds the pod may be active on the node relative to
   * StartTime before the system will actively try to mark it failed and kill associated containers.
   * Value must be a positive integer.
   * +optional
   */
  activeDeadlineSeconds?: number;
  /**
   * If specified, the pod's scheduling constraints
   * +optional
   */
  affinity?: V1Affinity;
  /**
   * AutomountServiceAccountToken indicates whether a service account token should be automatically mounted.
   * +optional
   */
  automountServiceAccountToken?: boolean;
  /**
   * List of containers belonging to the pod.
   * Containers cannot currently be added or removed.
   * There must be at least one container in a Pod.
   * Cannot be updated.
   * +patchMergeKey=name
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=name
   */
  containers?: Array<V1Container>;
  /**
   * Specifies the DNS parameters of a pod.
   * Parameters specified here will be merged to the generated DNS
   * configuration based on DNSPolicy.
   * +optional
   */
  dnsConfig?: V1PodDnsConfig;
  /**
   * Set DNS policy for the pod.
   * Defaults to "ClusterFirst".
   * Valid values are 'ClusterFirstWithHostNet', 'ClusterFirst', 'Default' or 'None'.
   * DNS parameters given in DNSConfig will be merged with the policy selected with DNSPolicy.
   * To have DNS options set along with hostNetwork, you have to specify DNS policy
   * explicitly to 'ClusterFirstWithHostNet'.
   * +optional
   */
  dnsPolicy?: V1DnsPolicy;
  /**
   * EnableServiceLinks indicates whether information about services should be injected into pod's
   * environment variables, matching the syntax of Docker links.
   * Optional: Defaults to true.
   * +optional
   */
  enableServiceLinks?: boolean;
  /**
   * List of ephemeral containers run in this pod. Ephemeral containers may be run in an existing
   * pod to perform user-initiated actions such as debugging. This list cannot be specified when
   * creating a pod, and it cannot be modified by updating the pod spec. In order to add an
   * ephemeral container to an existing pod, use the pod's ephemeralcontainers subresource.
   * +optional
   * +patchMergeKey=name
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=name
   */
  ephemeralContainers?: Array<V1EphemeralContainer>;
  /**
   * HostAliases is an optional list of hosts and IPs that will be injected into the pod's hosts
   * file if specified.
   * +optional
   * +patchMergeKey=ip
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=ip
   */
  hostAliases?: Array<V1HostAlias>;
  /**
   * Use the host's ipc namespace.
   * Optional: Default to false.
   * +k8s:conversion-gen=false
   * +optional
   */
  hostIPC?: boolean;
  /**
   * Host networking requested for this pod. Use the host's network namespace.
   * When using HostNetwork you should specify ports so the scheduler is aware.
   * When `hostNetwork` is true, specified `hostPort` fields in port definitions must match `containerPort`,
   * and unspecified `hostPort` fields in port definitions are defaulted to match `containerPort`.
   * Default to false.
   * +k8s:conversion-gen=false
   * +optional
   */
  hostNetwork?: boolean;
  /**
   * Use the host's pid namespace.
   * Optional: Default to false.
   * +k8s:conversion-gen=false
   * +optional
   */
  hostPID?: boolean;
  /**
   * Use the host's user namespace.
   * Optional: Default to true.
   * If set to true or not present, the pod will be run in the host user namespace, useful
   * for when the pod needs a feature only available to the host user namespace, such as
   * loading a kernel module with CAP_SYS_MODULE.
   * When set to false, a new userns is created for the pod. Setting false is useful for
   * mitigating container breakout vulnerabilities even allowing users to run their
   * containers as root without actually having root privileges on the host.
   * This field is alpha-level and is only honored by servers that enable the UserNamespacesSupport feature.
   * +k8s:conversion-gen=false
   * +optional
   */
  hostUsers?: boolean;
  /**
   * Specifies the hostname of the Pod
   * If not specified, the pod's hostname will be set to a system-defined value.
   * +optional
   */
  hostname?: string;
  /**
   * HostnameOverride specifies an explicit override for the pod's hostname as perceived by the pod.
   * This field only specifies the pod's hostname and does not affect its DNS records.
   * When this field is set to a non-empty string:
   * - It takes precedence over the values set in `hostname` and `subdomain`.
   * - The Pod's hostname will be set to this value.
   * - `setHostnameAsFQDN` must be nil or set to false.
   * - `hostNetwork` must be set to false.
   *
   * This field must be a valid DNS subdomain as defined in RFC 1123 and contain at most 64 characters.
   * Requires the HostnameOverride feature gate to be enabled.
   *
   * +featureGate=HostnameOverride
   * +optional
   */
  hostnameOverride?: string;
  /**
   * ImagePullSecrets is an optional list of references to secrets in the same namespace to use for pulling any of the images used by this PodSpec.
   * If specified, these secrets will be passed to individual puller implementations for them to use.
   * More info: https://kubernetes.io/docs/concepts/containers/images#specifying-imagepullsecrets-on-a-pod
   * +optional
   * +patchMergeKey=name
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=name
   */
  imagePullSecrets?: Array<K8sIoApiCoreV1LocalObjectReference>;
  /**
   * List of initialization containers belonging to the pod.
   * Init containers are executed in order prior to containers being started. If any
   * init container fails, the pod is considered to have failed and is handled according
   * to its restartPolicy. The name for an init container or normal container must be
   * unique among all containers.
   * Init containers may not have Lifecycle actions, Readiness probes, Liveness probes, or Startup probes.
   * The resourceRequirements of an init container are taken into account during scheduling
   * by finding the highest request/limit for each resource type, and then using the max of
   * that value or the sum of the normal containers. Limits are applied to init containers
   * in a similar fashion.
   * Init containers cannot currently be added or removed.
   * Cannot be updated.
   * More info: https://kubernetes.io/docs/concepts/workloads/pods/init-containers/
   * +patchMergeKey=name
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=name
   */
  initContainers?: Array<V1Container>;
  /**
   * NodeName indicates in which node this pod is scheduled.
   * If empty, this pod is a candidate for scheduling by the scheduler defined in schedulerName.
   * Once this field is set, the kubelet for this node becomes responsible for the lifecycle of this pod.
   * This field should not be used to express a desire for the pod to be scheduled on a specific node.
   * https://kubernetes.io/docs/concepts/scheduling-eviction/assign-pod-node/#nodename
   * +optional
   */
  nodeName?: string;
  /**
   * NodeSelector is a selector which must be true for the pod to fit on a node.
   * Selector which must match a node's labels for the pod to be scheduled on that node.
   * More info: https://kubernetes.io/docs/concepts/configuration/assign-pod-node/
   * +optional
   * +mapType=atomic
   */
  nodeSelector?: {
    [key: string]: string;
  };
  /**
   * Specifies the OS of the containers in the pod.
   * Some pod and container fields are restricted if this is set.
   *
   * If the OS field is set to linux, the following fields must be unset:
   * -securityContext.windowsOptions
   *
   * If the OS field is set to windows, following fields must be unset:
   * - spec.hostPID
   * - spec.hostIPC
   * - spec.hostUsers
   * - spec.resources
   * - spec.securityContext.appArmorProfile
   * - spec.securityContext.seLinuxOptions
   * - spec.securityContext.seccompProfile
   * - spec.securityContext.fsGroup
   * - spec.securityContext.fsGroupChangePolicy
   * - spec.securityContext.sysctls
   * - spec.shareProcessNamespace
   * - spec.securityContext.runAsUser
   * - spec.securityContext.runAsGroup
   * - spec.securityContext.supplementalGroups
   * - spec.securityContext.supplementalGroupsPolicy
   * - spec.containers[*].securityContext.appArmorProfile
   * - spec.containers[*].securityContext.seLinuxOptions
   * - spec.containers[*].securityContext.seccompProfile
   * - spec.containers[*].securityContext.capabilities
   * - spec.containers[*].securityContext.readOnlyRootFilesystem
   * - spec.containers[*].securityContext.privileged
   * - spec.containers[*].securityContext.allowPrivilegeEscalation
   * - spec.containers[*].securityContext.procMount
   * - spec.containers[*].securityContext.runAsUser
   * - spec.containers[*].securityContext.runAsGroup
   * +optional
   */
  os?: V1PodOs;
  /**
   * Overhead represents the resource overhead associated with running a pod for a given RuntimeClass.
   * This field will be autopopulated at admission time by the RuntimeClass admission controller. If
   * the RuntimeClass admission controller is enabled, overhead must not be set in Pod create requests.
   * The RuntimeClass admission controller will reject Pod create requests which have the overhead already
   * set. If RuntimeClass is configured and selected in the PodSpec, Overhead will be set to the value
   * defined in the corresponding RuntimeClass, otherwise it will remain unset and treated as zero.
   * More info: https://git.k8s.io/enhancements/keps/sig-node/688-pod-overhead/README.md
   * +optional
   */
  overhead?: V1ResourceList;
  /**
   * PreemptionPolicy is the Policy for preempting pods with lower priority.
   * One of Never, PreemptLowerPriority.
   * Defaults to PreemptLowerPriority if unset.
   * +optional
   */
  preemptionPolicy?: V1PreemptionPolicy;
  /**
   * The priority value. Various system components use this field to find the
   * priority of the pod. When Priority Admission Controller is enabled, it
   * prevents users from setting this field. The admission controller populates
   * this field from PriorityClassName.
   * The higher the value, the higher the priority.
   * +optional
   */
  priority?: number;
  /**
   * If specified, indicates the pod's priority. "system-node-critical" and
   * "system-cluster-critical" are two special keywords which indicate the
   * highest priorities with the former being the highest priority. Any other
   * name must be defined by creating a PriorityClass object with that name.
   * If not specified, the pod priority will be default or zero if there is no
   * default.
   * +optional
   */
  priorityClassName?: string;
  /**
   * If specified, all readiness gates will be evaluated for pod readiness.
   * A pod is ready when all its containers are ready AND
   * all conditions specified in the readiness gates have status equal to "True"
   * More info: https://git.k8s.io/enhancements/keps/sig-network/580-pod-readiness-gates
   * +optional
   * +listType=atomic
   */
  readinessGates?: Array<V1PodReadinessGate>;
  /**
   * ResourceClaims defines which ResourceClaims must be allocated
   * and reserved before the Pod is allowed to start. The resources
   * will be made available to those containers which consume them
   * by name.
   *
   * This is a stable field but requires that the
   * DynamicResourceAllocation feature gate is enabled.
   *
   * This field is immutable.
   *
   * +patchMergeKey=name
   * +patchStrategy=merge,retainKeys
   * +listType=map
   * +listMapKey=name
   * +featureGate=DynamicResourceAllocation
   * +optional
   */
  resourceClaims?: Array<V1PodResourceClaim>;
  /**
   * Resources is the total amount of CPU and Memory resources required by all
   * containers in the pod. It supports specifying Requests and Limits for
   * "cpu", "memory" and "hugepages-" resource names only. ResourceClaims are not supported.
   *
   * This field enables fine-grained control over resource allocation for the
   * entire pod, allowing resource sharing among containers in a pod.
   * TODO: For beta graduation, expand this comment with a detailed explanation.
   *
   * This is an alpha field and requires enabling the PodLevelResources feature
   * gate.
   *
   * +featureGate=PodLevelResources
   * +optional
   */
  resources?: V1ResourceRequirements;
  /**
   * Restart policy for all containers within the pod.
   * One of Always, OnFailure, Never. In some contexts, only a subset of those values may be permitted.
   * Default to Always.
   * More info: https://kubernetes.io/docs/concepts/workloads/pods/pod-lifecycle/#restart-policy
   * +optional
   */
  restartPolicy?: V1RestartPolicy;
  /**
   * RuntimeClassName refers to a RuntimeClass object in the node.k8s.io group, which should be used
   * to run this pod.  If no RuntimeClass resource matches the named class, the pod will not be run.
   * If unset or empty, the "legacy" RuntimeClass will be used, which is an implicit class with an
   * empty definition that uses the default runtime handler.
   * More info: https://git.k8s.io/enhancements/keps/sig-node/585-runtime-class
   * +optional
   */
  runtimeClassName?: string;
  /**
   * If specified, the pod will be dispatched by specified scheduler.
   * If not specified, the pod will be dispatched by default scheduler.
   * +optional
   */
  schedulerName?: string;
  /**
   * SchedulingGates is an opaque list of values that if specified will block scheduling the pod.
   * If schedulingGates is not empty, the pod will stay in the SchedulingGated state and the
   * scheduler will not attempt to schedule the pod.
   *
   * SchedulingGates can only be set at pod creation time, and be removed only afterwards.
   *
   * +patchMergeKey=name
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=name
   * +optional
   */
  schedulingGates?: Array<V1PodSchedulingGate>;
  /**
   * SecurityContext holds pod-level security attributes and common container settings.
   * Optional: Defaults to empty.  See type description for default values of each field.
   * +optional
   */
  securityContext?: V1PodSecurityContext;
  /**
   * DeprecatedServiceAccount is a deprecated alias for ServiceAccountName.
   * Deprecated: Use serviceAccountName instead.
   * +k8s:conversion-gen=false
   * +optional
   */
  serviceAccount?: string;
  /**
   * ServiceAccountName is the name of the ServiceAccount to use to run this pod.
   * More info: https://kubernetes.io/docs/tasks/configure-pod-container/configure-service-account/
   * +optional
   */
  serviceAccountName?: string;
  /**
   * If true the pod's hostname will be configured as the pod's FQDN, rather than the leaf name (the default).
   * In Linux containers, this means setting the FQDN in the hostname field of the kernel (the nodename field of struct utsname).
   * In Windows containers, this means setting the registry value of hostname for the registry key HKEY_LOCAL_MACHINE\\SYSTEM\\CurrentControlSet\\Services\\Tcpip\\Parameters to FQDN.
   * If a pod does not have FQDN, this has no effect.
   * Default to false.
   * +optional
   */
  setHostnameAsFQDN?: boolean;
  /**
   * Share a single process namespace between all of the containers in a pod.
   * When this is set containers will be able to view and signal processes from other containers
   * in the same pod, and the first process in each container will not be assigned PID 1.
   * HostPID and ShareProcessNamespace cannot both be set.
   * Optional: Default to false.
   * +k8s:conversion-gen=false
   * +optional
   */
  shareProcessNamespace?: boolean;
  /**
   * If specified, the fully qualified Pod hostname will be "<hostname>.<subdomain>.<pod namespace>.svc.<cluster domain>".
   * If not specified, the pod will not have a domainname at all.
   * +optional
   */
  subdomain?: string;
  /**
   * Optional duration in seconds the pod needs to terminate gracefully. May be decreased in delete request.
   * Value must be non-negative integer. The value zero indicates stop immediately via
   * the kill signal (no opportunity to shut down).
   * If this value is nil, the default grace period will be used instead.
   * The grace period is the duration in seconds after the processes running in the pod are sent
   * a termination signal and the time when the processes are forcibly halted with a kill signal.
   * Set this value longer than the expected cleanup time for your process.
   * Defaults to 30 seconds.
   * +optional
   */
  terminationGracePeriodSeconds?: number;
  /**
   * If specified, the pod's tolerations.
   * +optional
   * +listType=atomic
   */
  tolerations?: Array<V1Toleration>;
  /**
   * TopologySpreadConstraints describes how a group of pods ought to spread across topology
   * domains. Scheduler will schedule pods in a way which abides by the constraints.
   * All topologySpreadConstraints are ANDed.
   * +optional
   * +patchMergeKey=topologyKey
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=topologyKey
   * +listMapKey=whenUnsatisfiable
   */
  topologySpreadConstraints?: Array<V1TopologySpreadConstraint>;
  /**
   * List of volumes that can be mounted by containers belonging to the pod.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes
   * +optional
   * +patchMergeKey=name
   * +patchStrategy=merge,retainKeys
   * +listType=map
   * +listMapKey=name
   */
  volumes?: Array<V1Volume>;
  /**
   * WorkloadRef provides a reference to the Workload object that this Pod belongs to.
   * This field is used by the scheduler to identify the PodGroup and apply the
   * correct group scheduling policies. The Workload object referenced
   * by this field may not exist at the time the Pod is created.
   * This field is immutable, but a Workload object with the same name
   * may be recreated with different policies. Doing this during pod scheduling
   * may result in the placement not conforming to the expected policies.
   *
   * +featureGate=GenericWorkload
   * +optional
   */
  workloadRef?: V1WorkloadReference;
};

export type V1PodStatus = {
  /**
   * AllocatedResources is the total requests allocated for this pod by the node.
   * If pod-level requests are not set, this will be the total requests aggregated
   * across containers in the pod.
   * +featureGate=InPlacePodLevelResourcesVerticalScaling
   * +optional
   */
  allocatedResources?: V1ResourceList;
  /**
   * Current service state of pod.
   * More info: https://kubernetes.io/docs/concepts/workloads/pods/pod-lifecycle#pod-conditions
   * +optional
   * +patchMergeKey=type
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=type
   */
  conditions?: Array<V1PodCondition>;
  /**
   * Statuses of containers in this pod.
   * Each container in the pod should have at most one status in this list,
   * and all statuses should be for containers in the pod.
   * However this is not enforced.
   * If a status for a non-existent container is present in the list, or the list has duplicate names,
   * the behavior of various Kubernetes components is not defined and those statuses might be
   * ignored.
   * More info: https://kubernetes.io/docs/concepts/workloads/pods/pod-lifecycle#pod-and-container-status
   * +optional
   * +listType=atomic
   */
  containerStatuses?: Array<V1ContainerStatus>;
  /**
   * Statuses for any ephemeral containers that have run in this pod.
   * Each ephemeral container in the pod should have at most one status in this list,
   * and all statuses should be for containers in the pod.
   * However this is not enforced.
   * If a status for a non-existent container is present in the list, or the list has duplicate names,
   * the behavior of various Kubernetes components is not defined and those statuses might be
   * ignored.
   * More info: https://kubernetes.io/docs/concepts/workloads/pods/pod-lifecycle#pod-and-container-status
   * +optional
   * +listType=atomic
   */
  ephemeralContainerStatuses?: Array<V1ContainerStatus>;
  /**
   * Status of extended resource claim backed by DRA.
   * +featureGate=DRAExtendedResource
   * +optional
   */
  extendedResourceClaimStatus?: V1PodExtendedResourceClaimStatus;
  /**
   * hostIP holds the IP address of the host to which the pod is assigned. Empty if the pod has not started yet.
   * A pod can be assigned to a node that has a problem in kubelet which in turns mean that HostIP will
   * not be updated even if there is a node is assigned to pod
   * +optional
   */
  hostIP?: string;
  /**
   * hostIPs holds the IP addresses allocated to the host. If this field is specified, the first entry must
   * match the hostIP field. This list is empty if the pod has not started yet.
   * A pod can be assigned to a node that has a problem in kubelet which in turns means that HostIPs will
   * not be updated even if there is a node is assigned to this pod.
   * +optional
   * +patchStrategy=merge
   * +patchMergeKey=ip
   * +listType=atomic
   */
  hostIPs?: Array<V1HostIp>;
  /**
   * Statuses of init containers in this pod. The most recent successful non-restartable
   * init container will have ready = true, the most recently started container will have
   * startTime set.
   * Each init container in the pod should have at most one status in this list,
   * and all statuses should be for containers in the pod.
   * However this is not enforced.
   * If a status for a non-existent container is present in the list, or the list has duplicate names,
   * the behavior of various Kubernetes components is not defined and those statuses might be
   * ignored.
   * More info: https://kubernetes.io/docs/concepts/workloads/pods/pod-lifecycle/#pod-and-container-status
   * +listType=atomic
   */
  initContainerStatuses?: Array<V1ContainerStatus>;
  /**
   * A human readable message indicating details about why the pod is in this condition.
   * +optional
   */
  message?: string;
  /**
   * nominatedNodeName is set only when this pod preempts other pods on the node, but it cannot be
   * scheduled right away as preemption victims receive their graceful termination periods.
   * This field does not guarantee that the pod will be scheduled on this node. Scheduler may decide
   * to place the pod elsewhere if other nodes become available sooner. Scheduler may also decide to
   * give the resources on this node to a higher priority pod that is created after preemption.
   * As a result, this field may be different than PodSpec.nodeName when the pod is
   * scheduled.
   * +optional
   */
  nominatedNodeName?: string;
  /**
   * If set, this represents the .metadata.generation that the pod status was set based upon.
   * The PodObservedGenerationTracking feature gate must be enabled to use this field.
   * +featureGate=PodObservedGenerationTracking
   * +optional
   */
  observedGeneration?: number;
  /**
   * The phase of a Pod is a simple, high-level summary of where the Pod is in its lifecycle.
   * The conditions array, the reason and message fields, and the individual container status
   * arrays contain more detail about the pod's status.
   * There are five possible phase values:
   *
   * Pending: The pod has been accepted by the Kubernetes system, but one or more of the
   * container images has not been created. This includes time before being scheduled as
   * well as time spent downloading images over the network, which could take a while.
   * Running: The pod has been bound to a node, and all of the containers have been created.
   * At least one container is still running, or is in the process of starting or restarting.
   * Succeeded: All containers in the pod have terminated in success, and will not be restarted.
   * Failed: All containers in the pod have terminated, and at least one container has
   * terminated in failure. The container either exited with non-zero status or was terminated
   * by the system.
   * Unknown: For some reason the state of the pod could not be obtained, typically due to an
   * error in communicating with the host of the pod.
   *
   * More info: https://kubernetes.io/docs/concepts/workloads/pods/pod-lifecycle#pod-phase
   * +optional
   */
  phase?: V1PodPhase;
  /**
   * podIP address allocated to the pod. Routable at least within the cluster.
   * Empty if not yet allocated.
   * +optional
   */
  podIP?: string;
  /**
   * podIPs holds the IP addresses allocated to the pod. If this field is specified, the 0th entry must
   * match the podIP field. Pods may be allocated at most 1 value for each of IPv4 and IPv6. This list
   * is empty if no IPs have been allocated yet.
   * +optional
   * +patchStrategy=merge
   * +patchMergeKey=ip
   * +listType=map
   * +listMapKey=ip
   */
  podIPs?: Array<V1PodIp>;
  /**
   * The Quality of Service (QOS) classification assigned to the pod based on resource requirements
   * See PodQOSClass type for available QOS classes
   * More info: https://kubernetes.io/docs/concepts/workloads/pods/pod-qos/#quality-of-service-classes
   * +optional
   */
  qosClass?: V1PodQosClass;
  /**
   * A brief CamelCase message indicating details about why the pod is in this state.
   * e.g. 'Evicted'
   * +optional
   */
  reason?: string;
  /**
   * Status of resources resize desired for pod's containers.
   * It is empty if no resources resize is pending.
   * Any changes to container resources will automatically set this to "Proposed"
   * Deprecated: Resize status is moved to two pod conditions PodResizePending and PodResizeInProgress.
   * PodResizePending will track states where the spec has been resized, but the Kubelet has not yet allocated the resources.
   * PodResizeInProgress will track in-progress resizes, and should be present whenever allocated resources != acknowledged resources.
   * +featureGate=InPlacePodVerticalScaling
   * +optional
   */
  resize?: V1PodResizeStatus;
  /**
   * Status of resource claims.
   * +patchMergeKey=name
   * +patchStrategy=merge,retainKeys
   * +listType=map
   * +listMapKey=name
   * +featureGate=DynamicResourceAllocation
   * +optional
   */
  resourceClaimStatuses?: Array<V1PodResourceClaimStatus>;
  /**
   * Resources represents the compute resource requests and limits that have been
   * applied at the pod level if pod-level requests or limits are set in
   * PodSpec.Resources
   * +featureGate=InPlacePodLevelResourcesVerticalScaling
   * +optional
   */
  resources?: V1ResourceRequirements;
  /**
   * RFC 3339 date and time at which the object was acknowledged by the Kubelet.
   * This is before the Kubelet pulled the container image(s) for the pod.
   * +optional
   */
  startTime?: string;
};

export type V1PodTemplateSpec = {
  /**
   * Standard object's metadata.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#metadata
   * +optional
   */
  metadata?: V1ObjectMeta;
  /**
   * Specification of the desired behavior of the pod.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#spec-and-status
   * +optional
   */
  spec?: V1PodSpec;
};

export type V1PolicyRule = {
  /**
   * APIGroups is the name of the APIGroup that contains the resources.  If multiple API groups are specified, any action requested against one of
   * the enumerated resources in any API group will be allowed. "" represents the core API group and "*" represents all API groups.
   * +optional
   * +listType=atomic
   */
  apiGroups?: Array<string>;
  /**
   * NonResourceURLs is a set of partial urls that a user should have access to.  *s are allowed, but only as the full, final step in the path
   * Since non-resource URLs are not namespaced, this field is only applicable for ClusterRoles referenced from a ClusterRoleBinding.
   * Rules can either apply to API resources (such as "pods" or "secrets") or non-resource URL paths (such as "/api"),  but not both.
   * +optional
   * +listType=atomic
   */
  nonResourceURLs?: Array<string>;
  /**
   * ResourceNames is an optional white list of names that the rule applies to.  An empty set means that everything is allowed.
   * +optional
   * +listType=atomic
   */
  resourceNames?: Array<string>;
  /**
   * Resources is a list of resources this rule applies to. '*' represents all resources.
   * +optional
   * +listType=atomic
   */
  resources?: Array<string>;
  /**
   * Verbs is a list of Verbs that apply to ALL the ResourceKinds contained in this rule. '*' represents all verbs.
   * +listType=atomic
   */
  verbs?: Array<string>;
};

export type V1PortworxVolumeSource = {
  /**
   * fSType represents the filesystem type to mount
   * Must be a filesystem type supported by the host operating system.
   * Ex. "ext4", "xfs". Implicitly inferred to be "ext4" if unspecified.
   */
  fsType?: string;
  /**
   * readOnly defaults to false (read/write). ReadOnly here will force
   * the ReadOnly setting in VolumeMounts.
   * +optional
   */
  readOnly?: boolean;
  /**
   * volumeID uniquely identifies a Portworx volume
   */
  volumeID?: string;
};

export const V1PreemptionPolicy = {
  /**
   * PreemptLowerPriority
   */
  PREEMPT_LOWER_PRIORITY: 'PreemptLowerPriority' /**
   * PreemptNever
   */,
  PREEMPT_NEVER: 'Never',
} as const;

export type V1PreemptionPolicy =
  (typeof V1PreemptionPolicy)[keyof typeof V1PreemptionPolicy];

export type V1PreferredSchedulingTerm = {
  /**
   * A node selector term, associated with the corresponding weight.
   */
  preference?: V1NodeSelectorTerm;
  /**
   * Weight associated with matching the corresponding nodeSelectorTerm, in the range 1-100.
   */
  weight?: number;
};

export type V1Probe = {
  /**
   * Exec specifies a command to execute in the container.
   * +optional
   */
  exec?: V1ExecAction;
  /**
   * Minimum consecutive failures for the probe to be considered failed after having succeeded.
   * Defaults to 3. Minimum value is 1.
   * +optional
   */
  failureThreshold?: number;
  /**
   * GRPC specifies a GRPC HealthCheckRequest.
   * +optional
   */
  grpc?: V1GrpcAction;
  /**
   * HTTPGet specifies an HTTP GET request to perform.
   * +optional
   */
  httpGet?: V1HttpGetAction;
  /**
   * Number of seconds after the container has started before liveness probes are initiated.
   * More info: https://kubernetes.io/docs/concepts/workloads/pods/pod-lifecycle#container-probes
   * +optional
   */
  initialDelaySeconds?: number;
  /**
   * How often (in seconds) to perform the probe.
   * Default to 10 seconds. Minimum value is 1.
   * +optional
   */
  periodSeconds?: number;
  /**
   * Minimum consecutive successes for the probe to be considered successful after having failed.
   * Defaults to 1. Must be 1 for liveness and startup. Minimum value is 1.
   * +optional
   */
  successThreshold?: number;
  /**
   * TCPSocket specifies a connection to a TCP port.
   * +optional
   */
  tcpSocket?: V1TcpSocketAction;
  /**
   * Optional duration in seconds the pod needs to terminate gracefully upon probe failure.
   * The grace period is the duration in seconds after the processes running in the pod are sent
   * a termination signal and the time when the processes are forcibly halted with a kill signal.
   * Set this value longer than the expected cleanup time for your process.
   * If this value is nil, the pod's terminationGracePeriodSeconds will be used. Otherwise, this
   * value overrides the value provided by the pod spec.
   * Value must be non-negative integer. The value zero indicates stop immediately via
   * the kill signal (no opportunity to shut down).
   * This is a beta field and requires enabling ProbeTerminationGracePeriod feature gate.
   * Minimum value is 1. spec.terminationGracePeriodSeconds is used if unset.
   * +optional
   */
  terminationGracePeriodSeconds?: number;
  /**
   * Number of seconds after which the probe times out.
   * Defaults to 1 second. Minimum value is 1.
   * More info: https://kubernetes.io/docs/concepts/workloads/pods/pod-lifecycle#container-probes
   * +optional
   */
  timeoutSeconds?: number;
};

export const V1ProcMountType = {
  /**
   * DefaultProcMount
   */
  DEFAULT_PROC_MOUNT: 'Default' /**
   * UnmaskedProcMount
   */,
  UNMASKED_PROC_MOUNT: 'Unmasked',
} as const;

export type V1ProcMountType =
  (typeof V1ProcMountType)[keyof typeof V1ProcMountType];

export type V1ProjectedVolumeSource = {
  /**
   * defaultMode are the mode bits used to set permissions on created files by default.
   * Must be an octal value between 0000 and 0777 or a decimal value between 0 and 511.
   * YAML accepts both octal and decimal values, JSON requires decimal values for mode bits.
   * Directories within the path are not affected by this setting.
   * This might be in conflict with other options that affect the file
   * mode, like fsGroup, and the result can be other mode bits set.
   * +optional
   */
  defaultMode?: number;
  /**
   * sources is the list of volume projections. Each entry in this list
   * handles one source.
   * +optional
   * +listType=atomic
   */
  sources?: Array<V1VolumeProjection>;
};

export const V1Protocol = {
  /**
   * ProtocolTCP
   */
  PROTOCOL_TCP: 'TCP',
  /**
   * ProtocolUDP
   */
  PROTOCOL_UDP: 'UDP',
  /**
   * ProtocolSCTP
   */
  PROTOCOL_SCTP: 'SCTP',
} as const;

export type V1Protocol = (typeof V1Protocol)[keyof typeof V1Protocol];

export const V1PullPolicy = {
  /**
   * PullAlways
   */
  PULL_ALWAYS: 'Always',
  /**
   * PullNever
   */
  PULL_NEVER: 'Never',
  /**
   * PullIfNotPresent
   */
  PULL_IF_NOT_PRESENT: 'IfNotPresent',
} as const;

export type V1PullPolicy = (typeof V1PullPolicy)[keyof typeof V1PullPolicy];

export type V1QuobyteVolumeSource = {
  /**
   * group to map volume access to
   * Default is no group
   * +optional
   */
  group?: string;
  /**
   * readOnly here will force the Quobyte volume to be mounted with read-only permissions.
   * Defaults to false.
   * +optional
   */
  readOnly?: boolean;
  /**
   * registry represents a single or multiple Quobyte Registry services
   * specified as a string as host:port pair (multiple entries are separated with commas)
   * which acts as the central registry for volumes
   */
  registry?: string;
  /**
   * tenant owning the given Quobyte volume in the Backend
   * Used with dynamically provisioned Quobyte volumes, value is set by the plugin
   * +optional
   */
  tenant?: string;
  /**
   * user to map volume access to
   * Defaults to serivceaccount user
   * +optional
   */
  user?: string;
  /**
   * volume is a string that references an already created Quobyte volume by name.
   */
  volume?: string;
};

export type V1RbdVolumeSource = {
  /**
   * fsType is the filesystem type of the volume that you want to mount.
   * Tip: Ensure that the filesystem type is supported by the host operating system.
   * Examples: "ext4", "xfs", "ntfs". Implicitly inferred to be "ext4" if unspecified.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#rbd
   * TODO: how do we prevent errors in the filesystem from compromising the machine
   * +optional
   */
  fsType?: string;
  /**
   * image is the rados image name.
   * More info: https://examples.k8s.io/volumes/rbd/README.md#how-to-use-it
   */
  image?: string;
  /**
   * keyring is the path to key ring for RBDUser.
   * Default is /etc/ceph/keyring.
   * More info: https://examples.k8s.io/volumes/rbd/README.md#how-to-use-it
   * +optional
   * +default="/etc/ceph/keyring"
   */
  keyring?: string;
  /**
   * monitors is a collection of Ceph monitors.
   * More info: https://examples.k8s.io/volumes/rbd/README.md#how-to-use-it
   * +listType=atomic
   */
  monitors?: Array<string>;
  /**
   * pool is the rados pool name.
   * Default is rbd.
   * More info: https://examples.k8s.io/volumes/rbd/README.md#how-to-use-it
   * +optional
   * +default="rbd"
   */
  pool?: string;
  /**
   * readOnly here will force the ReadOnly setting in VolumeMounts.
   * Defaults to false.
   * More info: https://examples.k8s.io/volumes/rbd/README.md#how-to-use-it
   * +optional
   */
  readOnly?: boolean;
  /**
   * secretRef is name of the authentication secret for RBDUser. If provided
   * overrides keyring.
   * Default is nil.
   * More info: https://examples.k8s.io/volumes/rbd/README.md#how-to-use-it
   * +optional
   */
  secretRef?: K8sIoApiCoreV1LocalObjectReference;
  /**
   * user is the rados user name.
   * Default is admin.
   * More info: https://examples.k8s.io/volumes/rbd/README.md#how-to-use-it
   * +optional
   * +default="admin"
   */
  user?: string;
};

export const V1RecursiveReadOnlyMode = {
  /**
   * RecursiveReadOnlyDisabled
   */
  RECURSIVE_READ_ONLY_DISABLED: 'Disabled',
  /**
   * RecursiveReadOnlyIfPossible
   */
  RECURSIVE_READ_ONLY_IF_POSSIBLE: 'IfPossible',
  /**
   * RecursiveReadOnlyEnabled
   */
  RECURSIVE_READ_ONLY_ENABLED: 'Enabled',
} as const;

export type V1RecursiveReadOnlyMode =
  (typeof V1RecursiveReadOnlyMode)[keyof typeof V1RecursiveReadOnlyMode];

export type V1ReplicaSet = {
  /**
   * APIVersion defines the versioned schema of this representation of an object.
   * Servers should convert recognized schemas to the latest internal value, and
   * may reject unrecognized values.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#resources
   * +optional
   */
  apiVersion?: string;
  /**
   * Kind is a string value representing the REST resource this object represents.
   * Servers may infer this from the endpoint the client submits requests to.
   * Cannot be updated.
   * In CamelCase.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
   * +optional
   */
  kind?: string;
  /**
   * If the Labels of a ReplicaSet are empty, they are defaulted to
   * be the same as the Pod(s) that the ReplicaSet manages.
   * Standard object's metadata.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#metadata
   * +optional
   */
  metadata?: V1ObjectMeta;
  /**
   * Spec defines the specification of the desired behavior of the ReplicaSet.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#spec-and-status
   * +optional
   */
  spec?: V1ReplicaSetSpec;
  /**
   * Status is the most recently observed status of the ReplicaSet.
   * This data may be out of date by some window of time.
   * Populated by the system.
   * Read-only.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#spec-and-status
   * +optional
   */
  status?: V1ReplicaSetStatus;
};

export type V1ReplicaSetCondition = {
  /**
   * The last time the condition transitioned from one status to another.
   * +optional
   */
  lastTransitionTime?: string;
  /**
   * A human readable message indicating details about the transition.
   * +optional
   */
  message?: string;
  /**
   * The reason for the condition's last transition.
   * +optional
   */
  reason?: string;
  /**
   * Status of the condition, one of True, False, Unknown.
   */
  status?: K8sIoApiCoreV1ConditionStatus;
  /**
   * Type of replica set condition.
   */
  type?: V1ReplicaSetConditionType;
};

export const V1ReplicaSetConditionType = {
  /**
   * ReplicaSetReplicaFailure
   */
  REPLICA_SET_REPLICA_FAILURE: 'ReplicaFailure',
} as const;

export type V1ReplicaSetConditionType =
  (typeof V1ReplicaSetConditionType)[keyof typeof V1ReplicaSetConditionType];

export type V1ReplicaSetSpec = {
  /**
   * Minimum number of seconds for which a newly created pod should be ready
   * without any of its container crashing, for it to be considered available.
   * Defaults to 0 (pod will be considered available as soon as it is ready)
   * +optional
   */
  minReadySeconds?: number;
  /**
   * Replicas is the number of desired pods.
   * This is a pointer to distinguish between explicit zero and unspecified.
   * Defaults to 1.
   * More info: https://kubernetes.io/docs/concepts/workloads/controllers/replicaset
   * +optional
   */
  replicas?: number;
  /**
   * Selector is a label query over pods that should match the replica count.
   * Label keys and values that must match in order to be controlled by this replica set.
   * It must match the pod template's labels.
   * More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/labels/#label-selectors
   */
  selector?: V1LabelSelector;
  /**
   * Template is the object that describes the pod that will be created if
   * insufficient replicas are detected.
   * More info: https://kubernetes.io/docs/concepts/workloads/controllers/replicaset/#pod-template
   * +optional
   */
  template?: V1PodTemplateSpec;
};

export type V1ReplicaSetStatus = {
  /**
   * The number of available non-terminating pods (ready for at least minReadySeconds) for this replica set.
   * +optional
   */
  availableReplicas?: number;
  /**
   * Represents the latest available observations of a replica set's current state.
   * +optional
   * +patchMergeKey=type
   * +patchStrategy=merge
   * +listType=map
   * +listMapKey=type
   */
  conditions?: Array<V1ReplicaSetCondition>;
  /**
   * The number of non-terminating pods that have labels matching the labels of the pod template of the replicaset.
   * +optional
   */
  fullyLabeledReplicas?: number;
  /**
   * ObservedGeneration reflects the generation of the most recently observed ReplicaSet.
   * +optional
   */
  observedGeneration?: number;
  /**
   * The number of non-terminating pods targeted by this ReplicaSet with a Ready Condition.
   * +optional
   */
  readyReplicas?: number;
  /**
   * Replicas is the most recently observed number of non-terminating pods.
   * More info: https://kubernetes.io/docs/concepts/workloads/controllers/replicaset
   */
  replicas?: number;
  /**
   * The number of terminating pods for this replica set. Terminating pods have a non-null .metadata.deletionTimestamp
   * and have not yet reached the Failed or Succeeded .status.phase.
   *
   * This is a beta field and requires enabling DeploymentReplicaSetTerminatingReplicas feature (enabled by default).
   * +optional
   */
  terminatingReplicas?: number;
};

export type V1ResourceFieldSelector = {
  /**
   * Container name: required for volumes, optional for env vars
   * +optional
   */
  containerName?: string;
  /**
   * Specifies the output format of the exposed resources, defaults to "1"
   * +optional
   */
  divisor?: ResourceQuantity;
  /**
   * Required: resource to select
   */
  resource?: string;
};

export type V1ResourceHealth = {
  /**
   * Health of the resource.
   * can be one of:
   * - Healthy: operates as normal
   * - Unhealthy: reported unhealthy. We consider this a temporary health issue
   * since we do not have a mechanism today to distinguish
   * temporary and permanent issues.
   * - Unknown: The status cannot be determined.
   * For example, Device Plugin got unregistered and hasn't been re-registered since.
   *
   * In future we may want to introduce the PermanentlyUnhealthy Status.
   */
  health?: V1ResourceHealthStatus;
  /**
   * ResourceID is the unique identifier of the resource. See the ResourceID type for more information.
   */
  resourceID?: string;
};

export const V1ResourceHealthStatus = {
  /**
   * ResourceHealthStatusHealthy
   */
  RESOURCE_HEALTH_STATUS_HEALTHY: 'Healthy',
  /**
   * ResourceHealthStatusUnhealthy
   */
  RESOURCE_HEALTH_STATUS_UNHEALTHY: 'Unhealthy',
  /**
   * ResourceHealthStatusUnknown
   */
  RESOURCE_HEALTH_STATUS_UNKNOWN: 'Unknown',
} as const;

export type V1ResourceHealthStatus =
  (typeof V1ResourceHealthStatus)[keyof typeof V1ResourceHealthStatus];

export type V1ResourceList = {
  [key: string]: ResourceQuantity;
};

export const V1ResourceName = {
  /**
   * ResourceCPU
   */
  RESOURCE_CPU: 'cpu',
  /**
   * ResourceMemory
   */
  RESOURCE_MEMORY: 'memory',
  /**
   * ResourceStorage
   */
  RESOURCE_STORAGE: 'storage',
  /**
   * ResourceEphemeralStorage
   */
  RESOURCE_EPHEMERAL_STORAGE: 'ephemeral-storage',
  /**
   * ResourcePods
   */
  RESOURCE_PODS: 'pods',
  /**
   * ResourceServices
   */
  RESOURCE_SERVICES: 'services',
  /**
   * ResourceReplicationControllers
   */
  RESOURCE_REPLICATION_CONTROLLERS: 'replicationcontrollers',
  /**
   * ResourceQuotas
   */
  RESOURCE_QUOTAS: 'resourcequotas',
  /**
   * ResourceSecrets
   */
  RESOURCE_SECRETS: 'secrets',
  /**
   * ResourceConfigMaps
   */
  RESOURCE_CONFIG_MAPS: 'configmaps',
  /**
   * ResourcePersistentVolumeClaims
   */
  RESOURCE_PERSISTENT_VOLUME_CLAIMS: 'persistentvolumeclaims',
  /**
   * ResourceServicesNodePorts
   */
  RESOURCE_SERVICES_NODE_PORTS: 'services.nodeports',
  /**
   * ResourceServicesLoadBalancers
   */
  RESOURCE_SERVICES_LOAD_BALANCERS: 'services.loadbalancers',
  /**
   * ResourceRequestsCPU
   */
  RESOURCE_REQUESTS_CPU: 'requests.cpu',
  /**
   * ResourceRequestsMemory
   */
  RESOURCE_REQUESTS_MEMORY: 'requests.memory',
  /**
   * ResourceRequestsStorage
   */
  RESOURCE_REQUESTS_STORAGE: 'requests.storage',
  /**
   * ResourceRequestsEphemeralStorage
   */
  RESOURCE_REQUESTS_EPHEMERAL_STORAGE: 'requests.ephemeral-storage',
  /**
   * ResourceLimitsCPU
   */
  RESOURCE_LIMITS_CPU: 'limits.cpu',
  /**
   * ResourceLimitsMemory
   */
  RESOURCE_LIMITS_MEMORY: 'limits.memory',
  /**
   * ResourceLimitsEphemeralStorage
   */
  RESOURCE_LIMITS_EPHEMERAL_STORAGE: 'limits.ephemeral-storage',
} as const;

export type V1ResourceName =
  (typeof V1ResourceName)[keyof typeof V1ResourceName];

export type V1ResourceQuota = {
  /**
   * APIVersion defines the versioned schema of this representation of an object.
   * Servers should convert recognized schemas to the latest internal value, and
   * may reject unrecognized values.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#resources
   * +optional
   */
  apiVersion?: string;
  /**
   * Kind is a string value representing the REST resource this object represents.
   * Servers may infer this from the endpoint the client submits requests to.
   * Cannot be updated.
   * In CamelCase.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
   * +optional
   */
  kind?: string;
  /**
   * Standard object's metadata.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#metadata
   * +optional
   */
  metadata?: V1ObjectMeta;
  /**
   * Spec defines the desired quota.
   * https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#spec-and-status
   * +optional
   */
  spec?: V1ResourceQuotaSpec;
  /**
   * Status defines the actual enforced quota and its current usage.
   * https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#spec-and-status
   * +optional
   */
  status?: V1ResourceQuotaStatus;
};

export const V1ResourceQuotaScope = {
  /**
   * ResourceQuotaScopeTerminating
   */
  RESOURCE_QUOTA_SCOPE_TERMINATING: 'Terminating',
  /**
   * ResourceQuotaScopeNotTerminating
   */
  RESOURCE_QUOTA_SCOPE_NOT_TERMINATING: 'NotTerminating',
  /**
   * ResourceQuotaScopeBestEffort
   */
  RESOURCE_QUOTA_SCOPE_BEST_EFFORT: 'BestEffort',
  /**
   * ResourceQuotaScopeNotBestEffort
   */
  RESOURCE_QUOTA_SCOPE_NOT_BEST_EFFORT: 'NotBestEffort',
  /**
   * ResourceQuotaScopePriorityClass
   */
  RESOURCE_QUOTA_SCOPE_PRIORITY_CLASS: 'PriorityClass',
  /**
   * ResourceQuotaScopeCrossNamespacePodAffinity
   */
  RESOURCE_QUOTA_SCOPE_CROSS_NAMESPACE_POD_AFFINITY:
    'CrossNamespacePodAffinity',
  /**
   * ResourceQuotaScopeVolumeAttributesClass
   */
  RESOURCE_QUOTA_SCOPE_VOLUME_ATTRIBUTES_CLASS: 'VolumeAttributesClass',
} as const;

export type V1ResourceQuotaScope =
  (typeof V1ResourceQuotaScope)[keyof typeof V1ResourceQuotaScope];

export type V1ResourceQuotaSpec = {
  /**
   * hard is the set of desired hard limits for each named resource.
   * More info: https://kubernetes.io/docs/concepts/policy/resource-quotas/
   * +optional
   */
  hard?: V1ResourceList;
  /**
   * scopeSelector is also a collection of filters like scopes that must match each object tracked by a quota
   * but expressed using ScopeSelectorOperator in combination with possible values.
   * For a resource to match, both scopes AND scopeSelector (if specified in spec), must be matched.
   * +optional
   */
  scopeSelector?: V1ScopeSelector;
  /**
   * A collection of filters that must match each object tracked by a quota.
   * If not specified, the quota matches all objects.
   * +optional
   * +listType=atomic
   */
  scopes?: Array<V1ResourceQuotaScope>;
};

export type V1ResourceQuotaStatus = {
  /**
   * Hard is the set of enforced hard limits for each named resource.
   * More info: https://kubernetes.io/docs/concepts/policy/resource-quotas/
   * +optional
   */
  hard?: V1ResourceList;
  /**
   * Used is the current observed total usage of the resource in the namespace.
   * +optional
   */
  used?: V1ResourceList;
};

export type V1ResourceRequirements = {
  /**
   * Claims lists the names of resources, defined in spec.resourceClaims,
   * that are used by this container.
   *
   * This field depends on the
   * DynamicResourceAllocation feature gate.
   *
   * This field is immutable. It can only be set for containers.
   *
   * +listType=map
   * +listMapKey=name
   * +featureGate=DynamicResourceAllocation
   * +optional
   */
  claims?: Array<K8sIoApiCoreV1ResourceClaim>;
  /**
   * Limits describes the maximum amount of compute resources allowed.
   * More info: https://kubernetes.io/docs/concepts/configuration/manage-resources-containers/
   * +optional
   */
  limits?: V1ResourceList;
  /**
   * Requests describes the minimum amount of compute resources required.
   * If Requests is omitted for a container, it defaults to Limits if that is explicitly specified,
   * otherwise to an implementation-defined value. Requests cannot exceed Limits.
   * More info: https://kubernetes.io/docs/concepts/configuration/manage-resources-containers/
   * +optional
   */
  requests?: V1ResourceList;
};

export const V1ResourceResizeRestartPolicy = {
  /**
   * NotRequired
   */
  NOT_REQUIRED: 'NotRequired' /**
   * RestartContainer
   */,
  RESTART_CONTAINER: 'RestartContainer',
} as const;

export type V1ResourceResizeRestartPolicy =
  (typeof V1ResourceResizeRestartPolicy)[keyof typeof V1ResourceResizeRestartPolicy];

export type V1ResourceStatus = {
  /**
   * Name of the resource. Must be unique within the pod and in case of non-DRA resource, match one of the resources from the pod spec.
   * For DRA resources, the value must be "claim:<claim_name>/<request>".
   * When this status is reported about a container, the "claim_name" and "request" must match one of the claims of this container.
   * +required
   */
  name?: V1ResourceName;
  /**
   * List of unique resources health. Each element in the list contains an unique resource ID and its health.
   * At a minimum, for the lifetime of a Pod, resource ID must uniquely identify the resource allocated to the Pod on the Node.
   * If other Pod on the same Node reports the status with the same resource ID, it must be the same resource they share.
   * See ResourceID type definition for a specific format it has in various use cases.
   * +listType=map
   * +listMapKey=resourceID
   */
  resources?: Array<V1ResourceHealth>;
};

export const V1RestartPolicy = {
  /**
   * RestartPolicyAlways
   */
  RESTART_POLICY_ALWAYS: 'Always',
  /**
   * RestartPolicyOnFailure
   */
  RESTART_POLICY_ON_FAILURE: 'OnFailure',
  /**
   * RestartPolicyNever
   */
  RESTART_POLICY_NEVER: 'Never',
} as const;

export type V1RestartPolicy =
  (typeof V1RestartPolicy)[keyof typeof V1RestartPolicy];

export type V1RoleRef = {
  /**
   * APIGroup is the group for the resource being referenced
   */
  apiGroup?: string;
  /**
   * Kind is the type of resource being referenced
   */
  kind?: string;
  /**
   * Name is the name of resource being referenced
   * +required
   * +k8s:required
   */
  name?: string;
};

export type V1RollingUpdateDeployment = {
  /**
   * The maximum number of pods that can be scheduled above the desired number of
   * pods.
   * Value can be an absolute number (ex: 5) or a percentage of desired pods (ex: 10%).
   * This can not be 0 if MaxUnavailable is 0.
   * Absolute number is calculated from percentage by rounding up.
   * Defaults to 25%.
   * Example: when this is set to 30%, the new ReplicaSet can be scaled up immediately when
   * the rolling update starts, such that the total number of old and new pods do not exceed
   * 130% of desired pods. Once old pods have been killed,
   * new ReplicaSet can be scaled up further, ensuring that total number of pods running
   * at any time during the update is at most 130% of desired pods.
   * +optional
   */
  maxSurge?: IntstrIntOrString;
  /**
   * The maximum number of pods that can be unavailable during the update.
   * Value can be an absolute number (ex: 5) or a percentage of desired pods (ex: 10%).
   * Absolute number is calculated from percentage by rounding down.
   * This can not be 0 if MaxSurge is 0.
   * Defaults to 25%.
   * Example: when this is set to 30%, the old ReplicaSet can be scaled down to 70% of desired pods
   * immediately when the rolling update starts. Once new pods are ready, old ReplicaSet
   * can be scaled down further, followed by scaling up the new ReplicaSet, ensuring
   * that the total number of pods available at all times during the update is at
   * least 70% of desired pods.
   * +optional
   */
  maxUnavailable?: IntstrIntOrString;
};

export type V1SeLinuxOptions = {
  /**
   * Level is SELinux level label that applies to the container.
   * +optional
   */
  level?: string;
  /**
   * Role is a SELinux role label that applies to the container.
   * +optional
   */
  role?: string;
  /**
   * Type is a SELinux type label that applies to the container.
   * +optional
   */
  type?: string;
  /**
   * User is a SELinux user label that applies to the container.
   * +optional
   */
  user?: string;
};

export type V1ScaleIoVolumeSource = {
  /**
   * fsType is the filesystem type to mount.
   * Must be a filesystem type supported by the host operating system.
   * Ex. "ext4", "xfs", "ntfs".
   * Default is "xfs".
   * +optional
   * +default="xfs"
   */
  fsType?: string;
  /**
   * gateway is the host address of the ScaleIO API Gateway.
   */
  gateway?: string;
  /**
   * protectionDomain is the name of the ScaleIO Protection Domain for the configured storage.
   * +optional
   */
  protectionDomain?: string;
  /**
   * readOnly Defaults to false (read/write). ReadOnly here will force
   * the ReadOnly setting in VolumeMounts.
   * +optional
   */
  readOnly?: boolean;
  /**
   * secretRef references to the secret for ScaleIO user and other
   * sensitive information. If this is not provided, Login operation will fail.
   */
  secretRef?: K8sIoApiCoreV1LocalObjectReference;
  /**
   * sslEnabled Flag enable/disable SSL communication with Gateway, default false
   * +optional
   */
  sslEnabled?: boolean;
  /**
   * storageMode indicates whether the storage for a volume should be ThickProvisioned or ThinProvisioned.
   * Default is ThinProvisioned.
   * +optional
   * +default="ThinProvisioned"
   */
  storageMode?: string;
  /**
   * storagePool is the ScaleIO Storage Pool associated with the protection domain.
   * +optional
   */
  storagePool?: string;
  /**
   * system is the name of the storage system as configured in ScaleIO.
   */
  system?: string;
  /**
   * volumeName is the name of a volume already created in the ScaleIO system
   * that is associated with this volume source.
   */
  volumeName?: string;
};

export type V1ScopeSelector = {
  /**
   * A list of scope selector requirements by scope of the resources.
   * +optional
   * +listType=atomic
   */
  matchExpressions?: Array<V1ScopedResourceSelectorRequirement>;
};

export const V1ScopeSelectorOperator = {
  /**
   * ScopeSelectorOpIn
   */
  SCOPE_SELECTOR_OP_IN: 'In',
  /**
   * ScopeSelectorOpNotIn
   */
  SCOPE_SELECTOR_OP_NOT_IN: 'NotIn',
  /**
   * ScopeSelectorOpExists
   */
  SCOPE_SELECTOR_OP_EXISTS: 'Exists',
  /**
   * ScopeSelectorOpDoesNotExist
   */
  SCOPE_SELECTOR_OP_DOES_NOT_EXIST: 'DoesNotExist',
} as const;

export type V1ScopeSelectorOperator =
  (typeof V1ScopeSelectorOperator)[keyof typeof V1ScopeSelectorOperator];

export type V1ScopedResourceSelectorRequirement = {
  /**
   * Represents a scope's relationship to a set of values.
   * Valid operators are In, NotIn, Exists, DoesNotExist.
   */
  operator?: V1ScopeSelectorOperator;
  /**
   * The name of the scope that the selector applies to.
   */
  scopeName?: V1ResourceQuotaScope;
  /**
   * An array of string values. If the operator is In or NotIn,
   * the values array must be non-empty. If the operator is Exists or DoesNotExist,
   * the values array must be empty.
   * This array is replaced during a strategic merge patch.
   * +optional
   * +listType=atomic
   */
  values?: Array<string>;
};

export type V1SeccompProfile = {
  /**
   * localhostProfile indicates a profile defined in a file on the node should be used.
   * The profile must be preconfigured on the node to work.
   * Must be a descending path, relative to the kubelet's configured seccomp profile location.
   * Must be set if type is "Localhost". Must NOT be set for any other type.
   * +optional
   */
  localhostProfile?: string;
  /**
   * type indicates which kind of seccomp profile will be applied.
   * Valid options are:
   *
   * Localhost - a profile defined in a file on the node should be used.
   * RuntimeDefault - the container runtime default profile should be used.
   * Unconfined - no profile should be applied.
   * +unionDiscriminator
   */
  type?: V1SeccompProfileType;
};

export const V1SeccompProfileType = {
  /**
   * SeccompProfileTypeUnconfined
   */
  SECCOMP_PROFILE_TYPE_UNCONFINED: 'Unconfined',
  /**
   * SeccompProfileTypeRuntimeDefault
   */
  SECCOMP_PROFILE_TYPE_RUNTIME_DEFAULT: 'RuntimeDefault',
  /**
   * SeccompProfileTypeLocalhost
   */
  SECCOMP_PROFILE_TYPE_LOCALHOST: 'Localhost',
} as const;

export type V1SeccompProfileType =
  (typeof V1SeccompProfileType)[keyof typeof V1SeccompProfileType];

export type V1SecretEnvSource = {
  /**
   * Name of the referent.
   * This field is effectively required, but due to backwards compatibility is
   * allowed to be empty. Instances of this type with an empty value here are
   * almost certainly wrong.
   * More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/names/#names
   * +optional
   * +default=""
   * +kubebuilder:default=""
   * TODO: Drop `kubebuilder:default` when controller-gen doesn't need it https://github.com/kubernetes-sigs/kubebuilder/issues/3896.
   */
  name?: string;
  /**
   * Specify whether the Secret must be defined
   * +optional
   */
  optional?: boolean;
};

export type V1SecretKeySelector = {
  /**
   * The key of the secret to select from.  Must be a valid secret key.
   */
  key?: string;
  /**
   * Name of the referent.
   * This field is effectively required, but due to backwards compatibility is
   * allowed to be empty. Instances of this type with an empty value here are
   * almost certainly wrong.
   * More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/names/#names
   * +optional
   * +default=""
   * +kubebuilder:default=""
   * TODO: Drop `kubebuilder:default` when controller-gen doesn't need it https://github.com/kubernetes-sigs/kubebuilder/issues/3896.
   */
  name?: string;
  /**
   * Specify whether the Secret or its key must be defined
   * +optional
   */
  optional?: boolean;
};

export type V1SecretProjection = {
  /**
   * items if unspecified, each key-value pair in the Data field of the referenced
   * Secret will be projected into the volume as a file whose name is the
   * key and content is the value. If specified, the listed keys will be
   * projected into the specified paths, and unlisted keys will not be
   * present. If a key is specified which is not present in the Secret,
   * the volume setup will error unless it is marked optional. Paths must be
   * relative and may not contain the '..' path or start with '..'.
   * +optional
   * +listType=atomic
   */
  items?: Array<V1KeyToPath>;
  /**
   * Name of the referent.
   * This field is effectively required, but due to backwards compatibility is
   * allowed to be empty. Instances of this type with an empty value here are
   * almost certainly wrong.
   * More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/names/#names
   * +optional
   * +default=""
   * +kubebuilder:default=""
   * TODO: Drop `kubebuilder:default` when controller-gen doesn't need it https://github.com/kubernetes-sigs/kubebuilder/issues/3896.
   */
  name?: string;
  /**
   * optional field specify whether the Secret or its key must be defined
   * +optional
   */
  optional?: boolean;
};

export type V1SecretReference = {
  /**
   * name is unique within a namespace to reference a secret resource.
   * +optional
   */
  name?: string;
  /**
   * namespace defines the space within which the secret name must be unique.
   * +optional
   */
  namespace?: string;
};

export type V1SecretVolumeSource = {
  /**
   * defaultMode is Optional: mode bits used to set permissions on created files by default.
   * Must be an octal value between 0000 and 0777 or a decimal value between 0 and 511.
   * YAML accepts both octal and decimal values, JSON requires decimal values
   * for mode bits. Defaults to 0644.
   * Directories within the path are not affected by this setting.
   * This might be in conflict with other options that affect the file
   * mode, like fsGroup, and the result can be other mode bits set.
   * +optional
   */
  defaultMode?: number;
  /**
   * items If unspecified, each key-value pair in the Data field of the referenced
   * Secret will be projected into the volume as a file whose name is the
   * key and content is the value. If specified, the listed keys will be
   * projected into the specified paths, and unlisted keys will not be
   * present. If a key is specified which is not present in the Secret,
   * the volume setup will error unless it is marked optional. Paths must be
   * relative and may not contain the '..' path or start with '..'.
   * +optional
   * +listType=atomic
   */
  items?: Array<V1KeyToPath>;
  /**
   * optional field specify whether the Secret or its keys must be defined
   * +optional
   */
  optional?: boolean;
  /**
   * secretName is the name of the secret in the pod's namespace to use.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#secret
   * +optional
   */
  secretName?: string;
};

export type V1SecurityContext = {
  /**
   * AllowPrivilegeEscalation controls whether a process can gain more
   * privileges than its parent process. This bool directly controls if
   * the no_new_privs flag will be set on the container process.
   * AllowPrivilegeEscalation is true always when the container is:
   * 1) run as Privileged
   * 2) has CAP_SYS_ADMIN
   * Note that this field cannot be set when spec.os.name is windows.
   * +optional
   */
  allowPrivilegeEscalation?: boolean;
  /**
   * appArmorProfile is the AppArmor options to use by this container. If set, this profile
   * overrides the pod's appArmorProfile.
   * Note that this field cannot be set when spec.os.name is windows.
   * +optional
   */
  appArmorProfile?: V1AppArmorProfile;
  /**
   * The capabilities to add/drop when running containers.
   * Defaults to the default set of capabilities granted by the container runtime.
   * Note that this field cannot be set when spec.os.name is windows.
   * +optional
   */
  capabilities?: V1Capabilities;
  /**
   * Run container in privileged mode.
   * Processes in privileged containers are essentially equivalent to root on the host.
   * Defaults to false.
   * Note that this field cannot be set when spec.os.name is windows.
   * +optional
   */
  privileged?: boolean;
  /**
   * procMount denotes the type of proc mount to use for the containers.
   * The default value is Default which uses the container runtime defaults for
   * readonly paths and masked paths.
   * This requires the ProcMountType feature flag to be enabled.
   * Note that this field cannot be set when spec.os.name is windows.
   * +optional
   */
  procMount?: V1ProcMountType;
  /**
   * Whether this container has a read-only root filesystem.
   * Default is false.
   * Note that this field cannot be set when spec.os.name is windows.
   * +optional
   */
  readOnlyRootFilesystem?: boolean;
  /**
   * The GID to run the entrypoint of the container process.
   * Uses runtime default if unset.
   * May also be set in PodSecurityContext.  If set in both SecurityContext and
   * PodSecurityContext, the value specified in SecurityContext takes precedence.
   * Note that this field cannot be set when spec.os.name is windows.
   * +optional
   */
  runAsGroup?: number;
  /**
   * Indicates that the container must run as a non-root user.
   * If true, the Kubelet will validate the image at runtime to ensure that it
   * does not run as UID 0 (root) and fail to start the container if it does.
   * If unset or false, no such validation will be performed.
   * May also be set in PodSecurityContext.  If set in both SecurityContext and
   * PodSecurityContext, the value specified in SecurityContext takes precedence.
   * +optional
   */
  runAsNonRoot?: boolean;
  /**
   * The UID to run the entrypoint of the container process.
   * Defaults to user specified in image metadata if unspecified.
   * May also be set in PodSecurityContext.  If set in both SecurityContext and
   * PodSecurityContext, the value specified in SecurityContext takes precedence.
   * Note that this field cannot be set when spec.os.name is windows.
   * +optional
   */
  runAsUser?: number;
  /**
   * The SELinux context to be applied to the container.
   * If unspecified, the container runtime will allocate a random SELinux context for each
   * container.  May also be set in PodSecurityContext.  If set in both SecurityContext and
   * PodSecurityContext, the value specified in SecurityContext takes precedence.
   * Note that this field cannot be set when spec.os.name is windows.
   * +optional
   */
  seLinuxOptions?: V1SeLinuxOptions;
  /**
   * The seccomp options to use by this container. If seccomp options are
   * provided at both the pod & container level, the container options
   * override the pod options.
   * Note that this field cannot be set when spec.os.name is windows.
   * +optional
   */
  seccompProfile?: V1SeccompProfile;
  /**
   * The Windows specific settings applied to all containers.
   * If unspecified, the options from the PodSecurityContext will be used.
   * If set in both SecurityContext and PodSecurityContext, the value specified in SecurityContext takes precedence.
   * Note that this field cannot be set when spec.os.name is linux.
   * +optional
   */
  windowsOptions?: V1WindowsSecurityContextOptions;
};

export type V1ServiceAccountTokenProjection = {
  /**
   * audience is the intended audience of the token. A recipient of a token
   * must identify itself with an identifier specified in the audience of the
   * token, and otherwise should reject the token. The audience defaults to the
   * identifier of the apiserver.
   * +optional
   */
  audience?: string;
  /**
   * expirationSeconds is the requested duration of validity of the service
   * account token. As the token approaches expiration, the kubelet volume
   * plugin will proactively rotate the service account token. The kubelet will
   * start trying to rotate the token if the token is older than 80 percent of
   * its time to live or if the token is older than 24 hours.Defaults to 1 hour
   * and must be at least 10 minutes.
   * +optional
   */
  expirationSeconds?: number;
  /**
   * path is the path relative to the mount point of the file to project the
   * token into.
   */
  path?: string;
};

export const V1Signal = {
  /**
   * SIGABRT
   */
  SIGABRT: 'SIGABRT',
  /**
   * SIGALRM
   */
  SIGALRM: 'SIGALRM',
  /**
   * SIGBUS
   */
  SIGBUS: 'SIGBUS',
  /**
   * SIGCHLD
   */
  SIGCHLD: 'SIGCHLD',
  /**
   * SIGCLD
   */
  SIGCLD: 'SIGCLD',
  /**
   * SIGCONT
   */
  SIGCONT: 'SIGCONT',
  /**
   * SIGFPE
   */
  SIGFPE: 'SIGFPE',
  /**
   * SIGHUP
   */
  SIGHUP: 'SIGHUP',
  /**
   * SIGILL
   */
  SIGILL: 'SIGILL',
  /**
   * SIGINT
   */
  SIGINT: 'SIGINT',
  /**
   * SIGIO
   */
  SIGIO: 'SIGIO',
  /**
   * SIGIOT
   */
  SIGIOT: 'SIGIOT',
  /**
   * SIGKILL
   */
  SIGKILL: 'SIGKILL',
  /**
   * SIGPIPE
   */
  SIGPIPE: 'SIGPIPE',
  /**
   * SIGPOLL
   */
  SIGPOLL: 'SIGPOLL',
  /**
   * SIGPROF
   */
  SIGPROF: 'SIGPROF',
  /**
   * SIGPWR
   */
  SIGPWR: 'SIGPWR',
  /**
   * SIGQUIT
   */
  SIGQUIT: 'SIGQUIT',
  /**
   * SIGSEGV
   */
  SIGSEGV: 'SIGSEGV',
  /**
   * SIGSTKFLT
   */
  SIGSTKFLT: 'SIGSTKFLT',
  /**
   * SIGSTOP
   */
  SIGSTOP: 'SIGSTOP',
  /**
   * SIGSYS
   */
  SIGSYS: 'SIGSYS',
  /**
   * SIGTERM
   */
  SIGTERM: 'SIGTERM',
  /**
   * SIGTRAP
   */
  SIGTRAP: 'SIGTRAP',
  /**
   * SIGTSTP
   */
  SIGTSTP: 'SIGTSTP',
  /**
   * SIGTTIN
   */
  SIGTTIN: 'SIGTTIN',
  /**
   * SIGTTOU
   */
  SIGTTOU: 'SIGTTOU',
  /**
   * SIGURG
   */
  SIGURG: 'SIGURG',
  /**
   * SIGUSR1
   */
  SIGUSR1: 'SIGUSR1',
  /**
   * SIGUSR2
   */
  SIGUSR2: 'SIGUSR2',
  /**
   * SIGVTALRM
   */
  SIGVTALRM: 'SIGVTALRM',
  /**
   * SIGWINCH
   */
  SIGWINCH: 'SIGWINCH',
  /**
   * SIGXCPU
   */
  SIGXCPU: 'SIGXCPU',
  /**
   * SIGXFSZ
   */
  SIGXFSZ: 'SIGXFSZ',
  /**
   * SIGRTMIN
   */
  SIGRTMIN: 'SIGRTMIN',
  /**
   * SIGRTMINPLUS1
   */
  SIGRTMINPLUS1: 'SIGRTMIN+1',
  /**
   * SIGRTMINPLUS2
   */
  SIGRTMINPLUS2: 'SIGRTMIN+2',
  /**
   * SIGRTMINPLUS3
   */
  SIGRTMINPLUS3: 'SIGRTMIN+3',
  /**
   * SIGRTMINPLUS4
   */
  SIGRTMINPLUS4: 'SIGRTMIN+4',
  /**
   * SIGRTMINPLUS5
   */
  SIGRTMINPLUS5: 'SIGRTMIN+5',
  /**
   * SIGRTMINPLUS6
   */
  SIGRTMINPLUS6: 'SIGRTMIN+6',
  /**
   * SIGRTMINPLUS7
   */
  SIGRTMINPLUS7: 'SIGRTMIN+7',
  /**
   * SIGRTMINPLUS8
   */
  SIGRTMINPLUS8: 'SIGRTMIN+8',
  /**
   * SIGRTMINPLUS9
   */
  SIGRTMINPLUS9: 'SIGRTMIN+9',
  /**
   * SIGRTMINPLUS10
   */
  SIGRTMINPLUS10: 'SIGRTMIN+10',
  /**
   * SIGRTMINPLUS11
   */
  SIGRTMINPLUS11: 'SIGRTMIN+11',
  /**
   * SIGRTMINPLUS12
   */
  SIGRTMINPLUS12: 'SIGRTMIN+12',
  /**
   * SIGRTMINPLUS13
   */
  SIGRTMINPLUS13: 'SIGRTMIN+13',
  /**
   * SIGRTMINPLUS14
   */
  SIGRTMINPLUS14: 'SIGRTMIN+14',
  /**
   * SIGRTMINPLUS15
   */
  SIGRTMINPLUS15: 'SIGRTMIN+15',
  /**
   * SIGRTMAXMINUS14
   */
  SIGRTMAXMINUS14: 'SIGRTMAX-14',
  /**
   * SIGRTMAXMINUS13
   */
  SIGRTMAXMINUS13: 'SIGRTMAX-13',
  /**
   * SIGRTMAXMINUS12
   */
  SIGRTMAXMINUS12: 'SIGRTMAX-12',
  /**
   * SIGRTMAXMINUS11
   */
  SIGRTMAXMINUS11: 'SIGRTMAX-11',
  /**
   * SIGRTMAXMINUS10
   */
  SIGRTMAXMINUS10: 'SIGRTMAX-10',
  /**
   * SIGRTMAXMINUS9
   */
  SIGRTMAXMINUS9: 'SIGRTMAX-9',
  /**
   * SIGRTMAXMINUS8
   */
  SIGRTMAXMINUS8: 'SIGRTMAX-8',
  /**
   * SIGRTMAXMINUS7
   */
  SIGRTMAXMINUS7: 'SIGRTMAX-7',
  /**
   * SIGRTMAXMINUS6
   */
  SIGRTMAXMINUS6: 'SIGRTMAX-6',
  /**
   * SIGRTMAXMINUS5
   */
  SIGRTMAXMINUS5: 'SIGRTMAX-5',
  /**
   * SIGRTMAXMINUS4
   */
  SIGRTMAXMINUS4: 'SIGRTMAX-4',
  /**
   * SIGRTMAXMINUS3
   */
  SIGRTMAXMINUS3: 'SIGRTMAX-3',
  /**
   * SIGRTMAXMINUS2
   */
  SIGRTMAXMINUS2: 'SIGRTMAX-2',
  /**
   * SIGRTMAXMINUS1
   */
  SIGRTMAXMINUS1: 'SIGRTMAX-1',
  /**
   * SIGRTMAX
   */
  SIGRTMAX: 'SIGRTMAX',
} as const;

export type V1Signal = (typeof V1Signal)[keyof typeof V1Signal];

export type V1SleepAction = {
  /**
   * Seconds is the number of seconds to sleep.
   */
  seconds?: number;
};

export const V1StorageMedium = {
  /**
   * StorageMediumDefault
   *
   * use whatever the default is for the node, assume anything we don't explicitly handle is this
   */
  STORAGE_MEDIUM_DEFAULT: '',
  /**
   * StorageMediumMemory
   *
   * use memory (e.g. tmpfs on linux)
   */
  STORAGE_MEDIUM_MEMORY: 'Memory',
  /**
   * StorageMediumHugePages
   *
   * use hugepages
   */
  STORAGE_MEDIUM_HUGE_PAGES: 'HugePages',
  /**
   * StorageMediumHugePagesPrefix
   *
   * prefix for full medium notation HugePages-<size>
   */
  STORAGE_MEDIUM_HUGE_PAGES_PREFIX: 'HugePages-',
} as const;

export type V1StorageMedium =
  (typeof V1StorageMedium)[keyof typeof V1StorageMedium];

export type V1StorageOsVolumeSource = {
  /**
   * fsType is the filesystem type to mount.
   * Must be a filesystem type supported by the host operating system.
   * Ex. "ext4", "xfs", "ntfs". Implicitly inferred to be "ext4" if unspecified.
   * +optional
   */
  fsType?: string;
  /**
   * readOnly defaults to false (read/write). ReadOnly here will force
   * the ReadOnly setting in VolumeMounts.
   * +optional
   */
  readOnly?: boolean;
  /**
   * secretRef specifies the secret to use for obtaining the StorageOS API
   * credentials.  If not specified, default values will be attempted.
   * +optional
   */
  secretRef?: K8sIoApiCoreV1LocalObjectReference;
  /**
   * volumeName is the human-readable name of the StorageOS volume.  Volume
   * names are only unique within a namespace.
   */
  volumeName?: string;
  /**
   * volumeNamespace specifies the scope of the volume within StorageOS.  If no
   * namespace is specified then the Pod's namespace will be used.  This allows the
   * Kubernetes name scoping to be mirrored within StorageOS for tighter integration.
   * Set VolumeName to any name to override the default behaviour.
   * Set to "default" if you are not using namespaces within StorageOS.
   * Namespaces that do not pre-exist within StorageOS will be created.
   * +optional
   */
  volumeNamespace?: string;
};

export const V1SupplementalGroupsPolicy = {
  /**
   * SupplementalGroupsPolicyMerge
   */
  SUPPLEMENTAL_GROUPS_POLICY_MERGE: 'Merge' /**
   * SupplementalGroupsPolicyStrict
   */,
  SUPPLEMENTAL_GROUPS_POLICY_STRICT: 'Strict',
} as const;

export type V1SupplementalGroupsPolicy =
  (typeof V1SupplementalGroupsPolicy)[keyof typeof V1SupplementalGroupsPolicy];

export type V1Sysctl = {
  /**
   * Name of a property to set
   */
  name?: string;
  /**
   * Value of a property to set
   */
  value?: string;
};

export type V1TcpSocketAction = {
  /**
   * Optional: Host name to connect to, defaults to the pod IP.
   * +optional
   */
  host?: string;
  /**
   * Number or name of the port to access on the container.
   * Number must be in the range 1 to 65535.
   * Name must be an IANA_SVC_NAME.
   */
  port?: IntstrIntOrString;
};

export type V1Taint = {
  /**
   * Required. The effect of the taint on pods
   * that do not tolerate the taint.
   * Valid effects are NoSchedule, PreferNoSchedule and NoExecute.
   */
  effect?: V1TaintEffect;
  /**
   * Required. The taint key to be applied to a node.
   */
  key?: string;
  /**
   * TimeAdded represents the time at which the taint was added.
   * +optional
   */
  timeAdded?: string;
  /**
   * The taint value corresponding to the taint key.
   * +optional
   */
  value?: string;
};

export const V1TaintEffect = {
  /**
   * TaintEffectNoSchedule
   */
  TAINT_EFFECT_NO_SCHEDULE: 'NoSchedule',
  /**
   * TaintEffectPreferNoSchedule
   */
  TAINT_EFFECT_PREFER_NO_SCHEDULE: 'PreferNoSchedule',
  /**
   * TaintEffectNoExecute
   */
  TAINT_EFFECT_NO_EXECUTE: 'NoExecute',
} as const;

export type V1TaintEffect = (typeof V1TaintEffect)[keyof typeof V1TaintEffect];

export const V1TerminationMessagePolicy = {
  /**
   * TerminationMessageReadFile
   */
  TERMINATION_MESSAGE_READ_FILE: 'File' /**
   * TerminationMessageFallbackToLogsOnError
   */,
  TERMINATION_MESSAGE_FALLBACK_TO_LOGS_ON_ERROR: 'FallbackToLogsOnError',
} as const;

export type V1TerminationMessagePolicy =
  (typeof V1TerminationMessagePolicy)[keyof typeof V1TerminationMessagePolicy];

export type V1Toleration = {
  /**
   * Effect indicates the taint effect to match. Empty means match all taint effects.
   * When specified, allowed values are NoSchedule, PreferNoSchedule and NoExecute.
   * +optional
   */
  effect?: V1TaintEffect;
  /**
   * Key is the taint key that the toleration applies to. Empty means match all taint keys.
   * If the key is empty, operator must be Exists; this combination means to match all values and all keys.
   * +optional
   */
  key?: string;
  /**
   * Operator represents a key's relationship to the value.
   * Valid operators are Exists, Equal, Lt, and Gt. Defaults to Equal.
   * Exists is equivalent to wildcard for value, so that a pod can
   * tolerate all taints of a particular category.
   * Lt and Gt perform numeric comparisons (requires feature gate TaintTolerationComparisonOperators).
   * +optional
   */
  operator?: V1TolerationOperator;
  /**
   * TolerationSeconds represents the period of time the toleration (which must be
   * of effect NoExecute, otherwise this field is ignored) tolerates the taint. By default,
   * it is not set, which means tolerate the taint forever (do not evict). Zero and
   * negative values will be treated as 0 (evict immediately) by the system.
   * +optional
   */
  tolerationSeconds?: number;
  /**
   * Value is the taint value the toleration matches to.
   * If the operator is Exists, the value should be empty, otherwise just a regular string.
   * +optional
   */
  value?: string;
};

export const V1TolerationOperator = {
  /**
   * TolerationOpExists
   */
  TOLERATION_OP_EXISTS: 'Exists',
  /**
   * TolerationOpEqual
   */
  TOLERATION_OP_EQUAL: 'Equal',
  /**
   * TolerationOpLt
   */
  TOLERATION_OP_LT: 'Lt',
  /**
   * TolerationOpGt
   */
  TOLERATION_OP_GT: 'Gt',
} as const;

export type V1TolerationOperator =
  (typeof V1TolerationOperator)[keyof typeof V1TolerationOperator];

export type V1TopologySpreadConstraint = {
  /**
   * LabelSelector is used to find matching pods.
   * Pods that match this label selector are counted to determine the number of pods
   * in their corresponding topology domain.
   * +optional
   */
  labelSelector?: V1LabelSelector;
  /**
   * MatchLabelKeys is a set of pod label keys to select the pods over which
   * spreading will be calculated. The keys are used to lookup values from the
   * incoming pod labels, those key-value labels are ANDed with labelSelector
   * to select the group of existing pods over which spreading will be calculated
   * for the incoming pod. The same key is forbidden to exist in both MatchLabelKeys and LabelSelector.
   * MatchLabelKeys cannot be set when LabelSelector isn't set.
   * Keys that don't exist in the incoming pod labels will
   * be ignored. A null or empty list means only match against labelSelector.
   *
   * This is a beta field and requires the MatchLabelKeysInPodTopologySpread feature gate to be enabled (enabled by default).
   * +listType=atomic
   * +optional
   */
  matchLabelKeys?: Array<string>;
  /**
   * MaxSkew describes the degree to which pods may be unevenly distributed.
   * When `whenUnsatisfiable=DoNotSchedule`, it is the maximum permitted difference
   * between the number of matching pods in the target topology and the global minimum.
   * The global minimum is the minimum number of matching pods in an eligible domain
   * or zero if the number of eligible domains is less than MinDomains.
   * For example, in a 3-zone cluster, MaxSkew is set to 1, and pods with the same
   * labelSelector spread as 2/2/1:
   * In this case, the global minimum is 1.
   * +-------+-------+-------+
   * | zone1 | zone2 | zone3 |
   * +-------+-------+-------+
   * |  P P  |  P P  |   P   |
   * +-------+-------+-------+
   * - if MaxSkew is 1, incoming pod can only be scheduled to zone3 to become 2/2/2;
   * scheduling it onto zone1(zone2) would make the ActualSkew(3-1) on zone1(zone2)
   * violate MaxSkew(1).
   * - if MaxSkew is 2, incoming pod can be scheduled onto any zone.
   * When `whenUnsatisfiable=ScheduleAnyway`, it is used to give higher precedence
   * to topologies that satisfy it.
   * It's a required field. Default value is 1 and 0 is not allowed.
   */
  maxSkew?: number;
  /**
   * MinDomains indicates a minimum number of eligible domains.
   * When the number of eligible domains with matching topology keys is less than minDomains,
   * Pod Topology Spread treats "global minimum" as 0, and then the calculation of Skew is performed.
   * And when the number of eligible domains with matching topology keys equals or greater than minDomains,
   * this value has no effect on scheduling.
   * As a result, when the number of eligible domains is less than minDomains,
   * scheduler won't schedule more than maxSkew Pods to those domains.
   * If value is nil, the constraint behaves as if MinDomains is equal to 1.
   * Valid values are integers greater than 0.
   * When value is not nil, WhenUnsatisfiable must be DoNotSchedule.
   *
   * For example, in a 3-zone cluster, MaxSkew is set to 2, MinDomains is set to 5 and pods with the same
   * labelSelector spread as 2/2/2:
   * +-------+-------+-------+
   * | zone1 | zone2 | zone3 |
   * +-------+-------+-------+
   * |  P P  |  P P  |  P P  |
   * +-------+-------+-------+
   * The number of domains is less than 5(MinDomains), so "global minimum" is treated as 0.
   * In this situation, new pod with the same labelSelector cannot be scheduled,
   * because computed skew will be 3(3 - 0) if new Pod is scheduled to any of the three zones,
   * it will violate MaxSkew.
   * +optional
   */
  minDomains?: number;
  /**
   * NodeAffinityPolicy indicates how we will treat Pod's nodeAffinity/nodeSelector
   * when calculating pod topology spread skew. Options are:
   * - Honor: only nodes matching nodeAffinity/nodeSelector are included in the calculations.
   * - Ignore: nodeAffinity/nodeSelector are ignored. All nodes are included in the calculations.
   *
   * If this value is nil, the behavior is equivalent to the Honor policy.
   * +optional
   */
  nodeAffinityPolicy?: V1NodeInclusionPolicy;
  /**
   * NodeTaintsPolicy indicates how we will treat node taints when calculating
   * pod topology spread skew. Options are:
   * - Honor: nodes without taints, along with tainted nodes for which the incoming pod
   * has a toleration, are included.
   * - Ignore: node taints are ignored. All nodes are included.
   *
   * If this value is nil, the behavior is equivalent to the Ignore policy.
   * +optional
   */
  nodeTaintsPolicy?: V1NodeInclusionPolicy;
  /**
   * TopologyKey is the key of node labels. Nodes that have a label with this key
   * and identical values are considered to be in the same topology.
   * We consider each <key, value> as a "bucket", and try to put balanced number
   * of pods into each bucket.
   * We define a domain as a particular instance of a topology.
   * Also, we define an eligible domain as a domain whose nodes meet the requirements of
   * nodeAffinityPolicy and nodeTaintsPolicy.
   * e.g. If TopologyKey is "kubernetes.io/hostname", each Node is a domain of that topology.
   * And, if TopologyKey is "topology.kubernetes.io/zone", each zone is a domain of that topology.
   * It's a required field.
   */
  topologyKey?: string;
  /**
   * WhenUnsatisfiable indicates how to deal with a pod if it doesn't satisfy
   * the spread constraint.
   * - DoNotSchedule (default) tells the scheduler not to schedule it.
   * - ScheduleAnyway tells the scheduler to schedule the pod in any location,
   * but giving higher precedence to topologies that would help reduce the
   * skew.
   * A constraint is considered "Unsatisfiable" for an incoming pod
   * if and only if every possible node assignment for that pod would violate
   * "MaxSkew" on some topology.
   * For example, in a 3-zone cluster, MaxSkew is set to 1, and pods with the same
   * labelSelector spread as 3/1/1:
   * +-------+-------+-------+
   * | zone1 | zone2 | zone3 |
   * +-------+-------+-------+
   * | P P P |   P   |   P   |
   * +-------+-------+-------+
   * If WhenUnsatisfiable is set to DoNotSchedule, incoming pod can only be scheduled
   * to zone2(zone3) to become 3/2/1(3/1/2) as ActualSkew(2-1) on zone2(zone3) satisfies
   * MaxSkew(1). In other words, the cluster can still be imbalanced, but scheduler
   * won't make it *more* imbalanced.
   * It's a required field.
   */
  whenUnsatisfiable?: V1UnsatisfiableConstraintAction;
};

export type V1TypedLocalObjectReference = {
  /**
   * APIGroup is the group for the resource being referenced.
   * If APIGroup is not specified, the specified Kind must be in the core API group.
   * For any other third-party types, APIGroup is required.
   * +optional
   */
  apiGroup?: string;
  /**
   * Kind is the type of resource being referenced
   */
  kind?: string;
  /**
   * Name is the name of resource being referenced
   */
  name?: string;
};

export type V1TypedObjectReference = {
  /**
   * APIGroup is the group for the resource being referenced.
   * If APIGroup is not specified, the specified Kind must be in the core API group.
   * For any other third-party types, APIGroup is required.
   * +optional
   */
  apiGroup?: string;
  /**
   * Kind is the type of resource being referenced
   */
  kind?: string;
  /**
   * Name is the name of resource being referenced
   */
  name?: string;
  /**
   * Namespace is the namespace of resource being referenced
   * Note that when a namespace is specified, a gateway.networking.k8s.io/ReferenceGrant object is required in the referent namespace to allow that namespace's owner to accept the reference. See the ReferenceGrant documentation for details.
   * (Alpha) This field requires the CrossNamespaceVolumeDataSource feature gate to be enabled.
   * +featureGate=CrossNamespaceVolumeDataSource
   * +optional
   */
  namespace?: string;
};

export const V1UriScheme = {
  /**
   * URISchemeHTTP
   */
  URI_SCHEME_HTTP: 'HTTP' /**
   * URISchemeHTTPS
   */,
  URI_SCHEME_HTTPS: 'HTTPS',
} as const;

export type V1UriScheme = (typeof V1UriScheme)[keyof typeof V1UriScheme];

export const V1UnsatisfiableConstraintAction = {
  /**
   * DoNotSchedule
   */
  DO_NOT_SCHEDULE: 'DoNotSchedule' /**
   * ScheduleAnyway
   */,
  SCHEDULE_ANYWAY: 'ScheduleAnyway',
} as const;

export type V1UnsatisfiableConstraintAction =
  (typeof V1UnsatisfiableConstraintAction)[keyof typeof V1UnsatisfiableConstraintAction];

export type V1Volume = {
  /**
   * awsElasticBlockStore represents an AWS Disk resource that is attached to a
   * kubelet's host machine and then exposed to the pod.
   * Deprecated: AWSElasticBlockStore is deprecated. All operations for the in-tree
   * awsElasticBlockStore type are redirected to the ebs.csi.aws.com CSI driver.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#awselasticblockstore
   * +optional
   */
  awsElasticBlockStore?: V1AwsElasticBlockStoreVolumeSource;
  /**
   * azureDisk represents an Azure Data Disk mount on the host and bind mount to the pod.
   * Deprecated: AzureDisk is deprecated. All operations for the in-tree azureDisk type
   * are redirected to the disk.csi.azure.com CSI driver.
   * +optional
   */
  azureDisk?: V1AzureDiskVolumeSource;
  /**
   * azureFile represents an Azure File Service mount on the host and bind mount to the pod.
   * Deprecated: AzureFile is deprecated. All operations for the in-tree azureFile type
   * are redirected to the file.csi.azure.com CSI driver.
   * +optional
   */
  azureFile?: V1AzureFileVolumeSource;
  /**
   * cephFS represents a Ceph FS mount on the host that shares a pod's lifetime.
   * Deprecated: CephFS is deprecated and the in-tree cephfs type is no longer supported.
   * +optional
   */
  cephfs?: V1CephFsVolumeSource;
  /**
   * cinder represents a cinder volume attached and mounted on kubelets host machine.
   * Deprecated: Cinder is deprecated. All operations for the in-tree cinder type
   * are redirected to the cinder.csi.openstack.org CSI driver.
   * More info: https://examples.k8s.io/mysql-cinder-pd/README.md
   * +optional
   */
  cinder?: V1CinderVolumeSource;
  /**
   * configMap represents a configMap that should populate this volume
   * +optional
   */
  configMap?: V1ConfigMapVolumeSource;
  /**
   * csi (Container Storage Interface) represents ephemeral storage that is handled by certain external CSI drivers.
   * +optional
   */
  csi?: V1CsiVolumeSource;
  /**
   * downwardAPI represents downward API about the pod that should populate this volume
   * +optional
   */
  downwardAPI?: V1DownwardApiVolumeSource;
  /**
   * emptyDir represents a temporary directory that shares a pod's lifetime.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#emptydir
   * +optional
   */
  emptyDir?: V1EmptyDirVolumeSource;
  /**
   * ephemeral represents a volume that is handled by a cluster storage driver.
   * The volume's lifecycle is tied to the pod that defines it - it will be created before the pod starts,
   * and deleted when the pod is removed.
   *
   * Use this if:
   * a) the volume is only needed while the pod runs,
   * b) features of normal volumes like restoring from snapshot or capacity
   * tracking are needed,
   * c) the storage driver is specified through a storage class, and
   * d) the storage driver supports dynamic volume provisioning through
   * a PersistentVolumeClaim (see EphemeralVolumeSource for more
   * information on the connection between this volume type
   * and PersistentVolumeClaim).
   *
   * Use PersistentVolumeClaim or one of the vendor-specific
   * APIs for volumes that persist for longer than the lifecycle
   * of an individual pod.
   *
   * Use CSI for light-weight local ephemeral volumes if the CSI driver is meant to
   * be used that way - see the documentation of the driver for
   * more information.
   *
   * A pod can use both types of ephemeral volumes and
   * persistent volumes at the same time.
   *
   * +optional
   */
  ephemeral?: V1EphemeralVolumeSource;
  /**
   * fc represents a Fibre Channel resource that is attached to a kubelet's host machine and then exposed to the pod.
   * +optional
   */
  fc?: V1FcVolumeSource;
  /**
   * flexVolume represents a generic volume resource that is
   * provisioned/attached using an exec based plugin.
   * Deprecated: FlexVolume is deprecated. Consider using a CSIDriver instead.
   * +optional
   */
  flexVolume?: V1FlexVolumeSource;
  /**
   * flocker represents a Flocker volume attached to a kubelet's host machine. This depends on the Flocker control service being running.
   * Deprecated: Flocker is deprecated and the in-tree flocker type is no longer supported.
   * +optional
   */
  flocker?: V1FlockerVolumeSource;
  /**
   * gcePersistentDisk represents a GCE Disk resource that is attached to a
   * kubelet's host machine and then exposed to the pod.
   * Deprecated: GCEPersistentDisk is deprecated. All operations for the in-tree
   * gcePersistentDisk type are redirected to the pd.csi.storage.gke.io CSI driver.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#gcepersistentdisk
   * +optional
   */
  gcePersistentDisk?: V1GcePersistentDiskVolumeSource;
  /**
   * gitRepo represents a git repository at a particular revision.
   * Deprecated: GitRepo is deprecated. To provision a container with a git repo, mount an
   * EmptyDir into an InitContainer that clones the repo using git, then mount the EmptyDir
   * into the Pod's container.
   * +optional
   */
  gitRepo?: V1GitRepoVolumeSource;
  /**
   * glusterfs represents a Glusterfs mount on the host that shares a pod's lifetime.
   * Deprecated: Glusterfs is deprecated and the in-tree glusterfs type is no longer supported.
   * +optional
   */
  glusterfs?: V1GlusterfsVolumeSource;
  /**
   * hostPath represents a pre-existing file or directory on the host
   * machine that is directly exposed to the container. This is generally
   * used for system agents or other privileged things that are allowed
   * to see the host machine. Most containers will NOT need this.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#hostpath
   * ---
   * TODO(jonesdl) We need to restrict who can use host directory mounts and who can/can not
   * mount host directories as read/write.
   * +optional
   */
  hostPath?: V1HostPathVolumeSource;
  /**
   * image represents an OCI object (a container image or artifact) pulled and mounted on the kubelet's host machine.
   * The volume is resolved at pod startup depending on which PullPolicy value is provided:
   *
   * - Always: the kubelet always attempts to pull the reference. Container creation will fail If the pull fails.
   * - Never: the kubelet never pulls the reference and only uses a local image or artifact. Container creation will fail if the reference isn't present.
   * - IfNotPresent: the kubelet pulls if the reference isn't already present on disk. Container creation will fail if the reference isn't present and the pull fails.
   *
   * The volume gets re-resolved if the pod gets deleted and recreated, which means that new remote content will become available on pod recreation.
   * A failure to resolve or pull the image during pod startup will block containers from starting and may add significant latency. Failures will be retried using normal volume backoff and will be reported on the pod reason and message.
   * The types of objects that may be mounted by this volume are defined by the container runtime implementation on a host machine and at minimum must include all valid types supported by the container image field.
   * The OCI object gets mounted in a single directory (spec.containers[*].volumeMounts.mountPath) by merging the manifest layers in the same way as for container images.
   * The volume will be mounted read-only (ro) and non-executable files (noexec).
   * Sub path mounts for containers are not supported (spec.containers[*].volumeMounts.subpath) before 1.33.
   * The field spec.securityContext.fsGroupChangePolicy has no effect on this volume type.
   * +featureGate=ImageVolume
   * +optional
   */
  image?: V1ImageVolumeSource;
  /**
   * iscsi represents an ISCSI Disk resource that is attached to a
   * kubelet's host machine and then exposed to the pod.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes/#iscsi
   * +optional
   */
  iscsi?: V1IscsiVolumeSource;
  /**
   * name of the volume.
   * Must be a DNS_LABEL and unique within the pod.
   * More info: https://kubernetes.io/docs/concepts/overview/working-with-objects/names/#names
   */
  name?: string;
  /**
   * nfs represents an NFS mount on the host that shares a pod's lifetime
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#nfs
   * +optional
   */
  nfs?: V1NfsVolumeSource;
  /**
   * persistentVolumeClaimVolumeSource represents a reference to a
   * PersistentVolumeClaim in the same namespace.
   * More info: https://kubernetes.io/docs/concepts/storage/persistent-volumes#persistentvolumeclaims
   * +optional
   */
  persistentVolumeClaim?: V1PersistentVolumeClaimVolumeSource;
  /**
   * photonPersistentDisk represents a PhotonController persistent disk attached and mounted on kubelets host machine.
   * Deprecated: PhotonPersistentDisk is deprecated and the in-tree photonPersistentDisk type is no longer supported.
   */
  photonPersistentDisk?: V1PhotonPersistentDiskVolumeSource;
  /**
   * portworxVolume represents a portworx volume attached and mounted on kubelets host machine.
   * Deprecated: PortworxVolume is deprecated. All operations for the in-tree portworxVolume type
   * are redirected to the pxd.portworx.com CSI driver when the CSIMigrationPortworx feature-gate
   * is on.
   * +optional
   */
  portworxVolume?: V1PortworxVolumeSource;
  /**
   * projected items for all in one resources secrets, configmaps, and downward API
   */
  projected?: V1ProjectedVolumeSource;
  /**
   * quobyte represents a Quobyte mount on the host that shares a pod's lifetime.
   * Deprecated: Quobyte is deprecated and the in-tree quobyte type is no longer supported.
   * +optional
   */
  quobyte?: V1QuobyteVolumeSource;
  /**
   * rbd represents a Rados Block Device mount on the host that shares a pod's lifetime.
   * Deprecated: RBD is deprecated and the in-tree rbd type is no longer supported.
   * +optional
   */
  rbd?: V1RbdVolumeSource;
  /**
   * scaleIO represents a ScaleIO persistent volume attached and mounted on Kubernetes nodes.
   * Deprecated: ScaleIO is deprecated and the in-tree scaleIO type is no longer supported.
   * +optional
   */
  scaleIO?: V1ScaleIoVolumeSource;
  /**
   * secret represents a secret that should populate this volume.
   * More info: https://kubernetes.io/docs/concepts/storage/volumes#secret
   * +optional
   */
  secret?: V1SecretVolumeSource;
  /**
   * storageOS represents a StorageOS volume attached and mounted on Kubernetes nodes.
   * Deprecated: StorageOS is deprecated and the in-tree storageos type is no longer supported.
   * +optional
   */
  storageos?: V1StorageOsVolumeSource;
  /**
   * vsphereVolume represents a vSphere volume attached and mounted on kubelets host machine.
   * Deprecated: VsphereVolume is deprecated. All operations for the in-tree vsphereVolume type
   * are redirected to the csi.vsphere.vmware.com CSI driver.
   * +optional
   */
  vsphereVolume?: V1VsphereVirtualDiskVolumeSource;
};

export type V1VolumeDevice = {
  /**
   * devicePath is the path inside of the container that the device will be mapped to.
   */
  devicePath?: string;
  /**
   * name must match the name of a persistentVolumeClaim in the pod
   */
  name?: string;
};

export type V1VolumeMount = {
  /**
   * Path within the container at which the volume should be mounted.  Must
   * not contain ':'.
   */
  mountPath?: string;
  /**
   * mountPropagation determines how mounts are propagated from the host
   * to container and the other way around.
   * When not set, MountPropagationNone is used.
   * This field is beta in 1.10.
   * When RecursiveReadOnly is set to IfPossible or to Enabled, MountPropagation must be None or unspecified
   * (which defaults to None).
   * +optional
   */
  mountPropagation?: V1MountPropagationMode;
  /**
   * This must match the Name of a Volume.
   */
  name?: string;
  /**
   * Mounted read-only if true, read-write otherwise (false or unspecified).
   * Defaults to false.
   * +optional
   */
  readOnly?: boolean;
  /**
   * RecursiveReadOnly specifies whether read-only mounts should be handled
   * recursively.
   *
   * If ReadOnly is false, this field has no meaning and must be unspecified.
   *
   * If ReadOnly is true, and this field is set to Disabled, the mount is not made
   * recursively read-only.  If this field is set to IfPossible, the mount is made
   * recursively read-only, if it is supported by the container runtime.  If this
   * field is set to Enabled, the mount is made recursively read-only if it is
   * supported by the container runtime, otherwise the pod will not be started and
   * an error will be generated to indicate the reason.
   *
   * If this field is set to IfPossible or Enabled, MountPropagation must be set to
   * None (or be unspecified, which defaults to None).
   *
   * If this field is not specified, it is treated as an equivalent of Disabled.
   * +optional
   */
  recursiveReadOnly?: V1RecursiveReadOnlyMode;
  /**
   * Path within the volume from which the container's volume should be mounted.
   * Defaults to "" (volume's root).
   * +optional
   */
  subPath?: string;
  /**
   * Expanded path within the volume from which the container's volume should be mounted.
   * Behaves similarly to SubPath but environment variable references $(VAR_NAME) are expanded using the container's environment.
   * Defaults to "" (volume's root).
   * SubPathExpr and SubPath are mutually exclusive.
   * +optional
   */
  subPathExpr?: string;
};

export type V1VolumeMountStatus = {
  /**
   * MountPath corresponds to the original VolumeMount.
   */
  mountPath?: string;
  /**
   * Name corresponds to the name of the original VolumeMount.
   */
  name?: string;
  /**
   * ReadOnly corresponds to the original VolumeMount.
   * +optional
   */
  readOnly?: boolean;
  /**
   * RecursiveReadOnly must be set to Disabled, Enabled, or unspecified (for non-readonly mounts).
   * An IfPossible value in the original VolumeMount must be translated to Disabled or Enabled,
   * depending on the mount result.
   * +optional
   */
  recursiveReadOnly?: V1RecursiveReadOnlyMode;
};

export type V1VolumeProjection = {
  /**
   * ClusterTrustBundle allows a pod to access the `.spec.trustBundle` field
   * of ClusterTrustBundle objects in an auto-updating file.
   *
   * Alpha, gated by the ClusterTrustBundleProjection feature gate.
   *
   * ClusterTrustBundle objects can either be selected by name, or by the
   * combination of signer name and a label selector.
   *
   * Kubelet performs aggressive normalization of the PEM contents written
   * into the pod filesystem.  Esoteric PEM features such as inter-block
   * comments and block headers are stripped.  Certificates are deduplicated.
   * The ordering of certificates within the file is arbitrary, and Kubelet
   * may change the order over time.
   *
   * +featureGate=ClusterTrustBundleProjection
   * +optional
   */
  clusterTrustBundle?: V1ClusterTrustBundleProjection;
  /**
   * configMap information about the configMap data to project
   * +optional
   */
  configMap?: V1ConfigMapProjection;
  /**
   * downwardAPI information about the downwardAPI data to project
   * +optional
   */
  downwardAPI?: V1DownwardApiProjection;
  /**
   * Projects an auto-rotating credential bundle (private key and certificate
   * chain) that the pod can use either as a TLS client or server.
   *
   * Kubelet generates a private key and uses it to send a
   * PodCertificateRequest to the named signer.  Once the signer approves the
   * request and issues a certificate chain, Kubelet writes the key and
   * certificate chain to the pod filesystem.  The pod does not start until
   * certificates have been issued for each podCertificate projected volume
   * source in its spec.
   *
   * Kubelet will begin trying to rotate the certificate at the time indicated
   * by the signer using the PodCertificateRequest.Status.BeginRefreshAt
   * timestamp.
   *
   * Kubelet can write a single file, indicated by the credentialBundlePath
   * field, or separate files, indicated by the keyPath and
   * certificateChainPath fields.
   *
   * The credential bundle is a single file in PEM format.  The first PEM
   * entry is the private key (in PKCS#8 format), and the remaining PEM
   * entries are the certificate chain issued by the signer (typically,
   * signers will return their certificate chain in leaf-to-root order).
   *
   * Prefer using the credential bundle format, since your application code
   * can read it atomically.  If you use keyPath and certificateChainPath,
   * your application must make two separate file reads. If these coincide
   * with a certificate rotation, it is possible that the private key and leaf
   * certificate you read may not correspond to each other.  Your application
   * will need to check for this condition, and re-read until they are
   * consistent.
   *
   * The named signer controls chooses the format of the certificate it
   * issues; consult the signer implementation's documentation to learn how to
   * use the certificates it issues.
   *
   * +featureGate=PodCertificateProjection
   * +optional
   */
  podCertificate?: V1PodCertificateProjection;
  /**
   * secret information about the secret data to project
   * +optional
   */
  secret?: V1SecretProjection;
  /**
   * serviceAccountToken is information about the serviceAccountToken data to project
   * +optional
   */
  serviceAccountToken?: V1ServiceAccountTokenProjection;
};

export type V1VolumeResourceRequirements = {
  /**
   * Limits describes the maximum amount of compute resources allowed.
   * More info: https://kubernetes.io/docs/concepts/configuration/manage-resources-containers/
   * +optional
   */
  limits?: V1ResourceList;
  /**
   * Requests describes the minimum amount of compute resources required.
   * If Requests is omitted for a container, it defaults to Limits if that is explicitly specified,
   * otherwise to an implementation-defined value. Requests cannot exceed Limits.
   * More info: https://kubernetes.io/docs/concepts/configuration/manage-resources-containers/
   * +optional
   */
  requests?: V1ResourceList;
};

export type V1VsphereVirtualDiskVolumeSource = {
  /**
   * fsType is filesystem type to mount.
   * Must be a filesystem type supported by the host operating system.
   * Ex. "ext4", "xfs", "ntfs". Implicitly inferred to be "ext4" if unspecified.
   * +optional
   */
  fsType?: string;
  /**
   * storagePolicyID is the storage Policy Based Management (SPBM) profile ID associated with the StoragePolicyName.
   * +optional
   */
  storagePolicyID?: string;
  /**
   * storagePolicyName is the storage Policy Based Management (SPBM) profile name.
   * +optional
   */
  storagePolicyName?: string;
  /**
   * volumePath is the path that identifies vSphere volume vmdk
   */
  volumePath?: string;
};

export type V1WeightedPodAffinityTerm = {
  /**
   * Required. A pod affinity term, associated with the corresponding weight.
   */
  podAffinityTerm?: V1PodAffinityTerm;
  /**
   * weight associated with matching the corresponding podAffinityTerm,
   * in the range 1-100.
   */
  weight?: number;
};

export type V1WindowsSecurityContextOptions = {
  /**
   * GMSACredentialSpec is where the GMSA admission webhook
   * (https://github.com/kubernetes-sigs/windows-gmsa) inlines the contents of the
   * GMSA credential spec named by the GMSACredentialSpecName field.
   * +optional
   */
  gmsaCredentialSpec?: string;
  /**
   * GMSACredentialSpecName is the name of the GMSA credential spec to use.
   * +optional
   */
  gmsaCredentialSpecName?: string;
  /**
   * HostProcess determines if a container should be run as a 'Host Process' container.
   * All of a Pod's containers must have the same effective HostProcess value
   * (it is not allowed to have a mix of HostProcess containers and non-HostProcess containers).
   * In addition, if HostProcess is true then HostNetwork must also be set to true.
   * +optional
   */
  hostProcess?: boolean;
  /**
   * The UserName in Windows to run the entrypoint of the container process.
   * Defaults to the user specified in image metadata if unspecified.
   * May also be set in PodSecurityContext. If set in both SecurityContext and
   * PodSecurityContext, the value specified in SecurityContext takes precedence.
   * +optional
   */
  runAsUserName?: string;
};

export type V1WorkloadReference = {
  /**
   * Name defines the name of the Workload object this Pod belongs to.
   * Workload must be in the same namespace as the Pod.
   * If it doesn't match any existing Workload, the Pod will remain unschedulable
   * until a Workload object is created and observed by the kube-scheduler.
   * It must be a DNS subdomain.
   *
   * +required
   */
  name?: string;
  /**
   * PodGroup is the name of the PodGroup within the Workload that this Pod
   * belongs to. If it doesn't match any existing PodGroup within the Workload,
   * the Pod will remain unschedulable until the Workload object is recreated
   * and observed by the kube-scheduler. It must be a DNS label.
   *
   * +required
   */
  podGroup?: string;
  /**
   * PodGroupReplicaKey specifies the replica key of the PodGroup to which this
   * Pod belongs. It is used to distinguish pods belonging to different replicas
   * of the same pod group. The pod group policy is applied separately to each replica.
   * When set, it must be a DNS label.
   *
   * +optional
   */
  podGroupReplicaKey?: string;
};

export type V1Beta1ContainerMetrics = {
  /**
   * Container name corresponding to the one from pod.spec.containers.
   */
  name?: string;
  /**
   * The memory usage is the memory working set.
   */
  usage?: V1ResourceList;
};

export type V1Beta1NodeMetrics = {
  /**
   * APIVersion defines the versioned schema of this representation of an object.
   * Servers should convert recognized schemas to the latest internal value, and
   * may reject unrecognized values.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#resources
   * +optional
   */
  apiVersion?: string;
  /**
   * Kind is a string value representing the REST resource this object represents.
   * Servers may infer this from the endpoint the client submits requests to.
   * Cannot be updated.
   * In CamelCase.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
   * +optional
   */
  kind?: string;
  /**
   * Standard object's metadata.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#metadata
   * +optional
   */
  metadata?: V1ObjectMeta;
  /**
   * The following fields define time interval from which metrics were
   * collected from the interval [Timestamp-Window, Timestamp].
   */
  timestamp?: string;
  /**
   * The memory usage is the memory working set.
   */
  usage?: V1ResourceList;
  window?: string;
};

export type V1Beta1NodeMetricsList = {
  /**
   * APIVersion defines the versioned schema of this representation of an object.
   * Servers should convert recognized schemas to the latest internal value, and
   * may reject unrecognized values.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#resources
   * +optional
   */
  apiVersion?: string;
  /**
   * List of node metrics.
   */
  items?: Array<V1Beta1NodeMetrics>;
  /**
   * Kind is a string value representing the REST resource this object represents.
   * Servers may infer this from the endpoint the client submits requests to.
   * Cannot be updated.
   * In CamelCase.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
   * +optional
   */
  kind?: string;
  /**
   * Standard list metadata.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
   */
  metadata?: V1ListMeta;
};

export type V1Beta1PodMetrics = {
  /**
   * APIVersion defines the versioned schema of this representation of an object.
   * Servers should convert recognized schemas to the latest internal value, and
   * may reject unrecognized values.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#resources
   * +optional
   */
  apiVersion?: string;
  /**
   * Metrics for all containers are collected within the same time window.
   * +listType=atomic
   */
  containers?: Array<V1Beta1ContainerMetrics>;
  /**
   * Kind is a string value representing the REST resource this object represents.
   * Servers may infer this from the endpoint the client submits requests to.
   * Cannot be updated.
   * In CamelCase.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
   * +optional
   */
  kind?: string;
  /**
   * Standard object's metadata.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#metadata
   * +optional
   */
  metadata?: V1ObjectMeta;
  /**
   * The following fields define time interval from which metrics were
   * collected from the interval [Timestamp-Window, Timestamp].
   */
  timestamp?: string;
  window?: string;
};

export type V1Beta1PodMetricsList = {
  /**
   * APIVersion defines the versioned schema of this representation of an object.
   * Servers should convert recognized schemas to the latest internal value, and
   * may reject unrecognized values.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#resources
   * +optional
   */
  apiVersion?: string;
  /**
   * List of pod metrics.
   */
  items?: Array<V1Beta1PodMetrics>;
  /**
   * Kind is a string value representing the REST resource this object represents.
   * Servers may infer this from the endpoint the client submits requests to.
   * Cannot be updated.
   * In CamelCase.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
   * +optional
   */
  kind?: string;
  /**
   * Standard list metadata.
   * More info: https://git.k8s.io/community/contributors/devel/sig-architecture/api-conventions.md#types-kinds
   */
  metadata?: V1ListMeta;
};

export type WebhooksWebhookCreatePayload = {
  EndpointID?: number;
  RegistryID?: number;
  ResourceID?: string;
  /**
   * Type of webhook (1 - service, 2 - container)
   */
  WebhookType?: PortainerWebhookType;
};

export type WebhooksWebhookReassignPayload = {
  ResourceID?: string;
  /**
   * Type of webhook (1 - service, 2 - container)
   */
  WebhookType?: PortainerWebhookType;
};

export type WebhooksWebhookUpdatePayload = {
  RegistryID?: number;
};

export type WorkflowsArtifactDetail = {
  autoUpdate?: PortainerAutoUpdateSettings;
  creationDate?: number;
  dependencies?: Array<string>;
  files?: Array<WorkflowsArtifactFileDetail>;
  id: number;
  lastSyncDate?: number;
  name: string;
  platform?: WorkflowsDeploymentPlatform;
  status?: WorkflowsWorkflowStatusObject;
  target?: WorkflowsTarget;
  type: WorkflowsType;
};

export type WorkflowsArtifactFileDetail = {
  hash?: string;
  path?: string;
  pathError?: string;
  pathStatus?: SourcesStatus;
  ref?: string;
  refError?: string;
  refStatus?: SourcesStatus;
  sourceId?: number;
};

export const WorkflowsDeploymentPlatform = {
  /**
   * DeploymentPlatformDockerStandalone
   */
  DEPLOYMENT_PLATFORM_DOCKER_STANDALONE: 'dockerStandalone',
  /**
   * DeploymentPlatformDockerSwarm
   */
  DEPLOYMENT_PLATFORM_DOCKER_SWARM: 'dockerSwarm',
  /**
   * DeploymentPlatformKubernetes
   */
  DEPLOYMENT_PLATFORM_KUBERNETES: 'kubernetes',
} as const;

export type WorkflowsDeploymentPlatform =
  (typeof WorkflowsDeploymentPlatform)[keyof typeof WorkflowsDeploymentPlatform];

export type WorkflowsStatusSummary = {
  error?: number;
  healthy?: number;
  paused?: number;
  syncing?: number;
  unknown?: number;
};

export type WorkflowsTarget = {
  edgeGroupIds?: Array<number>;
  endpointId?: number;
  groupStatus?: {
    [key: string]: GithubComPortainerPortainerApiGitopsWorkflowsStatus;
  };
  namespace?: string;
  resolvedEndpointIds?: Array<number>;
};

export const WorkflowsType = {
  /**
   * TypeStack
   */
  TYPE_STACK: 'stack' /**
   * TypeEdgeStack
   */,
  TYPE_EDGE_STACK: 'edgeStack',
} as const;

export type WorkflowsType = (typeof WorkflowsType)[keyof typeof WorkflowsType];

export type WorkflowsWorkflow = {
  artifacts?: Array<WorkflowsArtifactDetail>;
  creationDate?: number;
  id: number;
  lastSyncDate?: number;
  name: string;
  status: WorkflowsWorkflowStatusObject;
};

export type WorkflowsWorkflowPhaseStatus = {
  error?: string;
  status?: GithubComPortainerPortainerApiGitopsWorkflowsStatus;
};

export type WorkflowsWorkflowStatusObject = {
  artifact?: WorkflowsWorkflowPhaseStatus;
  source?: WorkflowsWorkflowPhaseStatus;
  target?: WorkflowsWorkflowPhaseStatus;
};

export type WorkflowsCreatePayload = {
  artifacts: Array<WorkflowsCreatePayloadArtifact>;
  name: string;
};

export type WorkflowsCreatePayloadArtifact = {
  config?: WorkflowsCreatePayloadArtifactConfig;
  dependencies?: Array<string>;
  deploymentType: string;
  files: Array<WorkflowsCreatePayloadFile>;
  name: string;
  targets: WorkflowsCreatePayloadArtifactTargets;
};

export type WorkflowsCreatePayloadArtifactConfig = {
  alwaysCloneGitRepoForRelativePath?: boolean;
  envVars?: Array<PortainerPair>;
  localFilesystemPath?: string;
  parallelConfig?: WorkflowsCreatePayloadArtifactParallelConfig;
  perDeviceConfigsGroupMatchType?: 'file' | 'dir';
  perDeviceConfigsMatchType?: 'file' | 'dir';
  perDeviceConfigsPath?: string;
  prePullImage?: boolean;
  registries?: Array<number>;
  retryPeriod?: number;
  useManifestNamespaces?: boolean;
};

export type WorkflowsCreatePayloadArtifactParallelConfig = {
  /**
   * When strictly positive, enables parallel deployment
   */
  batchCount?: number;
  /**
   * When strictly positive and BatchCount provided, enables incremental parallel deployments
   */
  batchIncrementBy?: number;
  delay?: string;
  failureAction?: string;
  timeout?: string;
};

export type WorkflowsCreatePayloadArtifactTargets = {
  edgeGroups: Array<number>;
};

export type WorkflowsCreatePayloadFile = {
  path: string;
  ref: string;
  sourceId: number;
};

export type WorkflowsUpdateArtifactConfigPayload = {
  envVars?: Array<PortainerPair>;
  parallelConfig?: WorkflowsCreatePayloadArtifactParallelConfig;
  prePullImage?: boolean;
  registries?: Array<number>;
  retryPeriod?: number;
};

export type WorkflowsUpdateArtifactPayload = {
  config?: WorkflowsUpdateArtifactConfigPayload;
  dependencies?: Array<string>;
  deploymentType?: string;
  files?: Array<WorkflowsCreatePayloadFile>;
  id: number;
  targets?: WorkflowsCreatePayloadArtifactTargets;
  type: WorkflowsType;
};

export type WorkflowsUpdatePayload = {
  artifacts?: Array<WorkflowsUpdateArtifactPayload>;
  name?: string;
};

export type ModelsK8sServiceInfo2 = ModelsK8sServiceInfo;

export type AddonsConfigEntryUpdatePayload2 = AddonsConfigEntryUpdatePayload;

/**
 * Ingress controllers
 */
export type ModelsK8sIngressControllerArray = Array<ModelsK8sIngressController>;

export type KubernetesK8sDeploymentWriteRequest2 =
  KubernetesK8sDeploymentWriteRequest;

export type Create = {
  /**
   * cloud provider such as aws, aks, civo, digitalocean, etc.
   */
  provider: string;
  /**
   * name of the credentials such as rnd-test-credential
   */
  name: string;
  /**
   * credentials in json format
   */
  credentials: string;
};

export type ModelsK8sNamespaceDetails2 = ModelsK8sNamespaceDetails;

export type AddonsConfigUpdatePayload2 = AddonsConfigUpdatePayload;

export type EndpointsEndpointDeleteBatchPayload2 =
  EndpointsEndpointDeleteBatchPayload;

export type KubernetesK8sConfigMapWriteRequest2 =
  KubernetesK8sConfigMapWriteRequest;

export type ModelsK8sIngressInfo2 = ModelsK8sIngressInfo;

export type KubernetesK8sSecretWriteRequest2 = KubernetesK8sSecretWriteRequest;

export type AddonStoreConfigListData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/addon-store/v1/config';
};

export type AddonStoreConfigListErrors = {
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type AddonStoreConfigListResponses = {
  /**
   * Stored configuration
   */
  200: AddonsConfigResponse;
};

export type AddonStoreConfigListResponse =
  AddonStoreConfigListResponses[keyof AddonStoreConfigListResponses];

export type AddonStoreConfigUpdateData = {
  /**
   * Configuration entries
   */
  body: AddonsConfigUpdatePayload2;
  path?: never;
  query?: never;
  url: '/addon-store/v1/config';
};

export type AddonStoreConfigUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type AddonStoreConfigUpdateResponses = {
  /**
   * Stored configuration
   */
  200: AddonsConfigMutationResponse;
};

export type AddonStoreConfigUpdateResponse =
  AddonStoreConfigUpdateResponses[keyof AddonStoreConfigUpdateResponses];

export type AddonStoreConfigEntryDeleteData = {
  body?: never;
  path: {
    /**
     * Configuration key (e.g. BASE_DOMAIN)
     */
    key: string;
  };
  query?: never;
  url: '/addon-store/v1/config/{key}';
};

export type AddonStoreConfigEntryDeleteErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * No such configuration entry
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type AddonStoreConfigEntryDeleteResponses = {
  /**
   * Stored configuration
   */
  200: AddonsConfigMutationResponse;
};

export type AddonStoreConfigEntryDeleteResponse =
  AddonStoreConfigEntryDeleteResponses[keyof AddonStoreConfigEntryDeleteResponses];

export type AddonStoreConfigEntryUpdateData = {
  /**
   * Configuration value
   */
  body: AddonsConfigEntryUpdatePayload2;
  path: {
    /**
     * Configuration key (e.g. BASE_DOMAIN)
     */
    key: string;
  };
  query?: never;
  url: '/addon-store/v1/config/{key}';
};

export type AddonStoreConfigEntryUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type AddonStoreConfigEntryUpdateResponses = {
  /**
   * Stored configuration
   */
  200: AddonsConfigMutationResponse;
};

export type AddonStoreConfigEntryUpdateResponse =
  AddonStoreConfigEntryUpdateResponses[keyof AddonStoreConfigEntryUpdateResponses];

export type AddonListData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Response view. 'switcher' returns the lightweight app-switcher listing.
     */
    view?: 'switcher';
  };
  url: '/addons';
};

export type AddonListErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type AddonListResponses = {
  /**
   * Success
   */
  200: AddonsAddonListResponse;
};

export type AddonListResponse = AddonListResponses[keyof AddonListResponses];

export type AddonUninstallData = {
  body?: never;
  path: {
    /**
     * Addon identifier (e.g. portal-template)
     */
    id: string;
  };
  query?: {
    /**
     * Uninstall even while an operation is in progress (recovery)
     */
    force?: boolean;
    /**
     * Also discard the addon's stored configuration (not reversible)
     */
    pruneConfig?: boolean;
  };
  url: '/addons/{id}';
};

export type AddonUninstallErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Addon not found in the addon catalog
   */
  404: unknown;
  /**
   * An uninstall for this addon is already in progress
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type AddonUninstallResponses = {
  /**
   * Uninstall accepted
   */
  200: AddonsAddonLifecycleResponse;
  /**
   * Addon is not installed; nothing to do
   */
  204: void;
};

export type AddonUninstallResponse =
  AddonUninstallResponses[keyof AddonUninstallResponses];

export type AddonDetailData = {
  body?: never;
  path: {
    /**
     * Addon identifier (e.g. portal-template)
     */
    id: string;
  };
  query?: never;
  url: '/addons/{id}';
};

export type AddonDetailErrors = {
  /**
   * Addon not found, or not accessible to the calling user
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type AddonDetailResponses = {
  /**
   * Success
   */
  200: AddonsAddonListItem;
};

export type AddonDetailResponse =
  AddonDetailResponses[keyof AddonDetailResponses];

export type AddonInstallData = {
  /**
   * Install options
   */
  body?: AddonsInstallPayload;
  path: {
    /**
     * Addon identifier (e.g. portal-template)
     */
    id: string;
  };
  query?: never;
  url: '/addons/{id}';
};

export type AddonInstallErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Addon not found in the addon catalog
   */
  404: unknown;
  /**
   * An operation for this add-on is already in progress
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type AddonInstallResponses = {
  /**
   * Upgrade of an already-installed addon accepted
   */
  200: AddonsAddonLifecycleResponse;
  /**
   * Install accepted
   */
  201: AddonsAddonLifecycleResponse;
};

export type AddonInstallResponse =
  AddonInstallResponses[keyof AddonInstallResponses];

export type AddonAccessUpdateData = {
  /**
   * Addon access configuration
   */
  body: AddonsAddonAccessUpdatePayload;
  path: {
    /**
     * Addon identifier (e.g. portal-template)
     */
    id: string;
  };
  query?: never;
  url: '/addons/{id}/access';
};

export type AddonAccessUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Addon not found in the addon catalog
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type AddonAccessUpdateResponses = {
  /**
   * Success
   */
  200: AddonsAddonAccessResponse;
};

export type AddonAccessUpdateResponse =
  AddonAccessUpdateResponses[keyof AddonAccessUpdateResponses];

export type AddonChartSourceData = {
  body?: never;
  path: {
    /**
     * Addon identifier (e.g. portal-template)
     */
    id: string;
  };
  query?: {
    /**
     * Portainer registry holding the chart. Omit to check the catalog's chart
     */
    registryId?: number;
    /**
     * Chart repository path within the registry. Required with registryId
     */
    chart?: string;
  };
  url: '/addons/{id}/chart-source';
};

export type AddonChartSourceErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Addon not found in the addon catalog
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type AddonChartSourceResponses = {
  /**
   * Success
   */
  200: AddonsChartSourceStatusResponse;
};

export type AddonChartSourceResponse =
  AddonChartSourceResponses[keyof AddonChartSourceResponses];

export type AddonConfigDeleteData = {
  body?: never;
  path: {
    /**
     * Addon identifier (e.g. portal-template)
     */
    id: string;
  };
  query?: never;
  url: '/addons/{id}/config';
};

export type AddonConfigDeleteErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Addon not found in the addon catalog
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type AddonConfigDeleteResponses = {
  /**
   * Stored configuration
   */
  200: AddonsConfigMutationResponse;
};

export type AddonConfigDeleteResponse =
  AddonConfigDeleteResponses[keyof AddonConfigDeleteResponses];

export type AddonConfigListData = {
  body?: never;
  path: {
    /**
     * Addon identifier (e.g. portal-template)
     */
    id: string;
  };
  query?: never;
  url: '/addons/{id}/config';
};

export type AddonConfigListErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Addon not found in the addon catalog
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type AddonConfigListResponses = {
  /**
   * Stored configuration
   */
  200: AddonsConfigResponse;
};

export type AddonConfigListResponse =
  AddonConfigListResponses[keyof AddonConfigListResponses];

export type AddonConfigUpdateData = {
  /**
   * Configuration entries
   */
  body: AddonsConfigUpdatePayload2;
  path: {
    /**
     * Addon identifier (e.g. portal-template)
     */
    id: string;
  };
  query?: never;
  url: '/addons/{id}/config';
};

export type AddonConfigUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Addon not found in the addon catalog
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type AddonConfigUpdateResponses = {
  /**
   * Stored configuration
   */
  200: AddonsConfigMutationResponse;
};

export type AddonConfigUpdateResponse =
  AddonConfigUpdateResponses[keyof AddonConfigUpdateResponses];

export type AddonConfigEntryDeleteData = {
  body?: never;
  path: {
    /**
     * Addon identifier (e.g. portal-template)
     */
    id: string;
    /**
     * Configuration key (e.g. BASE_DOMAIN)
     */
    key: string;
  };
  query?: never;
  url: '/addons/{id}/config/{key}';
};

export type AddonConfigEntryDeleteErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Addon not found in the addon catalog, or no such configuration entry
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type AddonConfigEntryDeleteResponses = {
  /**
   * Stored configuration
   */
  200: AddonsConfigMutationResponse;
};

export type AddonConfigEntryDeleteResponse =
  AddonConfigEntryDeleteResponses[keyof AddonConfigEntryDeleteResponses];

export type AddonConfigEntryUpdateData = {
  /**
   * Configuration value
   */
  body: AddonsConfigEntryUpdatePayload2;
  path: {
    /**
     * Addon identifier (e.g. portal-template)
     */
    id: string;
    /**
     * Configuration key (e.g. BASE_DOMAIN)
     */
    key: string;
  };
  query?: never;
  url: '/addons/{id}/config/{key}';
};

export type AddonConfigEntryUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Addon not found in the addon catalog
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type AddonConfigEntryUpdateResponses = {
  /**
   * Stored configuration
   */
  200: AddonsConfigMutationResponse;
};

export type AddonConfigEntryUpdateResponse =
  AddonConfigEntryUpdateResponses[keyof AddonConfigEntryUpdateResponses];

export type AddonRepairData = {
  body?: never;
  path: {
    /**
     * Addon identifier (e.g. portal-template)
     */
    id: string;
  };
  query?: never;
  url: '/addons/{id}/repair';
};

export type AddonRepairErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Addon not found in the addon catalog, or not installed
   */
  404: unknown;
  /**
   * An operation for this add-on is already in progress
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type AddonRepairResponses = {
  /**
   * Credential repaired
   */
  200: AddonsAddonLifecycleResponse;
};

export type AddonRepairResponse =
  AddonRepairResponses[keyof AddonRepairResponses];

export type AddonEnvironmentData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/addons/environment';
};

export type AddonEnvironmentErrors = {
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Portainer is not running on Kubernetes, where addons do not exist
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type AddonEnvironmentResponses = {
  /**
   * Success
   */
  200: AddonsAddonEnvironmentResponse;
};

export type AddonEnvironmentResponse =
  AddonEnvironmentResponses[keyof AddonEnvironmentResponses];

export type AllowListInspectData = {
  body?: never;
  path: {
    /**
     * AllowList ID
     */
    id: number;
  };
  query?: never;
  url: '/allowlist/{id}';
};

export type AllowListInspectErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type AllowListInspectResponses = {
  /**
   * Success
   */
  200: PortainerAllowList;
};

export type AllowListInspectResponse =
  AllowListInspectResponses[keyof AllowListInspectResponses];

export type AllowListUpdateData = {
  /**
   * URL allow list
   */
  body: AllowlistAllowListUpdatePayload;
  path: {
    /**
     * AllowList ID
     */
    id: number;
  };
  query?: never;
  url: '/allowlist/{id}';
};

export type AllowListUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type AllowListUpdateResponses = {
  /**
   * Success
   */
  200: PortainerAllowList;
};

export type AllowListUpdateResponse =
  AllowListUpdateResponses[keyof AllowListUpdateResponses];

export type AuthenticateUserData = {
  /**
   * Credentials used for authentication
   */
  body: AuthAuthenticatePayload;
  path?: never;
  query?: never;
  url: '/auth';
};

export type AuthenticateUserErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Invalid Credentials
   */
  422: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type AuthenticateUserResponses = {
  /**
   * Success
   */
  200: AuthAuthenticateResponse;
};

export type AuthenticateUserResponse =
  AuthenticateUserResponses[keyof AuthenticateUserResponses];

export type LogoutData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/auth/logout';
};

export type LogoutErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type LogoutResponses = {
  /**
   * Success
   */
  204: void;
};

export type LogoutResponse = LogoutResponses[keyof LogoutResponses];

export type ValidateOAuthData = {
  /**
   * OAuth Credentials used for authentication
   */
  body: AuthOauthPayload;
  path?: never;
  query?: never;
  url: '/auth/oauth/validate';
};

export type ValidateOAuthErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Invalid Credentials
   */
  422: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type ValidateOAuthResponses = {
  /**
   * Success
   */
  200: AuthAuthenticateResponse;
};

export type ValidateOAuthResponse =
  ValidateOAuthResponses[keyof ValidateOAuthResponses];

export type AutoUpdateListData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/auto_updates';
};

export type AutoUpdateListErrors = {
  /**
   * Forbidden
   */
  403: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
};

export type AutoUpdateListResponses = {
  /**
   * OK
   */
  200: AutoupdatesListResponse;
};

export type AutoUpdateListResponse =
  AutoUpdateListResponses[keyof AutoUpdateListResponses];

export type BackupData = {
  /**
   * An object contains the password to encrypt the backup with
   */
  body?: BackupBackupPayload;
  path?: never;
  query?: never;
  url: '/backup';
};

export type BackupErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type BackupResponses = {
  /**
   * Success
   */
  200: unknown;
};

export type BackupToAzureBlobData = {
  /**
   * Azure Blob backup settings
   */
  body: PortainereeAzureBlobBackupSettings;
  path?: never;
  query?: never;
  url: '/backup/azure/execute';
};

export type BackupToAzureBlobErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type BackupToAzureBlobResponses = {
  /**
   * Success
   */
  204: void;
};

export type BackupToAzureBlobResponse =
  BackupToAzureBlobResponses[keyof BackupToAzureBlobResponses];

export type RestoreFromAzureBlobData = {
  /**
   * Azure Blob location and credentials
   */
  body: BackupRestoreAzureSettings;
  headers?: {
    /**
     * Setup token (required when instance is uninitialized and --no-setup-token is not set)
     */
    'X-Setup-Token'?: string;
  };
  path?: never;
  query?: never;
  url: '/backup/azure/restore';
};

export type RestoreFromAzureBlobErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Access denied - invalid or missing setup token
   */
  403: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type RestoreFromAzureBlobResponses = {
  /**
   * Success
   */
  200: unknown;
};

export type AzureBlobBackupSettingsFetchData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/backup/azure/settings';
};

export type AzureBlobBackupSettingsFetchErrors = {
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type AzureBlobBackupSettingsFetchResponses = {
  /**
   * Success
   */
  200: PortainereeAzureBlobBackupSettings;
};

export type AzureBlobBackupSettingsFetchResponse =
  AzureBlobBackupSettingsFetchResponses[keyof AzureBlobBackupSettingsFetchResponses];

export type UpdateAzureBlobSettingsData = {
  /**
   * Azure Blob backup settings
   */
  body?: PortainereeAzureBlobBackupSettings;
  path?: never;
  query?: never;
  url: '/backup/azure/settings';
};

export type UpdateAzureBlobSettingsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type UpdateAzureBlobSettingsResponses = {
  /**
   * Success
   */
  200: unknown;
};

export type AzureBlobBackupStatusFetchData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/backup/azure/status';
};

export type AzureBlobBackupStatusFetchErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type AzureBlobBackupStatusFetchResponses = {
  /**
   * Success
   */
  200: BackupAzureBackupStatus;
};

export type AzureBlobBackupStatusFetchResponse =
  AzureBlobBackupStatusFetchResponses[keyof AzureBlobBackupStatusFetchResponses];

export type AzureBlobTestConnectionData = {
  /**
   * Azure Blob connection settings
   */
  body: PortainereeAzureBlobBackupSettings;
  path?: never;
  query?: never;
  url: '/backup/azure/test';
};

export type AzureBlobTestConnectionErrors = {
  /**
   * Invalid request or Azure authentication failed
   */
  400: unknown;
  /**
   * Container not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
  /**
   * Network error reaching Azure storage endpoint
   */
  502: unknown;
};

export type AzureBlobTestConnectionResponses = {
  /**
   * Connection successful
   */
  204: void;
};

export type AzureBlobTestConnectionResponse =
  AzureBlobTestConnectionResponses[keyof AzureBlobTestConnectionResponses];

export type RunLocalBackupNowData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/backup/local/run';
};

export type RunLocalBackupNowErrors = {
  /**
   * A backup is already in progress
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type RunLocalBackupNowResponses = {
  /**
   * Success
   */
  204: void;
};

export type RunLocalBackupNowResponse =
  RunLocalBackupNowResponses[keyof RunLocalBackupNowResponses];

export type LocalBackupSettingsFetchData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/backup/local/settings';
};

export type LocalBackupSettingsFetchErrors = {
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type LocalBackupSettingsFetchResponses = {
  /**
   * Success
   */
  200: PortainereeLocalBackupSettings;
};

export type LocalBackupSettingsFetchResponse =
  LocalBackupSettingsFetchResponses[keyof LocalBackupSettingsFetchResponses];

export type UpdateLocalBackupSettingsData = {
  /**
   * Local backup settings
   */
  body?: PortainereeLocalBackupSettings;
  path?: never;
  query?: never;
  url: '/backup/local/settings';
};

export type UpdateLocalBackupSettingsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type UpdateLocalBackupSettingsResponses = {
  /**
   * Success
   */
  200: unknown;
};

export type LocalBackupStatusFetchData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/backup/local/status';
};

export type LocalBackupStatusFetchErrors = {
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type LocalBackupStatusFetchResponses = {
  /**
   * Success
   */
  200: BackupLocalBackupStatus;
};

export type LocalBackupStatusFetchResponse =
  LocalBackupStatusFetchResponses[keyof LocalBackupStatusFetchResponses];

export type BackupToS3Data = {
  /**
   * S3 backup settings
   */
  body: BackupS3BackupPayload;
  path?: never;
  query?: never;
  url: '/backup/s3/execute';
};

export type BackupToS3Errors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type BackupToS3Responses = {
  /**
   * Success
   */
  204: void;
};

export type BackupToS3Response = BackupToS3Responses[keyof BackupToS3Responses];

export type RestoreFromS3Data = {
  /**
   * S3 Location Payload
   */
  body?: BackupRestoreS3Settings;
  headers?: {
    /**
     * Setup token (required when instance is uninitialized and --no-setup-token is not set)
     */
    'X-Setup-Token'?: string;
  };
  path?: never;
  query?: never;
  url: '/backup/s3/restore';
};

export type RestoreFromS3Errors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Access denied - invalid or missing setup token
   */
  403: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type RestoreFromS3Responses = {
  /**
   * Success
   */
  200: unknown;
};

export type BackupSettingsFetchData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/backup/s3/settings';
};

export type BackupSettingsFetchErrors = {
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type BackupSettingsFetchResponses = {
  /**
   * Success
   */
  200: PortainereeS3BackupSettings;
};

export type BackupSettingsFetchResponse =
  BackupSettingsFetchResponses[keyof BackupSettingsFetchResponses];

export type UpdateS3SettingsData = {
  /**
   * S3 backup settings
   */
  body?: PortainereeS3BackupSettings;
  path?: never;
  query?: never;
  url: '/backup/s3/settings';
};

export type UpdateS3SettingsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type UpdateS3SettingsResponses = {
  /**
   * Success
   */
  200: unknown;
};

export type BackupStatusFetchData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/backup/s3/status';
};

export type BackupStatusFetchErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type BackupStatusFetchResponses = {
  /**
   * Success
   */
  200: BackupBackupStatus;
};

export type BackupStatusFetchResponse =
  BackupStatusFetchResponses[keyof BackupStatusFetchResponses];

export type GetAllData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/cloud/credentials';
};

export type GetAllErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GetAllResponses = {
  /**
   * OK
   */
  200: ModelsCloudCredential;
};

export type GetAllResponse = GetAllResponses[keyof GetAllResponses];

export type CreateData = {
  body: Create;
  path?: never;
  query?: never;
  url: '/cloud/credentials';
};

export type CreateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type CreateResponses = {
  /**
   * OK
   */
  200: ModelsCloudCredential;
};

export type CreateResponse = CreateResponses[keyof CreateResponses];

export type CloudCredsDeleteData = {
  body?: never;
  path: {
    /**
     * ID of the cloud credential
     */
    id: string;
  };
  query?: never;
  url: '/cloud/credentials/{id}';
};

export type CloudCredsDeleteErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Forbidden: the requested credential is associated with endpoint(s)
   */
  403: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type CloudCredsDeleteResponses = {
  /**
   * OK
   */
  200: ModelsCloudCredential;
};

export type CloudCredsDeleteResponse =
  CloudCredsDeleteResponses[keyof CloudCredsDeleteResponses];

export type CloudCredsGetByIdData = {
  body?: never;
  path: {
    /**
     * ID of the cloud credential
     */
    id: string;
  };
  query?: never;
  url: '/cloud/credentials/{id}';
};

export type CloudCredsGetByIdErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type CloudCredsGetByIdResponses = {
  /**
   * OK
   */
  200: ModelsCloudCredential;
};

export type CloudCredsGetByIdResponse =
  CloudCredsGetByIdResponses[keyof CloudCredsGetByIdResponses];

export type CloudCredsUpdateData = {
  body: Create;
  path: {
    /**
     * ID of the cloud credential
     */
    id: string;
  };
  query?: never;
  url: '/cloud/credentials/{id}';
};

export type CloudCredsUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type CloudCredsUpdateResponses = {
  /**
   * OK
   */
  200: ModelsCloudCredential;
};

export type CloudCredsUpdateResponse =
  CloudCredsUpdateResponses[keyof CloudCredsUpdateResponses];

export type UpgradeData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    environmentId: number;
  };
  query?: never;
  url: '/cloud/endpoints/{environmentId}/upgrade';
};

export type UpgradeErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Server error
   */
  500: unknown;
  /**
   * Missing configuration
   */
  503: unknown;
};

export type UpgradeResponses = {
  /**
   * Success
   */
  200: unknown;
};

export type SharedGitGetAllData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/cloud/gitcredentials';
};

export type SharedGitGetAllErrors = {
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type SharedGitGetAllResponses = {
  /**
   * Success
   */
  200: Array<GitShadowedGitCredential>;
};

export type SharedGitGetAllResponse =
  SharedGitGetAllResponses[keyof SharedGitGetAllResponses];

export type SharedGitCreateData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/cloud/gitcredentials';
};

export type SharedGitCreateErrors = {
  /**
   * Invalid request payload
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type SharedGitCreateResponses = {
  /**
   * Success
   */
  201: unknown;
};

export type SharedGitDeleteData = {
  body?: never;
  path: {
    /**
     * Git credential identifier
     */
    id: number;
  };
  query?: never;
  url: '/cloud/gitcredentials/{id}';
};

export type SharedGitDeleteErrors = {
  /**
   * Invalid request payload
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * The credential is used by one or more GitOps sources
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type SharedGitDeleteResponses = {
  /**
   * Success
   */
  204: void;
};

export type SharedGitDeleteResponse =
  SharedGitDeleteResponses[keyof SharedGitDeleteResponses];

export type SharedGitGetData = {
  body?: never;
  path: {
    /**
     * Git credential identifier
     */
    id: number;
  };
  query?: never;
  url: '/cloud/gitcredentials/{id}';
};

export type SharedGitGetErrors = {
  /**
   * Invalid request payload
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type SharedGitGetResponses = {
  /**
   * Success
   */
  200: unknown;
};

export type SharedGitUpdateData = {
  body?: never;
  path: {
    /**
     * Git credential identifier
     */
    id: number;
  };
  query?: never;
  url: '/cloud/gitcredentials/{id}';
};

export type SharedGitUpdateErrors = {
  /**
   * Invalid request payload
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type SharedGitUpdateResponses = {
  /**
   * Success
   */
  200: unknown;
};

export type CustomTemplateListData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Template types
     */
    type: Array<1 | 2 | 3>;
    /**
     * Filter by edge templates
     */
    edge?: boolean;
  };
  url: '/custom_templates';
};

export type CustomTemplateListErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type CustomTemplateListResponses = {
  /**
   * Success
   */
  200: Array<PortainereeCustomTemplate>;
};

export type CustomTemplateListResponse =
  CustomTemplateListResponses[keyof CustomTemplateListResponses];

export type CustomTemplateDeleteData = {
  body?: never;
  path: {
    /**
     * Template identifier
     */
    id: number;
  };
  query?: never;
  url: '/custom_templates/{id}';
};

export type CustomTemplateDeleteErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Access denied to resource
   */
  403: unknown;
  /**
   * Template not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type CustomTemplateDeleteResponses = {
  /**
   * Success
   */
  204: void;
};

export type CustomTemplateDeleteResponse =
  CustomTemplateDeleteResponses[keyof CustomTemplateDeleteResponses];

export type CustomTemplateInspectData = {
  body?: never;
  path: {
    /**
     * Template identifier
     */
    id: number;
  };
  query?: never;
  url: '/custom_templates/{id}';
};

export type CustomTemplateInspectErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Template not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type CustomTemplateInspectResponses = {
  /**
   * Success
   */
  200: PortainereeCustomTemplate;
};

export type CustomTemplateInspectResponse =
  CustomTemplateInspectResponses[keyof CustomTemplateInspectResponses];

export type CustomTemplateUpdateData = {
  /**
   * Template details
   */
  body: GithubComPortainerPortainerEeApiHttpHandlerCustomtemplatesCustomTemplateUpdatePayload;
  path: {
    /**
     * Template identifier
     */
    id: number;
  };
  query?: never;
  url: '/custom_templates/{id}';
};

export type CustomTemplateUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied to access template
   */
  403: unknown;
  /**
   * Template not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type CustomTemplateUpdateResponses = {
  /**
   * Success
   */
  200: PortainereeCustomTemplate;
};

export type CustomTemplateUpdateResponse =
  CustomTemplateUpdateResponses[keyof CustomTemplateUpdateResponses];

export type CustomTemplateFileData = {
  body?: never;
  path: {
    /**
     * Template identifier
     */
    id: number;
  };
  query?: never;
  url: '/custom_templates/{id}/file';
};

export type CustomTemplateFileErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Custom template not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type CustomTemplateFileResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerCustomtemplatesFileResponse;
};

export type CustomTemplateFileResponse =
  CustomTemplateFileResponses[keyof CustomTemplateFileResponses];

export type CustomTemplateGitFetchData = {
  body?: never;
  path: {
    /**
     * Template identifier
     */
    id: number;
  };
  query?: never;
  url: '/custom_templates/{id}/git_fetch';
};

export type CustomTemplateGitFetchErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Custom template not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type CustomTemplateGitFetchResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerCustomtemplatesFileResponse;
};

export type CustomTemplateGitFetchResponse =
  CustomTemplateGitFetchResponses[keyof CustomTemplateGitFetchResponses];

export type CustomTemplateCreateFileData = {
  body: {
    /**
     * Title of the template
     */
    Title: string;
    /**
     * Description of the template
     */
    Description: string;
    /**
     * A note that will be displayed in the UI. Supports HTML content
     */
    Note: string;
    /**
     * Platform associated to the template (1 - 'linux', 2 - 'windows')
     */
    Platform: 1 | 2;
    /**
     * Type of created stack (1 - swarm, 2 - compose, 3 - kubernetes)
     */
    Type: 1 | 2 | 3;
    /**
     * File
     */
    File: Blob | File;
    /**
     * URL of the template's logo
     */
    Logo?: string;
    /**
     * A json array of variables definitions
     */
    Variables?: string;
    /**
     * Indicates if this template purpose for Edge Stack
     */
    EdgeTemplate?: boolean;
    /**
     * A json object of edge config
     */
    EdgeSettings?: string;
  };
  path?: never;
  query?: never;
  url: '/custom_templates/create/file';
};

export type CustomTemplateCreateFileErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type CustomTemplateCreateFileResponses = {
  /**
   * OK
   */
  200: PortainereeCustomTemplate;
};

export type CustomTemplateCreateFileResponse =
  CustomTemplateCreateFileResponses[keyof CustomTemplateCreateFileResponses];

export type CustomTemplateCreateRepositoryData = {
  /**
   * Required when using method=repository
   */
  body: GithubComPortainerPortainerEeApiHttpHandlerCustomtemplatesCustomTemplateFromGitRepositoryPayload;
  path?: never;
  query?: never;
  url: '/custom_templates/create/repository';
};

export type CustomTemplateCreateRepositoryErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type CustomTemplateCreateRepositoryResponses = {
  /**
   * OK
   */
  200: PortainereeCustomTemplate;
};

export type CustomTemplateCreateRepositoryResponse =
  CustomTemplateCreateRepositoryResponses[keyof CustomTemplateCreateRepositoryResponses];

export type CustomTemplateCreateStringData = {
  /**
   * body
   */
  body: GithubComPortainerPortainerEeApiHttpHandlerCustomtemplatesCustomTemplateFromFileContentPayload;
  path?: never;
  query?: never;
  url: '/custom_templates/create/string';
};

export type CustomTemplateCreateStringErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type CustomTemplateCreateStringResponses = {
  /**
   * OK
   */
  200: PortainereeCustomTemplate;
};

export type CustomTemplateCreateStringResponse =
  CustomTemplateCreateStringResponses[keyof CustomTemplateCreateStringResponses];

export type DockerContainerGpusInspectData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    environmentId: number;
    /**
     * Container identifier
     */
    containerId: number;
  };
  query?: never;
  url: '/docker/{environmentId}/containers/{containerId}/gpus';
};

export type DockerContainerGpusInspectErrors = {
  /**
   * Bad request
   */
  400: unknown;
  /**
   * Environment or container not found
   */
  404: unknown;
  /**
   * Internal server error
   */
  500: unknown;
};

export type DockerContainerGpusInspectResponses = {
  /**
   * Success
   */
  200: ContainersContainerGpusResponse;
};

export type DockerContainerGpusInspectResponse =
  DockerContainerGpusInspectResponses[keyof DockerContainerGpusInspectResponses];

export type ContainerImageStatusData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    environmentId: number;
    /**
     * Container identifier
     */
    containerId: number;
  };
  query?: {
    /**
     * Refresh will force a refresh of the image status cache
     */
    refresh?: boolean;
  };
  url: '/docker/{environmentId}/containers/{containerId}/image_status';
};

export type ContainerImageStatusErrors = {
  /**
   * Bad request
   */
  400: unknown;
  /**
   * Internal server error
   */
  500: unknown;
};

export type ContainerImageStatusResponses = {
  /**
   * Success
   */
  200: ImagesStatusResponse;
};

export type ContainerImageStatusResponse =
  ContainerImageStatusResponses[keyof ContainerImageStatusResponses];

export type ContainersImageStatusClearData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    environmentId: number;
  };
  query?: never;
  url: '/docker/{environmentId}/containers/image_status/clear';
};

export type ContainersImageStatusClearErrors = {
  /**
   * Bad request
   */
  400: unknown;
  /**
   * Internal server error
   */
  500: unknown;
};

export type ContainersImageStatusClearResponses = {
  /**
   * Success
   */
  204: void;
};

export type ContainersImageStatusClearResponse =
  ContainersImageStatusClearResponses[keyof ContainersImageStatusClearResponses];

export type DockerDashboardData2 = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    environmentId: number;
  };
  query?: never;
  url: '/docker/{environmentId}/dashboard';
};

export type DockerDashboardErrors = {
  /**
   * Bad request
   */
  400: unknown;
  /**
   * Internal server error
   */
  500: unknown;
};

export type DockerDashboardResponses = {
  /**
   * Success
   */
  200: DockerDashboardData;
};

export type DockerDashboardResponse =
  DockerDashboardResponses[keyof DockerDashboardResponses];

export type DockerImagesListData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    environmentId: number;
  };
  query?: {
    /**
     * Include image usage information
     */
    withUsage?: boolean;
  };
  url: '/docker/{environmentId}/images';
};

export type DockerImagesListErrors = {
  /**
   * Bad request
   */
  400: unknown;
  /**
   * Internal server error
   */
  500: unknown;
};

export type DockerImagesListResponses = {
  /**
   * Success
   */
  200: Array<ImagesImageResponse>;
};

export type DockerImagesListResponse =
  DockerImagesListResponses[keyof DockerImagesListResponses];

export type ServiceImageStatusData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    environmentId: number;
    /**
     * Service identifier
     */
    serviceId: number;
  };
  query?: {
    /**
     * Refresh will force a refresh of the image status cache
     */
    refresh?: boolean;
  };
  url: '/docker/{environmentId}/services/{serviceId}/image_status';
};

export type ServiceImageStatusErrors = {
  /**
   * Bad request
   */
  400: unknown;
  /**
   * Internal server error
   */
  500: unknown;
};

export type ServiceImageStatusResponses = {
  /**
   * Success
   */
  200: ImagesStatusResponse;
};

export type ServiceImageStatusResponse =
  ServiceImageStatusResponses[keyof ServiceImageStatusResponses];

export type ServiceImageStatusClearData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    environmentId: number;
  };
  query?: never;
  url: '/docker/{environmentId}/services/image_status/clear';
};

export type ServiceImageStatusClearErrors = {
  /**
   * Bad request
   */
  400: unknown;
  /**
   * Internal server error
   */
  500: unknown;
};

export type ServiceImageStatusClearResponses = {
  /**
   * Success
   */
  204: void;
};

export type ServiceImageStatusClearResponse =
  ServiceImageStatusClearResponses[keyof ServiceImageStatusClearResponses];

export type SnapshotInspectData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    environmentId: number;
  };
  query?: never;
  url: '/docker/{environmentId}/snapshot';
};

export type SnapshotInspectErrors = {
  /**
   * Environment not found
   */
  404: unknown;
};

export type SnapshotInspectResponses = {
  /**
   * Success
   */
  200: PortainerDockerSnapshotRaw;
};

export type SnapshotInspectResponse =
  SnapshotInspectResponses[keyof SnapshotInspectResponses];

export type SnapshotContainersListData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    environmentId: number;
  };
  query?: {
    /**
     * Edge stack identifier, will return only containers for this edge stack
     */
    edgeStackId?: number;
  };
  url: '/docker/{environmentId}/snapshot/containers';
};

export type SnapshotContainersListErrors = {
  /**
   * Environment not found
   */
  404: unknown;
};

export type SnapshotContainersListResponses = {
  /**
   * Success
   */
  200: PortainerDockerContainerSnapshot;
};

export type SnapshotContainersListResponse =
  SnapshotContainersListResponses[keyof SnapshotContainersListResponses];

export type SnapshotContainerInspectData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    environmentId: number;
    /**
     * Container identifier
     */
    containerId: number;
  };
  query?: never;
  url: '/docker/{environmentId}/snapshot/containers/{containerId}';
};

export type SnapshotContainerInspectErrors = {
  /**
   * Environment not found
   */
  404: unknown;
};

export type SnapshotContainerInspectResponses = {
  /**
   * Success
   */
  200: PortainerDockerContainerSnapshot;
};

export type SnapshotContainerInspectResponse =
  SnapshotContainerInspectResponses[keyof SnapshotContainerInspectResponses];

export type EdgeConfigListData = {
  /**
   * body
   */
  body: EdgeconfigsEdgeConfigCreatePayload;
  path?: never;
  query?: never;
  url: '/edge_configurations';
};

export type EdgeConfigListErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type EdgeConfigListResponses = {
  /**
   * Success
   */
  200: Array<PortainereeEdgeConfig>;
};

export type EdgeConfigListResponse =
  EdgeConfigListResponses[keyof EdgeConfigListResponses];

export type EdgeConfigCreateData = {
  body?: {
    BaseDir?: string;
    Category?: 'configuration' | 'secret';
    EdgeGroupIDs?: Array<number>;
    Name?: string;
    Type?: string;
    /**
     * File
     */
    File: Blob | File;
  };
  path?: never;
  query?: never;
  url: '/edge_configurations';
};

export type EdgeConfigCreateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
};

export type EdgeConfigCreateResponses = {
  /**
   * No Content
   */
  204: void;
};

export type EdgeConfigCreateResponse =
  EdgeConfigCreateResponses[keyof EdgeConfigCreateResponses];

export type EdgeConfigDeleteData = {
  body?: never;
  path: {
    /**
     * Edge configuration identifier
     */
    id: number;
  };
  query?: never;
  url: '/edge_configurations/{id}';
};

export type EdgeConfigDeleteErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Edge configuration not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EdgeConfigDeleteResponses = {
  /**
   * Success
   */
  204: void;
};

export type EdgeConfigDeleteResponse =
  EdgeConfigDeleteResponses[keyof EdgeConfigDeleteResponses];

export type EdgeConfigInspectData = {
  body?: never;
  path: {
    /**
     * Edge configuration identifier
     */
    id: number;
  };
  query?: never;
  url: '/edge_configurations/{id}';
};

export type EdgeConfigInspectErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Edge configuration not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EdgeConfigInspectResponses = {
  /**
   * Success
   */
  200: PortainereeEdgeConfig;
};

export type EdgeConfigInspectResponse =
  EdgeConfigInspectResponses[keyof EdgeConfigInspectResponses];

export type EdgeConfigUpdateData = {
  body?: {
    EdgeGroupIDs?: Array<number>;
    Type?: string;
    /**
     * File
     */
    File: Blob | File;
  };
  path: {
    /**
     * Edge configuration identifier
     */
    id: number;
  };
  query?: never;
  url: '/edge_configurations/{id}';
};

export type EdgeConfigUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
};

export type EdgeConfigUpdateResponses = {
  /**
   * No Content
   */
  204: void;
};

export type EdgeConfigUpdateResponse =
  EdgeConfigUpdateResponses[keyof EdgeConfigUpdateResponses];

export type EdgeConfigStateData = {
  body?: never;
  path: {
    /**
     * Edge configuration identifier
     */
    id: number;
    /**
     * Edge configuration state
     */
    state: number;
  };
  query?: never;
  url: '/edge_configurations/{id}/{state}';
};

export type EdgeConfigStateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied to access environment
   */
  403: unknown;
  /**
   * Edge configuration not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EdgeConfigStateResponses = {
  /**
   * Success
   */
  204: void;
};

export type EdgeConfigStateResponse =
  EdgeConfigStateResponses[keyof EdgeConfigStateResponses];

export type EdgeConfigFilesData = {
  body?: never;
  path: {
    /**
     * Edge configuration identifier
     */
    id: number;
  };
  query?: never;
  url: '/edge_configurations/{id}/files';
};

export type EdgeConfigFilesErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied to access environment
   */
  403: unknown;
  /**
   * Edge configuration not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EdgeConfigFilesResponses = {
  /**
   * Success
   */
  200: string;
};

export type EdgeConfigFilesResponse =
  EdgeConfigFilesResponses[keyof EdgeConfigFilesResponses];

export type EdgeGroupListData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * will summarize the env count
     */
    summarize?: boolean;
  };
  url: '/edge_groups';
};

export type EdgeGroupListErrors = {
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeGroupListResponses = {
  /**
   * EdgeGroups
   */
  200: Array<EdgegroupsDecoratedEdgeGroup>;
};

export type EdgeGroupListResponse =
  EdgeGroupListResponses[keyof EdgeGroupListResponses];

export type EdgeGroupCreateData = {
  /**
   * EdgeGroup data
   */
  body: EdgegroupsEdgeGroupCreatePayload;
  path?: never;
  query?: never;
  url: '/edge_groups';
};

export type EdgeGroupCreateErrors = {
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeGroupCreateResponses = {
  /**
   * OK
   */
  200: PortainereeEdgeGroup;
};

export type EdgeGroupCreateResponse =
  EdgeGroupCreateResponses[keyof EdgeGroupCreateResponses];

export type EdgeGroupDeleteData = {
  body?: never;
  path: {
    /**
     * EdgeGroup Id
     */
    id: number;
  };
  query?: never;
  url: '/edge_groups/{id}';
};

export type EdgeGroupDeleteErrors = {
  /**
   * Edge group is in use by an Edge stack, Edge job, Edge config or Workflow
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeGroupDeleteResponses = {
  /**
   * No Content
   */
  204: void;
};

export type EdgeGroupDeleteResponse =
  EdgeGroupDeleteResponses[keyof EdgeGroupDeleteResponses];

export type EdgeGroupInspectData = {
  body?: never;
  path: {
    /**
     * EdgeGroup Id
     */
    id: number;
  };
  query?: never;
  url: '/edge_groups/{id}';
};

export type EdgeGroupInspectErrors = {
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeGroupInspectResponses = {
  /**
   * OK
   */
  200: PortainereeEdgeGroup;
};

export type EdgeGroupInspectResponse =
  EdgeGroupInspectResponses[keyof EdgeGroupInspectResponses];

export type EdgeGroupUpdateData = {
  /**
   * EdgeGroup data
   */
  body: EdgegroupsEdgeGroupUpdatePayload;
  path: {
    /**
     * EdgeGroup Id
     */
    id: number;
  };
  query?: never;
  url: '/edge_groups/{id}';
};

export type EdgeGroupUpdateErrors = {
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeGroupUpdateResponses = {
  /**
   * OK
   */
  200: PortainereeEdgeGroup;
};

export type EdgeGroupUpdateResponse =
  EdgeGroupUpdateResponses[keyof EdgeGroupUpdateResponses];

export type EdgeJobListData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/edge_jobs';
};

export type EdgeJobListErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeJobListResponses = {
  /**
   * OK
   */
  200: Array<PortainerEdgeJob>;
};

export type EdgeJobListResponse =
  EdgeJobListResponses[keyof EdgeJobListResponses];

export type EdgeJobDeleteData = {
  body?: never;
  path: {
    /**
     * EdgeJob Id
     */
    id: number;
  };
  query?: never;
  url: '/edge_jobs/{id}';
};

export type EdgeJobDeleteErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeJobDeleteResponses = {
  /**
   * No Content
   */
  204: void;
};

export type EdgeJobDeleteResponse =
  EdgeJobDeleteResponses[keyof EdgeJobDeleteResponses];

export type EdgeJobInspectData = {
  body?: never;
  path: {
    /**
     * EdgeJob Id
     */
    id: number;
  };
  query?: never;
  url: '/edge_jobs/{id}';
};

export type EdgeJobInspectErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeJobInspectResponses = {
  /**
   * OK
   */
  200: PortainerEdgeJob;
};

export type EdgeJobInspectResponse =
  EdgeJobInspectResponses[keyof EdgeJobInspectResponses];

export type EdgeJobUpdateData = {
  /**
   * EdgeGroup data
   */
  body: EdgejobsEdgeJobUpdatePayload;
  path: {
    /**
     * EdgeJob Id
     */
    id: number;
  };
  query?: never;
  url: '/edge_jobs/{id}';
};

export type EdgeJobUpdateErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeJobUpdateResponses = {
  /**
   * OK
   */
  200: PortainerEdgeJob;
};

export type EdgeJobUpdateResponse =
  EdgeJobUpdateResponses[keyof EdgeJobUpdateResponses];

export type EdgeJobFileData = {
  body?: never;
  path: {
    /**
     * EdgeJob Id
     */
    id: number;
  };
  query?: never;
  url: '/edge_jobs/{id}/file';
};

export type EdgeJobFileErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeJobFileResponses = {
  /**
   * OK
   */
  200: EdgejobsEdgeJobFileResponse;
};

export type EdgeJobFileResponse =
  EdgeJobFileResponses[keyof EdgeJobFileResponses];

export type EdgeJobTasksListData = {
  body?: never;
  path: {
    /**
     * EdgeJob Id
     */
    id: number;
  };
  query?: never;
  url: '/edge_jobs/{id}/tasks';
};

export type EdgeJobTasksListErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeJobTasksListResponses = {
  /**
   * OK
   */
  200: Array<EdgejobsTaskContainer>;
};

export type EdgeJobTasksListResponse =
  EdgeJobTasksListResponses[keyof EdgeJobTasksListResponses];

export type EdgeJobTasksClearData = {
  body?: never;
  path: {
    /**
     * EdgeJob Id
     */
    id: number;
    /**
     * Task Id
     */
    taskID: number;
  };
  query?: never;
  url: '/edge_jobs/{id}/tasks/{taskID}/logs';
};

export type EdgeJobTasksClearErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeJobTasksClearResponses = {
  /**
   * No Content
   */
  204: void;
};

export type EdgeJobTasksClearResponse =
  EdgeJobTasksClearResponses[keyof EdgeJobTasksClearResponses];

export type EdgeJobTaskLogsInspectData = {
  body?: never;
  path: {
    /**
     * EdgeJob Id
     */
    id: number;
    /**
     * Task Id
     */
    taskID: number;
  };
  query?: never;
  url: '/edge_jobs/{id}/tasks/{taskID}/logs';
};

export type EdgeJobTaskLogsInspectErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeJobTaskLogsInspectResponses = {
  /**
   * OK
   */
  200: EdgejobsFileResponse;
};

export type EdgeJobTaskLogsInspectResponse =
  EdgeJobTaskLogsInspectResponses[keyof EdgeJobTaskLogsInspectResponses];

export type EdgeJobTasksCollectData = {
  body?: never;
  path: {
    /**
     * EdgeJob Id
     */
    id: number;
    /**
     * Task Id
     */
    taskID: number;
  };
  query?: never;
  url: '/edge_jobs/{id}/tasks/{taskID}/logs';
};

export type EdgeJobTasksCollectErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeJobTasksCollectResponses = {
  /**
   * No Content
   */
  204: void;
};

export type EdgeJobTasksCollectResponse =
  EdgeJobTasksCollectResponses[keyof EdgeJobTasksCollectResponses];

export type EdgeJobCreateFileData = {
  body: {
    /**
     * Content of the Stack file
     */
    file: Blob | File;
    /**
     * Name of the stack
     */
    Name: string;
    /**
     * A cron expression to schedule this job
     */
    CronExpression: string;
    /**
     * JSON stringified array of Edge Groups ids
     */
    EdgeGroups: string;
    /**
     * JSON stringified array of Environment ids
     */
    Endpoints: string;
    /**
     * If recurring
     */
    Recurring?: boolean;
  };
  path?: never;
  query?: never;
  url: '/edge_jobs/create/file';
};

export type EdgeJobCreateFileErrors = {
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeJobCreateFileResponses = {
  /**
   * OK
   */
  200: PortainereeEdgeGroup;
};

export type EdgeJobCreateFileResponse =
  EdgeJobCreateFileResponses[keyof EdgeJobCreateFileResponses];

export type EdgeJobCreateStringData = {
  /**
   * EdgeGroup data when method is string
   */
  body: EdgejobsEdgeJobCreateFromFileContentPayload;
  path?: never;
  query?: never;
  url: '/edge_jobs/create/string';
};

export type EdgeJobCreateStringErrors = {
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeJobCreateStringResponses = {
  /**
   * OK
   */
  200: PortainereeEdgeGroup;
};

export type EdgeJobCreateStringResponse =
  EdgeJobCreateStringResponses[keyof EdgeJobCreateStringResponses];

export type EdgeStackListData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * when true summarize the edge stack statuses in the StatusSummary field and void the Status field.
     */
    summarizeStatuses?: boolean;
  };
  url: '/edge_stacks';
};

export type EdgeStackListErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeStackListResponses = {
  /**
   * OK
   */
  200: Array<GithubComPortainerPortainerEeApiHttpHandlerEdgestacksEdgeStackListResponseItem>;
};

export type EdgeStackListResponse =
  EdgeStackListResponses[keyof EdgeStackListResponses];

export type EdgeStackDeleteData = {
  body?: never;
  path: {
    /**
     * EdgeStack Id
     */
    id: number;
  };
  query?: never;
  url: '/edge_stacks/{id}';
};

export type EdgeStackDeleteErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeStackDeleteResponses = {
  /**
   * No Content
   */
  204: void;
};

export type EdgeStackDeleteResponse =
  EdgeStackDeleteResponses[keyof EdgeStackDeleteResponses];

export type EdgeStackInspectData = {
  body?: never;
  path: {
    /**
     * EdgeStack Id
     */
    id: number;
  };
  query?: never;
  url: '/edge_stacks/{id}';
};

export type EdgeStackInspectErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeStackInspectResponses = {
  /**
   * OK
   */
  200: PortainereeEdgeStack;
};

export type EdgeStackInspectResponse =
  EdgeStackInspectResponses[keyof EdgeStackInspectResponses];

export type EdgeStackUpdateData = {
  /**
   * EdgeStack data
   */
  body: GithubComPortainerPortainerEeApiHttpHandlerEdgestacksUpdateEdgeStackPayload;
  path: {
    /**
     * EdgeStack Id
     */
    id: number;
  };
  query?: never;
  url: '/edge_stacks/{id}';
};

export type EdgeStackUpdateErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeStackUpdateResponses = {
  /**
   * OK
   */
  200: PortainereeEdgeStack;
};

export type EdgeStackUpdateResponse =
  EdgeStackUpdateResponses[keyof EdgeStackUpdateResponses];

export type EdgeStackFileData = {
  body?: never;
  path: {
    /**
     * EdgeStack Id
     */
    id: number;
  };
  query?: {
    /**
     * Stack file version maintained by Portainer. If both version and commitHash are provided, the commitHash will be used
     */
    version?: number;
    /**
     * Git repository commit hash. If both version and commitHash are provided, the commitHash will be used
     */
    commitHash?: string;
  };
  url: '/edge_stacks/{id}/file';
};

export type EdgeStackFileErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeStackFileResponses = {
  /**
   * OK
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerEdgestacksStackFileResponse;
};

export type EdgeStackFileResponse =
  EdgeStackFileResponses[keyof EdgeStackFileResponses];

export type EdgeStackUpdateFromGitData = {
  /**
   * Git configurations
   */
  body: EdgestacksStackGitUpdatePayload;
  path: {
    /**
     * Stack identifier
     */
    id: number;
  };
  query?: never;
  url: '/edge_stacks/{id}/git';
};

export type EdgeStackUpdateFromGitErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EdgeStackUpdateFromGitResponses = {
  /**
   * Success
   */
  204: void;
};

export type EdgeStackUpdateFromGitResponse =
  EdgeStackUpdateFromGitResponses[keyof EdgeStackUpdateFromGitResponses];

export type EdgeStackUpdateHelmRepositoryData = {
  /**
   * Helm repository update configuration
   */
  body: EdgestacksUpdateEdgeStackHelmRepositoryPayload;
  path: {
    /**
     * Stack identifier
     */
    id: number;
  };
  query?: never;
  url: '/edge_stacks/{id}/helmRepo';
};

export type EdgeStackUpdateHelmRepositoryErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EdgeStackUpdateHelmRepositoryResponses = {
  /**
   * OK
   */
  200: PortainereeEdgeStack;
};

export type EdgeStackUpdateHelmRepositoryResponse =
  EdgeStackUpdateHelmRepositoryResponses[keyof EdgeStackUpdateHelmRepositoryResponses];

export type EdgeStackLogsDeleteData = {
  body?: never;
  path: {
    /**
     * EdgeStack Id
     */
    id: number;
    /**
     * Endpoint Id
     */
    endpoint_id: number;
  };
  query?: never;
  url: '/edge_stacks/{id}/logs/{endpoint_id}';
};

export type EdgeStackLogsDeleteErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeStackLogsDeleteResponses = {
  /**
   * No Content
   */
  204: void;
};

export type EdgeStackLogsDeleteResponse =
  EdgeStackLogsDeleteResponses[keyof EdgeStackLogsDeleteResponses];

export type EdgeStackLogsStatusGetData = {
  body?: never;
  path: {
    /**
     * EdgeStack Id
     */
    id: number;
    /**
     * Environment Id
     */
    endpoint_id: number;
  };
  query?: never;
  url: '/edge_stacks/{id}/logs/{endpoint_id}';
};

export type EdgeStackLogsStatusGetErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeStackLogsStatusGetResponses = {
  /**
   * OK
   */
  200: unknown;
};

export type EdgeStackLogsCollectData = {
  body?: never;
  path: {
    /**
     * EdgeStack Id
     */
    id: number;
    /**
     * Environment Id
     */
    endpoint_id: number;
  };
  query?: {
    /**
     * Number of lines to request for the logs
     */
    tail?: number;
    /**
     * Container Id
     */
    container_id?: string;
    /**
     * Start time to request for the logs
     */
    since?: string;
    /**
     * End time to request for the logs
     */
    until?: string;
  };
  url: '/edge_stacks/{id}/logs/{endpoint_id}';
};

export type EdgeStackLogsCollectErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Not Found
   */
  404: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeStackLogsCollectResponses = {
  /**
   * No Content
   */
  204: void;
};

export type EdgeStackLogsCollectResponse =
  EdgeStackLogsCollectResponses[keyof EdgeStackLogsCollectResponses];

export type EdgeStackLogsDownloadData = {
  body?: never;
  path: {
    /**
     * EdgeStack Id
     */
    id: number;
    /**
     * Endpoint Id
     */
    endpoint_id: number;
  };
  query?: never;
  url: '/edge_stacks/{id}/logs/{endpoint_id}/file';
};

export type EdgeStackLogsDownloadErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Not Found
   */
  404: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeStackLogsDownloadResponses = {
  /**
   * OK
   */
  200: unknown;
};

export type EdgeStackStaggerStatusInspectData = {
  body?: never;
  path: {
    /**
     * EdgeStack Id
     */
    id: number;
  };
  query?: never;
  url: '/edge_stacks/{id}/stagger/status';
};

export type EdgeStackStaggerStatusInspectErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeStackStaggerStatusInspectResponses = {
  /**
   * OK
   */
  200: EdgestacksEdgeStackStaggerStatusResponse;
};

export type EdgeStackStaggerStatusInspectResponse =
  EdgeStackStaggerStatusInspectResponses[keyof EdgeStackStaggerStatusInspectResponses];

export type EdgeStackStatusUpdateData = {
  /**
   * EdgeStack status payload
   */
  body: GithubComPortainerPortainerEeApiHttpHandlerEdgestacksUpdateStatusPayload;
  path: {
    /**
     * EdgeStack Id
     */
    id: number;
  };
  query?: never;
  url: '/edge_stacks/{id}/status';
};

export type EdgeStackStatusUpdateErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Forbidden
   */
  403: unknown;
  /**
   * Not Found
   */
  404: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
};

export type EdgeStackStatusUpdateResponses = {
  /**
   * OK
   */
  200: PortainereeEdgeStack;
};

export type EdgeStackStatusUpdateResponse =
  EdgeStackStatusUpdateResponses[keyof EdgeStackStatusUpdateResponses];

export type EdgeStackCreateFileData = {
  body: {
    /**
     * Name of the stack. it must only consist of lowercase alphanumeric characters, hyphens, or underscores as well as start with a letter or number
     */
    Name: string;
    /**
     * Content of the Stack file
     */
    file: Blob | File;
    /**
     * JSON stringified array of Edge Groups ids
     */
    EdgeGroups: string;
    /**
     * deploy type 0 - 'compose', 1 - 'kubernetes'
     */
    DeploymentType: number;
    /**
     * JSON stringified array of Registry ids to use for this stack
     */
    Registries?: string;
    /**
     * Uses the manifest's namespaces instead of the default one, relevant only for kube environments
     */
    UseManifestNamespaces?: boolean;
    /**
     * Pre Pull image
     */
    PrePullImage?: boolean;
    /**
     * Retry deploy
     */
    RetryDeploy?: boolean;
    /**
     * Duration, in seconds, for which the agent should continue attempting to deploy the stack after a failure
     */
    RetryPeriod?: number;
    /**
     * unique webhook id
     */
    Webhook: string;
    /**
     * JSON stringified array of environment variables {name, value}
     */
    EnvVars?: string;
    /**
     * JSON stringified object of stagger config
     */
    StaggerConfig?: string;
  };
  path?: never;
  query?: {
    /**
     * if true, will not create an edge stack, but just will check the settings and return a non-persisted edge stack object
     */
    dryrun?: string;
  };
  url: '/edge_stacks/create/file';
};

export type EdgeStackCreateFileErrors = {
  /**
   * Bad request
   */
  400: unknown;
  /**
   * Internal server error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeStackCreateFileResponses = {
  /**
   * OK
   */
  200: PortainereeEdgeStack;
};

export type EdgeStackCreateFileResponse =
  EdgeStackCreateFileResponses[keyof EdgeStackCreateFileResponses];

export type EdgeStackCreateHelmRepositoryData = {
  /**
   * stack config
   */
  body: EdgestacksEdgeStackFromHelmRepositoryPayload;
  path?: never;
  query?: {
    /**
     * if true, will not create an edge stack, but just will check the settings and return a non-persisted edge stack object
     */
    dryrun?: string;
  };
  url: '/edge_stacks/create/helmRepo';
};

export type EdgeStackCreateHelmRepositoryErrors = {
  /**
   * Bad request
   */
  400: unknown;
  /**
   * Internal server error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeStackCreateHelmRepositoryResponses = {
  /**
   * OK
   */
  200: PortainereeEdgeStack;
};

export type EdgeStackCreateHelmRepositoryResponse =
  EdgeStackCreateHelmRepositoryResponses[keyof EdgeStackCreateHelmRepositoryResponses];

export type EdgeStackCreateRepositoryData = {
  /**
   * stack config
   */
  body: GithubComPortainerPortainerEeApiHttpHandlerEdgestacksEdgeStackFromGitRepositoryPayload;
  path?: never;
  query?: {
    /**
     * if true, will not create an edge stack, but just will check the settings and return a non-persisted edge stack object
     */
    dryrun?: string;
  };
  url: '/edge_stacks/create/repository';
};

export type EdgeStackCreateRepositoryErrors = {
  /**
   * Bad request
   */
  400: unknown;
  /**
   * Webhook ID already exists
   */
  409: unknown;
  /**
   * Internal server error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeStackCreateRepositoryResponses = {
  /**
   * OK
   */
  200: PortainereeEdgeStack;
};

export type EdgeStackCreateRepositoryResponse =
  EdgeStackCreateRepositoryResponses[keyof EdgeStackCreateRepositoryResponses];

export type EdgeStackCreateStringData = {
  /**
   * stack config
   */
  body: GithubComPortainerPortainerEeApiHttpHandlerEdgestacksEdgeStackFromStringPayload;
  path?: never;
  query?: {
    /**
     * if true, will not create an edge stack, but just will check the settings and return a non-persisted edge stack object
     */
    dryrun?: string;
  };
  url: '/edge_stacks/create/string';
};

export type EdgeStackCreateStringErrors = {
  /**
   * Bad request
   */
  400: unknown;
  /**
   * Internal server error
   */
  500: unknown;
  /**
   * Edge compute features are disabled
   */
  503: unknown;
};

export type EdgeStackCreateStringResponses = {
  /**
   * OK
   */
  200: PortainereeEdgeStack;
};

export type EdgeStackCreateStringResponse =
  EdgeStackCreateStringResponses[keyof EdgeStackCreateStringResponses];

export type EdgeStackParseRegistriesData = {
  body: {
    /**
     * stack file
     */
    file: Blob | File;
  };
  path?: never;
  query?: never;
  url: '/edge_stacks/parse_registries';
};

export type EdgeStackParseRegistriesErrors = {
  /**
   * Invalid request payload
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EdgeStackParseRegistriesResponses = {
  /**
   * List of registries IDs
   */
  200: Array<number>;
};

export type EdgeStackParseRegistriesResponse =
  EdgeStackParseRegistriesResponses[keyof EdgeStackParseRegistriesResponses];

export type EdgeStackWebhookInvokeData = {
  body?: never;
  path: {
    /**
     * Stack identifier
     */
    webhookID: string;
  };
  query?: never;
  url: '/edge_stacks/webhooks/{webhookID}';
};

export type EdgeStackWebhookInvokeErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Conflict
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EdgeStackWebhookInvokeResponses = {
  /**
   * Success
   */
  200: unknown;
};

export type EdgeUpdateScheduleListData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Include Edge Stacks in the response
     */
    includeEdgeStacks?: boolean;
  };
  url: '/edge_update_schedules';
};

export type EdgeUpdateScheduleListErrors = {
  /**
   * Internal Server Error
   */
  500: unknown;
};

export type EdgeUpdateScheduleListResponses = {
  /**
   * OK
   */
  200: Array<EdgeupdateschedulesDecoratedUpdateSchedule>;
};

export type EdgeUpdateScheduleListResponse =
  EdgeUpdateScheduleListResponses[keyof EdgeUpdateScheduleListResponses];

export type EdgeUpdateScheduleCreateData = {
  /**
   * Schedule details
   */
  body: EdgeupdateschedulesCreatePayload;
  path?: never;
  query?: never;
  url: '/edge_update_schedules';
};

export type EdgeUpdateScheduleCreateErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * Edge update schedule name already in use.
   */
  409: unknown;
  /**
   * Server error occurred while attempting to create the remote update procedure.
   */
  500: unknown;
};

export type EdgeUpdateScheduleCreateResponses = {
  /**
   * Remote update procedure successfully created
   */
  200: TypesUpdateSchedule;
};

export type EdgeUpdateScheduleCreateResponse =
  EdgeUpdateScheduleCreateResponses[keyof EdgeUpdateScheduleCreateResponses];

export type EdgeUpdateScheduleDeleteData = {
  body?: never;
  path: {
    /**
     * EdgeUpdate Id
     */
    id: number;
  };
  query?: never;
  url: '/edge_update_schedules/{id}';
};

export type EdgeUpdateScheduleDeleteErrors = {
  /**
   * Internal Server Error
   */
  500: unknown;
};

export type EdgeUpdateScheduleDeleteResponses = {
  /**
   * No Content
   */
  204: void;
};

export type EdgeUpdateScheduleDeleteResponse =
  EdgeUpdateScheduleDeleteResponses[keyof EdgeUpdateScheduleDeleteResponses];

export type EdgeUpdateScheduleInspectData = {
  body?: never;
  path: {
    /**
     * EdgeUpdate Id
     */
    id: number;
  };
  query?: never;
  url: '/edge_update_schedules/{id}';
};

export type EdgeUpdateScheduleInspectErrors = {
  /**
   * Internal Server Error
   */
  500: unknown;
};

export type EdgeUpdateScheduleInspectResponses = {
  /**
   * OK
   */
  200: EdgeupdateschedulesInspectResponse;
};

export type EdgeUpdateScheduleInspectResponse =
  EdgeUpdateScheduleInspectResponses[keyof EdgeUpdateScheduleInspectResponses];

export type EdgeUpdateScheduleUpdateData = {
  /**
   * Schedule details
   */
  body: EdgeupdateschedulesUpdatePayload;
  path: {
    /**
     * EdgeUpdate Id
     */
    id: number;
  };
  query?: never;
  url: '/edge_update_schedules/{id}';
};

export type EdgeUpdateScheduleUpdateErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * Unable to find an Edge Update with the specified identifier inside the database
   */
  404: unknown;
  /**
   * Edge update schedule name already in use.
   */
  409: unknown;
  /**
   * Server error occurred while attempting to update the remote update procedure.
   */
  500: unknown;
};

export type EdgeUpdateScheduleUpdateResponses = {
  /**
   * Remote update procedure successfully updated
   */
  204: TypesUpdateSchedule;
};

export type EdgeUpdateScheduleUpdateResponse =
  EdgeUpdateScheduleUpdateResponses[keyof EdgeUpdateScheduleUpdateResponses];

export type EdgeUpdateScheduleActiveSchedulesListData = {
  /**
   * Active schedule query
   */
  body: EdgeupdateschedulesActiveSchedulePayload;
  path?: never;
  query?: never;
  url: '/edge_update_schedules/active';
};

export type EdgeUpdateScheduleActiveSchedulesListErrors = {
  /**
   * Internal Server Error
   */
  500: unknown;
};

export type EdgeUpdateScheduleActiveSchedulesListResponses = {
  /**
   * OK
   */
  200: Array<TypesEndpointUpdateScheduleRelation>;
};

export type EdgeUpdateScheduleActiveSchedulesListResponse =
  EdgeUpdateScheduleActiveSchedulesListResponses[keyof EdgeUpdateScheduleActiveSchedulesListResponses];

export type RemoteUpdatesAgentVersionsData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/edge_update_schedules/agent_versions';
};

export type RemoteUpdatesAgentVersionsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type RemoteUpdatesAgentVersionsResponses = {
  /**
   * OK
   */
  200: Array<string>;
};

export type RemoteUpdatesAgentVersionsResponse =
  RemoteUpdatesAgentVersionsResponses[keyof RemoteUpdatesAgentVersionsResponses];

export type EdgeUpdateScheduleInfoData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/edge_update_schedules/info';
};

export type EdgeUpdateScheduleInfoErrors = {
  /**
   * Internal Server Error
   */
  500: unknown;
};

export type EdgeUpdateScheduleInfoResponses = {
  /**
   * OK
   */
  200: EdgeupdateschedulesInfoResponse;
};

export type EdgeUpdateScheduleInfoResponse =
  EdgeUpdateScheduleInfoResponses[keyof EdgeUpdateScheduleInfoResponses];

export type EdgeUpdatePreviousVersionsData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Environment IDs
     */
    environmentIds?: Array<number>;
    /**
     * Edge Group IDs
     */
    edgeGroupIds?: Array<number>;
  };
  url: '/edge_update_schedules/previous_versions';
};

export type EdgeUpdatePreviousVersionsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EdgeUpdatePreviousVersionsResponses = {
  /**
   * OK
   */
  200: {
    [key: string]: string;
  };
};

export type EdgeUpdatePreviousVersionsResponse =
  EdgeUpdatePreviousVersionsResponses[keyof EdgeUpdatePreviousVersionsResponses];

export type EndpointGroupListData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * If true, each environment(endpoint) group will include the number of environments(endpoints) associated to it and breakdown by type
     */
    size?: boolean;
  };
  url: '/endpoint_groups';
};

export type EndpointGroupListErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointGroupListResponses = {
  /**
   * Environment(Endpoint) group
   */
  200: Array<EndpointgroupsEndpointGroupResponse>;
};

export type EndpointGroupListResponse =
  EndpointGroupListResponses[keyof EndpointGroupListResponses];

export type EndpointGroupCreateData = {
  /**
   * Environment(Endpoint) Group details
   */
  body: GithubComPortainerPortainerEeApiHttpHandlerEndpointgroupsEndpointGroupCreatePayload;
  path?: never;
  query?: never;
  url: '/endpoint_groups';
};

export type EndpointGroupCreateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointGroupCreateResponses = {
  /**
   * Success
   */
  200: PortainerEndpointGroup;
};

export type EndpointGroupCreateResponse =
  EndpointGroupCreateResponses[keyof EndpointGroupCreateResponses];

export type EndpointGroupDeleteData = {
  body?: never;
  path: {
    /**
     * EndpointGroup identifier
     */
    id: number;
  };
  query?: never;
  url: '/endpoint_groups/{id}';
};

export type EndpointGroupDeleteErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * EndpointGroup not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointGroupDeleteResponses = {
  /**
   * Success
   */
  204: void;
};

export type EndpointGroupDeleteResponse =
  EndpointGroupDeleteResponses[keyof EndpointGroupDeleteResponses];

export type GetEndpointGroupsByIdData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) group identifier
     */
    id: number;
  };
  query?: {
    /**
     * If true, include the number of environments and breakdown by type
     */
    size?: boolean;
  };
  url: '/endpoint_groups/{id}';
};

export type GetEndpointGroupsByIdErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * EndpointGroup not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GetEndpointGroupsByIdResponses = {
  /**
   * Success
   */
  200: EndpointgroupsEndpointGroupResponse;
};

export type GetEndpointGroupsByIdResponse =
  GetEndpointGroupsByIdResponses[keyof GetEndpointGroupsByIdResponses];

export type EndpointGroupUpdateData = {
  /**
   * EndpointGroup details
   */
  body: GithubComPortainerPortainerEeApiHttpHandlerEndpointgroupsEndpointGroupUpdatePayload;
  path: {
    /**
     * EndpointGroup identifier
     */
    id: number;
  };
  query?: never;
  url: '/endpoint_groups/{id}';
};

export type EndpointGroupUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * EndpointGroup not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointGroupUpdateResponses = {
  /**
   * Success
   */
  200: PortainerEndpointGroup;
};

export type EndpointGroupUpdateResponse =
  EndpointGroupUpdateResponses[keyof EndpointGroupUpdateResponses];

export type EndpointGroupDeleteEndpointData = {
  body?: never;
  path: {
    /**
     * EndpointGroup identifier
     */
    id: number;
    /**
     * Environment(Endpoint) identifier
     */
    endpointId: number;
  };
  query?: never;
  url: '/endpoint_groups/{id}/endpoints/{endpointId}';
};

export type EndpointGroupDeleteEndpointErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * EndpointGroup not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointGroupDeleteEndpointResponses = {
  /**
   * Success
   */
  204: void;
};

export type EndpointGroupDeleteEndpointResponse =
  EndpointGroupDeleteEndpointResponses[keyof EndpointGroupDeleteEndpointResponses];

export type EndpointGroupAddEndpointData = {
  body?: never;
  path: {
    /**
     * EndpointGroup identifier
     */
    id: number;
    /**
     * Environment(Endpoint) identifier
     */
    endpointId: number;
  };
  query?: never;
  url: '/endpoint_groups/{id}/endpoints/{endpointId}';
};

export type EndpointGroupAddEndpointErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * EndpointGroup not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointGroupAddEndpointResponses = {
  /**
   * Success
   */
  204: void;
};

export type EndpointGroupAddEndpointResponse =
  EndpointGroupAddEndpointResponses[keyof EndpointGroupAddEndpointResponses];

export type EndpointListData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Start searching from
     */
    start?: number;
    /**
     * Limit results to this value
     */
    limit?: number;
    /**
     * Sort results by this value
     */
    sort?:
      | 'Name'
      | 'Group'
      | 'Status'
      | 'LastCheckIn'
      | 'EdgeID'
      | 'PlatformType'
      | 'Health'
      | 'Id';
    /**
     * Order sorted results by desc/asc
     */
    order?: string;
    /**
     * If true, an `X-Update-Available` header will be added to the response to indicate if an update is available
     */
    updateInformation?: boolean;
    /**
     * If true, an `X-K8S-Env-Admin` header will be added to the response to indicate if the user is a K8S environment admin for any of the returned environments
     */
    k8sEnvAdmin?: boolean;
    /**
     * Search query
     */
    search?: string;
    /**
     * List environments(endpoints) of these groups
     */
    groupIds?: Array<number>;
    /**
     * List environments(endpoints) by this status
     */
    status?: Array<number>;
    /**
     * List environments(endpoints) of this type
     */
    types?: Array<0 | 1 | 2 | 3 | 4 | 5 | 6 | 7>;
    /**
     * Filter environments by platform type
     */
    platformTypes?: Array<0 | 1 | 2 | 3 | 4>;
    /**
     * If true, return only environments with an outdated agent
     */
    outdated?: boolean;
    /**
     * Exclude environments of these groups
     */
    excludeGroupIds?: Array<number>;
    /**
     * search environments(endpoints) with these tags (depends on tagsPartialMatch)
     */
    tagIds?: Array<number>;
    /**
     * If true, will return environment(endpoint) which has one of tagIds, if false (or missing) will return only environments(endpoints) that has all the tags
     */
    tagsPartialMatch?: boolean;
    /**
     * will return only these environments(endpoints)
     */
    endpointIds?: Array<number>;
    /**
     * will exclude these environments(endpoints)
     */
    excludeIds?: Array<number>;
    /**
     * If true, will return environment(endpoint) that were provisioned
     */
    provisioned?: boolean;
    /**
     * will return only environments with on of these agent versions
     */
    agentVersions?: Array<string>;
    /**
     * if exists true show only edge async agents, false show only standard edge agents. if missing, will show both types (relevant only for edge agents)
     */
    edgeAsync?: boolean;
    /**
     * if true, show only untrusted edge agents, if false show only trusted edge agents (relevant only for edge agents)
     */
    edgeDeviceUntrusted?: boolean;
    /**
     * if bigger then zero, show only edge agents that checked-in in the last provided seconds (relevant only for edge agents)
     */
    edgeCheckInPassedSeconds?: number;
    /**
     * if true, the snapshot data won't be retrieved
     */
    excludeSnapshots?: boolean;
    /**
     * will return only environments(endpoints) with this name
     */
    name?: string;
    /**
     * Filter environments by partial name match (case-insensitive, searches name only)
     */
    nameFilter?: string;
    /**
     * will return the environments of the specified edge stack
     */
    edgeStackId?: number;
    /**
     * only applied when edgeStackId exists. Filter the returned environments based on their deployment status in the stack (not the environment status!)
     */
    edgeStackStatus?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13;
    /**
     * List environments(endpoints) of these edge groups
     */
    edgeGroupIds?: Array<number>;
    /**
     * Exclude environments(endpoints) of these edge groups
     */
    excludeEdgeGroupIds?: Array<number>;
    /**
     * If true, will apply policy data to the returned environments(endpoints)
     */
    policy?: boolean;
    /**
     * List environments(endpoints) associated with these policies
     */
    policyIds?: Array<number>;
    /**
     * Filter environments by policy status (applied, failed, in_progress, warning, not_supported). Only applies when policyIds is specified.
     */
    policyStatus?: Array<
      'applied' | 'failed' | 'in_progress' | 'warning' | 'not_supported'
    >;
  };
  url: '/endpoints';
};

export type EndpointListErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointListResponses = {
  /**
   * Endpoints
   */
  200: Array<EndpointsEndpointResponse>;
};

export type EndpointListResponse =
  EndpointListResponses[keyof EndpointListResponses];

export type EndpointCreateData = {
  body: {
    /**
     * Name that will be used to identify this environment(endpoint) (example: my-environment)
     */
    Name: string;
    /**
     * Environment(Endpoint) type. Value must be one of: 1 (Local Docker environment), 2 (Agent environment), 3 (Azure environment), 4 (Edge agent environment) or 5 (Local Kubernetes Environment)
     */
    EndpointCreationType: 0 | 1 | 2 | 3 | 4 | 5 | 6;
    /**
     * Container engine used by the environment(endpoint). Value must be one of: 'docker' or 'podman'
     */
    ContainerEngine?: string;
    /**
     * URL or IP address of a Docker host (example: docker.mydomain.tld:2375). Defaults to local if not specified (Linux: /var/run/docker.sock, Windows: //./pipe/docker_engine). Cannot be empty if EndpointCreationType is set to 4 (Edge agent environment)
     */
    URL?: string;
    /**
     * URL or IP address where exposed containers will be reachable. Defaults to URL if not specified (example: docker.mydomain.tld:2375)
     */
    PublicURL?: string;
    /**
     * Environment(Endpoint) group identifier. If not specified will default to 1 (unassigned).
     */
    GroupID?: number;
    /**
     * Require TLS to connect against this environment(endpoint). Must be true if EndpointCreationType is set to 2 (Agent environment)
     */
    TLS?: boolean;
    /**
     * Skip server verification when using TLS. Must be true if EndpointCreationType is set to 2 (Agent environment)
     */
    TLSSkipVerify?: boolean;
    /**
     * Skip client verification when using TLS. Must be true if EndpointCreationType is set to 2 (Agent environment)
     */
    TLSSkipClientVerify?: boolean;
    /**
     * TLS CA certificate file
     */
    TLSCACertFile?: Blob | File;
    /**
     * TLS client certificate file
     */
    TLSCertFile?: Blob | File;
    /**
     * TLS client key file
     */
    TLSKeyFile?: Blob | File;
    /**
     * Azure application ID. Required if environment(endpoint) type is set to 3
     */
    AzureApplicationID?: string;
    /**
     * Azure tenant ID. Required if environment(endpoint) type is set to 3
     */
    AzureTenantID?: string;
    /**
     * Azure authentication key. Required if environment(endpoint) type is set to 3
     */
    AzureAuthenticationKey?: string;
    /**
     * JSON-parsable array of tag identifiers to which this environment(endpoint) is associated
     */
    TagIds?: string;
    /**
     * The check in interval for edge agent (in seconds)
     */
    EdgeCheckinInterval?: number;
    /**
     * URL or IP address that will be used to establish a reverse tunnel. Required when settings.EnableEdgeComputeFeatures is set to false or when settings.Edge.TunnelServerAddress is not set
     */
    EdgeTunnelServerAddress?: string;
    /**
     * Enable async mode for edge agent
     */
    EdgeAsyncMode?: boolean;
    /**
     * The ping interval for edge agent (in seconds)
     */
    EdgePingInterval?: number;
    /**
     * The snapshot interval for edge agent (in seconds)
     */
    EdgeSnapshotInterval?: number;
    /**
     * The command interval for edge agent (in seconds)
     */
    EdgeCommandInterval?: number;
    /**
     * KaaS: custom template identifier to deploy on environment creation
     */
    CustomTemplateID?: number;
    /**
     * KaaS: custom template content to deploy on environment creation
     */
    CustomTemplateContent?: string;
    /**
     * KaaS: stack name for the custom template deployment
     */
    StackName?: string;
    /**
     * Kubernetes configuration file content (base64 encoded). Required when EndpointCreationType is set to 6 (KubeConfigEnvironment)
     */
    KubeConfig?: string;
    /**
     * List of GPUs - json stringified array of {name, value} structs
     */
    Gpus?: string;
  };
  path?: never;
  query?: never;
  url: '/endpoints';
};

export type EndpointCreateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Name is not unique
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointCreateResponses = {
  /**
   * Success
   */
  200: PortainereeEndpoint;
};

export type EndpointCreateResponse =
  EndpointCreateResponses[keyof EndpointCreateResponses];

export type EndpointDeleteData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: never;
  url: '/endpoints/{id}';
};

export type EndpointDeleteErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * Unable to find the environment with the specified identifier inside the database.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to delete the environment.
   */
  500: unknown;
};

export type EndpointDeleteResponses = {
  /**
   * Environment successfully deleted.
   */
  204: void;
};

export type EndpointDeleteResponse =
  EndpointDeleteResponses[keyof EndpointDeleteResponses];

export type EndpointInspectData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: {
    /**
     * if true, the snapshot data won't be retrieved
     */
    excludeSnapshot?: boolean;
  };
  url: '/endpoints/{id}';
};

export type EndpointInspectErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Environment(Endpoint) not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointInspectResponses = {
  /**
   * Success
   */
  200: PortainereeEndpoint;
};

export type EndpointInspectResponse =
  EndpointInspectResponses[keyof EndpointInspectResponses];

export type EndpointUpdateData = {
  /**
   * Environment(Endpoint) details
   */
  body: EndpointsEndpointUpdatePayload;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: never;
  url: '/endpoints/{id}';
};

export type EndpointUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Environment(Endpoint) not found
   */
  404: unknown;
  /**
   * Name is not unique
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointUpdateResponses = {
  /**
   * Success
   */
  200: PortainereeEndpoint;
};

export type EndpointUpdateResponse =
  EndpointUpdateResponses[keyof EndpointUpdateResponses];

export type EndpointAssociationDeleteData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: never;
  url: '/endpoints/{id}/association';
};

export type EndpointAssociationDeleteErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Environment(Endpoint) not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointAssociationDeleteResponses = {
  /**
   * Success
   */
  200: PortainereeEndpoint;
};

export type EndpointAssociationDeleteResponse =
  EndpointAssociationDeleteResponses[keyof EndpointAssociationDeleteResponses];

export type EndpointDockerBrowsePutData = {
  body: {
    /**
     * The destination path to upload the file to
     */
    Path: string;
    /**
     * The file to upload
     */
    file: Blob | File;
  };
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: {
    /**
     * Optional volume identifier to upload the file
     */
    volumeID?: string;
  };
  url: '/endpoints/{id}/docker/v2/browse/put';
};

export type EndpointDockerBrowsePutErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointDockerBrowsePutResponses = {
  /**
   * Success
   */
  204: void;
};

export type EndpointDockerBrowsePutResponse =
  EndpointDockerBrowsePutResponses[keyof EndpointDockerBrowsePutResponses];

export type EndpointDockerhubStatusData = {
  body?: never;
  path: {
    /**
     * endpoint ID
     */
    id: number;
    /**
     * registry ID
     */
    registryId: number;
  };
  query?: never;
  url: '/endpoints/{id}/dockerhub/{registryId}';
};

export type EndpointDockerhubStatusErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * registry or endpoint not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointDockerhubStatusResponses = {
  /**
   * Success
   */
  200: EndpointsDockerhubStatusResponse;
};

export type EndpointDockerhubStatusResponse =
  EndpointDockerhubStatusResponses[keyof EndpointDockerhubStatusResponses];

export type EndpointSetPolicyStatusesData = {
  /**
   * Per-policy statuses
   */
  body: EndpointedgePolicyStatusesPayload;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: never;
  url: '/endpoints/{id}/edge/policies/statuses';
};

export type EndpointSetPolicyStatusesErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied to access environment(endpoint)
   */
  403: unknown;
  /**
   * Environment(Endpoint) not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointSetPolicyStatusesResponses = {
  /**
   * Success
   */
  204: void;
};

export type EndpointSetPolicyStatusesResponse =
  EndpointSetPolicyStatusesResponses[keyof EndpointSetPolicyStatusesResponses];

export type EndpointForceUpdateServiceData = {
  /**
   * details
   */
  body: EndpointsForceUpdateServicePayload;
  path: {
    /**
     * endpoint identifier
     */
    id: number;
  };
  query?: never;
  url: '/endpoints/{id}/forceupdateservice';
};

export type EndpointForceUpdateServiceErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * endpoint not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointForceUpdateServiceResponses = {
  /**
   * Success
   */
  200: SwarmServiceUpdateResponse;
};

export type EndpointForceUpdateServiceResponse =
  EndpointForceUpdateServiceResponses[keyof EndpointForceUpdateServiceResponses];

export type HelmListData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: {
    /**
     * specify an optional namespace
     */
    namespace?: string;
    /**
     * specify an optional filter
     */
    filter?: string;
    /**
     * specify an optional selector
     */
    selector?: string;
  };
  url: '/endpoints/{id}/kubernetes/helm';
};

export type HelmListErrors = {
  /**
   * Invalid environment(endpoint) identifier
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Environment(Endpoint) or ServiceAccount not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type HelmListResponses = {
  /**
   * Success
   */
  200: Array<ReleaseReleaseElement>;
};

export type HelmListResponse = HelmListResponses[keyof HelmListResponses];

export type HelmInstallData = {
  /**
   * Chart details
   */
  body: HelmInstallChartPayload;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: {
    /**
     * Dry run
     */
    dryRun?: boolean;
  };
  url: '/endpoints/{id}/kubernetes/helm';
};

export type HelmInstallErrors = {
  /**
   * Invalid request payload
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Environment(Endpoint) or ServiceAccount not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type HelmInstallResponses = {
  /**
   * Created
   */
  201: ReleaseRelease;
};

export type HelmInstallResponse =
  HelmInstallResponses[keyof HelmInstallResponses];

export type HelmDeleteData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * The name of the release/application to uninstall
     */
    release: string;
  };
  query?: {
    /**
     * An optional namespace
     */
    namespace?: string;
  };
  url: '/endpoints/{id}/kubernetes/helm/{release}';
};

export type HelmDeleteErrors = {
  /**
   * Invalid environment(endpoint) id or bad request
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Environment(Endpoint) or ServiceAccount not found
   */
  404: unknown;
  /**
   * Server error or helm error
   */
  500: unknown;
};

export type HelmDeleteResponses = {
  /**
   * Success
   */
  204: void;
};

export type HelmDeleteResponse = HelmDeleteResponses[keyof HelmDeleteResponses];

export type HelmGetData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * Helm release name
     */
    release: string;
  };
  query?: {
    /**
     * specify an optional namespace
     */
    namespace?: string;
    /**
     * show resources of the release
     */
    showResources?: boolean;
    /**
     * specify an optional revision
     */
    revision?: number;
  };
  url: '/endpoints/{id}/kubernetes/helm/{release}';
};

export type HelmGetErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the release.
   */
  500: unknown;
};

export type HelmGetResponses = {
  /**
   * Success
   */
  200: ReleaseRelease;
};

export type HelmGetResponse = HelmGetResponses[keyof HelmGetResponses];

export type HelmGetHistoryData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * Helm release name
     */
    release: string;
  };
  query?: {
    /**
     * specify an optional namespace
     */
    namespace?: string;
  };
  url: '/endpoints/{id}/kubernetes/helm/{release}/history';
};

export type HelmGetHistoryErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the historical list of releases.
   */
  500: unknown;
};

export type HelmGetHistoryResponses = {
  /**
   * Success
   */
  200: Array<ReleaseRelease>;
};

export type HelmGetHistoryResponse =
  HelmGetHistoryResponses[keyof HelmGetHistoryResponses];

export type HelmRollbackData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * Helm release name
     */
    release: string;
  };
  query?: {
    /**
     * specify an optional namespace
     */
    namespace?: string;
    /**
     * specify the revision to rollback to (defaults to previous revision if not specified)
     */
    revision?: number;
    /**
     * wait for resources to be ready (default: false)
     */
    wait?: boolean;
    /**
     * wait for jobs to complete before marking the release as successful (default: false)
     */
    waitForJobs?: boolean;
    /**
     * performs pods restart for the resource if applicable (default: true)
     */
    recreate?: boolean;
    /**
     * force resource update through delete/recreate if needed (default: false)
     */
    force?: boolean;
    /**
     * time to wait for any individual Kubernetes operation in seconds (default: 300)
     */
    timeout?: number;
  };
  url: '/endpoints/{id}/kubernetes/helm/{release}/rollback';
};

export type HelmRollbackErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or release name.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to rollback the release.
   */
  500: unknown;
};

export type HelmRollbackResponses = {
  /**
   * Success
   */
  200: ReleaseRelease;
};

export type HelmRollbackResponse =
  HelmRollbackResponses[keyof HelmRollbackResponses];

export type HelmGitDryRunData = {
  /**
   * Git Helm dry run details
   */
  body: HelmGitHelmDryRunPayload;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: never;
  url: '/endpoints/{id}/kubernetes/helm/git/dryrun';
};

export type HelmGitDryRunErrors = {
  /**
   * Bad request
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Environment(Endpoint) not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type HelmGitDryRunResponses = {
  /**
   * Success
   */
  200: ReleaseRelease;
};

export type HelmGitDryRunResponse =
  HelmGitDryRunResponses[keyof HelmGitDryRunResponses];

export type EndpointMtlsCertificateData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: never;
  url: '/endpoints/{id}/mtls_certificate';
};

export type EndpointMtlsCertificateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Environment(Endpoint) not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointMtlsCertificateResponses = {
  /**
   * Success
   */
  200: EndpointsEndpointMtlsCertResponse;
};

export type EndpointMtlsCertificateResponse =
  EndpointMtlsCertificateResponses[keyof EndpointMtlsCertificateResponses];

export type EndpointMtlsAgentCertificateErrorData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: never;
  url: '/endpoints/{id}/mtls_certificate_error';
};

export type EndpointMtlsAgentCertificateErrorErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Environment(Endpoint) not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointMtlsAgentCertificateErrorResponses = {
  /**
   * Success
   */
  200: EndpointsEndpointMtlsCertResponse;
};

export type EndpointMtlsAgentCertificateErrorResponse =
  EndpointMtlsAgentCertificateErrorResponses[keyof EndpointMtlsAgentCertificateErrorResponses];

export type NamespacesAccessUpdateData = {
  /**
   * Payload containing lists of users and teams to add or remove access for
   */
  body: EndpointsResourcePoolUpdatePayload;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Namespace identifier
     */
    rpn: number;
  };
  query?: never;
  url: '/endpoints/{id}/pools/{rpn}/access';
};

export type NamespacesAccessUpdateErrors = {
  /**
   * Invalid request payload (such as missing required fields or fields not meeting validation criteria).
   */
  400: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Namespace access is managed by an applied RBAC policy and cannot be updated manually.
   */
  409: unknown;
  /**
   * Server error occurred while attempting to update namespace access permissions.
   */
  500: unknown;
};

export type NamespacesAccessUpdateResponses = {
  /**
   * Success - Access permissions were successfully updated
   */
  204: void;
};

export type NamespacesAccessUpdateResponse =
  NamespacesAccessUpdateResponses[keyof NamespacesAccessUpdateResponses];

export type EndpointRegistriesListData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: {
    /**
     * required if kubernetes environment, will show registries by namespace
     */
    namespace?: string;
  };
  url: '/endpoints/{id}/registries';
};

export type EndpointRegistriesListErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointRegistriesListResponses = {
  /**
   * Success
   */
  200: Array<PortainereeRegistry>;
};

export type EndpointRegistriesListResponse =
  EndpointRegistriesListResponses[keyof EndpointRegistriesListResponses];

export type EndpointRegistryAccessData = {
  /**
   * details
   */
  body: EndpointsRegistryAccessPayload;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * Registry identifier
     */
    registryId: number;
  };
  query?: never;
  url: '/endpoints/{id}/registries/{registryId}';
};

export type EndpointRegistryAccessErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Endpoint not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointRegistryAccessResponses = {
  /**
   * Success
   */
  204: void;
};

export type EndpointRegistryAccessResponse =
  EndpointRegistryAccessResponses[keyof EndpointRegistryAccessResponses];

export type EndpointSettingsUpdateData = {
  /**
   * Environment(Endpoint) details
   */
  body: EndpointsEndpointSettingsUpdatePayload;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: never;
  url: '/endpoints/{id}/settings';
};

export type EndpointSettingsUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Environment(Endpoint) not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointSettingsUpdateResponses = {
  /**
   * Success
   */
  200: PortainereeEndpoint;
};

export type EndpointSettingsUpdateResponse =
  EndpointSettingsUpdateResponses[keyof EndpointSettingsUpdateResponses];

export type EndpointSnapshotData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: never;
  url: '/endpoints/{id}/snapshot';
};

export type EndpointSnapshotErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Environment(Endpoint) not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointSnapshotResponses = {
  /**
   * Success
   */
  204: void;
};

export type EndpointSnapshotResponse =
  EndpointSnapshotResponses[keyof EndpointSnapshotResponses];

export type AgentVersionsData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/endpoints/agent_versions';
};

export type AgentVersionsErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type AgentVersionsResponses = {
  /**
   * List of available agent versions
   */
  200: Array<string>;
};

export type AgentVersionsResponse =
  AgentVersionsResponses[keyof AgentVersionsResponses];

export type EndpointDeleteBatchData = {
  /**
   * List of environments to delete, with optional deleteCluster flag to clean-up associated resources (cloud environments only)
   */
  body: EndpointsEndpointDeleteBatchPayload2;
  path?: never;
  query?: never;
  url: '/endpoints/delete';
};

export type EndpointDeleteBatchErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to delete the specified environments.
   */
  500: unknown;
};

export type EndpointDeleteBatchResponses = {
  /**
   * Environment(s) successfully deleted.
   */
  204: void;
  /**
   * Partial success. Some environments were deleted successfully, while others failed.
   */
  207: EndpointsEndpointDeleteBatchPartialResponse;
};

export type EndpointDeleteBatchResponse =
  EndpointDeleteBatchResponses[keyof EndpointDeleteBatchResponses];

export type EndpointEdgeGenerateKeyData = {
  /**
   * Generate Key Info
   */
  body: EndpointedgeGenerateKeyResponse;
  path?: never;
  query?: never;
  url: '/endpoints/edge/generate-key';
};

export type EndpointEdgeGenerateKeyErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
};

export type EndpointEdgeGenerateKeyResponses = {
  /**
   * OK
   */
  200: unknown;
};

export type TrustEdgeEndpointsData = {
  /**
   * Information about the environments to trust
   */
  body: EndpointedgeEndpointsTrustPayload;
  path?: never;
  query?: never;
  url: '/endpoints/edge/trust';
};

export type TrustEdgeEndpointsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Server error occurred while attempting to associate the environments.
   */
  500: unknown;
};

export type TrustEdgeEndpointsResponses = {
  /**
   * Environment(s) successfully associated.
   */
  204: void;
};

export type TrustEdgeEndpointsResponse =
  TrustEdgeEndpointsResponses[keyof TrustEdgeEndpointsResponses];

export type EndpointCreateGlobalKeyData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/endpoints/global-key';
};

export type EndpointCreateGlobalKeyErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointCreateGlobalKeyResponses = {
  /**
   * Success
   */
  200: EndpointsEndpointCreateGlobalKeyResponse;
};

export type EndpointCreateGlobalKeyResponse =
  EndpointCreateGlobalKeyResponses[keyof EndpointCreateGlobalKeyResponses];

export type EndpointUpdateRelationsData = {
  /**
   * Environment relations data
   */
  body: EndpointsEndpointUpdateRelationsPayload;
  path?: never;
  query?: never;
  url: '/endpoints/relations';
};

export type EndpointUpdateRelationsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointUpdateRelationsResponses = {
  /**
   * Success
   */
  204: void;
};

export type EndpointUpdateRelationsResponse =
  EndpointUpdateRelationsResponses[keyof EndpointUpdateRelationsResponses];

export type EndpointSnapshotsData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/endpoints/snapshot';
};

export type EndpointSnapshotsErrors = {
  /**
   * Server Error
   */
  500: unknown;
};

export type EndpointSnapshotsResponses = {
  /**
   * Success
   */
  204: void;
};

export type EndpointSnapshotsResponse =
  EndpointSnapshotsResponses[keyof EndpointSnapshotsResponses];

export type EndpointSummaryCountsData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/endpoints/summary';
};

export type EndpointSummaryCountsErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type EndpointSummaryCountsResponses = {
  /**
   * Environment summary counts
   */
  200: EndpointsEnvironmentSummaryCountsResponse;
};

export type EndpointSummaryCountsResponse =
  EndpointSummaryCountsResponses[keyof EndpointSummaryCountsResponses];

export type GitOperationRepoFilePreviewData = {
  /**
   * Template details
   */
  body: GitopsRepositoryFilePreviewPayload;
  path?: never;
  query?: never;
  url: '/gitops/repo/file/preview';
};

export type GitOperationRepoFilePreviewErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Source not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GitOperationRepoFilePreviewResponses = {
  /**
   * Success
   */
  200: GitopsFileResponse;
};

export type GitOperationRepoFilePreviewResponse =
  GitOperationRepoFilePreviewResponses[keyof GitOperationRepoFilePreviewResponses];

export type GitOperationRepoFilesSearchData = {
  /**
   * details
   */
  body: GitopsRepositoryFileSearchPayload;
  path?: never;
  query?: {
    /**
     * list the results without using cache
     */
    force?: boolean;
  };
  url: '/gitops/repo/files/search';
};

export type GitOperationRepoFilesSearchErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GitOperationRepoFilesSearchResponses = {
  /**
   * Success
   */
  200: Array<string>;
};

export type GitOperationRepoFilesSearchResponse =
  GitOperationRepoFilesSearchResponses[keyof GitOperationRepoFilesSearchResponses];

export type GitOperationRepoHelmValuesData = {
  /**
   * Helm values preview request
   */
  body: GitopsHelmValuesPreviewPayload;
  path?: never;
  query?: never;
  url: '/gitops/repo/helm/values';
};

export type GitOperationRepoHelmValuesErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Source not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GitOperationRepoHelmValuesResponses = {
  /**
   * Success
   */
  200: GitopsHelmValuesPreviewResponse;
};

export type GitOperationRepoHelmValuesResponse =
  GitOperationRepoHelmValuesResponses[keyof GitOperationRepoHelmValuesResponses];

export type GitOperationRepoRefsData = {
  /**
   * details
   */
  body: GitopsRepositoryReferenceListPayload;
  path?: never;
  query?: {
    /**
     * list the results without using cache
     */
    force?: boolean;
  };
  url: '/gitops/repo/refs';
};

export type GitOperationRepoRefsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GitOperationRepoRefsResponses = {
  /**
   * Success
   */
  200: Array<string>;
};

export type GitOperationRepoRefsResponse =
  GitOperationRepoRefsResponses[keyof GitOperationRepoRefsResponses];

export type GitOpsSourcesListData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Search term (matches URL)
     */
    search?: string;
    /**
     * Sort field: name | status | type
     */
    sort?: string;
    /**
     * Sort order: asc or desc
     */
    order?: string;
    /**
     * Pagination start index
     */
    start?: number;
    /**
     * Pagination limit (0 = unlimited)
     */
    limit?: number;
    /**
     * Filter by status: healthy | syncing | error | paused | unknown
     */
    status?: string;
    /**
     * Filter by source type: git | oci | helm
     */
    type?: 'git' | 'helm' | 'oci';
    /**
     * Filter by source IDs
     */
    ids?: Array<number>;
  };
  url: '/gitops/sources';
};

export type GitOpsSourcesListErrors = {
  /**
   * Invalid status parameter
   */
  400: unknown;
  /**
   * Access denied
   */
  403: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GitOpsSourcesListResponses = {
  /**
   * OK
   */
  200: Array<SourcesSource>;
};

export type GitOpsSourcesListResponse =
  GitOpsSourcesListResponses[keyof GitOpsSourcesListResponses];

export type GitOpsSourcesDeleteData = {
  body?: never;
  path: {
    /**
     * Source identifier
     */
    id: number;
  };
  query?: never;
  url: '/gitops/sources/{id}';
};

export type GitOpsSourcesDeleteErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Access denied
   */
  403: unknown;
  /**
   * Source not found
   */
  404: unknown;
  /**
   * Source is in use by one or more workflows or custom templates
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GitOpsSourcesDeleteResponses = {
  /**
   * Source deleted
   */
  204: void;
};

export type GitOpsSourcesDeleteResponse =
  GitOpsSourcesDeleteResponses[keyof GitOpsSourcesDeleteResponses];

export type GitOpsSourceGetData = {
  body?: never;
  path: {
    /**
     * Source identifier
     */
    id: number;
  };
  query?: never;
  url: '/gitops/sources/{id}';
};

export type GitOpsSourceGetErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Access denied
   */
  403: unknown;
  /**
   * Source not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GitOpsSourceGetResponses = {
  /**
   * OK
   */
  200: SourcesSourceDetail;
};

export type GitOpsSourceGetResponse =
  GitOpsSourceGetResponses[keyof GitOpsSourceGetResponses];

export type GitOpsSourcesUpdateGitData = {
  /**
   * Git source details
   */
  body: SourcesGitSourceUpdatePayload;
  path: {
    /**
     * Source identifier
     */
    id: number;
  };
  query?: never;
  url: '/gitops/sources/{id}';
};

export type GitOpsSourcesUpdateGitErrors = {
  /**
   * Invalid request payload
   */
  400: unknown;
  /**
   * Access denied
   */
  403: unknown;
  /**
   * Source not found
   */
  404: unknown;
  /**
   * A source with this URL and credentials already exists
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GitOpsSourcesUpdateGitResponses = {
  /**
   * OK
   */
  200: PortainerSource;
};

export type GitOpsSourcesUpdateGitResponse =
  GitOpsSourcesUpdateGitResponses[keyof GitOpsSourcesUpdateGitResponses];

export type GitOpsSourcesUpdateAccessData = {
  /**
   * Source access control
   */
  body: SourcesSourceAccessUpdatePayload;
  path: {
    /**
     * Source identifier
     */
    id: number;
  };
  query?: never;
  url: '/gitops/sources/{id}/access';
};

export type GitOpsSourcesUpdateAccessErrors = {
  /**
   * Invalid request payload
   */
  400: unknown;
  /**
   * Access denied
   */
  403: unknown;
  /**
   * Source not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GitOpsSourcesUpdateAccessResponses = {
  /**
   * OK
   */
  200: PortainerSource;
};

export type GitOpsSourcesUpdateAccessResponse =
  GitOpsSourcesUpdateAccessResponses[keyof GitOpsSourcesUpdateAccessResponses];

export type GitOpsSourcesTestByIdData = {
  /**
   * Optional connection overrides; omitted fields fall back to stored values
   */
  body?: SourcesGitSourceUpdatePayload;
  path: {
    /**
     * Source identifier
     */
    id: number;
  };
  query?: never;
  url: '/gitops/sources/{id}/test';
};

export type GitOpsSourcesTestByIdErrors = {
  /**
   * Invalid request payload
   */
  400: unknown;
  /**
   * Access denied
   */
  403: unknown;
  /**
   * Source not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GitOpsSourcesTestByIdResponses = {
  /**
   * Connection test result
   */
  200: SourcesConnectionTestResult;
};

export type GitOpsSourcesTestByIdResponse =
  GitOpsSourcesTestByIdResponses[keyof GitOpsSourcesTestByIdResponses];

export type GitOpsSourceWorkflowsListData = {
  body?: never;
  path: {
    /**
     * Source identifier
     */
    id: number;
  };
  query?: never;
  url: '/gitops/sources/{id}/workflows';
};

export type GitOpsSourceWorkflowsListErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Access denied
   */
  403: unknown;
  /**
   * Source not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GitOpsSourceWorkflowsListResponses = {
  /**
   * OK
   */
  200: Array<SourcesWorkflow>;
};

export type GitOpsSourceWorkflowsListResponse =
  GitOpsSourceWorkflowsListResponses[keyof GitOpsSourceWorkflowsListResponses];

export type GitOpsSourcesCreateGitData = {
  /**
   * Git source details
   */
  body: SourcesGitSourceCreatePayload;
  path?: never;
  query?: never;
  url: '/gitops/sources/git';
};

export type GitOpsSourcesCreateGitErrors = {
  /**
   * Invalid request payload
   */
  400: unknown;
  /**
   * Access denied
   */
  403: unknown;
  /**
   * A source with this URL and credentials already exists
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GitOpsSourcesCreateGitResponses = {
  /**
   * Created
   */
  201: PortainerSource;
};

export type GitOpsSourcesCreateGitResponse =
  GitOpsSourcesCreateGitResponses[keyof GitOpsSourcesCreateGitResponses];

export type GitOpsSourcesSummaryData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/gitops/sources/summary';
};

export type GitOpsSourcesSummaryErrors = {
  /**
   * Access denied
   */
  403: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GitOpsSourcesSummaryResponses = {
  /**
   * OK
   */
  200: WorkflowsStatusSummary;
};

export type GitOpsSourcesSummaryResponse =
  GitOpsSourcesSummaryResponses[keyof GitOpsSourcesSummaryResponses];

export type GitOpsSourcesTestData = {
  /**
   * Git connection details
   */
  body: SourcesGitSourceCreatePayload;
  path?: never;
  query?: never;
  url: '/gitops/sources/test';
};

export type GitOpsSourcesTestErrors = {
  /**
   * Invalid request payload
   */
  400: unknown;
  /**
   * Access denied
   */
  403: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GitOpsSourcesTestResponses = {
  /**
   * Connection test result
   */
  200: SourcesConnectionTestResult;
};

export type GitOpsSourcesTestResponse =
  GitOpsSourcesTestResponses[keyof GitOpsSourcesTestResponses];

export type GitOpsWorkflowsListData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Search term (matches workflow name)
     */
    search?: string;
    /**
     * Sort field
     */
    sort?: 'name' | 'status' | 'creationDate' | 'lastSyncDate';
    /**
     * Sort order
     */
    order?: 'asc' | 'desc';
    /**
     * Pagination start index
     */
    start?: number;
    /**
     * Pagination limit (0 = unlimited)
     */
    limit?: number;
    /**
     * Filter by environment IDs (e.g. endpointIds[]=1&endpointIds[]=2)
     */
    endpointIds?: Array<number>;
    /**
     * Filter by status
     */
    status?: 'healthy' | 'syncing' | 'error' | 'paused' | 'unknown';
    /**
     * Keep workflows that have at least one artifact of this type
     */
    type?: 'stack' | 'edgeStack';
    /**
     * Keep workflows that have at least one artifact on this platform
     */
    platform?: 'dockerStandalone' | 'dockerSwarm' | 'kubernetes';
  };
  url: '/gitops/workflows';
};

export type GitOpsWorkflowsListErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GitOpsWorkflowsListResponses = {
  /**
   * OK
   */
  200: Array<WorkflowsWorkflow>;
};

export type GitOpsWorkflowsListResponse =
  GitOpsWorkflowsListResponses[keyof GitOpsWorkflowsListResponses];

export type WorkflowCreateData = {
  /**
   * Workflow details
   */
  body: WorkflowsCreatePayload;
  path?: never;
  query?: never;
  url: '/gitops/workflows';
};

export type WorkflowCreateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type WorkflowCreateResponses = {
  /**
   * Success
   */
  200: PortainerWorkflow;
};

export type WorkflowCreateResponse =
  WorkflowCreateResponses[keyof WorkflowCreateResponses];

export type GitOpsWorkflowGetData = {
  body?: never;
  path: {
    /**
     * Workflow identifier
     */
    id: number;
  };
  query?: never;
  url: '/gitops/workflows/{id}';
};

export type GitOpsWorkflowGetErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Workflow not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GitOpsWorkflowGetResponses = {
  /**
   * OK
   */
  200: WorkflowsWorkflow;
};

export type GitOpsWorkflowGetResponse =
  GitOpsWorkflowGetResponses[keyof GitOpsWorkflowGetResponses];

export type WorkflowUpdateData = {
  /**
   * Workflow update
   */
  body: WorkflowsUpdatePayload;
  path: {
    /**
     * Workflow identifier
     */
    id: number;
  };
  query?: never;
  url: '/gitops/workflows/{id}';
};

export type WorkflowUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Workflow not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type WorkflowUpdateResponses = {
  /**
   * Success
   */
  200: WorkflowsWorkflow;
};

export type WorkflowUpdateResponse =
  WorkflowUpdateResponses[keyof WorkflowUpdateResponses];

export type GitOpsWorkflowDestroyData = {
  body?: never;
  path: {
    /**
     * Workflow identifier
     */
    id: number;
  };
  query?: never;
  url: '/gitops/workflows/{id}/destroy';
};

export type GitOpsWorkflowDestroyErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Workflow not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GitOpsWorkflowDestroyResponses = {
  /**
   * Success
   */
  204: void;
};

export type GitOpsWorkflowDestroyResponse =
  GitOpsWorkflowDestroyResponses[keyof GitOpsWorkflowDestroyResponses];

export type GitOpsWorkflowDetachData = {
  body?: never;
  path: {
    /**
     * Workflow identifier
     */
    id: number;
  };
  query?: never;
  url: '/gitops/workflows/{id}/detach';
};

export type GitOpsWorkflowDetachErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Workflow not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GitOpsWorkflowDetachResponses = {
  /**
   * Success
   */
  204: void;
};

export type GitOpsWorkflowDetachResponse =
  GitOpsWorkflowDetachResponses[keyof GitOpsWorkflowDetachResponses];

export type GitOpsWorkflowsSummaryData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/gitops/workflows/summary';
};

export type GitOpsWorkflowsSummaryErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type GitOpsWorkflowsSummaryResponses = {
  /**
   * OK
   */
  200: WorkflowsStatusSummary;
};

export type GitOpsWorkflowsSummaryResponse =
  GitOpsWorkflowsSummaryResponses[keyof GitOpsWorkflowsSummaryResponses];

export type GetKubernetesPodSecurityRuleData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    environmentId: number;
  };
  query?: never;
  url: '/kubernetes/{environmentId}/opa';
};

export type GetKubernetesPodSecurityRuleErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Endpoint not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GetKubernetesPodSecurityRuleResponses = {
  /**
   * Success
   */
  200: PodsecurityPodSecurityRule;
};

export type GetKubernetesPodSecurityRuleResponse =
  GetKubernetesPodSecurityRuleResponses[keyof GetKubernetesPodSecurityRuleResponses];

export type UpdateK8sPodSecurityRuleData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    environmentId: number;
  };
  query?: never;
  url: '/kubernetes/{environmentId}/opa';
};

export type UpdateK8sPodSecurityRuleErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Pod Security Rule not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type UpdateK8sPodSecurityRuleResponses = {
  /**
   * Success
   */
  200: unknown;
};

export type GetAllKubernetesApplicationsData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query: {
    /**
     * Namespace name
     */
    namespace: string;
    /**
     * Node name
     */
    nodeName: string;
  };
  url: '/kubernetes/{id}/applications';
};

export type GetAllKubernetesApplicationsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Environment(Endpoint) not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GetAllKubernetesApplicationsResponses = {
  /**
   * Success
   */
  200: Array<ModelsK8sApplication>;
};

export type GetAllKubernetesApplicationsResponse =
  GetAllKubernetesApplicationsResponses[keyof GetAllKubernetesApplicationsResponses];

export type GetAllKubernetesApplicationsCountData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/applications/count';
};

export type GetAllKubernetesApplicationsCountErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve the count of all applications from the cluster.
   */
  500: unknown;
};

export type GetAllKubernetesApplicationsCountResponses = {
  /**
   * Success
   */
  200: number;
};

export type GetAllKubernetesApplicationsCountResponse =
  GetAllKubernetesApplicationsCountResponses[keyof GetAllKubernetesApplicationsCountResponses];

export type DeleteClusterRoleBindingsData = {
  /**
   * A list of cluster role bindings to delete
   */
  body: Array<string>;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/cluster_role_bindings/delete';
};

export type DeleteClusterRoleBindingsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or unable to find a specific cluster role binding.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to delete cluster role bindings.
   */
  500: unknown;
};

export type DeleteClusterRoleBindingsResponses = {
  /**
   * Success
   */
  204: void;
};

export type DeleteClusterRoleBindingsResponse =
  DeleteClusterRoleBindingsResponses[keyof DeleteClusterRoleBindingsResponses];

export type DeleteClusterRolesData = {
  /**
   * A list of cluster roles to delete
   */
  body: Array<string>;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/cluster_roles/delete';
};

export type DeleteClusterRolesErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or unable to find a specific cluster role.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to delete cluster roles.
   */
  500: unknown;
};

export type DeleteClusterRolesResponses = {
  /**
   * Success
   */
  204: void;
};

export type DeleteClusterRolesResponse =
  DeleteClusterRolesResponses[keyof DeleteClusterRolesResponses];

export type GetAllKubernetesClusterRoleBindingsData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/clusterrolebindings';
};

export type GetAllKubernetesClusterRoleBindingsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the list of cluster role bindings.
   */
  500: unknown;
};

export type GetAllKubernetesClusterRoleBindingsResponses = {
  /**
   * Success
   */
  200: Array<KubernetesK8sClusterRoleBinding>;
};

export type GetAllKubernetesClusterRoleBindingsResponse =
  GetAllKubernetesClusterRoleBindingsResponses[keyof GetAllKubernetesClusterRoleBindingsResponses];

export type GetAllKubernetesClusterRolesData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/clusterroles';
};

export type GetAllKubernetesClusterRolesErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the list of cluster roles.
   */
  500: unknown;
};

export type GetAllKubernetesClusterRolesResponses = {
  /**
   * Success
   */
  200: Array<KubernetesK8sClusterRole>;
};

export type GetAllKubernetesClusterRolesResponse =
  GetAllKubernetesClusterRolesResponses[keyof GetAllKubernetesClusterRolesResponses];

export type GetAllKubernetesConfigMapsData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query: {
    /**
     * Set to true to include information about applications that use the ConfigMaps in the response
     */
    isUsed: boolean;
  };
  url: '/kubernetes/{id}/configmaps';
};

export type GetAllKubernetesConfigMapsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve all configmaps from the cluster.
   */
  500: unknown;
};

export type GetAllKubernetesConfigMapsResponses = {
  /**
   * Success
   */
  200: Array<ModelsK8sConfigMap>;
};

export type GetAllKubernetesConfigMapsResponse =
  GetAllKubernetesConfigMapsResponses[keyof GetAllKubernetesConfigMapsResponses];

export type GetAllKubernetesConfigMapsCountData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/configmaps/count';
};

export type GetAllKubernetesConfigMapsCountErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the count of all configmaps from the cluster.
   */
  500: unknown;
};

export type GetAllKubernetesConfigMapsCountResponses = {
  /**
   * Success
   */
  200: number;
};

export type GetAllKubernetesConfigMapsCountResponse =
  GetAllKubernetesConfigMapsCountResponses[keyof GetAllKubernetesConfigMapsCountResponses];

export type GetKubernetesCronJobsData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/cron_jobs';
};

export type GetKubernetesCronJobsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the list of Cron Jobs.
   */
  500: unknown;
};

export type GetKubernetesCronJobsResponses = {
  /**
   * Success
   */
  200: Array<KubernetesK8sCronJob>;
};

export type GetKubernetesCronJobsResponse =
  GetKubernetesCronJobsResponses[keyof GetKubernetesCronJobsResponses];

export type DeleteCronJobsData = {
  /**
   * A map where the key is the namespace and the value is an array of Cron Jobs to delete
   */
  body: KubernetesK8sCronJobDeleteRequests;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/cron_jobs/delete';
};

export type DeleteCronJobsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or unable to find a specific service account.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to delete Cron Jobs.
   */
  500: unknown;
};

export type DeleteCronJobsResponses = {
  /**
   * Success
   */
  204: void;
};

export type DeleteCronJobsResponse =
  DeleteCronJobsResponses[keyof DeleteCronJobsResponses];

export type GetKubernetesCustomResourceDefinitionsData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/customresourcedefinitions';
};

export type GetKubernetesCustomResourceDefinitionsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the custom resource definitions.
   */
  500: unknown;
};

export type GetKubernetesCustomResourceDefinitionsResponses = {
  /**
   * Success
   */
  200: Array<ModelsK8sCustomResourceDefinition>;
};

export type GetKubernetesCustomResourceDefinitionsResponse =
  GetKubernetesCustomResourceDefinitionsResponses[keyof GetKubernetesCustomResourceDefinitionsResponses];

export type DeleteKubernetesCustomResourceDefinitionData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * The name of the Custom Resource Definition to delete.
     */
    name: string;
  };
  query?: never;
  url: '/kubernetes/{id}/customresourcedefinitions/{name}';
};

export type DeleteKubernetesCustomResourceDefinitionErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to delete the custom resource definition.
   */
  500: unknown;
};

export type DeleteKubernetesCustomResourceDefinitionResponses = {
  /**
   * No content.
   */
  204: void;
};

export type DeleteKubernetesCustomResourceDefinitionResponse =
  DeleteKubernetesCustomResourceDefinitionResponses[keyof DeleteKubernetesCustomResourceDefinitionResponses];

export type GetKubernetesCustomResourceDefinitionData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * The name of the Custom Resource Definition to get.
     */
    name: string;
  };
  query?: never;
  url: '/kubernetes/{id}/customresourcedefinitions/{name}';
};

export type GetKubernetesCustomResourceDefinitionErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the custom resource definition.
   */
  500: unknown;
};

export type GetKubernetesCustomResourceDefinitionResponses = {
  /**
   * Success
   */
  200: ModelsK8sCustomResourceDefinition;
};

export type GetKubernetesCustomResourceDefinitionResponse =
  GetKubernetesCustomResourceDefinitionResponses[keyof GetKubernetesCustomResourceDefinitionResponses];

export type GetKubernetesAllCustomResourcesData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query: {
    /**
     * The CustomResourceDefinition name of the custom resource, e.g. 'clusters.postgresql.cnpg.io'
     */
    definition: string;
  };
  url: '/kubernetes/{id}/customresources';
};

export type GetKubernetesAllCustomResourcesErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the custom resources.
   */
  500: unknown;
};

export type GetKubernetesAllCustomResourcesResponses = {
  /**
   * Success
   */
  200: Array<ModelsK8sCustomResource>;
};

export type GetKubernetesAllCustomResourcesResponse =
  GetKubernetesAllCustomResourcesResponses[keyof GetKubernetesAllCustomResourcesResponses];

export type DeleteKubernetesClusterScopedCustomResourceData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * The name of the custom resource, e.g. 'pg-cluster'
     */
    name: string;
  };
  query: {
    /**
     * The CustomResourceDefinition name of the custom resource, e.g. 'clusters.postgresql.cnpg.io'
     */
    definition: string;
  };
  url: '/kubernetes/{id}/customresources/{name}';
};

export type DeleteKubernetesClusterScopedCustomResourceErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to delete the custom resource.
   */
  500: unknown;
};

export type DeleteKubernetesClusterScopedCustomResourceResponses = {
  /**
   * No Content
   */
  204: void;
};

export type DeleteKubernetesClusterScopedCustomResourceResponse =
  DeleteKubernetesClusterScopedCustomResourceResponses[keyof DeleteKubernetesClusterScopedCustomResourceResponses];

export type GetKubernetesClusterScopedCustomResourceData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * The name of the custom resource, e.g. 'pg-cluster'
     */
    name: string;
  };
  query: {
    /**
     * The CustomResourceDefinition name of the custom resource, e.g. 'clusters.postgresql.cnpg.io'
     */
    definition: string;
    /**
     * The format of the custom resource, e.g. 'yaml' or 'json'
     */
    format?: string;
  };
  url: '/kubernetes/{id}/customresources/{name}';
};

export type GetKubernetesClusterScopedCustomResourceErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the custom resource.
   */
  500: unknown;
};

export type GetKubernetesClusterScopedCustomResourceResponses = {
  /**
   * Success
   */
  200: UnstructuredUnstructured;
};

export type GetKubernetesClusterScopedCustomResourceResponse =
  GetKubernetesClusterScopedCustomResourceResponses[keyof GetKubernetesClusterScopedCustomResourceResponses];

export type DeleteKubernetesNamespacedCustomResourceData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * The namespace name to delete the custom resource from
     */
    namespace: string;
    /**
     * The name of the custom resource, e.g. 'pg-cluster'
     */
    name: string;
  };
  query: {
    /**
     * The CustomResourceDefinition name of the custom resource, e.g. 'clusters.postgresql.cnpg.io'
     */
    definition: string;
  };
  url: '/kubernetes/{id}/customresources/{namespace}/{name}';
};

export type DeleteKubernetesNamespacedCustomResourceErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to delete the custom resource.
   */
  500: unknown;
};

export type DeleteKubernetesNamespacedCustomResourceResponses = {
  /**
   * No Content
   */
  204: void;
};

export type DeleteKubernetesNamespacedCustomResourceResponse =
  DeleteKubernetesNamespacedCustomResourceResponses[keyof DeleteKubernetesNamespacedCustomResourceResponses];

export type GetKubernetesNamespacedCustomResourceData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * The namespace name to get details for
     */
    namespace: string;
    /**
     * The name of the custom resource, e.g. 'pg-cluster'
     */
    name: string;
  };
  query: {
    /**
     * The CustomResourceDefinition name of the custom resource, e.g. 'clusters.postgresql.cnpg.io'
     */
    definition: string;
    /**
     * The format of the custom resource, e.g. 'yaml' or 'json'
     */
    format?: string;
  };
  url: '/kubernetes/{id}/customresources/{namespace}/{name}';
};

export type GetKubernetesNamespacedCustomResourceErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the custom resource.
   */
  500: unknown;
};

export type GetKubernetesNamespacedCustomResourceResponses = {
  /**
   * Success
   */
  200: Array<UnstructuredUnstructured>;
};

export type GetKubernetesNamespacedCustomResourceResponse =
  GetKubernetesNamespacedCustomResourceResponses[keyof GetKubernetesNamespacedCustomResourceResponses];

export type GetKubernetesDashboardData = {
  body?: never;
  path: {
    /**
     * Environment (Endpoint) identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/dashboard';
};

export type GetKubernetesDashboardErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GetKubernetesDashboardResponses = {
  /**
   * Success
   */
  200: Array<ModelsK8sDashboard>;
};

export type GetKubernetesDashboardResponse =
  GetKubernetesDashboardResponses[keyof GetKubernetesDashboardResponses];

export type GetAllKubernetesDeploymentsData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: {
    /**
     * Kubernetes label selector to filter the deployments
     */
    labelSelector?: string;
    /**
     * Kubernetes field selector to filter the deployments
     */
    fieldSelector?: string;
  };
  url: '/kubernetes/{id}/deployments';
};

export type GetAllKubernetesDeploymentsErrors = {
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve the deployments.
   */
  500: unknown;
};

export type GetAllKubernetesDeploymentsResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesDeploymentListResponse;
};

export type GetAllKubernetesDeploymentsResponse =
  GetAllKubernetesDeploymentsResponses[keyof GetAllKubernetesDeploymentsResponses];

export type DescribeResourceData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query: {
    /**
     * Resource name
     */
    name: string;
    /**
     * Resource kind
     */
    kind: string;
    /**
     * Namespace
     */
    namespace?: string;
  };
  url: '/kubernetes/{id}/describe';
};

export type DescribeResourceErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve resource description
   */
  500: unknown;
};

export type DescribeResourceResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerKubernetesDescribeResourceResponse;
};

export type DescribeResourceResponse =
  DescribeResourceResponses[keyof DescribeResourceResponses];

export type GetKubernetesEndpointsData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/endpoints';
};

export type GetKubernetesEndpointsErrors = {
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve endpoints.
   */
  500: unknown;
};

export type GetKubernetesEndpointsResponses = {
  /**
   * Success
   */
  200: Array<KubernetesK8sEndpoint>;
};

export type GetKubernetesEndpointsResponse =
  GetKubernetesEndpointsResponses[keyof GetKubernetesEndpointsResponses];

export type GetAllKubernetesEventsData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: {
    /**
     * The resource id of the involved kubernetes object
     */
    resourceId?: string;
  };
  url: '/kubernetes/{id}/events';
};

export type GetAllKubernetesEventsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve the events.
   */
  500: unknown;
};

export type GetAllKubernetesEventsResponses = {
  /**
   * Success
   */
  200: Array<KubernetesK8sEvent>;
};

export type GetAllKubernetesEventsResponse =
  GetAllKubernetesEventsResponses[keyof GetAllKubernetesEventsResponses];

export type GetKubernetesGpuInfoData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/gpu';
};

export type GetKubernetesGpuInfoErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve GPU information.
   */
  500: unknown;
};

export type GetKubernetesGpuInfoResponses = {
  /**
   * Success
   */
  200: ModelsK8sGpuInfo;
};

export type GetKubernetesGpuInfoResponse =
  GetKubernetesGpuInfoResponses[keyof GetKubernetesGpuInfoResponses];

export type GetKubernetesIngressClassesData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/ingressclasses';
};

export type GetKubernetesIngressClassesErrors = {
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve the ingress classes.
   */
  500: unknown;
};

export type GetKubernetesIngressClassesResponses = {
  /**
   * Success
   */
  200: Array<KubernetesK8sIngressClass>;
};

export type GetKubernetesIngressClassesResponse =
  GetKubernetesIngressClassesResponses[keyof GetKubernetesIngressClassesResponses];

export type GetAllKubernetesIngressControllersData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: {
    /**
     * Only return allowed ingress controllers
     */
    allowedOnly?: boolean;
  };
  url: '/kubernetes/{id}/ingresscontrollers';
};

export type GetAllKubernetesIngressControllersErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve ingress controllers
   */
  500: unknown;
};

export type GetAllKubernetesIngressControllersResponses = {
  /**
   * Success
   */
  200: Array<ModelsK8sIngressController>;
};

export type GetAllKubernetesIngressControllersResponse =
  GetAllKubernetesIngressControllersResponses[keyof GetAllKubernetesIngressControllersResponses];

export type UpdateKubernetesIngressControllersData = {
  /**
   * Ingress controllers
   */
  body: ModelsK8sIngressControllerArray;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/ingresscontrollers';
};

export type UpdateKubernetesIngressControllersErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or unable to find the ingress controllers to update.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to update ingress controllers.
   */
  500: unknown;
};

export type UpdateKubernetesIngressControllersResponses = {
  /**
   * Success
   */
  204: void;
};

export type UpdateKubernetesIngressControllersResponse =
  UpdateKubernetesIngressControllersResponses[keyof UpdateKubernetesIngressControllersResponses];

export type GetAllKubernetesClusterIngressesData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: {
    /**
     * Lookup services associated with each ingress
     */
    withServices?: boolean;
  };
  url: '/kubernetes/{id}/ingresses';
};

export type GetAllKubernetesClusterIngressesErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve ingresses.
   */
  500: unknown;
};

export type GetAllKubernetesClusterIngressesResponses = {
  /**
   * Success
   */
  200: Array<ModelsK8sIngressInfo>;
};

export type GetAllKubernetesClusterIngressesResponse =
  GetAllKubernetesClusterIngressesResponses[keyof GetAllKubernetesClusterIngressesResponses];

export type GetAllKubernetesClusterIngressesCountData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/ingresses/count';
};

export type GetAllKubernetesClusterIngressesCountErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve ingresses count.
   */
  500: unknown;
};

export type GetAllKubernetesClusterIngressesCountResponses = {
  /**
   * Success
   */
  200: number;
};

export type GetAllKubernetesClusterIngressesCountResponse =
  GetAllKubernetesClusterIngressesCountResponses[keyof GetAllKubernetesClusterIngressesCountResponses];

export type DeleteKubernetesIngressesData = {
  /**
   * Ingress details
   */
  body: ModelsK8sIngressDeleteRequests;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/ingresses/delete';
};

export type DeleteKubernetesIngressesErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or unable to find a specific ingress.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to delete specified ingresses.
   */
  500: unknown;
};

export type DeleteKubernetesIngressesResponses = {
  /**
   * Success
   */
  204: void;
};

export type DeleteKubernetesIngressesResponse =
  DeleteKubernetesIngressesResponses[keyof DeleteKubernetesIngressesResponses];

export type GetKubernetesJobsData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: {
    /**
     * Whether to include Jobs that have a cronjob owner
     */
    includeCronJobChildren?: boolean;
  };
  url: '/kubernetes/{id}/jobs';
};

export type GetKubernetesJobsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the list of Jobs.
   */
  500: unknown;
};

export type GetKubernetesJobsResponses = {
  /**
   * Success
   */
  200: Array<KubernetesK8sJob>;
};

export type GetKubernetesJobsResponse =
  GetKubernetesJobsResponses[keyof GetKubernetesJobsResponses];

export type DeleteJobsData = {
  /**
   * A map where the key is the namespace and the value is an array of Jobs to delete
   */
  body: KubernetesK8sJobDeleteRequests;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/jobs/delete';
};

export type DeleteJobsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or unable to find a specific service account.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to delete Jobs.
   */
  500: unknown;
};

export type DeleteJobsResponses = {
  /**
   * Success
   */
  204: void;
};

export type DeleteJobsResponse = DeleteJobsResponses[keyof DeleteJobsResponses];

export type DryRunKubernetesManifestsData = {
  /**
   * The manifests to validate
   */
  body: GithubComPortainerPortainerEeApiHttpHandlerKubernetesManifestDryRunPayload;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/manifests/dry_run';
};

export type DryRunKubernetesManifestsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to validate the manifests.
   */
  500: unknown;
};

export type DryRunKubernetesManifestsResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerKubernetesManifestDryRunResponse;
};

export type DryRunKubernetesManifestsResponse =
  DryRunKubernetesManifestsResponses[keyof DryRunKubernetesManifestsResponses];

export type GetKubernetesMaxResourceLimitsData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/max_resource_limits';
};

export type GetKubernetesMaxResourceLimitsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve nodes limits.
   */
  500: unknown;
};

export type GetKubernetesMaxResourceLimitsResponses = {
  /**
   * Success
   */
  200: PortainerK8sNodesLimits;
};

export type GetKubernetesMaxResourceLimitsResponse =
  GetKubernetesMaxResourceLimitsResponses[keyof GetKubernetesMaxResourceLimitsResponses];

export type GetApplicationsResourcesData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query: {
    /**
     * Node name
     */
    node: string;
  };
  url: '/kubernetes/{id}/metrics/applications_resources';
};

export type GetApplicationsResourcesErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Environment(Endpoint) not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GetApplicationsResourcesResponses = {
  /**
   * Success
   */
  200: ModelsK8sApplicationResource;
};

export type GetApplicationsResourcesResponse =
  GetApplicationsResourcesResponses[keyof GetApplicationsResourcesResponses];

export type GetKubernetesMetricsForAllNodesData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/metrics/nodes';
};

export type GetKubernetesMetricsForAllNodesErrors = {
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Server error occurred while attempting to retrieve the list of nodes with their live metrics.
   */
  500: unknown;
};

export type GetKubernetesMetricsForAllNodesResponses = {
  /**
   * Success
   */
  200: V1Beta1NodeMetricsList;
};

export type GetKubernetesMetricsForAllNodesResponse =
  GetKubernetesMetricsForAllNodesResponses[keyof GetKubernetesMetricsForAllNodesResponses];

export type GetKubernetesMetricsForNodeData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Node identifier
     */
    name: string;
  };
  query?: never;
  url: '/kubernetes/{id}/metrics/nodes/{name}';
};

export type GetKubernetesMetricsForNodeErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Server error occurred while attempting to retrieve the live metrics for the specified node.
   */
  500: unknown;
};

export type GetKubernetesMetricsForNodeResponses = {
  /**
   * Success
   */
  200: V1Beta1NodeMetricsList;
};

export type GetKubernetesMetricsForNodeResponse =
  GetKubernetesMetricsForNodeResponses[keyof GetKubernetesMetricsForNodeResponses];

export type GetKubernetesMetricsForPodData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
    /**
     * Pod identifier
     */
    name: string;
  };
  query?: never;
  url: '/kubernetes/{id}/metrics/pods/{namespace}/{name}';
};

export type GetKubernetesMetricsForPodErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Server error occurred while attempting to retrieve the live metrics for the specified pod.
   */
  500: unknown;
};

export type GetKubernetesMetricsForPodResponses = {
  /**
   * Success
   */
  200: V1Beta1PodMetrics;
};

export type GetKubernetesMetricsForPodResponse =
  GetKubernetesMetricsForPodResponses[keyof GetKubernetesMetricsForPodResponses];

export type GetKubernetesMetricsForAllPodsData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
  };
  query?: never;
  url: '/kubernetes/{id}/metrics/pods/namespace/{namespace}';
};

export type GetKubernetesMetricsForAllPodsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but is not authorized to access pod metrics in the specified namespace. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve the list of pods with their live metrics.
   */
  500: unknown;
};

export type GetKubernetesMetricsForAllPodsResponses = {
  /**
   * Success
   */
  200: V1Beta1PodMetricsList;
};

export type GetKubernetesMetricsForAllPodsResponse =
  GetKubernetesMetricsForAllPodsResponses[keyof GetKubernetesMetricsForAllPodsResponses];

export type DeleteKubernetesNamespaceData = {
  /**
   * List of namespace names to delete
   */
  body: Array<string>;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces';
};

export type DeleteKubernetesNamespaceErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to delete the namespace.
   */
  500: unknown;
};

export type DeleteKubernetesNamespaceResponses = {
  /**
   * Success
   */
  200: string;
};

export type DeleteKubernetesNamespaceResponse =
  DeleteKubernetesNamespaceResponses[keyof DeleteKubernetesNamespaceResponses];

export type GetKubernetesNamespacesData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: {
    /**
     * When set to true, include the resource quota information as part of the Namespace information. Default is false
     */
    withResourceQuota?: boolean;
    /**
     * When set to true, include the unhealthy events information as part of the Namespace information. Default is false
     */
    withUnhealthyEvents?: boolean;
  };
  url: '/kubernetes/{id}/namespaces';
};

export type GetKubernetesNamespacesErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the list of namespaces.
   */
  500: unknown;
};

export type GetKubernetesNamespacesResponses = {
  /**
   * Success
   */
  200: Array<PortainerK8sNamespaceInfo>;
};

export type GetKubernetesNamespacesResponse =
  GetKubernetesNamespacesResponses[keyof GetKubernetesNamespacesResponses];

export type CreateKubernetesNamespaceData = {
  /**
   * Namespace configuration details
   */
  body: ModelsK8sNamespaceDetails;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces';
};

export type CreateKubernetesNamespaceErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Conflict - the namespace already exists.
   */
  409: unknown;
  /**
   * Server error occurred while attempting to create the namespace.
   */
  500: unknown;
};

export type CreateKubernetesNamespaceResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesCreateNamespaceResponse;
};

export type CreateKubernetesNamespaceResponse =
  CreateKubernetesNamespaceResponses[keyof CreateKubernetesNamespaceResponses];

export type UpdateKubernetesNamespaceDeprecatedData = {
  /**
   * Namespace details
   */
  body: ModelsK8sNamespaceDetails2;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces';
};

export type UpdateKubernetesNamespaceDeprecatedErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or unable to find a specific namespace.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to update the namespace.
   */
  500: unknown;
};

export type UpdateKubernetesNamespaceDeprecatedResponses = {
  /**
   * Success
   */
  200: PortainerK8sNamespaceInfo;
};

export type UpdateKubernetesNamespaceDeprecatedResponse =
  UpdateKubernetesNamespaceDeprecatedResponses[keyof UpdateKubernetesNamespaceDeprecatedResponses];

export type GetKubernetesNamespaceData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * The namespace name to get details for
     */
    namespace: string;
  };
  query?: {
    /**
     * When set to true, include the resource quota information as part of the Namespace information. Default is false
     */
    withResourceQuota?: boolean;
  };
  url: '/kubernetes/{id}/namespaces/{namespace}';
};

export type GetKubernetesNamespaceErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or unable to find a specific namespace.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve specified namespace information.
   */
  500: unknown;
};

export type GetKubernetesNamespaceResponses = {
  /**
   * Success
   */
  200: PortainerK8sNamespaceInfo;
};

export type GetKubernetesNamespaceResponse =
  GetKubernetesNamespaceResponses[keyof GetKubernetesNamespaceResponses];

export type UpdateKubernetesNamespaceData = {
  /**
   * Namespace details
   */
  body: ModelsK8sNamespaceDetails2;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}';
};

export type UpdateKubernetesNamespaceErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or unable to find a specific namespace.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to update the namespace.
   */
  500: unknown;
};

export type UpdateKubernetesNamespaceResponses = {
  /**
   * Success
   */
  200: PortainerK8sNamespaceInfo;
};

export type UpdateKubernetesNamespaceResponse =
  UpdateKubernetesNamespaceResponses[keyof UpdateKubernetesNamespaceResponses];

export type RestartKubernetesApplicationData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * The namespace
     */
    namespace: string;
    /**
     * deployment, statefulset or daemonset
     */
    kind: string;
    /**
     * name of the application
     */
    name: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/applications/{kind}/{name}/restart';
};

export type RestartKubernetesApplicationErrors = {
  /**
   * Invalid request, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or unable to find a specific application.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to restart application.
   */
  500: unknown;
};

export type RestartKubernetesApplicationResponses = {
  /**
   * Success
   */
  204: void;
};

export type RestartKubernetesApplicationResponse =
  RestartKubernetesApplicationResponses[keyof RestartKubernetesApplicationResponses];

export type GetAllKubernetesApplicationData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * The namespace
     */
    namespace: string;
    /**
     * Application name
     */
    name: string;
  };
  query?: {
    /**
     * The resource type to get the application for
     */
    resourceType?: string;
  };
  url: '/kubernetes/{id}/namespaces/{namespace}/applications/{name}';
};

export type GetAllKubernetesApplicationErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier, or the requested application does not exist in the namespace.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the application.
   */
  500: unknown;
};

export type GetAllKubernetesApplicationResponses = {
  /**
   * Success
   */
  200: ModelsK8sApplication;
};

export type GetAllKubernetesApplicationResponse =
  GetAllKubernetesApplicationResponses[keyof GetAllKubernetesApplicationResponses];

export type CreateKubernetesConfigMapData = {
  /**
   * ConfigMap definition
   */
  body: KubernetesK8sConfigMapWriteRequest2;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * The namespace name where the configmap is created
     */
    namespace: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/configmaps';
};

export type CreateKubernetesConfigMapErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * A configmap with the same name already exists in the namespace.
   */
  409: unknown;
  /**
   * Server error occurred while attempting to create the configmap.
   */
  500: unknown;
};

export type CreateKubernetesConfigMapResponses = {
  /**
   * Success
   */
  200: ModelsK8sConfigMap;
};

export type CreateKubernetesConfigMapResponse =
  CreateKubernetesConfigMapResponses[keyof CreateKubernetesConfigMapResponses];

export type DeleteKubernetesConfigMapData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * The namespace name where the configmap is located
     */
    namespace: string;
    /**
     * The configmap name to delete
     */
    configmap: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/configmaps/{configmap}';
};

export type DeleteKubernetesConfigMapErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find the configmap to delete.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to delete the configmap.
   */
  500: unknown;
};

export type DeleteKubernetesConfigMapResponses = {
  /**
   * Success
   */
  204: void;
};

export type DeleteKubernetesConfigMapResponse =
  DeleteKubernetesConfigMapResponses[keyof DeleteKubernetesConfigMapResponses];

export type GetKubernetesConfigMapData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * The namespace name where the configmap is located
     */
    namespace: string;
    /**
     * The configmap name to get details for
     */
    configmap: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/configmaps/{configmap}';
};

export type GetKubernetesConfigMapErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or a configmap with the specified name in the given namespace.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve a configmap by name within the specified namespace.
   */
  500: unknown;
};

export type GetKubernetesConfigMapResponses = {
  /**
   * Success
   */
  200: ModelsK8sConfigMap;
};

export type GetKubernetesConfigMapResponse =
  GetKubernetesConfigMapResponses[keyof GetKubernetesConfigMapResponses];

export type UpdateKubernetesConfigMapData = {
  /**
   * ConfigMap definition
   */
  body: KubernetesK8sConfigMapWriteRequest2;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * The namespace name where the configmap is located
     */
    namespace: string;
    /**
     * The configmap name to update
     */
    configmap: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/configmaps/{configmap}';
};

export type UpdateKubernetesConfigMapErrors = {
  /**
   * Invalid request payload, such as missing required fields, fields not meeting validation criteria, or a payload name that does not match the route.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find the configmap to update.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to update the configmap.
   */
  500: unknown;
};

export type UpdateKubernetesConfigMapResponses = {
  /**
   * Success
   */
  200: ModelsK8sConfigMap;
};

export type UpdateKubernetesConfigMapResponse =
  UpdateKubernetesConfigMapResponses[keyof UpdateKubernetesConfigMapResponses];

export type GetKubernetesDeploymentsForNamespaceData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
  };
  query?: {
    /**
     * Kubernetes label selector to filter the deployments
     */
    labelSelector?: string;
    /**
     * Kubernetes field selector to filter the deployments
     */
    fieldSelector?: string;
  };
  url: '/kubernetes/{id}/namespaces/{namespace}/deployments';
};

export type GetKubernetesDeploymentsForNamespaceErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve the deployments within the specified namespace.
   */
  500: unknown;
};

export type GetKubernetesDeploymentsForNamespaceResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesDeploymentListResponse;
};

export type GetKubernetesDeploymentsForNamespaceResponse =
  GetKubernetesDeploymentsForNamespaceResponses[keyof GetKubernetesDeploymentsForNamespaceResponses];

export type CreateKubernetesDeploymentData = {
  /**
   * Deployment definition
   */
  body: KubernetesK8sDeploymentWriteRequest2;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/deployments';
};

export type CreateKubernetesDeploymentErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * A deployment with the same name already exists in the namespace.
   */
  409: unknown;
  /**
   * Server error occurred while attempting to create the deployment.
   */
  500: unknown;
};

export type CreateKubernetesDeploymentResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesDeploymentResponse;
};

export type CreateKubernetesDeploymentResponse =
  CreateKubernetesDeploymentResponses[keyof CreateKubernetesDeploymentResponses];

export type DeleteKubernetesDeploymentData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
    /**
     * Deployment name
     */
    name: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/deployments/{name}';
};

export type DeleteKubernetesDeploymentErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find the deployment to delete.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to delete the deployment.
   */
  500: unknown;
};

export type DeleteKubernetesDeploymentResponses = {
  /**
   * Success
   */
  204: void;
};

export type DeleteKubernetesDeploymentResponse =
  DeleteKubernetesDeploymentResponses[keyof DeleteKubernetesDeploymentResponses];

export type GetKubernetesDeploymentData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
    /**
     * Deployment name
     */
    name: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/deployments/{name}';
};

export type GetKubernetesDeploymentErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve the deployment.
   */
  500: unknown;
};

export type GetKubernetesDeploymentResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesDeploymentResponse;
};

export type GetKubernetesDeploymentResponse =
  GetKubernetesDeploymentResponses[keyof GetKubernetesDeploymentResponses];

export type PatchKubernetesDeploymentData = {
  /**
   * Annotations to apply
   */
  body: KubernetesK8sDeploymentPatchRequest;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
    /**
     * Deployment name
     */
    name: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/deployments/{name}';
};

export type PatchKubernetesDeploymentErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find the deployment to annotate.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to annotate the deployment.
   */
  500: unknown;
};

export type PatchKubernetesDeploymentResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesDeploymentResponse;
};

export type PatchKubernetesDeploymentResponse =
  PatchKubernetesDeploymentResponses[keyof PatchKubernetesDeploymentResponses];

export type UpdateKubernetesDeploymentData = {
  /**
   * Deployment definition
   */
  body: KubernetesK8sDeploymentWriteRequest2;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
    /**
     * Deployment name
     */
    name: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/deployments/{name}';
};

export type UpdateKubernetesDeploymentErrors = {
  /**
   * Invalid request payload, such as missing required fields, fields not meeting validation criteria, or a payload name that does not match the route.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find the deployment to update.
   */
  404: unknown;
  /**
   * The deployment was modified concurrently.
   */
  409: unknown;
  /**
   * Server error occurred while attempting to update the deployment.
   */
  500: unknown;
};

export type UpdateKubernetesDeploymentResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesDeploymentResponse;
};

export type UpdateKubernetesDeploymentResponse =
  UpdateKubernetesDeploymentResponses[keyof UpdateKubernetesDeploymentResponses];

export type RollbackKubernetesDeploymentData = {
  /**
   * Revision to roll back to
   */
  body: KubernetesK8sDeploymentRollbackRequest;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
    /**
     * Deployment name
     */
    name: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/deployments/{name}/rollback';
};

export type RollbackKubernetesDeploymentErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find the deployment, or the requested revision is not part of its rollout history.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to roll the deployment back.
   */
  500: unknown;
};

export type RollbackKubernetesDeploymentResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesDeploymentResponse;
};

export type RollbackKubernetesDeploymentResponse =
  RollbackKubernetesDeploymentResponses[keyof RollbackKubernetesDeploymentResponses];

export type ScaleKubernetesDeploymentData = {
  /**
   * Desired replica count
   */
  body: KubernetesK8sDeploymentScaleRequest;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
    /**
     * Deployment name
     */
    name: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/deployments/{name}/scale';
};

export type ScaleKubernetesDeploymentErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find the deployment to scale.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to scale the deployment.
   */
  500: unknown;
};

export type ScaleKubernetesDeploymentResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesDeploymentResponse;
};

export type ScaleKubernetesDeploymentResponse =
  ScaleKubernetesDeploymentResponses[keyof ScaleKubernetesDeploymentResponses];

export type GetKubernetesEventsForNamespaceData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * The namespace name the events are associated to
     */
    namespace: string;
  };
  query?: {
    /**
     * The resource id of the involved kubernetes object
     */
    resourceId?: string;
  };
  url: '/kubernetes/{id}/namespaces/{namespace}/events';
};

export type GetKubernetesEventsForNamespaceErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve the events within the specified namespace.
   */
  500: unknown;
};

export type GetKubernetesEventsForNamespaceResponses = {
  /**
   * Success
   */
  200: Array<KubernetesK8sEvent>;
};

export type GetKubernetesEventsForNamespaceResponse =
  GetKubernetesEventsForNamespaceResponses[keyof GetKubernetesEventsForNamespaceResponses];

export type GetKubernetesIngressControllersByNamespaceData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/ingresscontrollers';
};

export type GetKubernetesIngressControllersByNamespaceErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or a namespace with the specified name.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve ingress controllers by a namespace
   */
  500: unknown;
};

export type GetKubernetesIngressControllersByNamespaceResponses = {
  /**
   * Success
   */
  200: Array<ModelsK8sIngressController>;
};

export type GetKubernetesIngressControllersByNamespaceResponse =
  GetKubernetesIngressControllersByNamespaceResponses[keyof GetKubernetesIngressControllersByNamespaceResponses];

export type UpdateKubernetesIngressControllersByNamespaceData = {
  /**
   * Ingress controllers
   */
  body: ModelsK8sIngressControllerArray;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Namespace name
     */
    namespace: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/ingresscontrollers';
};

export type UpdateKubernetesIngressControllersByNamespaceErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to update ingress controllers by namespace.
   */
  500: unknown;
};

export type UpdateKubernetesIngressControllersByNamespaceResponses = {
  /**
   * Success
   */
  204: void;
};

export type UpdateKubernetesIngressControllersByNamespaceResponse =
  UpdateKubernetesIngressControllersByNamespaceResponses[keyof UpdateKubernetesIngressControllersByNamespaceResponses];

export type GetAllKubernetesIngressesData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Namespace name
     */
    namespace: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/ingresses';
};

export type GetAllKubernetesIngressesErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve ingresses
   */
  500: unknown;
};

export type GetAllKubernetesIngressesResponses = {
  /**
   * Success
   */
  200: Array<ModelsK8sIngressInfo>;
};

export type GetAllKubernetesIngressesResponse =
  GetAllKubernetesIngressesResponses[keyof GetAllKubernetesIngressesResponses];

export type CreateKubernetesIngressData = {
  /**
   * Ingress details
   */
  body: ModelsK8sIngressInfo2;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Namespace name
     */
    namespace: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/ingresses';
};

export type CreateKubernetesIngressErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Conflict - an ingress with the same name already exists in the specified namespace.
   */
  409: unknown;
  /**
   * Server error occurred while attempting to create an ingress.
   */
  500: unknown;
};

export type CreateKubernetesIngressResponses = {
  /**
   * Success
   */
  204: void;
};

export type CreateKubernetesIngressResponse =
  CreateKubernetesIngressResponses[keyof CreateKubernetesIngressResponses];

export type UpdateKubernetesIngressData = {
  /**
   * Ingress details
   */
  body: ModelsK8sIngressInfo2;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Namespace name
     */
    namespace: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/ingresses';
};

export type UpdateKubernetesIngressErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or unable to find the specified ingress.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to update the specified ingress.
   */
  500: unknown;
};

export type UpdateKubernetesIngressResponses = {
  /**
   * Success
   */
  204: void;
};

export type UpdateKubernetesIngressResponse =
  UpdateKubernetesIngressResponses[keyof UpdateKubernetesIngressResponses];

export type GetKubernetesIngressData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Namespace name
     */
    namespace: string;
    /**
     * Ingress name
     */
    ingress: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/ingresses/{ingress}';
};

export type GetKubernetesIngressErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or unable to find an ingress with the specified name.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve an ingress.
   */
  500: unknown;
};

export type GetKubernetesIngressResponses = {
  /**
   * Success
   */
  200: ModelsK8sIngressInfo;
};

export type GetKubernetesIngressResponse =
  GetKubernetesIngressResponses[keyof GetKubernetesIngressResponses];

export type GetKubernetesPersistentVolumeClaimsInNamespaceData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Namespace name
     */
    namespace: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/persistent_volume_claims';
};

export type GetKubernetesPersistentVolumeClaimsInNamespaceErrors = {
  /**
   * Invalid request payload.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve persistent volume claims.
   */
  500: unknown;
};

export type GetKubernetesPersistentVolumeClaimsInNamespaceResponses = {
  /**
   * Success
   */
  200: Array<ModelsK8sPersistentVolumeClaim>;
};

export type GetKubernetesPersistentVolumeClaimsInNamespaceResponse =
  GetKubernetesPersistentVolumeClaimsInNamespaceResponses[keyof GetKubernetesPersistentVolumeClaimsInNamespaceResponses];

export type CreateKubernetesPersistentVolumeClaimData = {
  /**
   * PersistentVolumeClaim definition
   */
  body: KubernetesK8sPersistentVolumeClaimCreateRequest;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/persistent_volume_claims';
};

export type CreateKubernetesPersistentVolumeClaimErrors = {
  /**
   * Invalid request payload, such as missing required fields or a storage size that is not a valid quantity.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * A persistent volume claim with the same name already exists in the namespace.
   */
  409: unknown;
  /**
   * Server error occurred while attempting to create the persistent volume claim.
   */
  500: unknown;
};

export type CreateKubernetesPersistentVolumeClaimResponses = {
  /**
   * Success
   */
  200: ModelsK8sPersistentVolumeClaim;
};

export type CreateKubernetesPersistentVolumeClaimResponse =
  CreateKubernetesPersistentVolumeClaimResponses[keyof CreateKubernetesPersistentVolumeClaimResponses];

export type GetKubernetesPersistentVolumeClaimData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Namespace name
     */
    namespace: string;
    /**
     * PVC name
     */
    name: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/persistent_volume_claims/{name}';
};

export type GetKubernetesPersistentVolumeClaimErrors = {
  /**
   * Invalid request payload.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * PVC not found.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the persistent volume claim.
   */
  500: unknown;
};

export type GetKubernetesPersistentVolumeClaimResponses = {
  /**
   * Success
   */
  200: ModelsK8sPersistentVolumeClaim;
};

export type GetKubernetesPersistentVolumeClaimResponse =
  GetKubernetesPersistentVolumeClaimResponses[keyof GetKubernetesPersistentVolumeClaimResponses];

export type GetKubernetesPodsForNamespaceData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
  };
  query?: {
    /**
     * Kubernetes label selector to filter the pods (e.g. app=nginx)
     */
    labelSelector?: string;
    /**
     * Kubernetes field selector to filter the pods (e.g. status.phase=Running)
     */
    fieldSelector?: string;
  };
  url: '/kubernetes/{id}/namespaces/{namespace}/pods';
};

export type GetKubernetesPodsForNamespaceErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve the pods within the specified namespace.
   */
  500: unknown;
};

export type GetKubernetesPodsForNamespaceResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesPodListResponse;
};

export type GetKubernetesPodsForNamespaceResponse =
  GetKubernetesPodsForNamespaceResponses[keyof GetKubernetesPodsForNamespaceResponses];

export type DeleteKubernetesPodData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
    /**
     * Pod name
     */
    name: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/pods/{name}';
};

export type DeleteKubernetesPodErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or unable to find the specified pod.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to delete the pod.
   */
  500: unknown;
};

export type DeleteKubernetesPodResponses = {
  /**
   * Success
   */
  204: void;
};

export type DeleteKubernetesPodResponse =
  DeleteKubernetesPodResponses[keyof DeleteKubernetesPodResponses];

export type GetKubernetesPodLogsData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
    /**
     * Pod name
     */
    name: string;
  };
  query?: {
    /**
     * Container name (required when the pod has multiple containers)
     */
    container?: string;
    /**
     * Number of lines from the end of the logs to return
     */
    tailLines?: number;
    /**
     * Only return logs newer than this many seconds
     */
    sinceSeconds?: number;
    /**
     * Prefix each log line with an RFC3339 timestamp
     */
    timestamps?: boolean;
    /**
     * Return the logs of the previous terminated container instance
     */
    previous?: boolean;
    /**
     * Stream new log lines as they are produced until the client disconnects
     */
    follow?: boolean;
  };
  url: '/kubernetes/{id}/namespaces/{namespace}/pods/{name}/log';
};

export type GetKubernetesPodLogsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve the pod logs.
   */
  500: unknown;
};

export type GetKubernetesPodLogsResponses = {
  /**
   * Success
   */
  200: string;
};

export type GetKubernetesPodLogsResponse =
  GetKubernetesPodLogsResponses[keyof GetKubernetesPodLogsResponses];

export type RestartKubernetesPodData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
    /**
     * Pod name
     */
    name: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/pods/{name}/restart';
};

export type RestartKubernetesPodErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment, the specified pod, or the cluster does not expose the pod-restart subresource (Kubernetes <1.35 or feature gate disabled).
   */
  404: unknown;
  /**
   * The cluster does not support the pod-restart subresource (Kubernetes <1.35 or feature gate disabled).
   */
  405: unknown;
  /**
   * Server error occurred while attempting to restart the pod.
   */
  500: unknown;
};

export type RestartKubernetesPodResponses = {
  /**
   * Success
   */
  204: void;
};

export type RestartKubernetesPodResponse =
  RestartKubernetesPodResponses[keyof RestartKubernetesPodResponses];

export type GetKubernetesReplicaSetsData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
  };
  query?: {
    /**
     * Only return replica sets owned by this deployment
     */
    deployment?: string;
    /**
     * Kubernetes label selector to filter the replica sets
     */
    labelSelector?: string;
    /**
     * Kubernetes field selector to filter the replica sets
     */
    fieldSelector?: string;
  };
  url: '/kubernetes/{id}/namespaces/{namespace}/replicasets';
};

export type GetKubernetesReplicaSetsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find the deployment given in the deployment query parameter.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the replica sets within the specified namespace.
   */
  500: unknown;
};

export type GetKubernetesReplicaSetsResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesReplicaSetListResponse;
};

export type GetKubernetesReplicaSetsResponse =
  GetKubernetesReplicaSetsResponses[keyof GetKubernetesReplicaSetsResponses];

export type GetKubernetesResourceQuotasData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/resource_quotas';
};

export type GetKubernetesResourceQuotasErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve the resource quotas within the specified namespace.
   */
  500: unknown;
};

export type GetKubernetesResourceQuotasResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesResourceQuotaListResponse;
};

export type GetKubernetesResourceQuotasResponse =
  GetKubernetesResourceQuotasResponses[keyof GetKubernetesResourceQuotasResponses];

export type CreateKubernetesSecretData = {
  /**
   * Secret definition
   */
  body: KubernetesK8sSecretWriteRequest2;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * The namespace name where the secret is created
     */
    namespace: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/secrets';
};

export type CreateKubernetesSecretErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * A secret with the same name already exists in the namespace.
   */
  409: unknown;
  /**
   * Server error occurred while attempting to create the secret.
   */
  500: unknown;
};

export type CreateKubernetesSecretResponses = {
  /**
   * Success
   */
  200: ModelsK8sSecret;
};

export type CreateKubernetesSecretResponse =
  CreateKubernetesSecretResponses[keyof CreateKubernetesSecretResponses];

export type DeleteKubernetesSecretData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * The namespace name where the secret is located
     */
    namespace: string;
    /**
     * The secret name to delete
     */
    secret: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/secrets/{secret}';
};

export type DeleteKubernetesSecretErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find the secret to delete.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to delete the secret.
   */
  500: unknown;
};

export type DeleteKubernetesSecretResponses = {
  /**
   * Success
   */
  204: void;
};

export type DeleteKubernetesSecretResponse =
  DeleteKubernetesSecretResponses[keyof DeleteKubernetesSecretResponses];

export type GetKubernetesSecretData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * The namespace name where the secret is located
     */
    namespace: string;
    /**
     * The secret name to get details for
     */
    secret: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/secrets/{secret}';
};

export type GetKubernetesSecretErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve a secret by name belong in a namespace.
   */
  500: unknown;
};

export type GetKubernetesSecretResponses = {
  /**
   * Success
   */
  200: ModelsK8sSecret;
};

export type GetKubernetesSecretResponse =
  GetKubernetesSecretResponses[keyof GetKubernetesSecretResponses];

export type UpdateKubernetesSecretData = {
  /**
   * Secret definition
   */
  body: KubernetesK8sSecretWriteRequest2;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * The namespace name where the secret is located
     */
    namespace: string;
    /**
     * The secret name to update
     */
    secret: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/secrets/{secret}';
};

export type UpdateKubernetesSecretErrors = {
  /**
   * Invalid request payload, such as missing required fields, fields not meeting validation criteria, or a payload name that does not match the route.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find the secret to update.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to update the secret.
   */
  500: unknown;
};

export type UpdateKubernetesSecretResponses = {
  /**
   * Success
   */
  200: ModelsK8sSecret;
};

export type UpdateKubernetesSecretResponse =
  UpdateKubernetesSecretResponses[keyof UpdateKubernetesSecretResponses];

export type GetKubernetesServiceAccountData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
    /**
     * Service account name
     */
    name: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/service_accounts/{name}';
};

export type GetKubernetesServiceAccountErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Service account not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GetKubernetesServiceAccountResponses = {
  /**
   * Success
   */
  200: KubernetesK8sServiceAccount;
};

export type GetKubernetesServiceAccountResponse =
  GetKubernetesServiceAccountResponses[keyof GetKubernetesServiceAccountResponses];

export type UpdateKubernetesServiceAccountImagePullSecretsData = {
  /**
   * New imagePullSecrets list
   */
  body: KubernetesK8sServiceAccountImagePullSecretsUpdatePayload;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Namespace
     */
    namespace: string;
    /**
     * Service account name
     */
    name: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/service_accounts/{name}/image_pull_secrets';
};

export type UpdateKubernetesServiceAccountImagePullSecretsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier, namespace, or service account.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to update image pull secrets for the service account.
   */
  500: unknown;
};

export type UpdateKubernetesServiceAccountImagePullSecretsResponses = {
  /**
   * Success
   */
  204: void;
};

export type UpdateKubernetesServiceAccountImagePullSecretsResponse =
  UpdateKubernetesServiceAccountImagePullSecretsResponses[keyof UpdateKubernetesServiceAccountImagePullSecretsResponses];

export type GetKubernetesServicesByNamespaceData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Namespace name
     */
    namespace: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/services';
};

export type GetKubernetesServicesByNamespaceErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve all services for a namespace.
   */
  500: unknown;
};

export type GetKubernetesServicesByNamespaceResponses = {
  /**
   * Success
   */
  200: Array<ModelsK8sServiceInfo>;
};

export type GetKubernetesServicesByNamespaceResponse =
  GetKubernetesServicesByNamespaceResponses[keyof GetKubernetesServicesByNamespaceResponses];

export type CreateKubernetesServiceData = {
  /**
   * Service definition
   */
  body: ModelsK8sServiceInfo2;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Namespace name
     */
    namespace: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/services';
};

export type CreateKubernetesServiceErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to create a service.
   */
  500: unknown;
};

export type CreateKubernetesServiceResponses = {
  /**
   * Success
   */
  204: void;
};

export type CreateKubernetesServiceResponse =
  CreateKubernetesServiceResponses[keyof CreateKubernetesServiceResponses];

export type UpdateKubernetesServiceData = {
  /**
   * Service definition
   */
  body: ModelsK8sServiceInfo2;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Namespace name
     */
    namespace: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/services';
};

export type UpdateKubernetesServiceErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or unable to find the service to update.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to update a service.
   */
  500: unknown;
};

export type UpdateKubernetesServiceResponses = {
  /**
   * Success
   */
  204: void;
};

export type UpdateKubernetesServiceResponse =
  UpdateKubernetesServiceResponses[keyof UpdateKubernetesServiceResponses];

export type KubernetesNamespacesToggleSystemData = {
  /**
   * Update details
   */
  body: GithubComPortainerPortainerEeApiHttpHandlerKubernetesNamespacesToggleSystemPayload;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Namespace name
     */
    namespace: string;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/{namespace}/system';
};

export type KubernetesNamespacesToggleSystemErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or unable to find the namespace to update.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to update the system state of the namespace.
   */
  500: unknown;
};

export type KubernetesNamespacesToggleSystemResponses = {
  /**
   * Success
   */
  204: void;
};

export type KubernetesNamespacesToggleSystemResponse =
  KubernetesNamespacesToggleSystemResponses[keyof KubernetesNamespacesToggleSystemResponses];

export type GetKubernetesVolumesInNamespaceData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Namespace identifier
     */
    namespace: string;
  };
  query?: {
    /**
     * When set to True, include the applications that are using the volumes. It is set to false by default
     */
    withApplications?: boolean;
  };
  url: '/kubernetes/{id}/namespaces/{namespace}/volumes';
};

export type GetKubernetesVolumesInNamespaceErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve kubernetes volumes in the namespace.
   */
  500: unknown;
};

export type GetKubernetesVolumesInNamespaceResponses = {
  /**
   * Success
   */
  200: {
    [key: string]: KubernetesK8sVolumeInfo;
  };
};

export type GetKubernetesVolumesInNamespaceResponse =
  GetKubernetesVolumesInNamespaceResponses[keyof GetKubernetesVolumesInNamespaceResponses];

export type GetKubernetesNamespacesCountData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/namespaces/count';
};

export type GetKubernetesNamespacesCountErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to compute the namespace count.
   */
  500: unknown;
};

export type GetKubernetesNamespacesCountResponses = {
  /**
   * Success
   */
  200: number;
};

export type GetKubernetesNamespacesCountResponse =
  GetKubernetesNamespacesCountResponses[keyof GetKubernetesNamespacesCountResponses];

export type GetKubernetesNodesData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/nodes';
};

export type GetKubernetesNodesErrors = {
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve the list of nodes.
   */
  500: unknown;
};

export type GetKubernetesNodesResponses = {
  /**
   * Success
   */
  200: Array<GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesNodeResponse>;
};

export type GetKubernetesNodesResponse =
  GetKubernetesNodesResponses[keyof GetKubernetesNodesResponses];

export type DrainNodeData = {
  /**
   * Drain options, matching kubectl drain flags. Defaults are applied to any omitted field.
   */
  body?: GithubComPortainerPortainerEeApiHttpHandlerKubernetesDrainNodePayload;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
    /**
     * Name of the node to drain
     */
    name: string;
  };
  query?: never;
  url: '/kubernetes/{id}/nodes/{name}/drain';
};

export type DrainNodeErrors = {
  /**
   * Invalid request, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or unable to find the specified node.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to drain node.
   */
  500: unknown;
};

export type DrainNodeResponses = {
  /**
   * Success
   */
  204: void;
};

export type DrainNodeResponse = DrainNodeResponses[keyof DrainNodeResponses];

export type GetKubernetesNodesLimitsData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/nodes_limits';
};

export type GetKubernetesNodesLimitsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve nodes limits.
   */
  500: unknown;
};

export type GetKubernetesNodesLimitsResponses = {
  /**
   * Success
   */
  200: PortainerK8sNodesLimits;
};

export type GetKubernetesNodesLimitsResponse =
  GetKubernetesNodesLimitsResponses[keyof GetKubernetesNodesLimitsResponses];

export type GetAllKubernetesPersistentVolumeClaimsData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/persistent_volume_claims';
};

export type GetAllKubernetesPersistentVolumeClaimsErrors = {
  /**
   * Invalid request payload.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve persistent volume claims.
   */
  500: unknown;
};

export type GetAllKubernetesPersistentVolumeClaimsResponses = {
  /**
   * Success
   */
  200: Array<ModelsK8sPersistentVolumeClaim>;
};

export type GetAllKubernetesPersistentVolumeClaimsResponse =
  GetAllKubernetesPersistentVolumeClaimsResponses[keyof GetAllKubernetesPersistentVolumeClaimsResponses];

export type DeleteKubernetesPersistentVolumeClaimsData = {
  /**
   * List of PVCs to delete (namespace + name)
   */
  body: Array<ModelsK8sVolumeDeleteRequest>;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/persistent_volume_claims/delete';
};

export type DeleteKubernetesPersistentVolumeClaimsErrors = {
  /**
   * Invalid request payload.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to delete persistent volume claims.
   */
  500: unknown;
};

export type DeleteKubernetesPersistentVolumeClaimsResponses = {
  /**
   * Success
   */
  204: void;
};

export type DeleteKubernetesPersistentVolumeClaimsResponse =
  DeleteKubernetesPersistentVolumeClaimsResponses[keyof DeleteKubernetesPersistentVolumeClaimsResponses];

export type ResizeKubernetesPersistentVolumeClaimData = {
  /**
   * PVC resize request
   */
  body: ModelsK8sPvcResizeRequest;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/persistent_volume_claims/resize';
};

export type ResizeKubernetesPersistentVolumeClaimErrors = {
  /**
   * Invalid request payload.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to resize the persistent volume claim.
   */
  500: unknown;
};

export type ResizeKubernetesPersistentVolumeClaimResponses = {
  /**
   * Success
   */
  204: void;
};

export type ResizeKubernetesPersistentVolumeClaimResponse =
  ResizeKubernetesPersistentVolumeClaimResponses[keyof ResizeKubernetesPersistentVolumeClaimResponses];

export type GetAllKubernetesPersistentVolumesData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/persistent_volumes';
};

export type GetAllKubernetesPersistentVolumesErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve persistent volumes.
   */
  500: unknown;
};

export type GetAllKubernetesPersistentVolumesResponses = {
  /**
   * Success
   */
  200: Array<ModelsK8sPersistentVolume>;
};

export type GetAllKubernetesPersistentVolumesResponse =
  GetAllKubernetesPersistentVolumesResponses[keyof GetAllKubernetesPersistentVolumesResponses];

export type GetKubernetesPersistentVolumeData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * PersistentVolume name
     */
    name: string;
  };
  query?: never;
  url: '/kubernetes/{id}/persistent_volumes/{name}';
};

export type GetKubernetesPersistentVolumeErrors = {
  /**
   * Invalid request payload.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * PersistentVolume not found.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the persistent volume.
   */
  500: unknown;
};

export type GetKubernetesPersistentVolumeResponses = {
  /**
   * Success
   */
  200: ModelsK8sPersistentVolume;
};

export type GetKubernetesPersistentVolumeResponse =
  GetKubernetesPersistentVolumeResponses[keyof GetKubernetesPersistentVolumeResponses];

export type DeleteKubernetesPersistentVolumesData = {
  /**
   * List of PV names to delete
   */
  body: Array<string>;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/persistent_volumes/delete';
};

export type DeleteKubernetesPersistentVolumesErrors = {
  /**
   * Invalid request payload.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to delete persistent volumes.
   */
  500: unknown;
};

export type DeleteKubernetesPersistentVolumesResponses = {
  /**
   * Success
   */
  204: void;
};

export type DeleteKubernetesPersistentVolumesResponse =
  DeleteKubernetesPersistentVolumesResponses[keyof DeleteKubernetesPersistentVolumesResponses];

export type UpdateKubernetesPersistentVolumeReclaimPolicyData = {
  /**
   * Reclaim policy update request
   */
  body: ModelsK8sPvReclaimPolicyRequest;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/persistent_volumes/reclaim_policy';
};

export type UpdateKubernetesPersistentVolumeReclaimPolicyErrors = {
  /**
   * Invalid request payload.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to update reclaim policy.
   */
  500: unknown;
};

export type UpdateKubernetesPersistentVolumeReclaimPolicyResponses = {
  /**
   * Success
   */
  204: void;
};

export type UpdateKubernetesPersistentVolumeReclaimPolicyResponse =
  UpdateKubernetesPersistentVolumeReclaimPolicyResponses[keyof UpdateKubernetesPersistentVolumeReclaimPolicyResponses];

export type GetAllKubernetesPodsData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: {
    /**
     * Kubernetes label selector to filter the pods (e.g. app=nginx)
     */
    labelSelector?: string;
    /**
     * Kubernetes field selector to filter the pods (e.g. status.phase=Running)
     */
    fieldSelector?: string;
  };
  url: '/kubernetes/{id}/pods';
};

export type GetAllKubernetesPodsErrors = {
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve the pods.
   */
  500: unknown;
};

export type GetAllKubernetesPodsResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesPodListResponse;
};

export type GetAllKubernetesPodsResponse =
  GetAllKubernetesPodsResponses[keyof GetAllKubernetesPodsResponses];

export type GetKubernetesRbacStatusData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/rbac_enabled';
};

export type GetKubernetesRbacStatusErrors = {
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the RBAC status.
   */
  500: unknown;
};

export type GetKubernetesRbacStatusResponses = {
  /**
   * RBAC status
   */
  200: boolean;
};

export type GetKubernetesRbacStatusResponse =
  GetKubernetesRbacStatusResponses[keyof GetKubernetesRbacStatusResponses];

export type DeleteRoleBindingsData = {
  /**
   * A map where the key is the namespace and the value is an array of role bindings to delete
   */
  body: ModelsK8sRoleBindingDeleteRequests;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/role_bindings/delete';
};

export type DeleteRoleBindingsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or unable to find a specific role binding.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to delete role bindings.
   */
  500: unknown;
};

export type DeleteRoleBindingsResponses = {
  /**
   * Success
   */
  204: void;
};

export type DeleteRoleBindingsResponse =
  DeleteRoleBindingsResponses[keyof DeleteRoleBindingsResponses];

export type GetKubernetesRoleBindingsData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/rolebindings';
};

export type GetKubernetesRoleBindingsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the list of role bindings.
   */
  500: unknown;
};

export type GetKubernetesRoleBindingsResponses = {
  /**
   * Success
   */
  200: Array<ModelsK8sRoleBinding>;
};

export type GetKubernetesRoleBindingsResponse =
  GetKubernetesRoleBindingsResponses[keyof GetKubernetesRoleBindingsResponses];

export type GetKubernetesRolesData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/roles';
};

export type GetKubernetesRolesErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the list of roles.
   */
  500: unknown;
};

export type GetKubernetesRolesResponses = {
  /**
   * Success
   */
  200: Array<ModelsK8sRole>;
};

export type GetKubernetesRolesResponse =
  GetKubernetesRolesResponses[keyof GetKubernetesRolesResponses];

export type DeleteRolesData = {
  /**
   * A map where the key is the namespace and the value is an array of roles to delete
   */
  body: ModelsK8sRoleDeleteRequests;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/roles/delete';
};

export type DeleteRolesErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or unable to find a specific role.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to delete roles.
   */
  500: unknown;
};

export type DeleteRolesResponses = {
  /**
   * Success
   */
  204: void;
};

export type DeleteRolesResponse =
  DeleteRolesResponses[keyof DeleteRolesResponses];

export type GetKubernetesSecretsData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query: {
    /**
     * When set to true, associate the Secrets with the applications that use them
     */
    isUsed: boolean;
  };
  url: '/kubernetes/{id}/secrets';
};

export type GetKubernetesSecretsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve all secrets from the cluster.
   */
  500: unknown;
};

export type GetKubernetesSecretsResponses = {
  /**
   * Success
   */
  200: Array<ModelsK8sSecret>;
};

export type GetKubernetesSecretsResponse =
  GetKubernetesSecretsResponses[keyof GetKubernetesSecretsResponses];

export type GetKubernetesSecretsCountData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/secrets/count';
};

export type GetKubernetesSecretsCountErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the count of all secrets from the cluster.
   */
  500: unknown;
};

export type GetKubernetesSecretsCountResponses = {
  /**
   * Success
   */
  200: number;
};

export type GetKubernetesSecretsCountResponse =
  GetKubernetesSecretsCountResponses[keyof GetKubernetesSecretsCountResponses];

export type DeleteServiceAccountsData = {
  /**
   * A map where the key is the namespace and the value is an array of service accounts to delete
   */
  body: KubernetesK8sServiceAccountDeleteRequests;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/service_accounts/delete';
};

export type DeleteServiceAccountsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or unable to find a specific service account.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to delete service accounts.
   */
  500: unknown;
};

export type DeleteServiceAccountsResponses = {
  /**
   * Success
   */
  204: void;
};

export type DeleteServiceAccountsResponse =
  DeleteServiceAccountsResponses[keyof DeleteServiceAccountsResponses];

export type GetKubernetesServiceAccountsData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/serviceaccounts';
};

export type GetKubernetesServiceAccountsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the list of service accounts.
   */
  500: unknown;
};

export type GetKubernetesServiceAccountsResponses = {
  /**
   * Success
   */
  200: Array<KubernetesK8sServiceAccount>;
};

export type GetKubernetesServiceAccountsResponse =
  GetKubernetesServiceAccountsResponses[keyof GetKubernetesServiceAccountsResponses];

export type GetKubernetesServicesData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: {
    /**
     * Lookup applications associated with each service
     */
    withApplications?: boolean;
  };
  url: '/kubernetes/{id}/services';
};

export type GetKubernetesServicesErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve all services.
   */
  500: unknown;
};

export type GetKubernetesServicesResponses = {
  /**
   * Success
   */
  200: Array<ModelsK8sServiceInfo>;
};

export type GetKubernetesServicesResponse =
  GetKubernetesServicesResponses[keyof GetKubernetesServicesResponses];

export type GetAllKubernetesServicesCountData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/services/count';
};

export type GetAllKubernetesServicesCountErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the total count of all services.
   */
  500: unknown;
};

export type GetAllKubernetesServicesCountResponses = {
  /**
   * Success
   */
  200: number;
};

export type GetAllKubernetesServicesCountResponse =
  GetAllKubernetesServicesCountResponses[keyof GetAllKubernetesServicesCountResponses];

export type DeleteKubernetesServicesData = {
  /**
   * A map where the key is the namespace and the value is an array of services to delete
   */
  body: ModelsK8sServiceDeleteRequests;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/services/delete';
};

export type DeleteKubernetesServicesErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier or unable to find a specific service.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to delete services.
   */
  500: unknown;
};

export type DeleteKubernetesServicesResponses = {
  /**
   * Success
   */
  204: void;
};

export type DeleteKubernetesServicesResponse =
  DeleteKubernetesServicesResponses[keyof DeleteKubernetesServicesResponses];

export type GetAllKubernetesStorageClassesData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/storage_classes';
};

export type GetAllKubernetesStorageClassesErrors = {
  /**
   * Invalid request payload.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve storage classes.
   */
  500: unknown;
};

export type GetAllKubernetesStorageClassesResponses = {
  /**
   * Success
   */
  200: Array<ModelsK8sStorageClass>;
};

export type GetAllKubernetesStorageClassesResponse =
  GetAllKubernetesStorageClassesResponses[keyof GetAllKubernetesStorageClassesResponses];

export type GetKubernetesStorageClassData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * StorageClass name
     */
    name: string;
  };
  query?: never;
  url: '/kubernetes/{id}/storage_classes/{name}';
};

export type GetKubernetesStorageClassErrors = {
  /**
   * Invalid request payload.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * StorageClass not found.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the storage class.
   */
  500: unknown;
};

export type GetKubernetesStorageClassResponses = {
  /**
   * Success
   */
  200: ModelsK8sStorageClass;
};

export type GetKubernetesStorageClassResponse =
  GetKubernetesStorageClassResponses[keyof GetKubernetesStorageClassResponses];

export type SetDefaultKubernetesStorageClassData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * StorageClass name
     */
    name: string;
  };
  query?: never;
  url: '/kubernetes/{id}/storage_classes/{name}/default';
};

export type SetDefaultKubernetesStorageClassErrors = {
  /**
   * Invalid request payload.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to set default storage class.
   */
  500: unknown;
};

export type SetDefaultKubernetesStorageClassResponses = {
  /**
   * Success
   */
  204: void;
};

export type SetDefaultKubernetesStorageClassResponse =
  SetDefaultKubernetesStorageClassResponses[keyof SetDefaultKubernetesStorageClassResponses];

export type DeleteKubernetesStorageClassesData = {
  /**
   * List of StorageClass names to delete
   */
  body: Array<string>;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/storage_classes/delete';
};

export type DeleteKubernetesStorageClassesErrors = {
  /**
   * Invalid request payload.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to delete storage classes.
   */
  500: unknown;
};

export type DeleteKubernetesStorageClassesResponses = {
  /**
   * Success
   */
  204: void;
};

export type DeleteKubernetesStorageClassesResponse =
  DeleteKubernetesStorageClassesResponses[keyof DeleteKubernetesStorageClassesResponses];

export type GetKubernetesVersionData = {
  body?: never;
  path: {
    /**
     * Environment(Endpoint) identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/version';
};

export type GetKubernetesVersionErrors = {
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation.
   */
  403: unknown;
  /**
   * Unable to find an environment with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the cluster version.
   */
  500: unknown;
};

export type GetKubernetesVersionResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerKubernetesKubernetesVersionResponse;
};

export type GetKubernetesVersionResponse =
  GetKubernetesVersionResponses[keyof GetKubernetesVersionResponses];

export type GetAllKubernetesVolumesData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: {
    /**
     * When set to True, include the applications that are using the volumes. It is set to false by default
     */
    withApplications?: boolean;
  };
  url: '/kubernetes/{id}/volumes';
};

export type GetAllKubernetesVolumesErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve kubernetes volumes.
   */
  500: unknown;
};

export type GetAllKubernetesVolumesResponses = {
  /**
   * Success
   */
  200: {
    [key: string]: KubernetesK8sVolumeInfo;
  };
};

export type GetAllKubernetesVolumesResponse =
  GetAllKubernetesVolumesResponses[keyof GetAllKubernetesVolumesResponses];

export type GetKubernetesVolumeData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
    /**
     * Namespace identifier
     */
    namespace: string;
    /**
     * Volume name
     */
    volume: string;
  };
  query?: never;
  url: '/kubernetes/{id}/volumes/{namespace}/{volume}';
};

export type GetKubernetesVolumeErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type GetKubernetesVolumeResponses = {
  /**
   * Success
   */
  200: KubernetesK8sVolumeInfo;
};

export type GetKubernetesVolumeResponse =
  GetKubernetesVolumeResponses[keyof GetKubernetesVolumeResponses];

export type GetAllKubernetesVolumesCountData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/kubernetes/{id}/volumes/count';
};

export type GetAllKubernetesVolumesCountErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access or operation not allowed.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve kubernetes volumes count.
   */
  500: unknown;
};

export type GetAllKubernetesVolumesCountResponses = {
  /**
   * Success
   */
  200: number;
};

export type GetAllKubernetesVolumesCountResponse =
  GetAllKubernetesVolumesCountResponses[keyof GetAllKubernetesVolumesCountResponses];

export type LdapAdminGroupsData = {
  /**
   * LDAPSettings
   */
  body: LdapAdminGroupsPayload;
  path?: never;
  query?: never;
  url: '/ldap/admin-groups';
};

export type LdapAdminGroupsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type LdapAdminGroupsResponses = {
  /**
   * Success
   */
  200: Array<string>;
};

export type LdapAdminGroupsResponse =
  LdapAdminGroupsResponses[keyof LdapAdminGroupsResponses];

export type LdapCheckData = {
  /**
   * details
   */
  body: LdapCheckPayload;
  path?: never;
  query?: never;
  url: '/ldap/check';
};

export type LdapCheckErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type LdapCheckResponses = {
  /**
   * Success
   */
  204: void;
};

export type LdapCheckResponse = LdapCheckResponses[keyof LdapCheckResponses];

export type LdapGroupsData = {
  /**
   * details
   */
  body: LdapGroupsPayload;
  path?: never;
  query?: never;
  url: '/ldap/groups';
};

export type LdapGroupsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type LdapGroupsResponses = {
  /**
   * Success
   */
  200: Array<PortainerLdapUser>;
};

export type LdapGroupsResponse = LdapGroupsResponses[keyof LdapGroupsResponses];

export type LdapTestLoginData = {
  /**
   * details
   */
  body: LdapTestLoginPayload;
  path?: never;
  query?: never;
  url: '/ldap/test';
};

export type LdapTestLoginErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type LdapTestLoginResponses = {
  /**
   * Success
   */
  200: LdapTestLoginResponse;
};

export type LdapTestLoginResponse2 =
  LdapTestLoginResponses[keyof LdapTestLoginResponses];

export type LdapUsersData = {
  /**
   * details
   */
  body: LdapUsersPayload;
  path?: never;
  query?: never;
  url: '/ldap/users';
};

export type LdapUsersErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type LdapUsersResponses = {
  /**
   * Success
   */
  200: Array<PortainerLdapUser>;
};

export type LdapUsersResponse = LdapUsersResponses[keyof LdapUsersResponses];

export type LicensesListData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/licenses';
};

export type LicensesListResponses = {
  /**
   * Licenses
   */
  200: Array<LiblicensePortainerLicense>;
};

export type LicensesListResponse =
  LicensesListResponses[keyof LicensesListResponses];

export type LicensesAttachData2 = {
  /**
   * list of licenses keys to attach
   */
  body: LicensesAttachPayload;
  path?: never;
  query?: {
    /**
     * remove conflicting licenses
     */
    force?: boolean;
  };
  url: '/licenses/add';
};

export type LicensesAttachResponses = {
  /**
   * Success license data will be in `body.Licenses`, Failures will be in `body.ConflictingKeys = error`
   */
  200: LicensesAttachData;
};

export type LicensesAttachResponse =
  LicensesAttachResponses[keyof LicensesAttachResponses];

export type LicensesInfoData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/licenses/info';
};

export type LicensesInfoResponses = {
  /**
   * License info
   */
  200: LicensesLicenseInfo;
};

export type LicensesInfoResponse =
  LicensesInfoResponses[keyof LicensesInfoResponses];

export type LicensesDeleteData = {
  /**
   * list of license keys to remove
   */
  body: LicensesDeletePayload;
  path?: never;
  query?: never;
  url: '/licenses/remove';
};

export type LicensesDeleteResponses = {
  /**
   * OK
   */
  200: unknown;
};

export type MotdData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/motd';
};

export type MotdResponses = {
  /**
   * OK
   */
  200: MotdMotd;
};

export type MotdResponse = MotdResponses[keyof MotdResponses];

export type GetAlertsData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Status of alerts to retrieve. Possible values: 'active' or 'silenced'.
     */
    status: string;
  };
  url: '/observability/alerting/alerts';
};

export type GetAlertsErrors = {
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve active or silenced alerts.
   */
  500: unknown;
};

export type GetAlertsResponses = {
  /**
   * Success
   */
  200: Array<{
    [key: string]: unknown;
  }>;
};

export type GetAlertsResponse = GetAlertsResponses[keyof GetAlertsResponses];

export type TestAlertManagerConnectivityData = {
  body?: never;
  path?: never;
  query: {
    /**
     * AlertManager URL
     */
    url: string;
  };
  url: '/observability/alerting/connectivity';
};

export type TestAlertManagerConnectivityErrors = {
  /**
   * URL query parameter is missing or invalid.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to test AlertManager connectivity.
   */
  500: unknown;
};

export type TestAlertManagerConnectivityResponses = {
  /**
   * Success
   */
  200: {
    [key: string]: unknown;
  };
};

export type TestAlertManagerConnectivityResponse =
  TestAlertManagerConnectivityResponses[keyof TestAlertManagerConnectivityResponses];

export type GetAllAlertRulesData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/observability/alerting/rules';
};

export type GetAllAlertRulesErrors = {
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve all alert rules.
   */
  500: unknown;
};

export type GetAllAlertRulesResponses = {
  /**
   * Success
   */
  200: Array<{
    [key: string]: unknown;
  }>;
};

export type GetAllAlertRulesResponse =
  GetAllAlertRulesResponses[keyof GetAllAlertRulesResponses];

export type CreateAlertRuleData = {
  /**
   * Alert rule details
   */
  body: ObservabilityAlertRuleCreatePayload;
  path?: never;
  query?: never;
  url: '/observability/alerting/rules';
};

export type CreateAlertRuleErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Referenced endpoint group not found.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to create the alert rule.
   */
  500: unknown;
};

export type CreateAlertRuleResponses = {
  /**
   * Success
   */
  200: {
    [key: string]: unknown;
  };
};

export type CreateAlertRuleResponse =
  CreateAlertRuleResponses[keyof CreateAlertRuleResponses];

export type DeleteAlertRuleData = {
  body?: never;
  path: {
    /**
     * Alert rule identifier
     */
    id: number;
  };
  query?: never;
  url: '/observability/alerting/rules/{id}';
};

export type DeleteAlertRuleErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an alert rule with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to delete the alert rule.
   */
  500: unknown;
};

export type DeleteAlertRuleResponses = {
  /**
   * Success
   */
  204: void;
};

export type DeleteAlertRuleResponse =
  DeleteAlertRuleResponses[keyof DeleteAlertRuleResponses];

export type GetAlertRuleData = {
  body?: never;
  path: {
    /**
     * Alert rule identifier
     */
    id: number;
  };
  query?: never;
  url: '/observability/alerting/rules/{id}';
};

export type GetAlertRuleErrors = {
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an alert rule with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the alert rule.
   */
  500: unknown;
};

export type GetAlertRuleResponses = {
  /**
   * Success
   */
  200: {
    [key: string]: unknown;
  };
};

export type GetAlertRuleResponse =
  GetAlertRuleResponses[keyof GetAlertRuleResponses];

export type UpdateAlertRuleData = {
  /**
   * Alert rule details
   */
  body: ObservabilityAlertRuleUpdatePayload;
  path: {
    /**
     * Alert rule identifier
     */
    id: number;
  };
  query?: never;
  url: '/observability/alerting/rules/{id}';
};

export type UpdateAlertRuleErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an alert rule with the specified identifier, or a referenced endpoint group does not exist.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to update the alert rule.
   */
  500: unknown;
};

export type UpdateAlertRuleResponses = {
  /**
   * Success
   */
  200: {
    [key: string]: unknown;
  };
};

export type UpdateAlertRuleResponse =
  UpdateAlertRuleResponses[keyof UpdateAlertRuleResponses];

export type GetAlertRuleEnvironmentsData = {
  body?: never;
  path: {
    /**
     * Alert rule identifier
     */
    id: number;
  };
  query?: never;
  url: '/observability/alerting/rules/{id}/environments';
};

export type GetAlertRuleEnvironmentsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Alert rule not found.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve alert rule environments.
   */
  500: unknown;
};

export type GetAlertRuleEnvironmentsResponses = {
  /**
   * Success
   */
  200: ObservabilityAlertRuleEnvironmentsResponse;
};

export type GetAlertRuleEnvironmentsResponse =
  GetAlertRuleEnvironmentsResponses[keyof GetAlertRuleEnvironmentsResponses];

export type UpdateAlertRuleGroupsData = {
  /**
   * Alert rule group attachments
   */
  body: ObservabilityAlertRuleGroupsUpdatePayload;
  path: {
    /**
     * Alert rule identifier
     */
    id: number;
  };
  query?: never;
  url: '/observability/alerting/rules/{id}/groups';
};

export type UpdateAlertRuleGroupsErrors = {
  /**
   * Invalid request payload, such as malformed or duplicate endpoint group IDs.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an alert rule with the specified identifier, or a referenced endpoint group does not exist.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to update alert rule groups.
   */
  500: unknown;
};

export type UpdateAlertRuleGroupsResponses = {
  /**
   * Success
   */
  200: PortainereeAlertingRule;
};

export type UpdateAlertRuleGroupsResponse =
  UpdateAlertRuleGroupsResponses[keyof UpdateAlertRuleGroupsResponses];

export type UpdateAlertRuleTiersData = {
  /**
   * Alert rule tier configuration
   */
  body: ObservabilityAlertRuleTiersUpdatePayload;
  path: {
    /**
     * Alert rule identifier
     */
    id: number;
  };
  query?: never;
  url: '/observability/alerting/rules/{id}/tiers';
};

export type UpdateAlertRuleTiersErrors = {
  /**
   * Invalid request payload, such as malformed tiers, unsupported operators, or invalid tier ordering.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - only editable internal alert rules can be tier-configured.
   */
  403: unknown;
  /**
   * Unable to find an alert rule with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to update alert rule tiers.
   */
  500: unknown;
};

export type UpdateAlertRuleTiersResponses = {
  /**
   * Success
   */
  200: PortainereeAlertingRule;
};

export type UpdateAlertRuleTiersResponse =
  UpdateAlertRuleTiersResponses[keyof UpdateAlertRuleTiersResponses];

export type GetAllAlertingSettingsData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/observability/alerting/settings';
};

export type GetAllAlertingSettingsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to retrieve all alerting settings.
   */
  500: unknown;
};

export type GetAllAlertingSettingsResponses = {
  /**
   * Success
   */
  200: Array<{
    [key: string]: unknown;
  }>;
};

export type GetAllAlertingSettingsResponse =
  GetAllAlertingSettingsResponses[keyof GetAllAlertingSettingsResponses];

export type UpdateAlertingSettingsData = {
  /**
   * Alerting settings details
   */
  body: ObservabilityAlertingUpdatePayload;
  path?: never;
  query?: never;
  url: '/observability/alerting/settings';
};

export type UpdateAlertingSettingsErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to update the alerting settings.
   */
  500: unknown;
};

export type UpdateAlertingSettingsResponses = {
  /**
   * Success
   */
  200: {
    [key: string]: unknown;
  };
};

export type UpdateAlertingSettingsResponse =
  UpdateAlertingSettingsResponses[keyof UpdateAlertingSettingsResponses];

export type GetAlertingSettingsData = {
  body?: never;
  path: {
    /**
     * Alerting settings identifier
     */
    id: number;
  };
  query?: never;
  url: '/observability/alerting/settings/{id}';
};

export type GetAlertingSettingsErrors = {
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find alerting settings with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to retrieve the alerting settings.
   */
  500: unknown;
};

export type GetAlertingSettingsResponses = {
  /**
   * Success
   */
  200: {
    [key: string]: unknown;
  };
};

export type GetAlertingSettingsResponse =
  GetAlertingSettingsResponses[keyof GetAlertingSettingsResponses];

export type CreateSilenceData = {
  /**
   * Silence details
   */
  body: ObservabilityCreateSilencePayload;
  path?: never;
  query?: never;
  url: '/observability/alerting/silence';
};

export type CreateSilenceErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Server error occurred while attempting to create the alert silence.
   */
  500: unknown;
};

export type CreateSilenceResponses = {
  /**
   * Success
   */
  200: {
    [key: string]: unknown;
  };
};

export type CreateSilenceResponse =
  CreateSilenceResponses[keyof CreateSilenceResponses];

export type DeleteSilenceData = {
  body?: never;
  path: {
    /**
     * Silence identifier
     */
    id: string;
  };
  query: {
    /**
     * AlertManager URL
     */
    alertManagerURL: string;
  };
  url: '/observability/alerting/silence/{id}';
};

export type DeleteSilenceErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Unable to find an alert silence with the specified identifier.
   */
  404: unknown;
  /**
   * Server error occurred while attempting to delete the alert silence.
   */
  500: unknown;
};

export type DeleteSilenceResponses = {
  /**
   * Success
   */
  200: {
    [key: string]: unknown;
  };
};

export type DeleteSilenceResponse =
  DeleteSilenceResponses[keyof DeleteSilenceResponses];

export type EnvironmentObservabilityDiscoveryData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query?: never;
  url: '/observability/environments/{id}';
};

export type EnvironmentObservabilityDiscoveryErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Environment not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EnvironmentObservabilityDiscoveryResponses = {
  /**
   * Success
   */
  200: ObservabilityEnvironmentObservabilityResponse;
};

export type EnvironmentObservabilityDiscoveryResponse =
  EnvironmentObservabilityDiscoveryResponses[keyof EnvironmentObservabilityDiscoveryResponses];

export type ObservabilityEnvironmentLogsData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query: {
    /**
     * Namespace to scope logs to; omit for cluster-wide logs
     */
    namespace?: string;
    /**
     * Workload kind (Deployment, StatefulSet, DaemonSet, Pod)
     */
    kind?: string;
    /**
     * Workload or pod name
     */
    name?: string;
    /**
     * Free-text search over the log message
     */
    search?: string;
    /**
     * Severity filter (repeatable); trace, debug, info, warn, error, or fatal. Multiple values must form a contiguous range
     */
    severity?: Array<string>;
    /**
     * Range start (Unix seconds or RFC3339)
     */
    from: string;
    /**
     * Range end (Unix seconds or RFC3339). The range from-to must not exceed 31 days
     */
    to: string;
    /**
     * Number of entries to skip (paging)
     */
    skip?: number;
    /**
     * Maximum number of entries to return
     */
    limit?: number;
  };
  url: '/observability/environments/{id}/logs';
};

export type ObservabilityEnvironmentLogsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Environment not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
  /**
   * Observability not enabled for this environment
   */
  503: unknown;
};

export type ObservabilityEnvironmentLogsResponses = {
  /**
   * Success
   */
  200: ObservabilityLogsResponse;
};

export type ObservabilityEnvironmentLogsResponse =
  ObservabilityEnvironmentLogsResponses[keyof ObservabilityEnvironmentLogsResponses];

export type ObservabilityEnvironmentMetricsData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    id: number;
  };
  query: {
    /**
     * Namespace. Omit to aggregate across every namespace; mutually exclusive with groupBy
     */
    namespace?: string;
    /**
     * Group by: namespace (returns one series per namespace, mutually exclusive with namespace)
     */
    groupBy?: string;
    /**
     * Workload kind (Deployment, StatefulSet, DaemonSet, Pod)
     */
    kind?: string;
    /**
     * Workload or pod name
     */
    name?: string;
    /**
     * Metric name: cpu | memory | filesystem | network_rx | network_tx | heartbeat
     */
    metric: string;
    /**
     * Aggregation type: Avg | Max | Min | Sum | Count | P50 | P90 | P95 | P99
     */
    aggregation: string;
    /**
     * Range start (Unix seconds or RFC3339)
     */
    from: string;
    /**
     * Range end (Unix seconds or RFC3339). The range from-to must not exceed 31 days
     */
    to: string;
  };
  url: '/observability/environments/{id}/metrics';
};

export type ObservabilityEnvironmentMetricsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Environment not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
  /**
   * Observability not enabled for this environment
   */
  503: unknown;
};

export type ObservabilityEnvironmentMetricsResponses = {
  /**
   * OK
   */
  200: OneuptimeMetricsAggregateResponse;
};

export type ObservabilityEnvironmentMetricsResponse =
  ObservabilityEnvironmentMetricsResponses[keyof ObservabilityEnvironmentMetricsResponses];

export type OmniGetClusterData = {
  body?: never;
  path: {
    /**
     * Cloud credential identifier
     */
    credentialID: number;
    /**
     * Cluster name
     */
    name: string;
  };
  query?: never;
  url: '/omni/{credentialID}/cluster/{name}';
};

export type OmniGetClusterErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type OmniGetClusterResponses = {
  /**
   * Cluster details
   */
  200: OmniClusterData;
};

export type OmniGetClusterResponse =
  OmniGetClusterResponses[keyof OmniGetClusterResponses];

export type OmniGetK8sVersionUpgradeStatusData = {
  body?: never;
  path: {
    /**
     * Cloud credential identifier
     */
    credentialID: number;
    /**
     * Cluster name
     */
    name: string;
  };
  query?: never;
  url: '/omni/{credentialID}/cluster/{name}/upgrade/status/k8s';
};

export type OmniGetK8sVersionUpgradeStatusErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type OmniGetK8sVersionUpgradeStatusResponses = {
  /**
   * Kubernetes upgrade status
   */
  200: OmniUpgradeStatusResponse;
};

export type OmniGetK8sVersionUpgradeStatusResponse =
  OmniGetK8sVersionUpgradeStatusResponses[keyof OmniGetK8sVersionUpgradeStatusResponses];

export type OmniGetTalosVersionUpgradeStatusData = {
  body?: never;
  path: {
    /**
     * Cloud credential identifier
     */
    credentialID: number;
    /**
     * Cluster name
     */
    name: string;
  };
  query?: never;
  url: '/omni/{credentialID}/cluster/{name}/upgrade/status/talos';
};

export type OmniGetTalosVersionUpgradeStatusErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type OmniGetTalosVersionUpgradeStatusResponses = {
  /**
   * Talos upgrade status
   */
  200: OmniUpgradeStatusResponse;
};

export type OmniGetTalosVersionUpgradeStatusResponse =
  OmniGetTalosVersionUpgradeStatusResponses[keyof OmniGetTalosVersionUpgradeStatusResponses];

export type OmniCreateClusterData = {
  /**
   * Cluster creation request
   */
  body: OmniCreateClusterPayload;
  path: {
    /**
     * Cloud credential identifier
     */
    credentialID: number;
  };
  query?: never;
  url: '/omni/{credentialID}/cluster/create';
};

export type OmniCreateClusterErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type OmniCreateClusterResponses = {
  /**
   * Created cluster environment
   */
  200: PortainereeEndpoint;
};

export type OmniCreateClusterResponse =
  OmniCreateClusterResponses[keyof OmniCreateClusterResponses];

export type OmniDeleteClusterData = {
  body?: never;
  path: {
    /**
     * Cloud credential identifier
     */
    credentialID: number;
  };
  query: {
    /**
     * Cluster name
     */
    name: string;
  };
  url: '/omni/{credentialID}/cluster/delete';
};

export type OmniDeleteClusterErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type OmniDeleteClusterResponses = {
  /**
   * Success
   */
  204: void;
};

export type OmniDeleteClusterResponse =
  OmniDeleteClusterResponses[keyof OmniDeleteClusterResponses];

export type OmniCancelProvisioningData = {
  /**
   * Provisioning cancellation request
   */
  body: OmniCancelProvisioningPayload;
  path: {
    /**
     * Cloud credential identifier
     */
    credentialID: number;
  };
  query?: never;
  url: '/omni/{credentialID}/cluster/provision/cancel';
};

export type OmniCancelProvisioningErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Environment not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type OmniCancelProvisioningResponses = {
  /**
   * Success
   */
  204: void;
};

export type OmniCancelProvisioningResponse =
  OmniCancelProvisioningResponses[keyof OmniCancelProvisioningResponses];

export type OmniUpdateClusterData = {
  /**
   * Cluster update request
   */
  body: OmniUpdateClusterPayload;
  path: {
    /**
     * Cloud credential identifier
     */
    credentialID: number;
  };
  query?: never;
  url: '/omni/{credentialID}/cluster/update';
};

export type OmniUpdateClusterErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type OmniUpdateClusterResponses = {
  /**
   * Success
   */
  204: void;
};

export type OmniUpdateClusterResponse =
  OmniUpdateClusterResponses[keyof OmniUpdateClusterResponses];

export type OmniValidateClusterData = {
  /**
   * Cluster validation request
   */
  body: OmniValidateClusterPayload;
  path: {
    /**
     * Cloud credential identifier
     */
    credentialID: number;
  };
  query?: never;
  url: '/omni/{credentialID}/cluster/validate';
};

export type OmniValidateClusterErrors = {
  /**
   * Invalid request or cluster configuration
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type OmniValidateClusterResponses = {
  /**
   * Cluster configuration is valid
   */
  204: void;
};

export type OmniValidateClusterResponse =
  OmniValidateClusterResponses[keyof OmniValidateClusterResponses];

export type OmniGetSupportedTalosVersionsData = {
  body?: never;
  path: {
    /**
     * Cloud credential identifier
     */
    credentialID: number;
  };
  query?: never;
  url: '/omni/{credentialID}/cluster/version/talos';
};

export type OmniGetSupportedTalosVersionsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type OmniGetSupportedTalosVersionsResponses = {
  /**
   * Map of Talos versions to compatible Kubernetes versions
   */
  200: PortainereeTalosVersions;
};

export type OmniGetSupportedTalosVersionsResponse =
  OmniGetSupportedTalosVersionsResponses[keyof OmniGetSupportedTalosVersionsResponses];

export type OmniGetMachineData = {
  body?: never;
  path: {
    /**
     * Cloud credential identifier
     */
    credentialID: number;
    /**
     * Machine name
     */
    name: string;
  };
  query?: never;
  url: '/omni/{credentialID}/machine/{name}';
};

export type OmniGetMachineErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type OmniGetMachineResponses = {
  /**
   * Machine details
   */
  200: OmniMachineData;
};

export type OmniGetMachineResponse =
  OmniGetMachineResponses[keyof OmniGetMachineResponses];

export type OmniGetMachineLogsData = {
  body?: never;
  path: {
    /**
     * Cloud credential identifier
     */
    credentialID: number;
    /**
     * Machine name
     */
    name: string;
  };
  query?: never;
  url: '/omni/{credentialID}/machine/{name}/logs';
};

export type OmniGetMachineLogsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type OmniGetMachineLogsResponses = {
  /**
   * Machine logs
   */
  200: string;
};

export type OmniGetMachineLogsResponse =
  OmniGetMachineLogsResponses[keyof OmniGetMachineLogsResponses];

export type OmniListMachinesData = {
  body?: never;
  path: {
    /**
     * Cloud credential identifier
     */
    credentialID: number;
  };
  query?: {
    /**
     * Filter machines by cluster name
     */
    clusterName?: string;
    /**
     * Filter to show only available machines
     */
    isAvailable?: boolean;
  };
  url: '/omni/{credentialID}/machines';
};

export type OmniListMachinesErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type OmniListMachinesResponses = {
  /**
   * List of machines
   */
  200: Array<OmniMachineData>;
};

export type OmniListMachinesResponse =
  OmniListMachinesResponses[keyof OmniListMachinesResponses];

export type OmniRebootNodeData = {
  body?: never;
  path: {
    /**
     * Cloud credential identifier
     */
    credentialID: number;
    /**
     * Node name
     */
    name: string;
  };
  query: {
    /**
     * Cluster name
     */
    cluster: string;
  };
  url: '/omni/{credentialID}/node/{name}';
};

export type OmniRebootNodeErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type OmniRebootNodeResponses = {
  /**
   * Success
   */
  204: void;
};

export type OmniRebootNodeResponse =
  OmniRebootNodeResponses[keyof OmniRebootNodeResponses];

export type OmniValidateServiceAccountData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Omni endpoint URL (OMNI_ENDPOINT)
     */
    endpoint?: string;
    /**
     * Omni service account key (OMNI_SERVICE_ACCOUNT_KEY)
     */
    serviceAccountKey?: string;
  };
  url: '/omni/serviceaccount/validate';
};

export type OmniValidateServiceAccountErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type OmniValidateServiceAccountResponses = {
  /**
   * Success
   */
  200: unknown;
};

export type PolicyListData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/policies';
};

export type PolicyListErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type PolicyListResponses = {
  /**
   * Success
   */
  200: PoliciesPolicyListResponse;
};

export type PolicyListResponse = PolicyListResponses[keyof PolicyListResponses];

export type PolicyCreateData = {
  /**
   * Policy details
   */
  body: PoliciesPolicyCreatePayload;
  path?: never;
  query?: never;
  url: '/policies';
};

export type PolicyCreateErrors = {
  /**
   * Invalid request payload
   */
  400: unknown;
  /**
   * This name is already associated to a policy
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type PolicyCreateResponses = {
  /**
   * Success
   */
  200: PoliciesPolicy;
};

export type PolicyCreateResponse =
  PolicyCreateResponses[keyof PolicyCreateResponses];

export type PolicyDeleteData = {
  body?: never;
  path: {
    /**
     * Policy identifier
     */
    id: number;
  };
  query?: never;
  url: '/policies/{id}';
};

export type PolicyDeleteErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Policy not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type PolicyDeleteResponses = {
  /**
   * Success
   */
  204: void;
};

export type PolicyDeleteResponse =
  PolicyDeleteResponses[keyof PolicyDeleteResponses];

export type PolicyInspectData = {
  body?: never;
  path: {
    /**
     * Policy identifier
     */
    id: number;
  };
  query?: never;
  url: '/policies/{id}';
};

export type PolicyInspectErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Policy not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type PolicyInspectResponses = {
  /**
   * Success
   */
  200: PoliciesPolicy;
};

export type PolicyInspectResponse =
  PolicyInspectResponses[keyof PolicyInspectResponses];

export type PolicyUpdateData = {
  /**
   * Policy details
   */
  body: PoliciesPolicyUpdatePayload;
  path: {
    /**
     * Policy identifier
     */
    id: number;
  };
  query?: never;
  url: '/policies/{id}';
};

export type PolicyUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Policy not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type PolicyUpdateResponses = {
  /**
   * Success
   */
  200: PoliciesPolicy;
};

export type PolicyUpdateResponse =
  PolicyUpdateResponses[keyof PolicyUpdateResponses];

export type PolicyConflictsData = {
  /**
   * Conflict check details
   */
  body: PoliciesPolicyConflictsPayload;
  path?: never;
  query?: never;
  url: '/policies/conflicts';
};

export type PolicyConflictsErrors = {
  /**
   * Invalid request payload
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type PolicyConflictsResponses = {
  /**
   * Success
   */
  200: PoliciesPolicyConflictsResponse;
};

export type PolicyConflictsResponse =
  PolicyConflictsResponses[keyof PolicyConflictsResponses];

export type PolicyMetadataData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/policies/metadata';
};

export type PolicyMetadataErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type PolicyMetadataResponses = {
  /**
   * Success
   */
  200: PoliciesPolicyMetadataResponse;
};

export type PolicyMetadataResponse =
  PolicyMetadataResponses[keyof PolicyMetadataResponses];

export type PolicyTestObservabilityConnectionData = {
  /**
   * Connection details
   */
  body: PoliciesTestObservabilityConnectionPayload;
  path?: never;
  query?: never;
  url: '/policies/observability-k8s/test';
};

export type PolicyTestObservabilityConnectionErrors = {
  /**
   * Invalid payload
   */
  400: unknown;
  /**
   * Policy not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type PolicyTestObservabilityConnectionResponses = {
  /**
   * OK
   */
  200: PoliciesTestObservabilityConnectionResponse;
};

export type PolicyTestObservabilityConnectionResponse =
  PolicyTestObservabilityConnectionResponses[keyof PolicyTestObservabilityConnectionResponses];

export type PolicyTemplateListData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Filter by category (rbac, security, setup, registry)
     */
    category?: string;
    /**
     * Filter by policy type (e.g., security-k8s, security-docker)
     */
    type?: string;
  };
  url: '/policies/templates';
};

export type PolicyTemplateListResponses = {
  /**
   * Success
   */
  200: PoliciesTemplateListResponse;
};

export type PolicyTemplateListResponse =
  PolicyTemplateListResponses[keyof PolicyTemplateListResponses];

export type PolicyTemplateInspectData = {
  body?: never;
  path: {
    /**
     * Template ID
     */
    id: string;
  };
  query?: never;
  url: '/policies/templates/{id}';
};

export type PolicyTemplateInspectErrors = {
  /**
   * Template not found
   */
  404: unknown;
};

export type PolicyTemplateInspectResponses = {
  /**
   * Success
   */
  200: PoliciesPolicyTemplate;
};

export type PolicyTemplateInspectResponse =
  PolicyTemplateInspectResponses[keyof PolicyTemplateInspectResponses];

export type RecommendationListData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Filter by category
     */
    category?: 'governance' | 'security' | 'operations';
    /**
     * Filter by severity
     */
    severity?: 'error' | 'warning' | 'info';
  };
  url: '/recommendations';
};

export type RecommendationListErrors = {
  /**
   * Unable to compute recommendations
   */
  500: unknown;
};

export type RecommendationListResponses = {
  /**
   * Success
   */
  200: Array<RecommendationsRecommendationResponse>;
};

export type RecommendationListResponse =
  RecommendationListResponses[keyof RecommendationListResponses];

export type RecommendationSummaryData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/recommendations/summary';
};

export type RecommendationSummaryResponses = {
  /**
   * Success
   */
  200: RecommendationsSummaryResponse;
};

export type RecommendationSummaryResponse =
  RecommendationSummaryResponses[keyof RecommendationSummaryResponses];

export type RegistryListData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/registries';
};

export type RegistryListErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type RegistryListResponses = {
  /**
   * Success
   */
  200: Array<PortainereeRegistry>;
};

export type RegistryListResponse =
  RegistryListResponses[keyof RegistryListResponses];

export type RegistryCreateData = {
  /**
   * Registry details
   */
  body: RegistriesRegistryCreatePayload;
  path?: never;
  query?: never;
  url: '/registries';
};

export type RegistryCreateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Registry type not available in FIPS mode
   */
  403: unknown;
  /**
   * Another registry with either the same name or same URL & credeintials already exists
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type RegistryCreateResponses = {
  /**
   * Success
   */
  200: PortainereeRegistry;
};

export type RegistryCreateResponse =
  RegistryCreateResponses[keyof RegistryCreateResponses];

export type RegistryDeleteData = {
  body?: never;
  path: {
    /**
     * Registry identifier
     */
    id: number;
  };
  query?: never;
  url: '/registries/{id}';
};

export type RegistryDeleteErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Registry not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type RegistryDeleteResponses = {
  /**
   * Success
   */
  204: void;
};

export type RegistryDeleteResponse =
  RegistryDeleteResponses[keyof RegistryDeleteResponses];

export type RegistryInspectData = {
  body?: never;
  path: {
    /**
     * Registry identifier
     */
    id: number;
  };
  query?: {
    /**
     * Environment identifier (applies policy overrides if provided)
     */
    endpointId?: number;
  };
  url: '/registries/{id}';
};

export type RegistryInspectErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied to access registry
   */
  403: unknown;
  /**
   * Registry not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type RegistryInspectResponses = {
  /**
   * Success
   */
  200: PortainereeRegistry;
};

export type RegistryInspectResponse =
  RegistryInspectResponses[keyof RegistryInspectResponses];

export type RegistryUpdateData = {
  /**
   * Registry details
   */
  body: RegistriesRegistryUpdatePayload;
  path: {
    /**
     * Registry identifier
     */
    id: number;
  };
  query?: never;
  url: '/registries/{id}';
};

export type RegistryUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Registry not found
   */
  404: unknown;
  /**
   * Another registry with either the same name or same URL & credentials already exists
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type RegistryUpdateResponses = {
  /**
   * Success
   */
  200: PortainereeRegistry;
};

export type RegistryUpdateResponse =
  RegistryUpdateResponses[keyof RegistryUpdateResponses];

export type RegistryConfigureData = {
  /**
   * Registry configuration
   */
  body: RegistriesRegistryConfigurePayload;
  path: {
    /**
     * Registry identifier
     */
    id: number;
  };
  query?: never;
  url: '/registries/{id}/configure';
};

export type RegistryConfigureErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Registry not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type RegistryConfigureResponses = {
  /**
   * Success
   */
  204: void;
};

export type RegistryConfigureResponse =
  RegistryConfigureResponses[keyof RegistryConfigureResponses];

export type EcrDeleteRepositoryData = {
  body?: never;
  path: {
    /**
     * Registry identifier
     */
    id: number;
    /**
     * Repository name
     */
    repositoryName: string;
  };
  query?: never;
  url: '/registries/{id}/ecr/repositories/{repositoryName}';
};

export type EcrDeleteRepositoryErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied to access registry
   */
  403: unknown;
  /**
   * Registry not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EcrDeleteRepositoryResponses = {
  /**
   * Success
   */
  200: unknown;
};

export type EcrDeleteTagsData = {
  /**
   * Tag Array
   */
  body: RegistriesDeleteTagsPayload;
  path: {
    /**
     * Registry identifier
     */
    id: number;
    /**
     * Repository name
     */
    repositoryName: number;
  };
  query?: never;
  url: '/registries/{id}/ecr/repositories/{repositoryName}/tags';
};

export type EcrDeleteTagsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied to access registry
   */
  403: unknown;
  /**
   * Registry not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type EcrDeleteTagsResponses = {
  /**
   * Success
   */
  204: void;
};

export type EcrDeleteTagsResponse =
  EcrDeleteTagsResponses[keyof EcrDeleteTagsResponses];

export type RepositoryTagsDeleteData = {
  /**
   * Tags to delete
   */
  body: RegistriesRepositoryTagsDeletePayload;
  path: {
    /**
     * Registry identifier
     */
    id: number;
    /**
     * Repository name
     */
    repositoryName: string;
  };
  query?: never;
  url: '/registries/{id}/repositories/{repositoryName}/tags';
};

export type RepositoryTagsDeleteErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied to access registry
   */
  403: unknown;
  /**
   * Registry not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type RepositoryTagsDeleteResponses = {
  /**
   * Success
   */
  204: void;
};

export type RepositoryTagsDeleteResponse =
  RepositoryTagsDeleteResponses[keyof RepositoryTagsDeleteResponses];

export type RepositoryTagsDetailsData = {
  body?: never;
  path: {
    /**
     * Registry identifier
     */
    id: number;
    /**
     * Repository name
     */
    repositoryName: string;
  };
  query: {
    /**
     * Tag names to fetch details for
     */
    tag: Array<string>;
    /**
     * Environment(Endpoint) identifier
     */
    endpointId?: number;
  };
  url: '/registries/{id}/repositories/{repositoryName}/tags/details';
};

export type RepositoryTagsDetailsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied to access registry
   */
  403: unknown;
  /**
   * Registry not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type RepositoryTagsDetailsResponses = {
  /**
   * Success
   */
  200: {
    [key: string]: RegistriesRepositoryTagDetails;
  };
};

export type RepositoryTagsDetailsResponse =
  RepositoryTagsDetailsResponses[keyof RepositoryTagsDetailsResponses];

export type RegistryPingData = {
  /**
   * Registry credentials to test
   */
  body: RegistriesRegistryPingPayload;
  path?: never;
  query?: never;
  url: '/registries/ping';
};

export type RegistryPingErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type RegistryPingResponses = {
  /**
   * Success
   */
  200: RegistriesRegistryPingResponse;
};

export type RegistryPingResponse =
  RegistryPingResponses[keyof RegistryPingResponses];

export type ResourceControlCreateData = {
  /**
   * Resource control details
   */
  body: ResourcecontrolsResourceControlCreatePayload;
  path?: never;
  query?: never;
  url: '/resource_controls';
};

export type ResourceControlCreateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * A resource control is already associated to this resource
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type ResourceControlCreateResponses = {
  /**
   * Success
   */
  200: PortainerResourceControl;
};

export type ResourceControlCreateResponse =
  ResourceControlCreateResponses[keyof ResourceControlCreateResponses];

export type ResourceControlDeleteData = {
  body?: never;
  path: {
    /**
     * Resource control identifier
     */
    id: number;
  };
  query?: never;
  url: '/resource_controls/{id}';
};

export type ResourceControlDeleteErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Resource control not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type ResourceControlDeleteResponses = {
  /**
   * Success
   */
  204: void;
};

export type ResourceControlDeleteResponse =
  ResourceControlDeleteResponses[keyof ResourceControlDeleteResponses];

export type ResourceControlUpdateData = {
  /**
   * Resource control details
   */
  body: ResourcecontrolsResourceControlUpdatePayload;
  path: {
    /**
     * Resource control identifier
     */
    id: number;
  };
  query?: never;
  url: '/resource_controls/{id}';
};

export type ResourceControlUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized
   */
  403: unknown;
  /**
   * Resource control not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type ResourceControlUpdateResponses = {
  /**
   * Success
   */
  200: PortainerResourceControl;
};

export type ResourceControlUpdateResponse =
  ResourceControlUpdateResponses[keyof ResourceControlUpdateResponses];

export type RestoreData = {
  /**
   * Restore request payload
   */
  body: BackupRestorePayload;
  headers?: {
    /**
     * Setup token (required when instance is uninitialized and --no-setup-token is not set)
     */
    'X-Setup-Token'?: string;
  };
  path?: never;
  query?: never;
  url: '/restore';
};

export type RestoreErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Access denied - invalid or missing setup token
   */
  403: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type RestoreResponses = {
  /**
   * Success
   */
  200: unknown;
};

export type RoleListData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/roles';
};

export type RoleListErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type RoleListResponses = {
  /**
   * Success
   */
  200: Array<PortainereeRole>;
};

export type RoleListResponse = RoleListResponses[keyof RoleListResponses];

export type SettingsInspectData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/settings';
};

export type SettingsInspectErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type SettingsInspectResponses = {
  /**
   * Success
   */
  200: PortainereeSettings;
};

export type SettingsInspectResponse =
  SettingsInspectResponses[keyof SettingsInspectResponses];

export type SettingsUpdateData = {
  /**
   * New settings
   */
  body: SettingsSettingsUpdatePayload;
  path?: never;
  query?: never;
  url: '/settings';
};

export type SettingsUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Edge Compute features cannot be disabled while they are in use
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type SettingsUpdateResponses = {
  /**
   * Success
   */
  200: PortainereeSettings;
};

export type SettingsUpdateResponse =
  SettingsUpdateResponses[keyof SettingsUpdateResponses];

export type SettingsAdditionalFunctionalityInspectData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/settings/additional_functionality';
};

export type SettingsAdditionalFunctionalityInspectErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type SettingsAdditionalFunctionalityInspectResponses = {
  /**
   * Success
   */
  200: SettingsSettingsAdditionalFunctionalityInspectResponse;
};

export type SettingsAdditionalFunctionalityInspectResponse =
  SettingsAdditionalFunctionalityInspectResponses[keyof SettingsAdditionalFunctionalityInspectResponses];

export type SettingsAdditionalFunctionalityUpdateData = {
  /**
   * New settings
   */
  body: SettingsSettingsAdditionalFunctionalityUpdatePayload;
  path?: never;
  query?: never;
  url: '/settings/additional_functionality';
};

export type SettingsAdditionalFunctionalityUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type SettingsAdditionalFunctionalityUpdateResponses = {
  /**
   * Success
   */
  204: void;
};

export type SettingsAdditionalFunctionalityUpdateResponse =
  SettingsAdditionalFunctionalityUpdateResponses[keyof SettingsAdditionalFunctionalityUpdateResponses];

export type DefaultRegistryUpdateData = {
  /**
   * Update default registry
   */
  body: SettingsDefaultRegistryUpdatePayload;
  path?: never;
  query?: never;
  url: '/settings/default_registry';
};

export type DefaultRegistryUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type DefaultRegistryUpdateResponses = {
  /**
   * Success
   */
  200: PortainereeSettings;
};

export type DefaultRegistryUpdateResponse =
  DefaultRegistryUpdateResponses[keyof DefaultRegistryUpdateResponses];

export type SettingsEdgeMtlscaCertificatesData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/settings/edge/mtls_ca_certificate';
};

export type SettingsEdgeMtlscaCertificatesErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type SettingsEdgeMtlscaCertificatesResponses = {
  /**
   * Success
   */
  200: SettingsSettingsCaCertResponse;
};

export type SettingsEdgeMtlscaCertificatesResponse =
  SettingsEdgeMtlscaCertificatesResponses[keyof SettingsEdgeMtlscaCertificatesResponses];

export type SettingsEdgeMtlsCertificatesData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/settings/edge/mtls_certificate';
};

export type SettingsEdgeMtlsCertificatesErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type SettingsEdgeMtlsCertificatesResponses = {
  /**
   * Success
   */
  200: SettingsSettingsCertResponse;
};

export type SettingsEdgeMtlsCertificatesResponse =
  SettingsEdgeMtlsCertificatesResponses[keyof SettingsEdgeMtlsCertificatesResponses];

export type SettingsExperimentalInspectData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/settings/experimental';
};

export type SettingsExperimentalInspectErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type SettingsExperimentalInspectResponses = {
  /**
   * Success
   */
  200: SettingsSettingsExperimentalInspectResponse;
};

export type SettingsExperimentalInspectResponse =
  SettingsExperimentalInspectResponses[keyof SettingsExperimentalInspectResponses];

export type SettingsExperimentalUpdateData = {
  /**
   * New settings
   */
  body: SettingsSettingsExperimentalUpdatePayload;
  path?: never;
  query?: never;
  url: '/settings/experimental';
};

export type SettingsExperimentalUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type SettingsExperimentalUpdateResponses = {
  /**
   * Success
   */
  204: void;
};

export type SettingsExperimentalUpdateResponse =
  SettingsExperimentalUpdateResponses[keyof SettingsExperimentalUpdateResponses];

export type SettingsPublicData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/settings/public';
};

export type SettingsPublicErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type SettingsPublicResponses = {
  /**
   * Success
   */
  200: SettingsPublicSettingsResponse;
};

export type SettingsPublicResponse =
  SettingsPublicResponses[keyof SettingsPublicResponses];

export type GenerateData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/sshkeygen';
};

export type GenerateErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type GenerateResponses = {
  /**
   * OK
   */
  200: ModelsSshKeyPair;
};

export type GenerateResponse = GenerateResponses[keyof GenerateResponses];

export type SslInspectData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/ssl';
};

export type SslInspectErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied to access settings
   */
  403: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type SslInspectResponses = {
  /**
   * Success
   */
  200: PortainereeSslSettings;
};

export type SslInspectResponse = SslInspectResponses[keyof SslInspectResponses];

export type SslUpdateData = {
  /**
   * SSL Settings
   */
  body: SslSslUpdatePayload;
  path?: never;
  query?: never;
  url: '/ssl';
};

export type SslUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied to access settings
   */
  403: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type SslUpdateResponses = {
  /**
   * Success
   */
  204: void;
};

export type SslUpdateResponse = SslUpdateResponses[keyof SslUpdateResponses];

export type StackListData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Filters to process on the stack list. Encoded as JSON (a map[string]string). For example, {'SwarmID': 'jpofkc0i9uo9wtx1zesuk649w'} will only return stacks that are part of the specified Swarm cluster. Available filters: EndpointID, SwarmID.
     */
    filters?: string;
  };
  url: '/stacks';
};

export type StackListErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackListResponses = {
  /**
   * Success
   */
  200: Array<PortainereeStack>;
  /**
   * Success
   */
  204: void;
};

export type StackListResponse = StackListResponses[keyof StackListResponses];

export type StackDeleteData = {
  body?: never;
  path: {
    /**
     * Stack identifier
     */
    id: number;
  };
  query: {
    /**
     * Set to true to delete an external stack. Only external Swarm stacks are supported
     */
    external?: boolean;
    /**
     * Set to true to delete named volumes declared in the stack file and anonymous volumes attached to containers. Only affects Docker Standalone stacks.
     */
    removeVolumes?: boolean;
    /**
     * Environment identifier
     */
    endpointId: number;
  };
  url: '/stacks/{id}';
};

export type StackDeleteErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackDeleteResponses = {
  /**
   * Success
   */
  204: void;
};

export type StackDeleteResponse =
  StackDeleteResponses[keyof StackDeleteResponses];

export type StackInspectData = {
  body?: never;
  path: {
    /**
     * Stack identifier
     */
    id: number;
  };
  query?: never;
  url: '/stacks/{id}';
};

export type StackInspectErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Stack not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackInspectResponses = {
  /**
   * Success
   */
  200: PortainereeStack;
};

export type StackInspectResponse =
  StackInspectResponses[keyof StackInspectResponses];

export type StackUpdateData = {
  /**
   * Stack details
   */
  body: StacksUpdateStackPayload;
  path: {
    /**
     * Stack identifier
     */
    id: number;
  };
  query: {
    /**
     * Environment identifier
     */
    endpointId: number;
  };
  url: '/stacks/{id}';
};

export type StackUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Not found
   */
  404: unknown;
  /**
   * Conflict
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackUpdateResponses = {
  /**
   * Success
   */
  200: PortainereeStack;
};

export type StackUpdateResponse =
  StackUpdateResponses[keyof StackUpdateResponses];

export type StackAssociateData = {
  body?: never;
  path: {
    /**
     * Stack identifier
     */
    id: number;
  };
  query: {
    /**
     * Environment identifier
     */
    endpointId: number;
    /**
     * Swarm identifier
     */
    swarmId: number;
    /**
     * Indicates whether the stack is orphaned
     */
    orphanedRunning: boolean;
  };
  url: '/stacks/{id}/associate';
};

export type StackAssociateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Stack not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackAssociateResponses = {
  /**
   * Success
   */
  200: PortainereeStack;
};

export type StackAssociateResponse =
  StackAssociateResponses[keyof StackAssociateResponses];

export type StackConvertData = {
  /**
   * Stack conversion details
   */
  body: StacksStackConvertPayload;
  path: {
    /**
     * Stack identifier
     */
    id: number;
  };
  query?: never;
  url: '/stacks/{id}/convert';
};

export type StackConvertErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Stack not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackConvertResponses = {
  /**
   * Success
   */
  200: StacksStackConvertResponse;
};

export type StackConvertResponse =
  StackConvertResponses[keyof StackConvertResponses];

export type StackFileInspectData = {
  body?: never;
  path: {
    /**
     * Stack identifier
     */
    id: number;
  };
  query?: {
    /**
     * Stack file version maintained by Portainer. If both version and commitHash are provided, the commitHash will be used
     */
    version?: number;
    /**
     * Git repository commit hash. If both version and commitHash are provided, the commitHash will be used
     */
    commitHash?: string;
  };
  url: '/stacks/{id}/file';
};

export type StackFileInspectErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Stack not found
   */
  404: unknown;
  /**
   * Git settings changed, redeploy required
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackFileInspectResponses = {
  /**
   * Success
   */
  200: StacksStackFileResponse;
};

export type StackFileInspectResponse =
  StackFileInspectResponses[keyof StackFileInspectResponses];

export type StackUpdateGitData = {
  /**
   * Git configs for pull and redeploy a stack
   */
  body: StacksStackGitUpdatePayload;
  path: {
    /**
     * Stack identifier
     */
    id: number;
  };
  query?: {
    /**
     * Stacks created before version 1.18.0 might not have an associated environment(endpoint) identifier. Use this optional parameter to set the environment(endpoint) identifier used by the stack.
     */
    endpointId?: number;
  };
  url: '/stacks/{id}/git';
};

export type StackUpdateGitErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackUpdateGitResponses = {
  /**
   * Success
   */
  200: StacksStackResponse;
};

export type StackUpdateGitResponse =
  StackUpdateGitResponses[keyof StackUpdateGitResponses];

export type StackGitRedeployData = {
  /**
   * Git configs for pull and redeploy of a stack. **StackName** may only be populated for Kuberenetes stacks, and if specified with a blank string, it will be set to blank
   */
  body: StacksStackGitRedeployPayload;
  path: {
    /**
     * Stack identifier
     */
    id: number;
  };
  query?: {
    /**
     * Stacks created before version 1.18.0 might not have an associated environment(endpoint) identifier. Use this optional parameter to set the environment(endpoint) identifier used by the stack.
     */
    endpointId?: number;
  };
  url: '/stacks/{id}/git/redeploy';
};

export type StackGitRedeployErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Not found
   */
  404: unknown;
  /**
   * Conflict
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackGitRedeployResponses = {
  /**
   * Success
   */
  200: PortainereeStack;
};

export type StackGitRedeployResponse =
  StackGitRedeployResponses[keyof StackGitRedeployResponses];

export type StackImagesStatusData = {
  body?: never;
  path: {
    /**
     * Stack identifier
     */
    id: number;
  };
  query?: {
    /**
     * Refresh will force a refresh of the image status cache
     */
    refresh?: boolean;
  };
  url: '/stacks/{id}/images_status';
};

export type StackImagesStatusErrors = {
  /**
   * Bad request
   */
  400: unknown;
  /**
   * Internal server error
   */
  500: unknown;
};

export type StackImagesStatusResponses = {
  /**
   * Success
   */
  200: ImagesStatusResponse;
};

export type StackImagesStatusResponse =
  StackImagesStatusResponses[keyof StackImagesStatusResponses];

export type StackMigrateData = {
  /**
   * Stack migration details
   */
  body: StacksStackMigratePayload;
  path: {
    /**
     * Stack identifier
     */
    id: number;
  };
  query?: {
    /**
     * Stacks created before version 1.18.0 might not have an associated environment(endpoint) identifier. Use this optional parameter to set the environment(endpoint) identifier used by the stack.
     */
    endpointId?: number;
  };
  url: '/stacks/{id}/migrate';
};

export type StackMigrateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Stack not found
   */
  404: unknown;
  /**
   * A stack with the same name is already running on the target environment(endpoint)
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackMigrateResponses = {
  /**
   * Success
   */
  200: PortainereeStack;
};

export type StackMigrateResponse =
  StackMigrateResponses[keyof StackMigrateResponses];

export type StackStartData = {
  body?: never;
  path: {
    /**
     * Stack identifier
     */
    id: number;
  };
  query: {
    /**
     * Environment identifier
     */
    endpointId: number;
  };
  url: '/stacks/{id}/start';
};

export type StackStartErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Not found
   */
  404: unknown;
  /**
   * Stack is already active, deploying, or in error state
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackStartResponses = {
  /**
   * Success
   */
  200: PortainereeStack;
};

export type StackStartResponse = StackStartResponses[keyof StackStartResponses];

export type StackStopData = {
  body?: never;
  path: {
    /**
     * Stack identifier
     */
    id: number;
  };
  query: {
    /**
     * Environment identifier
     */
    endpointId: number;
  };
  url: '/stacks/{id}/stop';
};

export type StackStopErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Not found
   */
  404: unknown;
  /**
   * Conflict
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackStopResponses = {
  /**
   * Success
   */
  200: PortainereeStack;
};

export type StackStopResponse = StackStopResponses[keyof StackStopResponses];

export type StackCreateKubernetesGitData = {
  /**
   * stack config
   */
  body: StacksKubernetesGitDeploymentPayload;
  path?: never;
  query: {
    /**
     * Identifier of the environment that will be used to deploy the stack
     */
    endpointId: number;
  };
  url: '/stacks/create/kubernetes/repository';
};

export type StackCreateKubernetesGitErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Webhook ID already exists
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackCreateKubernetesGitResponses = {
  /**
   * OK
   */
  200: StacksCreateKubernetesStackResponse;
};

export type StackCreateKubernetesGitResponse =
  StackCreateKubernetesGitResponses[keyof StackCreateKubernetesGitResponses];

export type StackCreateKubernetesFileData = {
  /**
   * stack config
   */
  body: StacksKubernetesStringDeploymentPayload;
  path?: never;
  query: {
    /**
     * Identifier of the environment that will be used to deploy the stack
     */
    endpointId: number;
  };
  url: '/stacks/create/kubernetes/string';
};

export type StackCreateKubernetesFileErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackCreateKubernetesFileResponses = {
  /**
   * OK
   */
  200: StacksCreateKubernetesStackResponse;
};

export type StackCreateKubernetesFileResponse =
  StackCreateKubernetesFileResponses[keyof StackCreateKubernetesFileResponses];

export type StackCreateKubernetesUrlData = {
  /**
   * stack config
   */
  body: StacksKubernetesManifestUrlDeploymentPayload;
  path?: never;
  query: {
    /**
     * Identifier of the environment that will be used to deploy the stack
     */
    endpointId: number;
  };
  url: '/stacks/create/kubernetes/url';
};

export type StackCreateKubernetesUrlErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackCreateKubernetesUrlResponses = {
  /**
   * OK
   */
  200: StacksCreateKubernetesStackResponse;
};

export type StackCreateKubernetesUrlResponse =
  StackCreateKubernetesUrlResponses[keyof StackCreateKubernetesUrlResponses];

export type StackCreateDockerStandaloneFileData = {
  body: {
    /**
     * Name of the stack
     */
    Name: string;
    /**
     * Environment variables passed during deployment, represented as a JSON array [{'name': 'name', 'value': 'value'}].
     */
    Env?: string;
    /**
     * Stack file
     */
    file?: Blob | File;
  };
  path?: never;
  query: {
    /**
     * Identifier of the environment that will be used to deploy the stack
     */
    endpointId: number;
  };
  url: '/stacks/create/standalone/file';
};

export type StackCreateDockerStandaloneFileErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackCreateDockerStandaloneFileResponses = {
  /**
   * OK
   */
  200: PortainereeStack;
};

export type StackCreateDockerStandaloneFileResponse =
  StackCreateDockerStandaloneFileResponses[keyof StackCreateDockerStandaloneFileResponses];

export type StackCreateDockerStandaloneRepositoryData = {
  /**
   * stack config
   */
  body: StacksComposeStackFromGitRepositoryPayload;
  path?: never;
  query: {
    /**
     * Identifier of the environment that will be used to deploy the stack
     */
    endpointId: number;
  };
  url: '/stacks/create/standalone/repository';
};

export type StackCreateDockerStandaloneRepositoryErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackCreateDockerStandaloneRepositoryResponses = {
  /**
   * OK
   */
  200: PortainereeStack;
};

export type StackCreateDockerStandaloneRepositoryResponse =
  StackCreateDockerStandaloneRepositoryResponses[keyof StackCreateDockerStandaloneRepositoryResponses];

export type StackCreateDockerStandaloneStringData = {
  /**
   * stack config
   */
  body: StacksComposeStackFromFileContentPayload;
  path?: never;
  query: {
    /**
     * Identifier of the environment that will be used to deploy the stack
     */
    endpointId: number;
  };
  url: '/stacks/create/standalone/string';
};

export type StackCreateDockerStandaloneStringErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackCreateDockerStandaloneStringResponses = {
  /**
   * OK
   */
  200: PortainereeStack;
};

export type StackCreateDockerStandaloneStringResponse =
  StackCreateDockerStandaloneStringResponses[keyof StackCreateDockerStandaloneStringResponses];

export type StackCreateDockerSwarmFileData = {
  body?: {
    /**
     * Name of the stack
     */
    Name?: string;
    /**
     * Swarm cluster identifier.
     */
    SwarmID?: string;
    /**
     * Environment variables passed during deployment, represented as a JSON array [{'name': 'name', 'value': 'value'}]. Optional
     */
    Env?: string;
    /**
     * Stack file
     */
    file?: Blob | File;
  };
  path?: never;
  query: {
    /**
     * Identifier of the environment that will be used to deploy the stack
     */
    endpointId: number;
  };
  url: '/stacks/create/swarm/file';
};

export type StackCreateDockerSwarmFileErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Stack name or webhook id is not unique
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackCreateDockerSwarmFileResponses = {
  /**
   * OK
   */
  200: PortainereeStack;
};

export type StackCreateDockerSwarmFileResponse =
  StackCreateDockerSwarmFileResponses[keyof StackCreateDockerSwarmFileResponses];

export type StackCreateDockerSwarmRepositoryData = {
  /**
   * stack config
   */
  body: StacksSwarmStackFromGitRepositoryPayload;
  path?: never;
  query: {
    /**
     * Identifier of the environment that will be used to deploy the stack
     */
    endpointId: number;
  };
  url: '/stacks/create/swarm/repository';
};

export type StackCreateDockerSwarmRepositoryErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Stack name or webhook id is not unique
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackCreateDockerSwarmRepositoryResponses = {
  /**
   * OK
   */
  200: PortainereeStack;
};

export type StackCreateDockerSwarmRepositoryResponse =
  StackCreateDockerSwarmRepositoryResponses[keyof StackCreateDockerSwarmRepositoryResponses];

export type StackCreateDockerSwarmStringData = {
  /**
   * stack config
   */
  body: StacksSwarmStackFromFileContentPayload;
  path?: never;
  query: {
    /**
     * Identifier of the environment that will be used to deploy the stack
     */
    endpointId: number;
  };
  url: '/stacks/create/swarm/string';
};

export type StackCreateDockerSwarmStringErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Stack name or webhook id is not unique
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackCreateDockerSwarmStringResponses = {
  /**
   * OK
   */
  200: PortainereeStack;
};

export type StackCreateDockerSwarmStringResponse =
  StackCreateDockerSwarmStringResponses[keyof StackCreateDockerSwarmStringResponses];

export type StacksImageStatusClearData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Identifier of the environment(endpoint) that will be used to filter the stacks to clear the image status cache for
     */
    environmentId?: number;
    /**
     * Identifier of the swarm cluster that will be used to filter the stacks to clear the image status cache for
     */
    swarmId?: string;
  };
  url: '/stacks/image_status/clear';
};

export type StacksImageStatusClearErrors = {
  /**
   * Bad request
   */
  400: unknown;
  /**
   * Internal server error
   */
  500: unknown;
};

export type StacksImageStatusClearResponses = {
  /**
   * Success
   */
  204: void;
};

export type StacksImageStatusClearResponse =
  StacksImageStatusClearResponses[keyof StacksImageStatusClearResponses];

export type StackDeleteKubernetesByNameData = {
  body?: never;
  path: {
    /**
     * Stack name
     */
    name: string;
  };
  query: {
    /**
     * Set to true to delete an external stack. Only external Swarm stacks are supported
     */
    external?: boolean;
    /**
     * Environment identifier
     */
    endpointId: number;
  };
  url: '/stacks/name/{name}';
};

export type StackDeleteKubernetesByNameErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StackDeleteKubernetesByNameResponses = {
  /**
   * Success
   */
  204: void;
};

export type StackDeleteKubernetesByNameResponse =
  StackDeleteKubernetesByNameResponses[keyof StackDeleteKubernetesByNameResponses];

export type StacksWebhookInvokeData = {
  body?: never;
  path: {
    /**
     * Stack identifier
     */
    webhookID: string;
  };
  query?: never;
  url: '/stacks/webhooks/{webhookID}';
};

export type StacksWebhookInvokeErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Autoupdate for the stack isn't available" or "Stack deployment is already in progress
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type StacksWebhookInvokeResponses = {
  /**
   * Accepted
   */
  202: StacksWebhookInvokeResponse;
};

export type StacksWebhookInvokeResponse2 =
  StacksWebhookInvokeResponses[keyof StacksWebhookInvokeResponses];

export type GetDebugLogStatusData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/support/debug_log';
};

export type GetDebugLogStatusErrors = {
  /**
   * Server error occurred.
   */
  500: unknown;
};

export type GetDebugLogStatusResponses = {
  /**
   * Success
   */
  200: SupportDebugLogPayload;
};

export type GetDebugLogStatusResponse =
  GetDebugLogStatusResponses[keyof GetDebugLogStatusResponses];

export type SetDebugLogStatusData = {
  /**
   * Debug log should be set to enabled or disabled
   */
  body: SupportDebugLogPayload;
  path?: never;
  query?: never;
  url: '/support/debug_log';
};

export type SetDebugLogStatusErrors = {
  /**
   * Invalid request payload
   */
  400: unknown;
  /**
   * Server error occurred.
   */
  500: unknown;
};

export type SetDebugLogStatusResponses = {
  /**
   * Success
   */
  200: SupportDebugLogPayload;
};

export type SetDebugLogStatusResponse =
  SetDebugLogStatusResponses[keyof SetDebugLogStatusResponses];

export type SupportBundleDownloadData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/support/download';
};

export type SupportBundleDownloadErrors = {
  /**
   * Invalid request payload, such as missing required fields or fields not meeting validation criteria.
   */
  400: unknown;
  /**
   * Unauthorized access - the user is not authenticated or does not have the necessary permissions. Ensure that you have provided a valid API key or JWT token, and that you have the required permissions.
   */
  401: unknown;
  /**
   * Permission denied - the user is authenticated but does not have the necessary permissions to access the requested resource or perform the specified operation. Check your user roles and permissions.
   */
  403: unknown;
  /**
   * Failed to create support archive
   */
  500: unknown;
};

export type SupportBundleDownloadResponses = {
  /**
   * Success
   */
  200: unknown;
};

export type SystemInfoData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/system/info';
};

export type SystemInfoErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type SystemInfoResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerSystemSystemInfoResponse;
};

export type SystemInfoResponse = SystemInfoResponses[keyof SystemInfoResponses];

export type SystemNodesCountData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/system/nodes';
};

export type SystemNodesCountErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type SystemNodesCountResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerSystemNodesCountResponse;
};

export type SystemNodesCountResponse =
  SystemNodesCountResponses[keyof SystemNodesCountResponses];

export type SystemStatusData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/system/status';
};

export type SystemStatusResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerSystemStatus;
};

export type SystemStatusResponse =
  SystemStatusResponses[keyof SystemStatusResponses];

export type SystemUpdateData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/system/update';
};

export type SystemUpdateErrors = {
  /**
   * Conflict, an automatic patch operation is already in progress
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type SystemUpdateResponses = {
  /**
   * Success
   */
  204: GithubComPortainerPortainerEeApiHttpHandlerSystemStatus;
};

export type SystemUpdateResponse =
  SystemUpdateResponses[keyof SystemUpdateResponses];

export type SystemVersionData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/system/version';
};

export type SystemVersionResponses = {
  /**
   * Success
   */
  200: GithubComPortainerPortainerEeApiHttpHandlerSystemVersionResponse;
};

export type SystemVersionResponse =
  SystemVersionResponses[keyof SystemVersionResponses];

export type TagListData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/tags';
};

export type TagListErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type TagListResponses = {
  /**
   * Success
   */
  200: Array<PortainerTag>;
};

export type TagListResponse = TagListResponses[keyof TagListResponses];

export type TagCreateData = {
  /**
   * Tag details
   */
  body: TagsTagCreatePayload;
  path?: never;
  query?: never;
  url: '/tags';
};

export type TagCreateErrors = {
  /**
   * This name is already associated to a tag
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type TagCreateResponses = {
  /**
   * Success
   */
  200: PortainerTag;
};

export type TagCreateResponse = TagCreateResponses[keyof TagCreateResponses];

export type TagDeleteData = {
  body?: never;
  path: {
    /**
     * Tag identifier
     */
    id: number;
  };
  query?: never;
  url: '/tags/{id}';
};

export type TagDeleteErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Tag not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type TagDeleteResponses = {
  /**
   * Success
   */
  204: void;
};

export type TagDeleteResponse = TagDeleteResponses[keyof TagDeleteResponses];

export type TeamMembershipListData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/team_memberships';
};

export type TeamMembershipListErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type TeamMembershipListResponses = {
  /**
   * Success
   */
  200: Array<PortainerTeamMembership>;
};

export type TeamMembershipListResponse =
  TeamMembershipListResponses[keyof TeamMembershipListResponses];

export type TeamMembershipCreateData = {
  /**
   * Team membership details
   */
  body: TeammembershipsTeamMembershipCreatePayload;
  path?: never;
  query?: never;
  url: '/team_memberships';
};

export type TeamMembershipCreateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied to manage memberships
   */
  403: unknown;
  /**
   * Team membership already registered
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type TeamMembershipCreateResponses = {
  /**
   * Success
   */
  200: PortainerTeamMembership;
};

export type TeamMembershipCreateResponse =
  TeamMembershipCreateResponses[keyof TeamMembershipCreateResponses];

export type TeamMembershipDeleteData = {
  body?: never;
  path: {
    /**
     * TeamMembership identifier
     */
    id: number;
  };
  query?: never;
  url: '/team_memberships/{id}';
};

export type TeamMembershipDeleteErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * TeamMembership not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type TeamMembershipDeleteResponses = {
  /**
   * Success
   */
  204: void;
};

export type TeamMembershipDeleteResponse =
  TeamMembershipDeleteResponses[keyof TeamMembershipDeleteResponses];

export type TeamMembershipUpdateData = {
  /**
   * Team membership details
   */
  body: TeammembershipsTeamMembershipUpdatePayload;
  path: {
    /**
     * Team membership identifier
     */
    id: number;
  };
  query?: never;
  url: '/team_memberships/{id}';
};

export type TeamMembershipUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * TeamMembership not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type TeamMembershipUpdateResponses = {
  /**
   * Success
   */
  200: PortainerTeamMembership;
};

export type TeamMembershipUpdateResponse =
  TeamMembershipUpdateResponses[keyof TeamMembershipUpdateResponses];

export type TeamListData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Only list teams that the user is leader of
     */
    onlyLedTeams?: boolean;
    /**
     * Identifier of the environment(endpoint) that will be used to filter the authorized teams
     */
    environmentId?: number;
  };
  url: '/teams';
};

export type TeamListErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type TeamListResponses = {
  /**
   * Success
   */
  200: Array<PortainerTeam>;
};

export type TeamListResponse = TeamListResponses[keyof TeamListResponses];

export type TeamCreateData = {
  /**
   * details
   */
  body: TeamsTeamCreatePayload;
  path?: never;
  query?: never;
  url: '/teams';
};

export type TeamCreateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * A team with the same name already exists
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type TeamCreateResponses = {
  /**
   * Success
   */
  200: PortainerTeam;
};

export type TeamCreateResponse = TeamCreateResponses[keyof TeamCreateResponses];

export type TeamDeleteData = {
  body?: never;
  path: {
    /**
     * Team Id
     */
    id: number;
  };
  query?: never;
  url: '/teams/{id}';
};

export type TeamDeleteErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Team not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type TeamDeleteResponses = {
  /**
   * Success
   */
  204: void;
};

export type TeamDeleteResponse = TeamDeleteResponses[keyof TeamDeleteResponses];

export type TeamInspectData = {
  body?: never;
  path: {
    /**
     * Team identifier
     */
    id: number;
  };
  query?: never;
  url: '/teams/{id}';
};

export type TeamInspectErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Team not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type TeamInspectResponses = {
  /**
   * Success
   */
  200: PortainerTeam;
};

export type TeamInspectResponse =
  TeamInspectResponses[keyof TeamInspectResponses];

export type TeamUpdateData = {
  /**
   * Team details
   */
  body: TeamsTeamUpdatePayload;
  path: {
    /**
     * Team identifier
     */
    id: number;
  };
  query?: never;
  url: '/teams/{id}';
};

export type TeamUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Team not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type TeamUpdateResponses = {
  /**
   * Success
   */
  200: PortainerTeam;
};

export type TeamUpdateResponse = TeamUpdateResponses[keyof TeamUpdateResponses];

export type TeamMembershipsData = {
  body?: never;
  path: {
    /**
     * Team Id
     */
    id: number;
  };
  query?: never;
  url: '/teams/{id}/memberships';
};

export type TeamMembershipsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type TeamMembershipsResponses = {
  /**
   * Success
   */
  200: Array<PortainerTeamMembership>;
};

export type TeamMembershipsResponse =
  TeamMembershipsResponses[keyof TeamMembershipsResponses];

export type TemplateListData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/templates';
};

export type TemplateListErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type TemplateListResponses = {
  /**
   * Success
   */
  200: TemplatesListResponse;
};

export type TemplateListResponse =
  TemplateListResponses[keyof TemplateListResponses];

export type TemplateFileData = {
  body?: never;
  path: {
    /**
     * Template identifier
     */
    id: number;
  };
  query?: never;
  url: '/templates/{id}/file';
};

export type TemplateFileErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type TemplateFileResponses = {
  /**
   * Success
   */
  200: TemplatesFileResponse;
};

export type TemplateFileResponse =
  TemplateFileResponses[keyof TemplateFileResponses];

export type HelmRepoSearchData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Helm repository URL (required if registryId not provided)
     */
    repo?: string;
    /**
     * Helm registry ID (required if repo not provided)
     */
    registryId?: string;
    /**
     * Helm chart name
     */
    chart?: string;
    /**
     * If true will use cache to search
     */
    useCache?: string;
  };
  url: '/templates/helm';
};

export type HelmRepoSearchErrors = {
  /**
   * Bad request
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type HelmRepoSearchResponses = {
  /**
   * Success
   */
  200: string;
};

export type HelmRepoSearchResponse =
  HelmRepoSearchResponses[keyof HelmRepoSearchResponses];

export type HelmShowData = {
  body?: never;
  path: {
    /**
     * chart/values/readme
     */
    command: string;
  };
  query: {
    /**
     * Helm repository URL (required if registryId not provided)
     */
    repo?: string;
    /**
     * Helm registry ID (required if repo not provided)
     */
    registryId?: string;
    /**
     * Environment ID (required if registryId is provided and user is not an admin)
     */
    environmentId?: string;
    /**
     * Chart name
     */
    chart: string;
    /**
     * Chart version
     */
    version?: string;
  };
  url: '/templates/helm/{command}';
};

export type HelmShowErrors = {
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Environment(Endpoint) or ServiceAccount not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type HelmShowResponses = {
  /**
   * Success
   */
  200: string;
};

export type HelmShowResponse = HelmShowResponses[keyof HelmShowResponses];

export type UploadTlsData = {
  body: {
    /**
     * Folder where the TLS file will be stored. Will be created if not existing
     */
    folder: string;
    /**
     * The file to upload
     */
    file: Blob | File;
  };
  path: {
    /**
     * TLS file type. Valid values are 'ca', 'cert' or 'key'.
     */
    certificate: 'ca' | 'cert' | 'key';
  };
  query?: never;
  url: '/upload/tls/{certificate}';
};

export type UploadTlsErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type UploadTlsResponses = {
  /**
   * Success
   */
  204: void;
};

export type UploadTlsResponse = UploadTlsResponses[keyof UploadTlsResponses];

export type AuthLogsListData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Pagination offset
     */
    offset?: number;
    /**
     * Limit results
     */
    limit?: number;
    /**
     * Results before timestamp (unix)
     */
    before?: number;
    /**
     * Results after timestamp (unix)
     */
    after?: number;
    /**
     * Sort by this column
     */
    sortBy?: string;
    /**
     * Sort order, if true will return results by descending order
     */
    sortDesc?: boolean;
    /**
     * Query logs by this keyword
     */
    keyword?: string;
  };
  url: '/useractivity/authlogs';
};

export type AuthLogsListErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type AuthLogsListResponses = {
  /**
   * Success
   */
  200: Array<PortainereeAuthActivityLog>;
};

export type AuthLogsListResponse =
  AuthLogsListResponses[keyof AuthLogsListResponses];

export type AuthLogsCsvData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Results before timestamp (unix)
     */
    before?: number;
    /**
     * Results after timestamp (unix)
     */
    after?: number;
    /**
     * Sort by this column
     */
    sortBy?: string;
    /**
     * Sort order, if true will return results by descending order
     */
    sortDesc?: boolean;
    /**
     * Limit results
     */
    limit?: number;
    /**
     * Query logs by this keyword
     */
    keyword?: string;
  };
  url: '/useractivity/authlogs.csv';
};

export type AuthLogsCsvErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type AuthLogsCsvResponses = {
  /**
   * Success
   */
  200: unknown;
};

export type LogsListData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Pagination offset
     */
    offset?: number;
    /**
     * Limit results
     */
    limit?: number;
    /**
     * Results before timestamp (unix)
     */
    before?: number;
    /**
     * Results after timestamp (unix)
     */
    after?: number;
    /**
     * Sort by this column
     */
    sortBy?: string;
    /**
     * Sort order, if true will return results by descending order
     */
    sortDesc?: boolean;
    /**
     * Query logs by this keyword
     */
    keyword?: string;
    /**
     * Filter by context
     */
    context?: Array<string>;
    /**
     * Filter by usernames
     */
    username?: Array<string>;
  };
  url: '/useractivity/logs';
};

export type LogsListErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type LogsListResponses = {
  /**
   * Success
   */
  200: UseractivityLogsListResponse;
};

export type LogsListResponse = LogsListResponses[keyof LogsListResponses];

export type LogsCsvData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Results before timestamp (unix)
     */
    before?: number;
    /**
     * Results after timestamp (unix)
     */
    after?: number;
    /**
     * Sort by this column
     */
    sortBy?: string;
    /**
     * Sort order, if true will return results by descending order
     */
    sortDesc?: boolean;
    /**
     * Limit results (defaults to 1000000 if not provided, zero, negative, or exceeds maximum)
     */
    limit?: number;
    /**
     * Query logs by this keyword
     */
    keyword?: string;
  };
  url: '/useractivity/logs.csv';
};

export type LogsCsvErrors = {
  /**
   * Server error
   */
  500: unknown;
};

export type LogsCsvResponses = {
  /**
   * Success
   */
  200: unknown;
};

export type UserListData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Identifier of the environment(endpoint) that will be used to filter the authorized users
     */
    environmentId?: number;
  };
  url: '/users';
};

export type UserListErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type UserListResponses = {
  /**
   * Success
   */
  200: Array<UsersUser>;
};

export type UserListResponse = UserListResponses[keyof UserListResponses];

export type UserCreateData = {
  /**
   * User details
   */
  body: UsersUserCreatePayload;
  path?: never;
  query?: never;
  url: '/users';
};

export type UserCreateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * User already exists
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type UserCreateResponses = {
  /**
   * Success
   */
  200: PortainereeUser;
};

export type UserCreateResponse = UserCreateResponses[keyof UserCreateResponses];

export type UserDeleteData = {
  body?: never;
  path: {
    /**
     * User identifier
     */
    id: number;
  };
  query?: never;
  url: '/users/{id}';
};

export type UserDeleteErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * User not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type UserDeleteResponses = {
  /**
   * Success
   */
  204: void;
};

export type UserDeleteResponse = UserDeleteResponses[keyof UserDeleteResponses];

export type UserInspectData = {
  body?: never;
  path: {
    /**
     * User identifier
     */
    id: number;
  };
  query?: never;
  url: '/users/{id}';
};

export type UserInspectErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * User not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type UserInspectResponses = {
  /**
   * Success
   */
  200: PortainereeUser;
};

export type UserInspectResponse =
  UserInspectResponses[keyof UserInspectResponses];

export type UserUpdateData = {
  /**
   * User details
   */
  body: UsersUserUpdatePayload;
  path: {
    /**
     * User identifier
     */
    id: number;
  };
  query?: never;
  url: '/users/{id}';
};

export type UserUpdateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * User not found
   */
  404: unknown;
  /**
   * Username already exist or Edge Compute features are not enabled
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type UserUpdateResponses = {
  /**
   * Success
   */
  200: PortainereeUser;
};

export type UserUpdateResponse = UserUpdateResponses[keyof UserUpdateResponses];

export type UserEffectiveAccessInspectData = {
  body?: never;
  path: {
    /**
     * User identifier
     */
    id: number;
  };
  query?: never;
  url: '/users/{id}/effective-access';
};

export type UserEffectiveAccessInspectErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * User not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type UserEffectiveAccessInspectResponses = {
  /**
   * Success
   */
  200: Array<UsersEffectiveAccessEntry>;
};

export type UserEffectiveAccessInspectResponse =
  UserEffectiveAccessInspectResponses[keyof UserEffectiveAccessInspectResponses];

export type HelmUserRepositoriesListData = {
  body?: never;
  path: {
    /**
     * User identifier
     */
    id: number;
  };
  query?: never;
  url: '/users/{id}/helm/repositories';
};

export type HelmUserRepositoriesListErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type HelmUserRepositoriesListResponses = {
  /**
   * Success
   */
  200: UsersHelmUserRepositoryResponse;
};

export type HelmUserRepositoriesListResponse =
  HelmUserRepositoriesListResponses[keyof HelmUserRepositoriesListResponses];

export type HelmUserRepositoryCreateData = {
  /**
   * Helm Repository
   */
  body: UsersAddHelmRepoUrlPayload;
  path: {
    /**
     * User identifier
     */
    id: number;
  };
  query?: never;
  url: '/users/{id}/helm/repositories';
};

export type HelmUserRepositoryCreateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type HelmUserRepositoryCreateResponses = {
  /**
   * Success
   */
  200: PortainerHelmUserRepository;
};

export type HelmUserRepositoryCreateResponse =
  HelmUserRepositoryCreateResponses[keyof HelmUserRepositoryCreateResponses];

export type HelmUserRepositoryDeleteData = {
  body?: never;
  path: {
    /**
     * User identifier
     */
    id: number;
    /**
     * Repository identifier
     */
    repositoryID: number;
  };
  query?: never;
  url: '/users/{id}/helm/repositories/{repositoryID}';
};

export type HelmUserRepositoryDeleteErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type HelmUserRepositoryDeleteResponses = {
  /**
   * Success
   */
  204: void;
};

export type HelmUserRepositoryDeleteResponse =
  HelmUserRepositoryDeleteResponses[keyof HelmUserRepositoryDeleteResponses];

export type UserMembershipsInspectData = {
  body?: never;
  path: {
    /**
     * User identifier
     */
    id: number;
  };
  query?: never;
  url: '/users/{id}/memberships';
};

export type UserMembershipsInspectErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type UserMembershipsInspectResponses = {
  /**
   * Success
   */
  200: PortainerTeamMembership;
};

export type UserMembershipsInspectResponse =
  UserMembershipsInspectResponses[keyof UserMembershipsInspectResponses];

export type UserMembershipsSyncData = {
  body?: never;
  path: {
    /**
     * User identifier
     */
    id: number;
  };
  query?: never;
  url: '/users/{id}/memberships/sync';
};

export type UserMembershipsSyncErrors = {
  /**
   * Invalid request or authentication method not eligible for sync
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * User not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type UserMembershipsSyncResponses = {
  /**
   * Updated team memberships
   */
  200: Array<PortainerTeamMembership>;
};

export type UserMembershipsSyncResponse =
  UserMembershipsSyncResponses[keyof UserMembershipsSyncResponses];

export type UserNamespacesData = {
  body?: never;
  path: {
    /**
     * User identifier
     */
    id: number;
  };
  query?: never;
  url: '/users/{id}/namespaces';
};

export type UserNamespacesErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * User not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type UserNamespacesResponses = {
  /**
   * Success
   */
  200: UsersNamespaceMapping;
};

export type UserNamespacesResponse =
  UserNamespacesResponses[keyof UserNamespacesResponses];

export type UserUpdatePasswordData = {
  /**
   * details
   */
  body: UsersUserUpdatePasswordPayload;
  path: {
    /**
     * identifier
     */
    id: number;
  };
  query?: never;
  url: '/users/{id}/passwd';
};

export type UserUpdatePasswordErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * User not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type UserUpdatePasswordResponses = {
  /**
   * Success
   */
  204: void;
};

export type UserUpdatePasswordResponse =
  UserUpdatePasswordResponses[keyof UserUpdatePasswordResponses];

export type UserGetApiKeysData = {
  body?: never;
  path: {
    /**
     * User identifier
     */
    id: number;
  };
  query?: never;
  url: '/users/{id}/tokens';
};

export type UserGetApiKeysErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * User not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type UserGetApiKeysResponses = {
  /**
   * Success
   */
  200: Array<PortainerApiKey>;
};

export type UserGetApiKeysResponse =
  UserGetApiKeysResponses[keyof UserGetApiKeysResponses];

export type UserGenerateApiKeyData = {
  /**
   * details
   */
  body: UsersUserAccessTokenCreatePayload;
  path: {
    /**
     * User identifier
     */
    id: number;
  };
  query?: never;
  url: '/users/{id}/tokens';
};

export type UserGenerateApiKeyErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Unauthorized
   */
  401: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * User not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type UserGenerateApiKeyResponses = {
  /**
   * Success
   */
  200: UsersAccessTokenResponse;
};

export type UserGenerateApiKeyResponse =
  UserGenerateApiKeyResponses[keyof UserGenerateApiKeyResponses];

export type UserRemoveApiKeyData = {
  body?: never;
  path: {
    /**
     * User identifier
     */
    id: number;
    /**
     * Api Key identifier
     */
    keyID: number;
  };
  query?: never;
  url: '/users/{id}/tokens/{keyID}';
};

export type UserRemoveApiKeyErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type UserRemoveApiKeyResponses = {
  /**
   * Success
   */
  204: void;
};

export type UserRemoveApiKeyResponse =
  UserRemoveApiKeyResponses[keyof UserRemoveApiKeyResponses];

export type UserAdminCheckData = {
  body?: never;
  path?: never;
  query?: never;
  url: '/users/admin/check';
};

export type UserAdminCheckErrors = {
  /**
   * User not found
   */
  404: unknown;
};

export type UserAdminCheckResponses = {
  /**
   * Success
   */
  204: void;
};

export type UserAdminCheckResponse =
  UserAdminCheckResponses[keyof UserAdminCheckResponses];

export type UserAdminInitData = {
  /**
   * User details
   */
  body: UsersAdminInitPayload;
  headers?: {
    /**
     * Setup token (required when instance is uninitialized and --no-setup-token is not set)
     */
    'X-Setup-Token'?: string;
  };
  path?: never;
  query?: never;
  url: '/users/admin/init';
};

export type UserAdminInitErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Access denied - invalid or missing setup token
   */
  403: unknown;
  /**
   * Admin user already initialized
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type UserAdminInitResponses = {
  /**
   * Success
   */
  200: PortainereeUser;
};

export type UserAdminInitResponse =
  UserAdminInitResponses[keyof UserAdminInitResponses];

export type CurrentUserInspectData = {
  body?: never;
  path?: never;
  query: {
    /**
     * Set to true to avoid including the environment authorizations in the response
     */
    noEndpointAuthorizations: boolean;
  };
  url: '/users/me';
};

export type CurrentUserInspectErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * User not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type CurrentUserInspectResponses = {
  /**
   * Success
   */
  200: UsersCurrentUserInspectResponse;
};

export type CurrentUserInspectResponse =
  CurrentUserInspectResponses[keyof CurrentUserInspectResponses];

export type CurrentUserEndpointAuthorizationsInspectData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    endpointID: number;
  };
  query?: never;
  url: '/users/me/auth/{endpointID}';
};

export type CurrentUserEndpointAuthorizationsInspectErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * User not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type CurrentUserEndpointAuthorizationsInspectResponses = {
  /**
   * Success
   */
  200: UsersCurrentUserEndpointAuthResponse;
};

export type CurrentUserEndpointAuthorizationsInspectResponse =
  CurrentUserEndpointAuthorizationsInspectResponses[keyof CurrentUserEndpointAuthorizationsInspectResponses];

export type CurrentUserEndpointNamespaceAuthorizationsInspectData = {
  body?: never;
  path: {
    /**
     * Environment identifier
     */
    endpointID: number;
  };
  query?: never;
  url: '/users/me/auth/{endpointID}/namespaces';
};

export type CurrentUserEndpointNamespaceAuthorizationsInspectErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Environment not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type CurrentUserEndpointNamespaceAuthorizationsInspectResponses = {
  /**
   * Success
   */
  200: UsersCurrentUserEndpointNamespaceAuthResponse;
};

export type CurrentUserEndpointNamespaceAuthorizationsInspectResponse =
  CurrentUserEndpointNamespaceAuthorizationsInspectResponses[keyof CurrentUserEndpointNamespaceAuthorizationsInspectResponses];

export type WebhookListData = {
  body?: never;
  path?: never;
  query?: {
    /**
     * Filters (json-string)
     */
    filters?: string;
  };
  url: '/webhooks';
};

export type WebhookListErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
};

export type WebhookListResponses = {
  /**
   * OK
   */
  200: Array<PortainerWebhook>;
};

export type WebhookListResponse =
  WebhookListResponses[keyof WebhookListResponses];

export type WebhookCreateData = {
  /**
   * Webhook data
   */
  body: WebhooksWebhookCreatePayload;
  path?: never;
  query?: never;
  url: '/webhooks';
};

export type WebhookCreateErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * A webhook for this resource already exists
   */
  409: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type WebhookCreateResponses = {
  /**
   * OK
   */
  200: PortainerWebhook;
};

export type WebhookCreateResponse =
  WebhookCreateResponses[keyof WebhookCreateResponses];

export type WebhookDeleteData = {
  body?: never;
  path: {
    /**
     * Webhook id
     */
    id: number;
  };
  query?: never;
  url: '/webhooks/{id}';
};

export type WebhookDeleteErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
};

export type WebhookDeleteResponses = {
  /**
   * Webhook deleted
   */
  202: unknown;
};

export type WebhookExecuteData = {
  body?: never;
  path: {
    /**
     * Webhook token
     */
    id: string;
  };
  query?: never;
  url: '/webhooks/{id}';
};

export type WebhookExecuteErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
};

export type WebhookExecuteResponses = {
  /**
   * Webhook executed
   */
  202: unknown;
};

export type WebhookUpdateData = {
  /**
   * Webhook data
   */
  body: WebhooksWebhookUpdatePayload;
  path: {
    /**
     * Webhook id
     */
    id: number;
  };
  query?: never;
  url: '/webhooks/{id}';
};

export type WebhookUpdateErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Conflict
   */
  409: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
};

export type WebhookUpdateResponses = {
  /**
   * OK
   */
  200: PortainerWebhook;
};

export type WebhookUpdateResponse =
  WebhookUpdateResponses[keyof WebhookUpdateResponses];

export type WebhookReassignData = {
  /**
   * Webhook data
   */
  body: WebhooksWebhookReassignPayload;
  path: {
    /**
     * Webhook id
     */
    id: number;
  };
  query?: never;
  url: '/webhooks/{id}/reassign';
};

export type WebhookReassignErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Not Found
   */
  404: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
};

export type WebhookReassignResponses = {
  /**
   * OK
   */
  200: PortainerWebhook;
  /**
   * No Content
   */
  204: void;
};

export type WebhookReassignResponse =
  WebhookReassignResponses[keyof WebhookReassignResponses];

export type WebsocketAttachData = {
  body?: never;
  path?: never;
  query: {
    /**
     * environment(endpoint) ID of the environment(endpoint) where the resource is located
     */
    endpointId: number;
    /**
     * node name
     */
    nodeName?: string;
  };
  url: '/websocket/attach';
};

export type WebsocketAttachErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Forbidden
   */
  403: unknown;
  /**
   * Not Found
   */
  404: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
};

export type WebsocketAttachResponses = {
  /**
   * OK
   */
  200: unknown;
};

export type WebsocketExecData = {
  body?: never;
  path?: never;
  query: {
    /**
     * environment(endpoint) ID of the environment(endpoint) where the resource is located
     */
    endpointId: number;
    /**
     * node name
     */
    nodeName?: string;
  };
  url: '/websocket/exec';
};

export type WebsocketExecErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Conflict
   */
  409: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
};

export type WebsocketExecResponses = {
  /**
   * OK
   */
  200: unknown;
};

export type WebsocketKubernetesShellData = {
  body?: never;
  path?: never;
  query: {
    /**
     * environment(endpoint) ID of the environment(endpoint) where the resource is located
     */
    endpointId: number;
  };
  url: '/websocket/kubernetes-shell';
};

export type WebsocketKubernetesShellErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Environment not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type WebsocketKubernetesShellResponses = {
  /**
   * Success
   */
  200: unknown;
};

export type WebsocketNodeShellExecData = {
  body?: never;
  path?: never;
  query: {
    /**
     * environment(endpoint) ID
     */
    endpointId: number;
    /**
     * name of the Kubernetes node to open a shell on
     */
    nodeName: string;
  };
  url: '/websocket/node-shell';
};

export type WebsocketNodeShellExecErrors = {
  /**
   * Invalid request
   */
  400: unknown;
  /**
   * Permission denied
   */
  403: unknown;
  /**
   * Environment not found
   */
  404: unknown;
  /**
   * Server error
   */
  500: unknown;
};

export type WebsocketNodeShellExecResponses = {
  /**
   * Success
   */
  200: unknown;
};

export type WebsocketPodExecData = {
  body?: never;
  path?: never;
  query: {
    /**
     * environment(endpoint) ID of the environment(endpoint) where the resource is located
     */
    endpointId: number;
    /**
     * namespace where the container is located
     */
    namespace: string;
    /**
     * name of the pod containing the container
     */
    podName: string;
    /**
     * name of the container
     */
    containerName: string;
    /**
     * command to execute in the container
     */
    command: string;
  };
  url: '/websocket/pod';
};

export type WebsocketPodExecErrors = {
  /**
   * Bad Request
   */
  400: unknown;
  /**
   * Forbidden
   */
  403: unknown;
  /**
   * Not Found
   */
  404: unknown;
  /**
   * Internal Server Error
   */
  500: unknown;
};

export type WebsocketPodExecResponses = {
  /**
   * OK
   */
  200: unknown;
};
